import * as THREE from 'three';
import { Vector3, Spherical, Box3, MathUtils, DataTextureLoader, HalfFloatType, DataUtils, LinearSRGBColorSpace, FloatType, RGBAFormat, RedFormat, NoColorSpace, LinearFilter, Ray as Ray$1, Plane, EventDispatcher, MOUSE, TOUCH, Quaternion, Vector2 } from 'three';
import * as fflate from 'fflate';

/**
 * AbortablePromise: A quick & dirty wrapper for JavaScript's Promise class that allows the underlying
 * asynchronous operation to be cancelled. It is only meant for simple situations where no complex promise
 * chaining or merging occurs. It needs a significant amount of work to truly replicate the full
 * functionality of JavaScript's Promise class. Look at Util.fetchWithProgress() for example usage.
 *
 * This class was primarily added to allow splat scene downloads to be cancelled. It has not been tested
 * very thoroughly and the implementation is kinda janky. If you can at all help it, please avoid using it :)
 */
class AbortablePromise {
    static idGen = 0;

    constructor(promiseFunc, abortHandler) {
        let resolver;
        let rejecter;
        this.promise = new Promise((resolve, reject) => {
            resolver = resolve;
            rejecter = reject;
        });

        const promiseResolve = resolver.bind(this);
        const promiseReject = rejecter.bind(this);

        const resolve = (...args) => {
            promiseResolve(...args);
        };

        const reject = (error) => {
            promiseReject(error);
        };

        promiseFunc(resolve.bind(this), reject.bind(this));
        this.abortHandler = abortHandler;
        this.id = AbortablePromise.idGen++;
    }

    then(onResolve, onReject) {
        return new AbortablePromise((resolve, reject) => {
            this.promise = this.promise
                .then((...args) => {
                    const onResolveResult = onResolve(...args);
                    if (onResolveResult instanceof Promise || onResolveResult instanceof AbortablePromise) {
                        onResolveResult.then((...args2) => {
                            resolve(...args2);
                        });
                    } else {
                        resolve(onResolveResult);
                    }
                })
                .catch((error) => {
                    if (onReject) onReject(error);
                    reject(error);
                });
        }, this.abortHandler);
    }

    catch(onFail) {
        return new AbortablePromise((resolve) => {
            this.promise = this.promise
                .then((...args) => {
                    resolve(...args);
                })
                .catch(onFail);
        }, this.abortHandler);
    }

    abort(reason) {
        if (this.abortHandler) this.abortHandler(reason);
    }
}

class AbortedPromiseError extends Error {
    constructor(msg) {
        super(msg);
    }
}

const floatToHalf = (function () {
    const floatView = new Float32Array(1);
    const int32View = new Int32Array(floatView.buffer);

    return function (val) {
        floatView[0] = val;
        const x = int32View[0];

        let bits = (x >> 16) & 0x8000;
        let m = (x >> 12) & 0x07ff;
        const e = (x >> 23) & 0xff;

        if (e < 103) return bits;

        if (e > 142) {
            bits |= 0x7c00;
            bits |= (e == 255 ? 0 : 1) && x & 0x007fffff;
            return bits;
        }

        if (e < 113) {
            m |= 0x0800;
            bits |= (m >> (114 - e)) + ((m >> (113 - e)) & 1);
            return bits;
        }

        bits |= ((e - 112) << 10) | (m >> 1);
        bits += m & 1;
        return bits;
    };
})();

const uintEncodedFloat = (function () {
    const floatView = new Float32Array(1);
    const int32View = new Int32Array(floatView.buffer);

    return function (f) {
        floatView[0] = f;
        return int32View[0];
    };
})();

const rgbaToInteger = function (r, g, b, a) {
    return r + (g << 8) + (b << 16) + (a << 24);
};

const rgbaArrayToInteger = function (arr, offset) {
    return arr[offset] + (arr[offset + 1] << 8) + (arr[offset + 2] << 16) + (arr[offset + 3] << 24);
};

const fetchWithProgress = function (path, onProgress, saveChunks = true, headers) {
    const abortController = new AbortController();
    const signal = abortController.signal;
    let aborted = false;
    let activeReader = null;
    const abortHandler = (reason) => {
        aborted = true;
        // Cancel the stream reader first to avoid a stray BodyStreamBuffer rejection,
        // then abort the controller (needed if the fetch hasn't resolved yet).
        if (activeReader) {
            activeReader.cancel(reason).catch(() => {});
        }
        abortController.abort(reason);
    };

    return new AbortablePromise((resolve, reject) => {
        const fetchOptions = { signal };
        if (headers) fetchOptions.headers = headers;
        fetch(path, fetchOptions)
            .then(async (data) => {
                // Handle error conditions where data is still returned
                if (!data.ok) {
                    const errorText = await data.text();
                    reject(new Error(`Fetch failed: ${data.status} ${data.statusText} ${errorText}`));
                    return;
                }

                const reader = data.body.getReader();
                activeReader = reader;
                let bytesDownloaded = 0;
                let _fileSize = data.headers.get('Content-Length');
                let fileSize = _fileSize ? parseInt(_fileSize) : undefined;

                const chunks = [];

                while (!aborted) {
                    try {
                        const { value: chunk, done } = await reader.read();
                        if (done) {
                            if (aborted) {
                                reject(new AbortedPromiseError('Fetch aborted'));
                                return;
                            }
                            if (onProgress) {
                                onProgress(100, '100%', chunk, fileSize);
                            }
                            if (saveChunks) {
                                const buffer = new Blob(chunks).arrayBuffer();
                                resolve(buffer);
                            } else {
                                resolve();
                            }
                            break;
                        }
                        bytesDownloaded += chunk.length;
                        let percent;
                        let percentLabel;
                        if (fileSize !== undefined) {
                            percent = (bytesDownloaded / fileSize) * 100;
                            percentLabel = `${percent.toFixed(2)}%`;
                        }
                        if (saveChunks) {
                            chunks.push(chunk);
                        }
                        if (onProgress) {
                            onProgress(percent, percentLabel, chunk, fileSize);
                        }
                    } catch (error) {
                        reject(error);
                        return;
                    }
                }
            })
            .catch((error) => {
                reject(new AbortedPromiseError(error));
            });
    }, abortHandler);
};

const clamp = function (val, min, max) {
    return Math.max(Math.min(val, max), min);
};

const getCurrentTime = function () {
    return performance.now() / 1000;
};

const disposeAllMeshes = (object3D) => {
    if (object3D.geometry) {
        object3D.geometry.dispose();
        object3D.geometry = null;
    }
    if (object3D.material) {
        object3D.material.dispose();
        object3D.material = null;
    }
    if (object3D.children) {
        for (let child of object3D.children) {
            disposeAllMeshes(child);
        }
    }
};

const delayedExecute = (func, fast) => {
    return new Promise((resolve) => {
        window.setTimeout(
            () => {
                resolve(func());
            },
            fast ? 1 : 50
        );
    });
};

const getSphericalHarmonicsComponentCountForDegree = (sphericalHarmonicsDegree = 0) => {
    switch (sphericalHarmonicsDegree) {
        case 1:
            return 9;
        case 2:
            return 24;
    }
    return 0;
};

const nativePromiseWithExtractedComponents = () => {
    let resolver;
    let rejecter;
    const promise = new Promise((resolve, reject) => {
        resolver = resolve;
        rejecter = reject;
    });
    return {
        promise: promise,
        resolve: resolver,
        reject: rejecter
    };
};

const abortablePromiseWithExtractedComponents = (abortHandler) => {
    let resolver;
    let rejecter;
    if (!abortHandler) {
        abortHandler = () => {};
    }
    const promise = new AbortablePromise((resolve, reject) => {
        resolver = resolve;
        rejecter = reject;
    }, abortHandler);
    return {
        promise: promise,
        resolve: resolver,
        reject: rejecter
    };
};

class Semver {
    constructor(major, minor, patch) {
        this.major = major;
        this.minor = minor;
        this.patch = patch;
    }

    toString() {
        return `${this.major}_${this.minor}_${this.patch}`;
    }
}

function isIOS() {
    const ua = navigator.userAgent;
    return ua.indexOf('iPhone') > 0 || ua.indexOf('iPad') > 0;
}

function getIOSSemever() {
    if (isIOS()) {
        const extract = navigator.userAgent.match(/OS (\d+)_(\d+)_?(\d+)?/);
        return new Semver(parseInt(extract[1] || 0, 10), parseInt(extract[2] || 0, 10), parseInt(extract[3] || 0, 10));
    } else {
        return null; // or [0,0,0]
    }
}

/**
 * SplatArray: Lightweight container for splat data with separate typed arrays.
 *
 * Data Layout (separate arrays for efficient texture creation):
 * - means: Float32Array [x0,y0,z0, x1,y1,z1, ...] - 3 floats per splat
 * - scales: Float32Array [sx0,sy0,sz0, sx1,sy1,sz1, ...] - 3 floats per splat
 * - quaternions: Float32Array [w0,x0,y0,z0, w1,x1,y1,z1, ...] - 4 floats per splat
 * - colors: Uint8Array [r0,g0,b0,a0, r1,g1,b1,a1, ...] - 4 bytes per splat
 * - sphericalHarmonics: Uint16Array (half-floats) - shComponentCount per splat
 *
 * Target texture compression:
 * - means: Float32
 * - scales: Float32
 * - quaternions: Float32
 * - colors: Uint8
 * - sphericalHarmonics: Float16 (Uint16)
 */
class SplatArray {
    static ModelType = {
        GS3D: 0,
        GS2D: 1
    };

    /**
     * Create a SplatArray from separate typed arrays.
     * @param {Object} data - Object containing typed arrays and metadata
     * @param {number} data.splatCount - Number of splats
     * @param {number} data.sphericalHarmonicsDegree - SH degree (0, 1, or 2)
     * @param {number} data.modelType - Model type (0=GS3D, 1=GS2D)
     * @param {Float32Array} data.means - Position data (3 floats per splat)
     * @param {Float32Array} data.scales - Scale data (3 floats per splat)
     * @param {Float32Array} data.quaternions - Quaternion data (4 floats per splat)
     * @param {Uint8Array} data.colors - Color data (4 bytes per splat, RGBA)
     * @param {Uint16Array} [data.sphericalHarmonics] - SH data (half-floats)
     * @param {Object} [data.min] - Bounding box min {x, y, z}
     * @param {Object} [data.max] - Bounding box max {x, y, z}
     * @param {Object} [data.sceneCenter] - Scene center {x, y, z}
     */
    constructor(data) {
        this.splatCount = data.splatCount;
        this.sphericalHarmonicsDegree = data.sphericalHarmonicsDegree;
        this.modelType = data.modelType;

        // Typed arrays
        this.means = data.means;
        this.scales = data.scales;
        this.quaternions = data.quaternions;
        this.colors = data.colors;
        this.sphericalHarmonics = data.sphericalHarmonics || null;

        // Compute SH component count
        this.shComponentCount = getSphericalHarmonicsComponentCountForDegree(this.sphericalHarmonicsDegree);

        // Compression level (0 = uncompressed Float32, for compatibility)
        this.compressionLevel = 0;

        // SH compression range (default values for Float16 SH)
        this.minSphericalHarmonicsCoeff = data.minSphericalHarmonicsCoeff ?? -2.0;
        this.maxSphericalHarmonicsCoeff = data.maxSphericalHarmonicsCoeff ?? 2.0;

        // Bounding box (computed during parsing or lazily here)
        if (data.min && data.max) {
            this.min = data.min;
            this.max = data.max;
        } else {
            const bbox = this.computeBoundingBox();
            this.min = bbox.min;
            this.max = bbox.max;
        }

        // Scene center
        if (data.sceneCenter) {
            this.sceneCenter = new THREE.Vector3(data.sceneCenter.x, data.sceneCenter.y, data.sceneCenter.z);
        } else {
            this.sceneCenter = new THREE.Vector3(
                (this.min.x + this.max.x) / 2,
                (this.min.y + this.max.y) / 2,
                (this.min.z + this.max.z) / 2
            );
        }
    }

    // ========== Accessors ==========

    getSplatCount() {
        return this.splatCount;
    }

    getMaxSplatCount() {
        return this.splatCount;
    }

    getSphericalHarmonicsDegree() {
        return this.sphericalHarmonicsDegree;
    }

    getMinSphericalHarmonicsDegree() {
        return this.sphericalHarmonicsDegree;
    }

    getModelType() {
        return this.modelType;
    }

    hasSphericalHarmonics() {
        return this.sphericalHarmonicsDegree > 0 && this.sphericalHarmonics !== null;
    }

    is2DModel() {
        return this.modelType === SplatArray.ModelType.GS2D;
    }

    // ========== Per-Splat Accessors (for SplatTree, Raycaster) ==========

    /**
     * Get a single splat's center position.
     * @param {number} index - Splat index
     * @param {THREE.Vector3} outCenter - Output vector
     * @param {THREE.Matrix4} [transform] - Optional transform to apply
     * @returns {THREE.Vector3}
     */
    getSplatCenter(index, outCenter, transform) {
        const base = index * 3;
        outCenter.set(this.means[base], this.means[base + 1], this.means[base + 2]);
        if (transform) {
            outCenter.applyMatrix4(transform);
        }
        return outCenter;
    }

    /**
     * Get a single splat's scale.
     * @param {number} index - Splat index
     * @param {THREE.Vector3} outScale - Output vector
     * @param {THREE.Matrix4} [transform] - Optional transform (scale component extracted)
     * @returns {THREE.Vector3}
     */
    getSplatScale(index, outScale, transform) {
        const base = index * 3;
        outScale.set(this.scales[base], this.scales[base + 1], this.scales[base + 2]);
        if (transform) {
            // Extract scale from transform matrix and apply
            const sx = Math.sqrt(
                transform.elements[0] * transform.elements[0] +
                    transform.elements[1] * transform.elements[1] +
                    transform.elements[2] * transform.elements[2]
            );
            const sy = Math.sqrt(
                transform.elements[4] * transform.elements[4] +
                    transform.elements[5] * transform.elements[5] +
                    transform.elements[6] * transform.elements[6]
            );
            const sz = Math.sqrt(
                transform.elements[8] * transform.elements[8] +
                    transform.elements[9] * transform.elements[9] +
                    transform.elements[10] * transform.elements[10]
            );
            outScale.set(outScale.x * sx, outScale.y * sy, outScale.z * sz);
        }
        return outScale;
    }

    /**
     * Get a single splat's quaternion rotation.
     * @param {number} index - Splat index
     * @param {THREE.Quaternion} outQuaternion - Output quaternion
     * @param {THREE.Matrix4} [transform] - Optional transform (rotation component extracted and applied)
     * @returns {THREE.Quaternion}
     */
    getSplatRotation(index, outQuaternion, transform) {
        const base = index * 4;
        outQuaternion.set(
            this.quaternions[base + 1], // x
            this.quaternions[base + 2], // y
            this.quaternions[base + 3], // z
            this.quaternions[base] // w
        );
        if (transform) {
            const transformQuat = new THREE.Quaternion();
            transformQuat.setFromRotationMatrix(transform);
            outQuaternion.premultiply(transformQuat);
        }
        return outQuaternion;
    }

    /**
     * Get a single splat's color (RGBA).
     * @param {number} index - Splat index
     * @param {THREE.Vector4} outColor - Output color vector (RGBA, 0-1 range for RGB, 0-255 for alpha)
     * @returns {THREE.Vector4}
     */
    getSplatColor(index, outColor) {
        const base = index * 4;
        outColor.set(
            this.colors[base] / 255,
            this.colors[base + 1] / 255,
            this.colors[base + 2] / 255,
            this.colors[base + 3] // Alpha as 0-255 for compatibility
        );
        return outColor;
    }

    /**
     * Get a single splat's opacity (alpha).
     * @param {number} index - Splat index
     * @returns {number} Opacity in 0-1 range
     */
    getSplatOpacity(index) {
        return this.colors[index * 4 + 3] / 255;
    }

    // ========== Bounding Box ==========

    /**
     * Compute bounding box from means array.
     * @returns {{min: {x, y, z}, max: {x, y, z}}}
     */
    computeBoundingBox() {
        const min = { x: Infinity, y: Infinity, z: Infinity };
        const max = { x: -Infinity, y: -Infinity, z: -Infinity };

        for (let i = 0; i < this.splatCount; i++) {
            const base = i * 3;
            const x = this.means[base];
            const y = this.means[base + 1];
            const z = this.means[base + 2];

            if (x < min.x) min.x = x;
            if (y < min.y) min.y = y;
            if (z < min.z) min.z = z;
            if (x > max.x) max.x = x;
            if (y > max.y) max.y = y;
            if (z > max.z) max.z = z;
        }

        return { min, max };
    }

    // ========== Bulk Data Extraction (for SplatMesh texture filling) ==========

    /**
     * Fill output arrays with splat data, optionally applying a transform.
     * This is the main method for extracting data for GPU textures.
     *
     * @param {Float32Array} outCenters - Output centers array (3 floats per splat)
     * @param {Float32Array} outScales - Output scales array (3 floats per splat)
     * @param {Float32Array} outRotations - Output rotations array (4 floats per splat)
     * @param {Uint8Array} outColors - Output colors array (4 bytes per splat)
     * @param {Uint16Array} outSphericalHarmonics - Output SH array (half-floats)
     * @param {Float32Array} outCentersPadded - Output padded centers (4 floats per splat)
     * @param {THREE.Vector3} sceneCenter - Scene center for distance calculation
     * @param {THREE.Matrix4} transform - Optional transform to apply
     * @param {number} srcStart - Start index in source data
     * @param {number} srcEnd - End index in source data
     * @param {number} destStart - Start index in destination arrays
     * @returns {number} Maximum distance from scene center
     */
    fillUnifiedSplatData = (function () {
        const tempCenter = new THREE.Vector3();
        const tempScale = new THREE.Vector3();
        const tempRotation = new THREE.Quaternion();

        return function (
            outCenters,
            outScales,
            outRotations,
            outColors,
            outSphericalHarmonics,
            outCentersPadded,
            sceneCenter,
            transform,
            srcStart,
            srcEnd,
            destStart = 0
        ) {
            const start = srcStart !== undefined && srcStart !== null ? srcStart : 0;
            const end = srcEnd !== undefined && srcEnd !== null ? srcEnd : this.splatCount - 1;
            const count = end - start + 1;

            let maxDistanceFromCenter = 0;

            // Extract transform components if provided
            let transformRotation = null;
            let transformScale = null;
            if (transform) {
                transformRotation = new THREE.Quaternion();
                transformRotation.setFromRotationMatrix(transform);
                transformScale = new THREE.Vector3();
                transformScale.setFromMatrixScale(transform);
            }

            for (let i = 0; i < count; i++) {
                const srcIdx = start + i;
                const destIdx = destStart + i;

                // Centers
                if (outCenters || outCentersPadded) {
                    const srcBase = srcIdx * 3;
                    tempCenter.set(this.means[srcBase], this.means[srcBase + 1], this.means[srcBase + 2]);

                    if (transform) {
                        tempCenter.applyMatrix4(transform);
                    }

                    if (outCenters) {
                        const destBase = destIdx * 3;
                        outCenters[destBase] = tempCenter.x;
                        outCenters[destBase + 1] = tempCenter.y;
                        outCenters[destBase + 2] = tempCenter.z;
                    }

                    if (outCentersPadded) {
                        const destBase = destIdx * 4;
                        outCentersPadded[destBase] = tempCenter.x;
                        outCentersPadded[destBase + 1] = tempCenter.y;
                        outCentersPadded[destBase + 2] = tempCenter.z;
                        outCentersPadded[destBase + 3] = 1.0;
                    }

                    // Calculate distance from scene center
                    if (sceneCenter) {
                        const dist = tempCenter.distanceTo(sceneCenter);
                        if (dist > maxDistanceFromCenter) {
                            maxDistanceFromCenter = dist;
                        }
                    }
                }

                // Scales
                if (outScales) {
                    const srcBase = srcIdx * 3;
                    const destBase = destIdx * 3;

                    tempScale.set(this.scales[srcBase], this.scales[srcBase + 1], this.scales[srcBase + 2]);

                    if (transformScale) {
                        tempScale.multiply(transformScale);
                    }

                    outScales[destBase] = tempScale.x;
                    outScales[destBase + 1] = tempScale.y;
                    outScales[destBase + 2] = tempScale.z;
                }

                // Rotations (stored as w,x,y,z in SplatArray)
                if (outRotations) {
                    const srcBase = srcIdx * 4;
                    const destBase = destIdx * 4;

                    // SplatArray stores as [w, x, y, z], output expects [x, y, z, w]
                    tempRotation.set(
                        this.quaternions[srcBase + 1], // x
                        this.quaternions[srcBase + 2], // y
                        this.quaternions[srcBase + 3], // z
                        this.quaternions[srcBase] // w
                    );

                    if (transformRotation) {
                        tempRotation.premultiply(transformRotation);
                    }

                    // Ensure w is positive (convention for quaternions)
                    if (tempRotation.w < 0) {
                        tempRotation.x = -tempRotation.x;
                        tempRotation.y = -tempRotation.y;
                        tempRotation.z = -tempRotation.z;
                        tempRotation.w = -tempRotation.w;
                    }

                    outRotations[destBase] = tempRotation.x;
                    outRotations[destBase + 1] = tempRotation.y;
                    outRotations[destBase + 2] = tempRotation.z;
                    outRotations[destBase + 3] = tempRotation.w;
                }

                // Colors (direct copy, no transform)
                if (outColors) {
                    const srcBase = srcIdx * 4;
                    const destBase = destIdx * 4;
                    outColors[destBase] = this.colors[srcBase];
                    outColors[destBase + 1] = this.colors[srcBase + 1];
                    outColors[destBase + 2] = this.colors[srcBase + 2];
                    outColors[destBase + 3] = this.colors[srcBase + 3];
                }

                // Spherical Harmonics (direct copy, already in half-float format)
                if (outSphericalHarmonics && this.sphericalHarmonics) {
                    const srcBase = srcIdx * this.shComponentCount;
                    const destBase = destIdx * this.shComponentCount;
                    for (let s = 0; s < this.shComponentCount; s++) {
                        outSphericalHarmonics[destBase + s] = this.sphericalHarmonics[srcBase + s];
                    }
                }
            }

            return maxDistanceFromCenter;
        };
    })();

    /**
     * Compute and fill covariance array from scales and rotations.
     * Covariance matrix is computed as: C = R * S * S^T * R^T
     *
     * @param {Float32Array} outCovariances - Output covariances array (6 floats per splat - upper triangle)
     * @param {THREE.Matrix4} transform - Optional transform to apply
     * @param {number} srcStart - Start index in source data
     * @param {number} srcEnd - End index in source data
     * @param {number} destStart - Start index in destination array
     * @param {number} compressionLevel - Output compression level (0=Float32, 1+=Uint16 half-float)
     */
    fillSplatCovarianceArray = (function () {
        const tempScale = new THREE.Vector3();
        const tempRotation = new THREE.Quaternion();
        const rotationMatrix = new THREE.Matrix4();

        return function (outCovariances, transform, srcStart, srcEnd, destStart = 0, compressionLevel = 0) {
            const start = srcStart !== undefined && srcStart !== null ? srcStart : 0;
            const end = srcEnd !== undefined && srcEnd !== null ? srcEnd : this.splatCount - 1;
            const count = end - start + 1;

            // Extract transform components if provided
            let transformRotation = null;
            let transformScale = null;
            if (transform) {
                transformRotation = new THREE.Quaternion();
                transformRotation.setFromRotationMatrix(transform);
                transformScale = new THREE.Vector3();
                transformScale.setFromMatrixScale(transform);
            }

            for (let i = 0; i < count; i++) {
                const srcIdx = start + i;
                const destIdx = destStart + i;

                // Get scale
                const scaleBase = srcIdx * 3;
                tempScale.set(this.scales[scaleBase], this.scales[scaleBase + 1], this.scales[scaleBase + 2]);

                if (transformScale) {
                    tempScale.multiply(transformScale);
                }

                // Get rotation (SplatArray stores as [w, x, y, z])
                const rotBase = srcIdx * 4;
                tempRotation.set(
                    this.quaternions[rotBase + 1], // x
                    this.quaternions[rotBase + 2], // y
                    this.quaternions[rotBase + 3], // z
                    this.quaternions[rotBase] // w
                );

                if (transformRotation) {
                    tempRotation.premultiply(transformRotation);
                }

                // Compute covariance: C = R * S * S^T * R^T
                // Since S is diagonal, S * S^T = diag(sx^2, sy^2, sz^2)
                rotationMatrix.makeRotationFromQuaternion(tempRotation);
                const R = rotationMatrix.elements;

                const sx2 = tempScale.x * tempScale.x;
                const sy2 = tempScale.y * tempScale.y;
                const sz2 = tempScale.z * tempScale.z;

                // Covariance matrix elements (symmetric, so we only need 6 values)
                // C = R * diag(sx^2, sy^2, sz^2) * R^T
                const cov00 = R[0] * R[0] * sx2 + R[4] * R[4] * sy2 + R[8] * R[8] * sz2;
                const cov01 = R[0] * R[1] * sx2 + R[4] * R[5] * sy2 + R[8] * R[9] * sz2;
                const cov02 = R[0] * R[2] * sx2 + R[4] * R[6] * sy2 + R[8] * R[10] * sz2;
                const cov11 = R[1] * R[1] * sx2 + R[5] * R[5] * sy2 + R[9] * R[9] * sz2;
                const cov12 = R[1] * R[2] * sx2 + R[5] * R[6] * sy2 + R[9] * R[10] * sz2;
                const cov22 = R[2] * R[2] * sx2 + R[6] * R[6] * sy2 + R[10] * R[10] * sz2;

                const destBase = destIdx * 6;
                if (compressionLevel >= 1) {
                    // Output as half-floats (Uint16)
                    outCovariances[destBase] = floatToHalf(cov00);
                    outCovariances[destBase + 1] = floatToHalf(cov01);
                    outCovariances[destBase + 2] = floatToHalf(cov02);
                    outCovariances[destBase + 3] = floatToHalf(cov11);
                    outCovariances[destBase + 4] = floatToHalf(cov12);
                    outCovariances[destBase + 5] = floatToHalf(cov22);
                } else {
                    // Output as Float32
                    outCovariances[destBase] = cov00;
                    outCovariances[destBase + 1] = cov01;
                    outCovariances[destBase + 2] = cov02;
                    outCovariances[destBase + 3] = cov11;
                    outCovariances[destBase + 4] = cov12;
                    outCovariances[destBase + 5] = cov22;
                }
            }
        };
    })();

    /**
     * Get scale and rotation for a single splat (compatibility method).
     * @param {number} index - Splat index
     * @param {THREE.Vector3} outScale - Output scale vector
     * @param {THREE.Quaternion} outRotation - Output quaternion
     * @param {THREE.Matrix4} transform - Optional transform to apply
     */
    getSplatScaleAndRotation(index, outScale, outRotation, transform) {
        this.getSplatScale(index, outScale, transform);
        this.getSplatRotation(index, outRotation, transform);
    }

    // ========== Static Factory Methods ==========

    /**
     * Create a SplatArray from transferred worker data.
     * Reconstructs typed arrays from ArrayBuffers.
     *
     * @param {Object} data - Transferred data from worker
     * @returns {SplatArray}
     */
    static createFromTransferredData(data) {
        return new SplatArray({
            splatCount: data.splatCount,
            sphericalHarmonicsDegree: data.sphericalHarmonicsDegree,
            modelType: data.modelType,
            means: new Float32Array(data.means),
            scales: new Float32Array(data.scales),
            quaternions: new Float32Array(data.quaternions),
            colors: new Uint8Array(data.colors),
            sphericalHarmonics: data.sphericalHarmonics ? new Uint16Array(data.sphericalHarmonics) : null,
            min: data.min,
            max: data.max,
            sceneCenter: data.sceneCenter
        });
    }

    /**
     * Get transferable buffers for postMessage.
     * @returns {ArrayBuffer[]}
     */
    getTransferableBuffers() {
        const buffers = [this.means.buffer, this.scales.buffer, this.quaternions.buffer, this.colors.buffer];
        if (this.sphericalHarmonics) {
            buffers.push(this.sphericalHarmonics.buffer);
        }
        return buffers;
    }

    /**
     * Convert to plain object for worker transfer.
     * @returns {Object}
     */
    toTransferableData() {
        return {
            splatCount: this.splatCount,
            sphericalHarmonicsDegree: this.sphericalHarmonicsDegree,
            modelType: this.modelType,
            means: this.means,
            scales: this.scales,
            quaternions: this.quaternions,
            colors: this.colors,
            sphericalHarmonics: this.sphericalHarmonics,
            min: this.min,
            max: this.max,
            sceneCenter: { x: this.sceneCenter.x, y: this.sceneCenter.y, z: this.sceneCenter.z }
        };
    }
}

var ParseWorkerCode = "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";

/**
 * ParseWorker: Web Worker for parsing GAUS files and generating SplatArray data off the main thread.
 *
 * This module creates a worker from a pre-bundled and minified script. The worker code
 * is bundled separately at build time, which ensures compatibility with minification.
 */


/**
 * Creates a new parse worker instance.
 *
 * @returns {Worker} A web worker ready to parse GAUS files
 *
 * @example
 * const worker = createParseWorker();
 *
 * worker.onmessage = (e) => {
 *     if (e.data.status === 'ready') {
 *         // Worker initialized, send parse request
 *         worker.postMessage({
 *             task: 'parse',
 *             arrayBuffer: fileBuffer
 *         }, [fileBuffer]);
 *     } else if (e.data.status === 'success') {
 *         const { splatArrayData, metadata } = e.data;
 *         console.log(`Parsed ${metadata.splatCount} splats`);
 *     } else if (e.data.status === 'error') {
 *         console.error('Parse error:', e.data.error.message);
 *     }
 * };
 *
 * // Initialize the worker
 * worker.postMessage({ task: 'init' });
 */
function createParseWorker() {
    // Decode the base64-encoded worker code
    const workerCode = atob(ParseWorkerCode);

    // Create a blob URL and instantiate the worker
    const blob = new Blob([workerCode], { type: 'application/javascript' });
    const worker = new Worker(URL.createObjectURL(blob));

    return worker;
}

class Constants {
    static DefaultSplatSortDistanceMapPrecision = 24;
    static MemoryPageSize = 65536;
    static BytesPerFloat = 4;
    static BytesPerInt = 4;
    static MaxScenes = 32;
    static ProgressiveLoadSectionSize = 262144;
    static ProgressiveLoadSectionDelayDuration = 15;
    static SphericalHarmonics8BitCompressionRange = 3;

    // ETA-based segment loading
    static SegmentLoadETAThreshold = 10.0; // seconds — load segments reachable within this time
    static CameraVelocitySmoothingFactor = 0.15; // EMA alpha (lower = smoother)

    // Performance logging - enable locally: Constants.PERF_LOG = true
    static PERF_LOG = false;
}

/**
 * GausLoader - Loads GAUS files using a Web Worker for off-main-thread parsing.
 *
 * This loader delegates all parsing to a ParseWorker and returns a SplatArray
 * with separate typed arrays for efficient texture creation.
 */
class GausLoader {
    constructor() {
        this.data = null;
    }

    /**
     * Load and parse GAUS file data using a Web Worker.
     * @param {ArrayBuffer} splatFileData The raw file data
     * @returns {Promise<SplatArray>} The parsed SplatArray
     */
    static loadFromFileData(splatFileData) {
        return new Promise((resolve, reject) => {
            let worker = null;

            try {
                worker = createParseWorker();

                worker.onmessage = (e) => {
                    if (e.data.status === 'ready') {
                        // Worker is ready, send parse request
                        worker.postMessage(
                            {
                                task: 'parse',
                                arrayBuffer: splatFileData
                            },
                            [splatFileData] // Transfer ownership
                        );
                    } else if (e.data.status === 'success') {
                        const { splatArrayData, metadata } = e.data;

                        if (Constants.PERF_LOG) {
                            console.log(
                                `GausLoader: parse=${metadata.parseTimeMs.toFixed(1)}ms, ` +
                                    `convert=${metadata.convertTimeMs.toFixed(1)}ms, ` +
                                    `splats=${metadata.splatCount}`
                            );
                        }

                        // Create SplatArray from the transferred data
                        const splatArray = SplatArray.createFromTransferredData(splatArrayData);

                        // Clean up worker
                        worker.terminate();
                        worker = null;

                        resolve(splatArray);
                    } else if (e.data.status === 'error') {
                        const error = new Error(e.data.error.message);
                        error.name = e.data.error.name;

                        // Clean up worker
                        if (worker) {
                            worker.terminate();
                            worker = null;
                        }

                        reject(error);
                    }
                };

                worker.onerror = (error) => {
                    console.error('GausLoader worker error:', error);

                    // Clean up worker
                    if (worker) {
                        worker.terminate();
                        worker = null;
                    }

                    reject(error);
                };

                // Initialize the worker
                worker.postMessage({ task: 'init' });
            } catch (error) {
                // Clean up worker on error
                if (worker) {
                    worker.terminate();
                    worker = null;
                }
                reject(error);
            }
        });
    }

    /**
     * Load and parse a GAUS file from a URL.
     * @param {string} url The URL to fetch
     * @param {function} onProgress Progress callback
     * @param {Object} headers Optional headers for the fetch request
     * @returns {Promise<SplatArray>} The parsed SplatArray
     */
    static loadFromURL(url, onProgress, headers) {
        return fetchWithProgress(url, onProgress, true, headers).then((splatFileData) => {
            return GausLoader.loadFromFileData(splatFileData);
        });
    }
}

class ImagePosesLoader {
    from_string(str, callback) {
        // Parse images from json buffer
        const json = JSON.parse(str);

        // Sort the array by sequence_index, frame_index, camera_index
        json.images.sort((a, b) => {
            return a.sequence_index - b.sequence_index || a.frame_index - b.frame_index || a.camera_index - b.camera_index;
        });

        // For each image from "images" array, create read the values from tvec
        const imageCameras = json.images.map((image) => {
            // Construct original rotation and translation
            const qvec = new THREE.Quaternion(image.qvec[1], image.qvec[2], image.qvec[3], image.qvec[0]);

            const tvec = new THREE.Vector3(image.tvec[0], image.tvec[1], image.tvec[2]);

            // Yugo qvec and tvec represent the inverse of the camera pose
            // Invert the rotation
            const qInv = qvec.clone().invert();

            // Invert the translation: t' = -R^T * t
            const tInv = tvec.clone().applyQuaternion(qInv).negate();

            // Extract the forward vector from the quaternion
            const rotationMatrix = new THREE.Matrix4().makeRotationFromQuaternion(qInv);
            const basis = new THREE.Matrix3().setFromMatrix4(rotationMatrix);
            const forward = new THREE.Vector3();
            basis.extractBasis(new THREE.Vector3(), new THREE.Vector3(), forward);

            // Transform the camera pose from ENU to EUN reference frame (Yugo uses Z-up, Three.js uses Y-up)
            // The same transformation is applied to the scene in SplatMesh class
            const worldTransform = new THREE.Quaternion(0.5, 0.5, 0.5, -0.5);
            // Rotate the translation and forward vectors by the world transform
            forward.applyQuaternion(worldTransform);
            const tInvT = tInv.clone().applyQuaternion(worldTransform);

            // Assign some random values to the camera. It's not yet clear whether storing these values
            // in a Three Camera object is useful.
            const camera = new THREE.PerspectiveCamera(70.0, 16.0 / 9.0, 0.1, 1000);

            // Set camera position and rotation and update the pose matrices
            camera.position.set(tInvT.x, tInvT.y, tInvT.z);
            camera.lookAt(forward);

            camera.updateMatrixWorld();

            return camera;
        });

        callback(imageCameras);
    }

    from_url(url, callback, onProgress) {
        fetchWithProgress(url, onProgress).then((buffer) => {
            const decoder = new TextDecoder('utf-8');
            const jsonString = decoder.decode(buffer);
            this.from_string(jsonString, callback);
        });
    }
}

/**
 * Enum for segment loading status.
 */
const SegmentStatus = {
    Pending: 'pending',
    Queued: 'queued',
    Loading: 'loading',
    Loaded: 'loaded',
    QueuedUnload: 'queued-unload',
    Unloading: 'unloading'
};

/**
 * Segment: Represents a lazy-loadable segment of a splat scene.
 * Contains view bounds and scaled preload/unload bounds for hysteresis.
 */
class Segment {
    /**
     * @param {number} index Segment index from manifest
     * @param {string} url URL to the segment file
     * @param {THREE.Box3} viewBoundingBox The view bounding box for this segment
     * @param {number} preloadDistance Distance in meters to expand view bounds for triggering load
     * @param {number} unloadDistance Distance in meters to expand view bounds for triggering unload
     * @param {string|null} environmentMapUrl Optional environment map URL for this segment
     */
    constructor(index, url, viewBoundingBox, preloadDistance = 50, unloadDistance = 50, environmentMapUrl = null) {
        this.index = index;
        this.url = url;
        this.status = SegmentStatus.Pending;
        this.sceneIndex = null;
        this.environmentMapUrl = environmentMapUrl;
        this.eta = Infinity;
        this.distanceToCenter = Infinity;

        // Original view bounding box
        this.viewBoundingBox = viewBoundingBox.clone();

        // Preload bounding box - view bounds expanded by a fixed distance to trigger load early
        this.preloadBoundingBox = viewBoundingBox.clone().expandByScalar(preloadDistance);

        // Unload bounding box - view bounds expanded by a fixed distance for hysteresis
        this.unloadBoundingBox = viewBoundingBox.clone().expandByScalar(unloadDistance);
    }

    /**
     * Check if a point is inside the preload bounds (should trigger loading).
     * @param {THREE.Vector3} point Point to test
     * @return {boolean} True if point is inside preload bounds
     */
    shouldLoad(point) {
        return this.status === SegmentStatus.Pending && this.preloadBoundingBox.containsPoint(point);
    }

    /**
     * Check if a point is outside the unload bounds (should trigger unloading).
     * @param {THREE.Vector3} point Point to test
     * @return {boolean} True if point is outside unload bounds
     */
    shouldUnload(point) {
        return this.status === SegmentStatus.Loaded && !this.unloadBoundingBox.containsPoint(point);
    }

    /**
     * Check if a point is inside the view bounding box.
     * @param {THREE.Vector3} point Point to test
     * @return {boolean} True if point is inside view bounds
     */
    containsPoint(point) {
        return this.viewBoundingBox.containsPoint(point);
    }

    /**
     * Check if segment can be queued for loading.
     * @return {boolean} True if segment can be loaded
     */
    canLoad() {
        return this.status === SegmentStatus.Pending;
    }

    /**
     * Check if segment can be queued for unloading.
     * @return {boolean} True if segment can be unloaded
     */
    canUnload() {
        return this.status === SegmentStatus.Loaded;
    }

    /**
     * Mark segment as queued for loading.
     */
    markQueued() {
        this.status = SegmentStatus.Queued;
    }

    /**
     * Mark segment as loading.
     */
    markLoading() {
        this.status = SegmentStatus.Loading;
    }

    /**
     * Mark segment as loaded with its mesh entry ID.
     * @param {number} meshId Stable ID of the SplatMeshEntry in the Viewer's splatMeshes Map
     */
    markLoaded(meshId) {
        this.status = SegmentStatus.Loaded;
        this.sceneIndex = meshId;
    }

    /**
     * Mark segment as queued for unloading.
     */
    markQueuedUnload() {
        this.status = SegmentStatus.QueuedUnload;
    }

    /**
     * Mark segment as unloading.
     */
    markUnloading() {
        this.status = SegmentStatus.Unloading;
    }

    /**
     * Reset segment to pending state (after unload or failed load).
     */
    markPending() {
        this.status = SegmentStatus.Pending;
        this.sceneIndex = null;
    }

    /**
     * Compute estimated time of arrival for the camera to reach this segment.
     * Uses the velocity component toward the segment (approach speed).
     * @param {THREE.Vector3} cameraPos Current camera position
     * @param {THREE.Vector3} velocity Smoothed camera velocity vector
     * @param {THREE.Vector3} scratch Scratch vector
     * @return {number} ETA in seconds (0 if already inside, Infinity if moving away)
     */
    computeETA(cameraPos, velocity, scratch) {
        // Distance to bbox center (used as tiebreaker when ETAs are equal)
        this.viewBoundingBox.getCenter(scratch);
        this.distanceToCenter = cameraPos.distanceTo(scratch);

        // Already inside the preload box — load immediately
        if (this.preloadBoundingBox.containsPoint(cameraPos)) {
            this.eta = 0;
            return this.eta;
        }

        // Direction and distance to nearest point on view bbox
        this.viewBoundingBox.clampPoint(cameraPos, scratch);
        scratch.sub(cameraPos);
        const dist = scratch.length();

        if (dist < 0.001) {
            this.eta = 0;
            return this.eta;
        }

        // Approach speed = velocity component toward the segment
        scratch.divideScalar(dist); // normalize
        const approachSpeed = velocity.dot(scratch);

        // Treat very small approach speeds as effectively zero to avoid unstable,
        // extremely large ETAs caused by numerical noise when the camera is
        // almost stationary or only jittering.
        if (approachSpeed <= 1e-6) {
            // Not moving toward this segment in any meaningful way

            this.eta = Infinity;
            return this.eta;
        }

        this.eta = dist / approachSpeed;
        return this.eta;
    }
}

const _lookDirection = new Vector3();
const _spherical = new Spherical();
const _target = new Vector3();

const PINCH_SPEED_FACTOR = 0.003; // Speed factor for pinch zooming

class FirstPersonControls {
    constructor(object, domElement) {
        this.object = object;
        this.domElement = domElement;

        // API

        this.enabled = true;

        this.movementSpeed = 1.0;
        this.lookSpeed = 0.005;

        this.lookVertical = true;
        this.autoForward = false;

        this.activeLook = true;

        this.heightSpeed = false;
        this.heightCoef = 1.0;
        this.heightMin = 0.0;
        this.heightMax = 1.0;

        this.constrainVertical = false;
        this.verticalMin = 0;
        this.verticalMax = Math.PI;

        this.mouseDragOn = false;
        this.activePointers = new Map();
        this.fingerDistance = 0;
        this.lastFingerDistance = 0;

        // internals

        this.autoSpeedFactor = 0.0;

        this.pointerX = 0;
        this.pointerY = 0;

        this.prevPointerX = 0;
        this.prevPointerY = 0;

        this.moveForward = false;
        this.moveBackward = false;
        this.moveLeft = false;
        this.moveRight = false;

        this.turnLeft = false;
        this.turnRight = false;
        this.turnUp = false;
        this.turnDown = false;

        this.permanentDragOn = false;

        this.viewHalfX = 0;
        this.viewHalfY = 0;

        // private variables

        let lat = 0;
        let lon = 0;

        this.boundingBox = new Box3();

        this.handleResize = function () {
            if (this.domElement === document) {
                this.viewHalfX = window.innerWidth / 2;
                this.viewHalfY = window.innerHeight / 2;
            } else {
                this.viewHalfX = this.domElement.offsetWidth / 2;
                this.viewHalfY = this.domElement.offsetHeight / 2;
            }
        };

        this.onPointerDown = function (event) {
            if (this.domElement !== document) {
                this.domElement.focus();
            }

            this.activePointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

            if (!this.permanentDragOn) {
                this.mouseDragOn = true;
                this.firstDragFrame = true;
            }
        };

        this.onPointerUp = function (event) {
            this.mouseDragOn = false;
            this.activePointers.delete(event.pointerId);
        };

        this.onPointerMove = function (event) {
            const singlePointer = this.activePointers.size === 1 && this.activePointers.has(event.pointerId);
            const twoFingers = this.activePointers.size === 2;

            // Update the position of the pointer
            this.activePointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

            // Pointer move should be used only when handling drag, because touch doesn't
            // invoke pointer move events when the finger is off the screen
            if (this.mouseDragOn && singlePointer) {
                if (this.domElement === document) {
                    this.pointerX = event.pageX - this.viewHalfX;
                    this.pointerY = event.pageY - this.viewHalfY;
                } else {
                    this.pointerX = event.pageX - this.domElement.offsetLeft - this.viewHalfX;
                    this.pointerY = event.pageY - this.domElement.offsetTop - this.viewHalfY;
                }

                if (this.firstDragFrame) {
                    this.prevPointerX = this.pointerX;
                    this.prevPointerY = this.pointerY;
                    this.firstDragFrame = false;
                }
            } else if (this.permanentDragOn && singlePointer) {
                const deltaX = event.movementX;
                const deltaY = event.movementY;

                // Reverse the signs because we are using the opposite direction of dragging
                this.pointerX -= deltaX;
                this.pointerY -= deltaY;
            } else if (twoFingers) {
                const x0 = this.activePointers.get(Array.from(this.activePointers.keys())[0]).x;
                const y0 = this.activePointers.get(Array.from(this.activePointers.keys())[0]).y;
                const x1 = this.activePointers.get(Array.from(this.activePointers.keys())[1]).x;
                const y1 = this.activePointers.get(Array.from(this.activePointers.keys())[1]).y;
                this.distance = Math.sqrt((x1 - x0) ** 2 + (y1 - y0) ** 2);

                if (this.firstDragFrame) {
                    this.lastFingerDistance = this.fingerDistance;
                    this.firstDragFrame = false;
                }
            }
        };

        this.onKeyDown = function (event) {
            switch (event.code) {
                case 'PageUp':
                    this.turnUp = true;
                    break;

                case 'ArrowUp':
                case 'KeyW':
                    this.moveForward = true;
                    break;

                case 'ArrowLeft':
                    this.turnLeft = true;
                    break;

                case 'KeyA':
                    this.moveLeft = true;
                    break;

                case 'PageDown':
                    this.turnDown = true;
                    break;

                case 'ArrowDown':
                case 'KeyS':
                    this.moveBackward = true;
                    break;

                case 'ArrowRight':
                    this.turnRight = true;
                    break;

                case 'KeyD':
                    this.moveRight = true;
                    break;

                case 'KeyE':
                    this.moveUp = true;
                    break;

                case 'KeyQ':
                    this.moveDown = true;
                    break;
                case 'KeyL':
                    this.permanentDragOn = !this.permanentDragOn;
                    if (this.permanentDragOn) {
                        this.domElement.requestPointerLock();
                    } else {
                        document.exitPointerLock();
                    }
                    break;
            }
        };

        this.onKeyUp = function (event) {
            switch (event.code) {
                case 'PageUp':
                    this.turnUp = false;
                    break;

                case 'ArrowUp':
                case 'KeyW':
                    this.moveForward = false;
                    break;

                case 'ArrowLeft':
                    this.turnLeft = false;
                    break;

                case 'KeyA':
                    this.moveLeft = false;
                    break;

                case 'PageDown':
                    this.turnDown = false;
                    break;

                case 'ArrowDown':
                case 'KeyS':
                    this.moveBackward = false;
                    break;

                case 'ArrowRight':
                    this.turnRight = false;
                    break;
                case 'KeyD':
                    this.moveRight = false;
                    break;

                case 'KeyE':
                    this.moveUp = false;
                    break;
                case 'KeyQ':
                    this.moveDown = false;
                    break;
            }
        };

        this.lookAt = function (x, y, z) {
            if (x.isVector3) {
                _target.copy(x);
            } else {
                _target.set(x, y, z);
            }

            this.object.lookAt(_target);

            setOrientation(this);

            return this;
        };

        this.update = (function () {
            const targetPosition = new Vector3();

            return function update(delta) {
                const pointerXDelta = this.pointerX - this.prevPointerX;
                const pointerYDelta = this.pointerY - this.prevPointerY;

                this.prevPointerX = this.pointerX;
                this.prevPointerY = this.pointerY;

                // Compute the change of distance between the two fingers
                const pinchDelta = this.fingerDistance - this.lastFingerDistance;
                this.lastFingerDistance = this.fingerDistance;

                if (this.enabled === false) return;

                if (this.heightSpeed) {
                    const y = MathUtils.clamp(this.object.position.y, this.heightMin, this.heightMax);
                    const heightDelta = y - this.heightMin;

                    this.autoSpeedFactor = delta * (heightDelta * this.heightCoef);
                } else {
                    this.autoSpeedFactor = 0.0;
                }

                const actualMoveSpeed = delta * this.movementSpeed;

                // On keyboard or mouse movement, we move the camera in the direction it is facing
                // but we ignore the vertical component of the forward vector to keep the movement horizontal.
                // This allows for a more natural movement in first-person controls.
                const horizontalForwardVector = new Vector3();
                this.object.getWorldDirection(horizontalForwardVector);
                horizontalForwardVector.y = 0; // Ignore the vertical component
                horizontalForwardVector.normalize();

                if (this.moveForward || (this.autoForward && !this.moveBackward)) {
                    this.object.position.addScaledVector(horizontalForwardVector, actualMoveSpeed + this.autoSpeedFactor);
                }
                if (this.moveBackward) {
                    this.object.position.addScaledVector(horizontalForwardVector, -actualMoveSpeed);
                }

                if (this.moveLeft) this.object.translateX(-actualMoveSpeed);
                if (this.moveRight) this.object.translateX(actualMoveSpeed);

                if (this.moveUp) {
                    this.object.translateY(actualMoveSpeed);
                }
                if (this.moveDown) {
                    this.object.translateY(-actualMoveSpeed);
                }

                const rotate = (lat, lon) => {
                    let phi = MathUtils.degToRad(90 + lat);
                    const theta = MathUtils.degToRad(lon);

                    if (this.constrainVertical) {
                        phi = MathUtils.mapLinear(phi, 0, Math.PI, this.verticalMin, this.verticalMax);
                    }

                    const position = this.object.position;

                    targetPosition.setFromSphericalCoords(1, phi, theta).add(position);

                    this.object.lookAt(targetPosition);
                };

                if (this.turnLeft) {
                    lon += this.lookSpeed * MathUtils.radToDeg(0.001);
                    rotate(lat, lon);
                }
                if (this.turnRight) {
                    lon -= this.lookSpeed * MathUtils.radToDeg(0.001);
                    rotate(lat, lon);
                }
                if (this.turnUp) {
                    lat -= this.lookSpeed * MathUtils.radToDeg(0.001);
                    lat = Math.max(-85, Math.min(85, lat));
                    rotate(lat, lon);
                }
                if (this.turnDown) {
                    lat += this.lookSpeed * MathUtils.radToDeg(0.001);
                    lat = Math.max(-85, Math.min(85, lat));
                    rotate(lat, lon);
                }

                let actualLookSpeed = delta * this.lookSpeed;
                // Make the look speed proportonal to screen width
                actualLookSpeed *= 1024 / 2 / this.viewHalfX;

                if (!this.activeLook) {
                    actualLookSpeed = 0;
                }

                let verticalLookRatio = 1;

                if (this.constrainVertical) {
                    verticalLookRatio = Math.PI / (this.verticalMax - this.verticalMin);
                }

                this.object.position.x = MathUtils.clamp(this.object.position.x, this.boundingBox.min.x, this.boundingBox.max.x);
                this.object.position.y = MathUtils.clamp(this.object.position.y, this.boundingBox.min.y, this.boundingBox.max.y);
                this.object.position.z = MathUtils.clamp(this.object.position.z, this.boundingBox.min.z, this.boundingBox.max.z);

                if ((this.mouseDragOn && this.activePointers.size === 1) || this.permanentDragOn) {
                    lon += pointerXDelta * actualLookSpeed;
                    if (this.lookVertical) lat -= pointerYDelta * actualLookSpeed * verticalLookRatio;

                    lat = Math.max(-85, Math.min(85, lat));

                    rotate(lat, lon);
                } else if (this.mouseDragOn && this.activePointers.size === 2) {
                    // On pinch zoom, we move the camera forward or backward based on the pinch distance
                    const forwardVector = new Vector3();
                    this.object.getWorldDirection(forwardVector);
                    this.object.position.addScaledVector(forwardVector, this.movementSpeed * pinchDelta * PINCH_SPEED_FACTOR);
                }
            };
        })();

        this.updateLookDirection = function () {
            const forwardVector = new Vector3();
            this.object.getWorldDirection(forwardVector);
            forwardVector.normalize();

            // The camera forward vector might have changed externally, so we need to recalculate latitude and longitude
            const spherical = new Spherical();
            spherical.setFromVector3(forwardVector);

            lat = MathUtils.radToDeg(spherical.phi) - 90;
            lon = MathUtils.radToDeg(spherical.theta);
        };

        this.dispose = function () {
            this.domElement.removeEventListener('contextmenu', contextmenu);
            this.domElement.removeEventListener('pointerdown', _onPointerDown);
            this.domElement.removeEventListener('pointermove', _onPointerMove);
            this.domElement.removeEventListener('pointerup', _onPointerUp);

            window.removeEventListener('keydown', _onKeyDown);
            window.removeEventListener('keyup', _onKeyUp);
        };

        const _onPointerMove = this.onPointerMove.bind(this);
        const _onPointerDown = this.onPointerDown.bind(this);
        const _onPointerUp = this.onPointerUp.bind(this);
        const _onKeyDown = this.onKeyDown.bind(this);
        const _onKeyUp = this.onKeyUp.bind(this);

        this.domElement.addEventListener('contextmenu', contextmenu);
        this.domElement.addEventListener('pointerdown', _onPointerDown, { passive: false });
        this.domElement.addEventListener('pointermove', _onPointerMove, { passive: false });
        this.domElement.addEventListener('pointerup', _onPointerUp, { passive: false });
        this.domElement.addEventListener('pointercancel', _onPointerUp);
        this.domElement.addEventListener('pointerleave', _onPointerUp);

        window.addEventListener('keydown', _onKeyDown);
        window.addEventListener('keyup', _onKeyUp);

        function setOrientation(controls) {
            const quaternion = controls.object.quaternion;

            _lookDirection.set(0, 0, -1).applyQuaternion(quaternion);
            _spherical.setFromVector3(_lookDirection);

            lat = 90 - MathUtils.radToDeg(_spherical.phi);
            lon = MathUtils.radToDeg(_spherical.theta);
        }

        this.handleResize();

        setOrientation(this);
    }
}

function contextmenu(event) {
    event.preventDefault();
}

const fadeElement = (element, out, displayStyle, duration, onComplete) => {
    const startTime = performance.now();

    let startOpacity = element.style.display === 'none' ? 0 : parseFloat(element.style.opacity);
    if (isNaN(startOpacity)) startOpacity = 1;

    const interval = window.setInterval(() => {
        const currentTime = performance.now();
        const elapsed = currentTime - startTime;

        let t = Math.min(elapsed / duration, 1.0);
        if (t > 0.999) t = 1;

        let opacity;
        if (out) {
            opacity = (1.0 - t) * startOpacity;
            if (opacity < 0.0001) opacity = 0;
        } else {
            opacity = (1.0 - startOpacity) * t + startOpacity;
        }

        if (opacity > 0) {
            element.style.display = displayStyle;
            element.style.opacity = opacity;
        } else {
            element.style.display = 'none';
        }

        if (t >= 1) {
            if (onComplete) onComplete();
            window.clearInterval(interval);
        }
    }, 16);
    return interval;
};

const cancelFade = (interval) => {
    window.clearInterval(interval);
};

const STANDARD_FADE_DURATION = 500;

class LoadingSpinner {
    static elementIDGen = 0;

    constructor(message, container) {
        this.taskIDGen = 0;
        this.elementID = LoadingSpinner.elementIDGen++;

        this.tasks = [];

        this.message = message || 'Loading...';
        this.container = container || document.body;

        this.spinnerContainerOuter = document.createElement('div');
        this.spinnerContainerOuter.className = `spinnerOuterContainer${this.elementID}`;
        this.spinnerContainerOuter.style.display = 'none';

        this.spinnerContainerPrimary = document.createElement('div');
        this.spinnerContainerPrimary.className = `spinnerContainerPrimary${this.elementID}`;
        this.spinnerPrimary = document.createElement('div');
        this.spinnerPrimary.classList.add(`spinner${this.elementID}`, `spinnerPrimary${this.elementID}`);
        this.messageContainerPrimary = document.createElement('div');
        this.messageContainerPrimary.classList.add(`messageContainer${this.elementID}`, `messageContainerPrimary${this.elementID}`);
        this.messageContainerPrimary.innerHTML = this.message;

        this.spinnerContainerMin = document.createElement('div');
        this.spinnerContainerMin.className = `spinnerContainerMin${this.elementID}`;
        this.spinnerMin = document.createElement('div');
        this.spinnerMin.classList.add(`spinner${this.elementID}`, `spinnerMin${this.elementID}`);
        this.messageContainerMin = document.createElement('div');
        this.messageContainerMin.classList.add(`messageContainer${this.elementID}`, `messageContainerMin${this.elementID}`);
        this.messageContainerMin.innerHTML = this.message;

        this.spinnerContainerPrimary.appendChild(this.spinnerPrimary);
        this.spinnerContainerPrimary.appendChild(this.messageContainerPrimary);
        this.spinnerContainerOuter.appendChild(this.spinnerContainerPrimary);

        this.spinnerContainerMin.appendChild(this.spinnerMin);
        this.spinnerContainerMin.appendChild(this.messageContainerMin);
        this.spinnerContainerOuter.appendChild(this.spinnerContainerMin);

        const style = document.createElement('style');
        style.innerHTML = `

            .spinnerOuterContainer${this.elementID} {
                width: 100%;
                height: 100%;
                margin: 0;
                top: 0;
                left: 0;
                position: absolute;
                pointer-events: none;
            }

            .messageContainer${this.elementID} {
                height: 20px;
                font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
                font-size: 13px;
                font-weight: 500;
                color: rgba(255, 255, 255, 0.85);
                text-align: center;
                vertical-align: middle;
                letter-spacing: 0.2px;
            }

            .spinner${this.elementID} {
                padding: 12px;
                background: rgba(255, 255, 255, 0.9);
                z-index: 99999;
                aspect-ratio: 1;
                border-radius: 50%;
                --_m:
                    conic-gradient(#0000, #000),
                    linear-gradient(#000 0 0) content-box;
                -webkit-mask: var(--_m);
                    mask: var(--_m);
                -webkit-mask-composite: source-out;
                    mask-composite: subtract;
                box-sizing: border-box;
                animation: load${this.elementID} 0.8s linear infinite;
            }

            .spinnerContainerPrimary${this.elementID} {
                z-index: 99999;
                background: rgba(15, 15, 26, 0.85);
                backdrop-filter: blur(12px);
                -webkit-backdrop-filter: blur(12px);
                border: 1px solid rgba(255, 255, 255, 0.08);
                border-radius: 12px;
                padding: 28px 24px 20px;
                margin: 0;
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                width: 180px;
                pointer-events: auto;
            }

            .spinnerPrimary${this.elementID} {
                width: 64px;
                margin: 0 auto;
                display: block;
            }

            .messageContainerPrimary${this.elementID} {
                padding-top: 18px;
            }

            .spinnerContainerMin${this.elementID} {
                z-index: 99999;
                background: rgba(15, 15, 26, 0.85);
                backdrop-filter: blur(12px);
                -webkit-backdrop-filter: blur(12px);
                border: 1px solid rgba(255, 255, 255, 0.08);
                border-radius: 10px;
                padding: 14px 20px;
                margin: 0;
                position: absolute;
                bottom: 40px;
                left: 50%;
                transform: translate(-50%, 0);
                display: flex;
                align-items: center;
                pointer-events: auto;
                min-width: 200px;
                gap: 14px;
            }

            .spinnerMin${this.elementID} {
                width: 28px;
                height: 28px;
                flex-shrink: 0;
            }

            .messageContainerMin${this.elementID} {
                margin: 0;
                padding: 0;
            }

            @keyframes load${this.elementID} {
                to { transform: rotate(1turn); }
            }

        `;
        this.spinnerContainerOuter.appendChild(style);
        this.container.appendChild(this.spinnerContainerOuter);

        this.setMinimized(false, true);

        this.fadeTransitions = [];
    }

    addTask(message) {
        const newTask = {
            message: message,
            id: this.taskIDGen++
        };
        this.tasks.push(newTask);
        this.update();
        return newTask.id;
    }

    removeTask(id) {
        let index = 0;
        for (let task of this.tasks) {
            if (task.id === id) {
                this.tasks.splice(index, 1);
                break;
            }
            index++;
        }
        this.update();
    }

    removeAllTasks() {
        this.tasks = [];
        this.update();
    }

    setMessageForTask(id, message) {
        for (let task of this.tasks) {
            if (task.id === id) {
                task.message = message;
                break;
            }
        }
        this.update();
    }

    update() {
        if (this.tasks.length > 0) {
            this.show();
            this.setMessage(this.tasks[this.tasks.length - 1].message);
        } else {
            this.hide();
        }
    }

    show() {
        this.spinnerContainerOuter.style.display = 'block';
        this.visible = true;
    }

    hide() {
        this.spinnerContainerOuter.style.display = 'none';
        this.visible = false;
    }

    setContainer(container) {
        if (this.container && this.spinnerContainerOuter.parentElement === this.container) {
            this.container.removeChild(this.spinnerContainerOuter);
        }
        if (container) {
            this.container = container;
            this.container.appendChild(this.spinnerContainerOuter);
            this.spinnerContainerOuter.style.zIndex = this.container.style.zIndex + 1;
        }
    }

    setMinimized(minimized, instant) {
        const showHideSpinner = (element, show, instant, displayStyle, fadeTransitionsIndex) => {
            if (instant) {
                element.style.display = show ? displayStyle : 'none';
            } else {
                this.fadeTransitions[fadeTransitionsIndex] = fadeElement(element, !show, displayStyle, STANDARD_FADE_DURATION, () => {
                    this.fadeTransitions[fadeTransitionsIndex] = null;
                });
            }
        };
        showHideSpinner(this.spinnerContainerPrimary, !minimized, instant, 'block', 0);
        showHideSpinner(this.spinnerContainerMin, minimized, instant, 'flex', 1);
        this.minimized = minimized;
    }

    setMessage(msg) {
        this.messageContainerPrimary.innerHTML = msg;
        this.messageContainerMin.innerHTML = msg;
    }
}

class LoadingProgressBar {
    constructor(container) {
        this.idGen = 0;

        this.tasks = [];

        this.container = container || document.body;

        this.progressBarContainerOuter = document.createElement('div');
        this.progressBarContainerOuter.className = 'progressBarOuterContainer';
        this.progressBarContainerOuter.style.display = 'none';

        this.progressBarBox = document.createElement('div');
        this.progressBarBox.className = 'progressBarBox';

        this.progressBarBackground = document.createElement('div');
        this.progressBarBackground.className = 'progressBarBackground';

        this.progressBar = document.createElement('div');
        this.progressBar.className = 'progressBar';

        this.progressBarBackground.appendChild(this.progressBar);
        this.progressBarBox.appendChild(this.progressBarBackground);
        this.progressBarContainerOuter.appendChild(this.progressBarBox);

        const style = document.createElement('style');
        style.innerHTML = `

            .progressBarOuterContainer {
                width: 100%;
                height: 100%;
                margin: 0;
                top: 0;
                left: 0;
                position: absolute;
                pointer-events: none;
            }

            .progressBarBox {
                z-index: 99999;
                padding: 6px;
                background: rgba(15, 15, 26, 0.85);
                backdrop-filter: blur(12px);
                -webkit-backdrop-filter: blur(12px);
                border: 1px solid rgba(255, 255, 255, 0.08);
                border-radius: 10px;
                margin: 0;
                position: absolute;
                bottom: 40px;
                left: 50%;
                transform: translate(-50%, 0);
                width: 220px;
                height: 24px;
                pointer-events: auto;
            }

            .progressBarBackground {
                width: 100%;
                height: 12px;
                border-radius: 6px;
                background-color: rgba(255, 255, 255, 0.08);
                border: none;
                overflow: hidden;
            }

            .progressBar {
                height: 12px;
                width: 0px;
                border-radius: 6px;
                background: linear-gradient(90deg, rgba(255, 255, 255, 0.6), rgba(255, 255, 255, 0.85));
                transition: width 0.2s ease-out;
            }

        `;
        this.progressBarContainerOuter.appendChild(style);
        this.container.appendChild(this.progressBarContainerOuter);
    }

    show() {
        this.progressBarContainerOuter.style.display = 'block';
    }

    hide() {
        this.progressBarContainerOuter.style.display = 'none';
    }

    setProgress(progress) {
        this.progressBar.style.width = progress + '%';
    }

    setContainer(container) {
        if (this.container && this.progressBarContainerOuter.parentElement === this.container) {
            this.container.removeChild(this.progressBarContainerOuter);
        }
        if (container) {
            this.container = container;
            this.container.appendChild(this.progressBarContainerOuter);
            this.progressBarContainerOuter.style.zIndex = this.container.style.zIndex + 1;
        }
    }
}

class InfoPanel {
    constructor(container) {
        this.container = container || document.body;

        this.infoCells = {};

        const layout = [
            ['FPS', 'fps'],
            ['Rendering:', 'renderSplatCount'],
            ['Sort time', 'sortTime']
        ];

        this.infoPanelContainer = document.createElement('div');
        const style = document.createElement('style');
        style.innerHTML = `

            .infoPanel {
                width: 430px;
                padding: 10px;
                background-color: rgba(50, 50, 50, 0.85);
                border: #555555 2px solid;
                color: #dddddd;
                border-radius: 10px;
                z-index: 9999;
                font-family: arial;
                font-size: 11pt;
                text-align: left;
                margin: 0;
                top: 10px;
                left:10px;
                position: absolute;
                pointer-events: auto;
            }

            .info-panel-cell {
                margin-bottom: 5px;
                padding-bottom: 2px;
            }

            .label-cell {
                font-weight: bold;
                font-size: 12pt;
                width: 140px;
            }

            .segment-list {
                display: flex;
                flex-wrap: wrap;
                gap: 4px;
                margin-top: 2px;
            }

            .segment-badge {
                display: inline-block;
                padding: 1px 6px;
                border-radius: 4px;
                font-size: 10pt;
                font-weight: bold;
                color: #fff;
                white-space: nowrap;
            }

            .segment-badge.pending    { background-color: #555; }
            .segment-badge.queued     { background-color: #b8860b; }
            .segment-badge.loading    { background-color: #c47a00; }
            .segment-badge.loaded     { background-color: #2a7a2a; }
            .segment-badge.queued-unload { background-color: #8b4513; }
            .segment-badge.unloading  { background-color: #8b0000; }
            .segment-badge.current    { outline: 2px solid #fff; }

        `;
        this.infoPanelContainer.append(style);

        this.infoPanel = document.createElement('div');
        this.infoPanel.className = 'infoPanel';

        const infoTable = document.createElement('div');
        infoTable.style.display = 'table';

        for (let layoutEntry of layout) {
            const row = document.createElement('div');
            row.style.display = 'table-row';
            row.className = 'info-panel-row';

            const labelCell = document.createElement('div');
            labelCell.style.display = 'table-cell';
            labelCell.innerHTML = `${layoutEntry[0]}: `;
            labelCell.classList.add('info-panel-cell', 'label-cell');

            const spacerCell = document.createElement('div');
            spacerCell.style.display = 'table-cell';
            spacerCell.style.width = '10px';
            spacerCell.innerHTML = ' ';
            spacerCell.className = 'info-panel-cell';

            const infoCell = document.createElement('div');
            infoCell.style.display = 'table-cell';
            infoCell.innerHTML = '';
            infoCell.className = 'info-panel-cell';

            this.infoCells[layoutEntry[1]] = infoCell;

            row.appendChild(labelCell);
            row.appendChild(spacerCell);
            row.appendChild(infoCell);

            infoTable.appendChild(row);
        }

        this.infoPanel.appendChild(infoTable);

        // Segment section (shown only when lazy-load segments exist)
        this.segmentSection = document.createElement('div');
        this.segmentSection.style.display = 'none';
        this.segmentSection.style.marginTop = '8px';
        this.segmentSection.style.borderTop = '1px solid #555';
        this.segmentSection.style.paddingTop = '6px';

        const segmentLabel = document.createElement('div');
        segmentLabel.style.fontWeight = 'bold';
        segmentLabel.style.fontSize = '12pt';
        segmentLabel.style.marginBottom = '4px';
        segmentLabel.innerHTML = 'Segments:';
        this.segmentSection.appendChild(segmentLabel);

        this.segmentList = document.createElement('div');
        this.segmentList.className = 'segment-list';
        this.segmentSection.appendChild(this.segmentList);

        this.infoPanel.appendChild(this.segmentSection);
        this.infoPanelContainer.append(this.infoPanel);
        this.infoPanelContainer.style.display = 'none';
        this.container.appendChild(this.infoPanelContainer);

        this.visible = false;
    }

    update = function (currentFPS, splatCount, splatRenderCount, splatRenderCountPct, lastSortTime, segments, cameraPosition) {
        this.infoCells.fps.innerHTML = currentFPS;
        this.infoCells.renderSplatCount.innerHTML = `${splatRenderCount} splats out of ${splatCount} (${splatRenderCountPct.toFixed(2)}%)`;
        this.infoCells.sortTime.innerHTML = `${lastSortTime.toFixed(3)} ms`;

        if (segments && segments.size > 0) {
            this.segmentSection.style.display = 'block';
            this.segmentList.innerHTML = '';
            for (const segment of segments.values()) {
                const isCurrent = cameraPosition ? segment.containsPoint(cameraPosition) : false;
                const badge = document.createElement('span');
                badge.className = `segment-badge ${segment.status}${isCurrent ? ' current' : ''}`;
                badge.title = `Segment ${segment.index}: ${segment.status}`;
                badge.innerHTML = `${segment.index}`;
                this.segmentList.appendChild(badge);
            }
        } else {
            this.segmentSection.style.display = 'none';
        }
    };

    setContainer(container) {
        if (this.container && this.infoPanelContainer.parentElement === this.container) {
            this.container.removeChild(this.infoPanelContainer);
        }
        if (container) {
            this.container = container;
            this.container.appendChild(this.infoPanelContainer);
            this.infoPanelContainer.style.zIndex = this.container.style.zIndex + 1;
        }
    }

    show() {
        this.infoPanelContainer.style.display = 'block';
        this.visible = true;
    }

    hide() {
        this.infoPanelContainer.style.display = 'none';
        this.visible = false;
    }
}

const _axis = new THREE.Vector3();

class ArrowHelper extends THREE.Object3D {
    constructor(
        dir = new THREE.Vector3(0, 0, 1),
        origin = new THREE.Vector3(0, 0, 0),
        length = 1,
        radius = 0.1,
        color = 0xffff00,
        headLength = length * 0.2,
        headRadius = headLength * 0.2
    ) {
        super();

        this.type = 'ArrowHelper';

        const lineGeometry = new THREE.CylinderGeometry(radius, radius, length, 32);
        lineGeometry.translate(0, length / 2.0, 0);
        const coneGeometry = new THREE.CylinderGeometry(0, headRadius, headLength, 32);
        coneGeometry.translate(0, length, 0);

        this.position.copy(origin);

        this.line = new THREE.Mesh(lineGeometry, new THREE.MeshBasicMaterial({ color: color, toneMapped: false }));
        this.line.matrixAutoUpdate = false;
        this.add(this.line);

        this.cone = new THREE.Mesh(coneGeometry, new THREE.MeshBasicMaterial({ color: color, toneMapped: false }));
        this.cone.matrixAutoUpdate = false;
        this.add(this.cone);

        this.setDirection(dir);
    }

    setDirection(dir) {
        if (dir.y > 0.99999) {
            this.quaternion.set(0, 0, 0, 1);
        } else if (dir.y < -0.99999) {
            this.quaternion.set(1, 0, 0, 0);
        } else {
            _axis.set(dir.z, 0, -dir.x).normalize();
            const radians = Math.acos(dir.y);
            this.quaternion.setFromAxisAngle(_axis, radians);
        }
    }

    setColor(color) {
        this.line.material.color.set(color);
        this.cone.material.color.set(color);
    }

    copy(source) {
        super.copy(source, false);
        this.line.copy(source.line);
        this.cone.copy(source.cone);
        return this;
    }

    dispose() {
        this.line.geometry.dispose();
        this.line.material.dispose();
        this.cone.geometry.dispose();
        this.cone.material.dispose();
    }
}

/**
 * POIMarker: A texture-based sprite marker that renders at a 3D position
 * with constant orientation relative to the camera.
 */
class POIMarker extends THREE.Sprite {
    /**
     * Create a POI marker
     * @param {Object} options
     * @param {THREE.Vector3} options.position - World position of the marker
     * @param {THREE.Texture|string} options.texture - Texture or URL to load
     * @param {number} [options.scale=1.0] - Base scale multiplier
     * @param {number} [options.svgResolution=256] - Canvas resolution for SVG rendering
     * @param {Object} [options.userData] - Custom user data
     */
    constructor(options = {}) {
        const material = new THREE.SpriteMaterial({
            map: null,
            alphaTest: 0.5, // Crucial: discard pixels with alpha < 0.5
            depthTest: true,
            depthWrite: true,
            sizeAttenuation: true
        });

        super(material);

        this.baseScale = options.scale ?? 1.0;
        this.scale.set(this.baseScale, this.baseScale, 1);

        if (options.position) {
            this.position.copy(options.position);
        }

        if (options.userData) {
            this.userData = { ...this.userData, ...options.userData };
        }

        this._ownsTexture = false;

        // Handle texture loading
        if (options.texture) {
            if (options.texture instanceof THREE.Texture) {
                this.material.map = options.texture;
                this.material.needsUpdate = true;
            } else if (typeof options.texture === 'string') {
                if (options.texture.toLowerCase().endsWith('.svg')) {
                    this._loadSVGTexture(options.texture, options.svgResolution ?? 256);
                } else {
                    const loader = new THREE.TextureLoader();
                    loader.load(options.texture, (texture) => {
                        this.material.map = texture;
                        this.material.needsUpdate = true;
                        this._ownsTexture = true;
                    });
                }
            }
        }
    }

    /**
     * Load an SVG file using THREE.SVGLoader and render it to a canvas texture
     * @param {string} url - URL of the SVG file
     * @param {number} resolution - Canvas size in pixels (square)
     */
    async _loadSVGTexture(url, resolution) {
        const { SVGLoader } = await import('three/addons/loaders/SVGLoader.js');
        const loader = new SVGLoader();
        loader.load(url, (data) => {
            const paths = data.paths;

            // Compute bounding box of all SVG paths
            const svgBounds = new THREE.Box2();
            for (const path of paths) {
                const shapes = SVGLoader.createShapes(path);
                for (const shape of shapes) {
                    const points = shape.getPoints();
                    for (const pt of points) {
                        svgBounds.expandByPoint(pt);
                    }
                }
            }

            const svgWidth = svgBounds.max.x - svgBounds.min.x;
            const svgHeight = svgBounds.max.y - svgBounds.min.y;
            if (svgWidth === 0 || svgHeight === 0) return;

            const canvas = document.createElement('canvas');
            canvas.width = resolution;
            canvas.height = resolution;
            const ctx = canvas.getContext('2d');

            // Scale SVG to fit the canvas while preserving aspect ratio
            const scale = Math.min(resolution / svgWidth, resolution / svgHeight);
            const offsetX = (resolution - svgWidth * scale) / 2 - svgBounds.min.x * scale;
            const offsetY = (resolution - svgHeight * scale) / 2 - svgBounds.min.y * scale;

            ctx.translate(offsetX, offsetY);
            ctx.scale(scale, scale);

            for (const path of paths) {
                const fillColor = path.userData.style.fill;
                const fillOpacity = path.userData.style.fillOpacity;
                const strokeColor = path.userData.style.stroke;
                const strokeWidth = path.userData.style.strokeWidth;

                const shapes = SVGLoader.createShapes(path);
                for (const shape of shapes) {
                    const points = shape.getPoints();
                    const holes = shape.holes;

                    ctx.beginPath();
                    // Draw outer shape
                    if (points.length > 0) {
                        ctx.moveTo(points[0].x, points[0].y);
                        for (let i = 1; i < points.length; i++) {
                            ctx.lineTo(points[i].x, points[i].y);
                        }
                        ctx.closePath();
                    }
                    // Draw holes
                    for (const hole of holes) {
                        const holePoints = hole.getPoints();
                        if (holePoints.length > 0) {
                            ctx.moveTo(holePoints[0].x, holePoints[0].y);
                            for (let i = 1; i < holePoints.length; i++) {
                                ctx.lineTo(holePoints[i].x, holePoints[i].y);
                            }
                            ctx.closePath();
                        }
                    }

                    if (fillColor && fillColor !== 'none') {
                        ctx.fillStyle = fillColor;
                        ctx.globalAlpha = fillOpacity ?? 1;
                        ctx.fill();
                    }
                    if (strokeColor && strokeColor !== 'none') {
                        ctx.strokeStyle = strokeColor;
                        ctx.lineWidth = parseFloat(strokeWidth) || 1;
                        ctx.globalAlpha = 1;
                        ctx.stroke();
                    }
                }
            }

            const texture = new THREE.CanvasTexture(canvas);
            texture.colorSpace = THREE.SRGBColorSpace;
            this.material.map = texture;
            this.material.needsUpdate = true;
            this._ownsTexture = true;
        });
    }

    /**
     * Set the base scale multiplier
     * @param {number} scale
     */
    setBaseScale(scale) {
        this.baseScale = scale;
        this.scale.set(scale, scale, 1);
    }

    /**
     * Dispose of the marker and its resources
     */
    dispose() {
        if (this.material) {
            if (this._ownsTexture && this.material.map) {
                this.material.map.dispose();
            }
            this.material.dispose();
        }
    }
}

class SceneHelper {
    constructor(threeScene) {
        this.threeScene = threeScene;
        this.splatRenderTarget = null;
        this.depthTarget = null;
        this.renderTargetCopyQuad = null;
        this.renderTargetCopyCamera = null;
        this.depthCompositingQuad = null;
        this.depthCompositingCamera = null;
        this.meshCursor = null;
        this.focusMarker = null;
        this.controlPlane = null;
        this.debugRoot = null;
        this.secondaryDebugRoot = null;
        this.poiMarkerGroup = null;
    }

    updateSplatRenderTargetForRenderDimensions(width, height) {
        this.destroySplatRendertarget();
        this.splatRenderTarget = new THREE.WebGLRenderTarget(width, height, {
            format: THREE.RGBAFormat,
            stencilBuffer: false,
            depthBuffer: true
        });
        this.splatRenderTarget.depthTexture = new THREE.DepthTexture(width, height);
        this.splatRenderTarget.depthTexture.format = THREE.DepthFormat;
        this.splatRenderTarget.depthTexture.type = THREE.UnsignedIntType;
    }

    destroySplatRendertarget() {
        if (this.splatRenderTarget) {
            this.splatRenderTarget = null;
        }
    }

    /**
     * Setup render target for the splat depth compositing pass.
     * Single RGBA16F target: the blend unit writes premultiplied depth (depth * alpha)
     * into .r and composited alpha coverage into .a, which is later used to un-premultiply.
     */
    setupDepthTarget(width, height) {
        this.destroyDepthTarget();
        this.depthTarget = new THREE.WebGLRenderTarget(width, height, {
            format: THREE.RGBAFormat,
            type: THREE.HalfFloatType,
            minFilter: THREE.NearestFilter,
            magFilter: THREE.NearestFilter,
            depthBuffer: false,
            stencilBuffer: false
        });
        this.depthTarget.texture.name = 'splatDepthAccum';
    }

    destroyDepthTarget() {
        if (this.depthTarget) {
            this.depthTarget.dispose();
            this.depthTarget = null;
        }
    }

    updateDepthTargetForRenderDimensions(width, height) {
        if (this.depthTarget) {
            if (this.depthTarget.width !== width || this.depthTarget.height !== height) {
                this.setupDepthTarget(width, height);
            }
        } else {
            this.setupDepthTarget(width, height);
        }
    }

    getDepthTarget() {
        return this.depthTarget;
    }

    setupRenderTargetCopyObjects() {
        const uniforms = {
            sourceColorTexture: {
                type: 't',
                value: null
            },
            sourceDepthTexture: {
                type: 't',
                value: null
            }
        };
        const renderTargetCopyMaterial = new THREE.ShaderMaterial({
            vertexShader: `
                varying vec2 vUv;
                void main() {
                    vUv = uv;
                    gl_Position = vec4( position.xy, 0.0, 1.0 );
                }
            `,
            fragmentShader: `
                #include <common>
                #include <packing>
                varying vec2 vUv;
                uniform sampler2D sourceColorTexture;
                uniform sampler2D sourceDepthTexture;
                void main() {
                    vec4 color = texture2D(sourceColorTexture, vUv);
                    float fragDepth = texture2D(sourceDepthTexture, vUv).x;
                    gl_FragDepth = fragDepth;
                    gl_FragColor = vec4(color.rgb, color.a * 2.0);
              }
            `,
            uniforms: uniforms,
            depthWrite: false,
            depthTest: false,
            transparent: true,
            blending: THREE.CustomBlending,
            blendSrc: THREE.SrcAlphaFactor,
            blendSrcAlpha: THREE.SrcAlphaFactor,
            blendDst: THREE.OneMinusSrcAlphaFactor,
            blendDstAlpha: THREE.OneMinusSrcAlphaFactor
        });
        renderTargetCopyMaterial.extensions.fragDepth = true;
        this.renderTargetCopyQuad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), renderTargetCopyMaterial);
        this.renderTargetCopyCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    }

    /**
     * Setup depth compositing pass. Reads accumulated depth from the depth target
     * and writes it to the main depth buffer via gl_FragDepth.
     * Color output is disabled (colorMask all false) — only depth is written.
     */
    setupDepthCompositingObjects() {
        const uniforms = {
            depthAccumTexture: {
                type: 't',
                value: null
            }
        };

        const depthCompositingMaterial = new THREE.ShaderMaterial({
            vertexShader: `
                varying vec2 vUv;
                void main() {
                    vUv = uv;
                    gl_Position = vec4(position.xy, 0.0, 1.0);
                }
            `,
            fragmentShader: `
                precision highp float;

                varying vec2 vUv;
                uniform sampler2D depthAccumTexture;

                void main() {
                    vec4 depthAccum = texture2D(depthAccumTexture, vUv);

                    float totalAlpha = depthAccum.a;

                    // No splat coverage: discard to preserve background depth buffer
                    if (totalAlpha < 0.001) discard;

                    gl_FragColor = vec4(0.0);
                    gl_FragDepth = depthAccum.x / totalAlpha;
                }
            `,
            uniforms: uniforms,
            depthWrite: true,
            depthTest: true,
            transparent: false,
            blending: THREE.NoBlending,
            colorWrite: false
        });
        depthCompositingMaterial.extensions.fragDepth = true;

        this.depthCompositingQuad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), depthCompositingMaterial);
        this.depthCompositingCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    }

    destroyDepthCompositingObjects() {
        if (this.depthCompositingQuad) {
            disposeAllMeshes(this.depthCompositingQuad);
            this.depthCompositingQuad = null;
        }
    }

    updateDepthCompositingUniforms() {
        if (this.depthCompositingQuad && this.depthTarget) {
            this.depthCompositingQuad.material.uniforms.depthAccumTexture.value = this.depthTarget.texture;
            this.depthCompositingQuad.material.uniformsNeedUpdate = true;
        }
    }

    getDepthCompositingQuad() {
        return this.depthCompositingQuad;
    }

    getDepthCompositingCamera() {
        return this.depthCompositingCamera;
    }

    destroyRenderTargetCopyObjects() {
        if (this.renderTargetCopyQuad) {
            disposeAllMeshes(this.renderTargetCopyQuad);
            this.renderTargetCopyQuad = null;
        }
    }

    setupMeshCursor() {
        if (!this.meshCursor) {
            const coneGeometry = new THREE.ConeGeometry(0.5, 1.5, 32);
            const coneMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff });

            const downArrow = new THREE.Mesh(coneGeometry, coneMaterial);
            downArrow.rotation.set(0, 0, Math.PI);
            downArrow.position.set(0, 1, 0);
            const upArrow = new THREE.Mesh(coneGeometry, coneMaterial);
            upArrow.position.set(0, -1, 0);
            const leftArrow = new THREE.Mesh(coneGeometry, coneMaterial);
            leftArrow.rotation.set(0, 0, Math.PI / 2.0);
            leftArrow.position.set(1, 0, 0);
            const rightArrow = new THREE.Mesh(coneGeometry, coneMaterial);
            rightArrow.rotation.set(0, 0, -Math.PI / 2.0);
            rightArrow.position.set(-1, 0, 0);

            this.meshCursor = new THREE.Object3D();
            this.meshCursor.add(downArrow);
            this.meshCursor.add(upArrow);
            this.meshCursor.add(leftArrow);
            this.meshCursor.add(rightArrow);
            this.meshCursor.scale.set(0.1, 0.1, 0.1);
            this.threeScene.add(this.meshCursor);
            this.meshCursor.visible = false;
        }
    }

    destroyMeshCursor() {
        if (this.meshCursor) {
            disposeAllMeshes(this.meshCursor);
            this.threeScene.remove(this.meshCursor);
            this.meshCursor = null;
        }
    }

    setMeshCursorVisibility(visible) {
        this.meshCursor.visible = visible;
    }

    getMeschCursorVisibility() {
        return this.meshCursor.visible;
    }

    setMeshCursorPosition(position) {
        this.meshCursor.position.copy(position);
    }

    positionAndOrientMeshCursor(position, camera) {
        this.meshCursor.position.copy(position);
        this.meshCursor.up.copy(camera.up);
        this.meshCursor.lookAt(camera.position);
    }

    setupFocusMarker() {
        if (!this.focusMarker) {
            const sphereGeometry = new THREE.SphereGeometry(0.5, 32, 32);
            const focusMarkerMaterial = SceneHelper.buildFocusMarkerMaterial();
            focusMarkerMaterial.depthTest = false;
            focusMarkerMaterial.depthWrite = false;
            focusMarkerMaterial.transparent = true;
            this.focusMarker = new THREE.Mesh(sphereGeometry, focusMarkerMaterial);
        }
    }

    destroyFocusMarker() {
        if (this.focusMarker) {
            disposeAllMeshes(this.focusMarker);
            this.focusMarker = null;
        }
    }

    updateFocusMarker = (function () {
        const tempPosition = new THREE.Vector3();
        const tempMatrix = new THREE.Matrix4();
        const toCamera = new THREE.Vector3();

        return function (position, camera, viewport) {
            tempMatrix.copy(camera.matrixWorld).invert();
            tempPosition.copy(position).applyMatrix4(tempMatrix);
            tempPosition.normalize().multiplyScalar(10);
            tempPosition.applyMatrix4(camera.matrixWorld);
            toCamera.copy(camera.position).sub(position);
            const toCameraDistance = toCamera.length();
            this.focusMarker.position.copy(position);
            this.focusMarker.scale.set(toCameraDistance, toCameraDistance, toCameraDistance);
            this.focusMarker.material.uniforms.realFocusPosition.value.copy(position);
            this.focusMarker.material.uniforms.viewport.value.copy(viewport);
            this.focusMarker.material.uniformsNeedUpdate = true;
        };
    })();

    setFocusMarkerVisibility(visible) {
        this.focusMarker.visible = visible;
    }

    setFocusMarkerOpacity(opacity) {
        this.focusMarker.material.uniforms.opacity.value = opacity;
        this.focusMarker.material.uniformsNeedUpdate = true;
    }

    getFocusMarkerOpacity() {
        return this.focusMarker.material.uniforms.opacity.value;
    }

    setupControlPlane() {
        if (!this.controlPlane) {
            const planeGeometry = new THREE.PlaneGeometry(1, 1);
            planeGeometry.rotateX(-Math.PI / 2);
            const planeMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff });
            planeMaterial.transparent = true;
            planeMaterial.opacity = 0.6;
            planeMaterial.depthTest = false;
            planeMaterial.depthWrite = false;
            planeMaterial.side = THREE.DoubleSide;
            const planeMesh = new THREE.Mesh(planeGeometry, planeMaterial);

            const arrowDir = new THREE.Vector3(0, 1, 0);
            arrowDir.normalize();
            const arrowOrigin = new THREE.Vector3(0, 0, 0);
            const arrowLength = 0.5;
            const arrowRadius = 0.01;
            const arrowColor = 0x00dd00;
            const arrowHelper = new ArrowHelper(arrowDir, arrowOrigin, arrowLength, arrowRadius, arrowColor, 0.1, 0.03);

            this.controlPlane = new THREE.Object3D();
            this.controlPlane.add(planeMesh);
            this.controlPlane.add(arrowHelper);
        }
    }

    destroyControlPlane() {
        if (this.controlPlane) {
            disposeAllMeshes(this.controlPlane);
            this.controlPlane = null;
        }
    }

    setControlPlaneVisibility(visible) {
        this.controlPlane.visible = visible;
    }

    positionAndOrientControlPlane = (function () {
        const tempQuaternion = new THREE.Quaternion();
        const defaultUp = new THREE.Vector3(0, 1, 0);

        return function (position, up) {
            tempQuaternion.setFromUnitVectors(defaultUp, up);
            this.controlPlane.position.copy(position);
            this.controlPlane.quaternion.copy(tempQuaternion);
        };
    })();

    setupPOIMarkers() {
        if (!this.poiMarkerGroup) {
            this.poiMarkerGroup = new THREE.Group();
        }
    }

    destroyPOIMarkers() {
        if (this.poiMarkerGroup) {
            for (const marker of this.poiMarkerGroup.children) {
                if (marker.dispose) {
                    marker.dispose();
                }
            }
            this.poiMarkerGroup.clear();
            this.poiMarkerGroup = null;
        }
    }

    /**
     * Add a POI marker
     * @param {Object} options
     * @param {THREE.Vector3} options.position - World position of the marker
     * @param {THREE.Texture|string} options.texture - Texture or URL to load
     * @param {number} [options.scale=1.0] - Base scale multiplier
     * @param {Object} [options.userData] - Custom user data
     * @returns {POIMarker} The created marker
     */
    addPOIMarker(options) {
        if (!this.poiMarkerGroup) {
            this.setupPOIMarkers();
        }
        const marker = new POIMarker(options);
        this.poiMarkerGroup.add(marker);
        return marker;
    }

    /**
     * Remove a POI marker
     * @param {POIMarker} marker - The marker to remove
     */
    removePOIMarker(marker) {
        if (this.poiMarkerGroup && marker) {
            this.poiMarkerGroup.remove(marker);
            if (marker.dispose) {
                marker.dispose();
            }
        }
    }

    /**
     * Remove all POI markers
     */
    clearPOIMarkers() {
        if (this.poiMarkerGroup) {
            for (const marker of [...this.poiMarkerGroup.children]) {
                this.removePOIMarker(marker);
            }
        }
    }

    updatePOIMarkers() {}

    /**
     * Get the POI marker group for rendering
     * @returns {THREE.Group|null}
     */
    getPOIMarkerGroup() {
        return this.poiMarkerGroup;
    }

    /**
     * Check if there are any POI markers to render
     * @returns {boolean}
     */
    hasPOIMarkers() {
        return this.poiMarkerGroup && this.poiMarkerGroup.children.length > 0;
    }

    addDebugMeshes() {
        this.debugRoot = this.createDebugMeshes();
        this.secondaryDebugRoot = this.createSecondaryDebugMeshes();
        this.threeScene.add(this.debugRoot);
        this.threeScene.add(this.secondaryDebugRoot);
    }

    destroyDebugMeshes() {
        for (let debugRoot of [this.debugRoot, this.secondaryDebugRoot]) {
            if (debugRoot) {
                disposeAllMeshes(debugRoot);
                this.threeScene.remove(debugRoot);
            }
        }
        this.debugRoot = null;
        this.secondaryDebugRoot = null;
    }

    createDebugMeshes(renderOrder) {
        const sphereGeometry = new THREE.SphereGeometry(1, 32, 32);
        const debugMeshRoot = new THREE.Object3D();

        const createMesh = (color, position) => {
            let sphereMesh = new THREE.Mesh(sphereGeometry, SceneHelper.buildDebugMaterial(color));
            sphereMesh.renderOrder = renderOrder;
            debugMeshRoot.add(sphereMesh);
            sphereMesh.position.fromArray(position);
        };

        createMesh(0xff0000, [-50, 0, 0]);
        createMesh(0xff0000, [50, 0, 0]);
        createMesh(0x00ff00, [0, 0, -50]);
        createMesh(0x00ff00, [0, 0, 50]);
        createMesh(0xffaa00, [5, 0, 5]);

        return debugMeshRoot;
    }

    createSecondaryDebugMeshes(renderOrder) {
        const boxGeometry = new THREE.BoxGeometry(3, 3, 3);
        const debugMeshRoot = new THREE.Object3D();

        let boxColor = 0xbbbbbb;
        const createMesh = (position) => {
            let boxMesh = new THREE.Mesh(boxGeometry, SceneHelper.buildDebugMaterial(boxColor));
            boxMesh.renderOrder = renderOrder;
            debugMeshRoot.add(boxMesh);
            boxMesh.position.fromArray(position);
        };

        let separation = 10;
        createMesh([-separation, 0, -separation]);
        createMesh([-separation, 0, separation]);
        createMesh([separation, 0, -separation]);
        createMesh([separation, 0, separation]);

        return debugMeshRoot;
    }

    static buildDebugMaterial(color) {
        const vertexShaderSource = `
            #include <common>
            varying float ndcDepth;

            void main() {
                gl_Position = projectionMatrix * viewMatrix * modelMatrix * vec4(position.xyz, 1.0);
                ndcDepth = gl_Position.z / gl_Position.w;
                gl_Position.x = gl_Position.x / gl_Position.w;
                gl_Position.y = gl_Position.y / gl_Position.w;
                gl_Position.z = 0.0;
                gl_Position.w = 1.0;
    
            }
        `;

        const fragmentShaderSource = `
            #include <common>
            uniform vec3 color;
            varying float ndcDepth;
            void main() {
                gl_FragDepth = (ndcDepth + 1.0) / 2.0;
                gl_FragColor = vec4(color.rgb, 0.0);
            }
        `;

        const uniforms = {
            color: {
                type: 'v3',
                value: new THREE.Color(color)
            }
        };

        const material = new THREE.ShaderMaterial({
            uniforms: uniforms,
            vertexShader: vertexShaderSource,
            fragmentShader: fragmentShaderSource,
            transparent: false,
            depthTest: true,
            depthWrite: true,
            side: THREE.FrontSide
        });
        material.extensions.fragDepth = true;

        return material;
    }

    static buildFocusMarkerMaterial(color) {
        const vertexShaderSource = `
            #include <common>

            uniform vec2 viewport;
            uniform vec3 realFocusPosition;

            varying vec4 ndcPosition;
            varying vec4 ndcCenter;
            varying vec4 ndcFocusPosition;

            void main() {
                float radius = 0.01;

                vec4 viewPosition = modelViewMatrix * vec4(position.xyz, 1.0);
                vec4 viewCenter = modelViewMatrix * vec4(0.0, 0.0, 0.0, 1.0);

                vec4 viewFocusPosition = modelViewMatrix * vec4(realFocusPosition, 1.0);

                ndcPosition = projectionMatrix * viewPosition;
                ndcPosition = ndcPosition * vec4(1.0 / ndcPosition.w);
                ndcCenter = projectionMatrix * viewCenter;
                ndcCenter = ndcCenter * vec4(1.0 / ndcCenter.w);

                ndcFocusPosition = projectionMatrix * viewFocusPosition;
                ndcFocusPosition = ndcFocusPosition * vec4(1.0 / ndcFocusPosition.w);

                gl_Position = projectionMatrix * viewPosition;

            }
        `;

        const fragmentShaderSource = `
            #include <common>
            uniform vec3 color;
            uniform vec2 viewport;
            uniform float opacity;

            varying vec4 ndcPosition;
            varying vec4 ndcCenter;
            varying vec4 ndcFocusPosition;

            void main() {
                vec2 screenPosition = vec2(ndcPosition) * viewport;
                vec2 screenCenter = vec2(ndcCenter) * viewport;

                vec2 screenVec = screenPosition - screenCenter;

                float projectedRadius = length(screenVec);

                float lineWidth = 0.0005 * viewport.y;
                float aaRange = 0.0025 * viewport.y;
                float radius = 0.06 * viewport.y;
                float radDiff = abs(projectedRadius - radius) - lineWidth;
                float alpha = 1.0 - clamp(radDiff / 5.0, 0.0, 1.0); 

                gl_FragColor = vec4(color.rgb, alpha * opacity);
            }
        `;

        const uniforms = {
            color: {
                type: 'v3',
                value: new THREE.Color(color)
            },
            realFocusPosition: {
                type: 'v3',
                value: new THREE.Vector3()
            },
            viewport: {
                type: 'v2',
                value: new THREE.Vector2()
            },
            opacity: {
                value: 0.0
            }
        };

        const material = new THREE.ShaderMaterial({
            uniforms: uniforms,
            vertexShader: vertexShaderSource,
            fragmentShader: fragmentShaderSource,
            transparent: true,
            depthTest: false,
            depthWrite: false,
            side: THREE.FrontSide
        });

        return material;
    }

    dispose() {
        this.destroyMeshCursor();
        this.destroyFocusMarker();
        this.destroyDebugMeshes();
        this.destroyControlPlane();
        this.destroyRenderTargetCopyObjects();
        this.destroySplatRendertarget();
        this.destroyDepthTarget();
        this.destroyDepthCompositingObjects();
        this.destroyPOIMarkers();
    }
}

const VectorRight = new THREE.Vector3(1, 0, 0);
const VectorUp = new THREE.Vector3(0, 1, 0);
const VectorBackward = new THREE.Vector3(0, 0, 1);

class Ray {
    constructor(origin = new THREE.Vector3(), direction = new THREE.Vector3()) {
        this.origin = new THREE.Vector3();
        this.direction = new THREE.Vector3();
        this.setParameters(origin, direction);
    }

    setParameters(origin, direction) {
        this.origin.copy(origin);
        this.direction.copy(direction).normalize();
    }

    boxContainsPoint(box, point, epsilon) {
        return point.x < box.min.x - epsilon ||
            point.x > box.max.x + epsilon ||
            point.y < box.min.y - epsilon ||
            point.y > box.max.y + epsilon ||
            point.z < box.min.z - epsilon ||
            point.z > box.max.z + epsilon
            ? false
            : true;
    }

    intersectBox = (function () {
        const planeIntersectionPoint = new THREE.Vector3();
        const planeIntersectionPointArray = [];
        const originArray = [];
        const directionArray = [];

        return function (box, outHit) {
            originArray[0] = this.origin.x;
            originArray[1] = this.origin.y;
            originArray[2] = this.origin.z;
            directionArray[0] = this.direction.x;
            directionArray[1] = this.direction.y;
            directionArray[2] = this.direction.z;

            if (this.boxContainsPoint(box, this.origin, 0.0001)) {
                if (outHit) {
                    outHit.origin.copy(this.origin);
                    outHit.normal.set(0, 0, 0);
                    outHit.distance = -1;
                }
                return true;
            }

            for (let i = 0; i < 3; i++) {
                if (directionArray[i] == 0.0) continue;

                const hitNormal = i == 0 ? VectorRight : i == 1 ? VectorUp : VectorBackward;
                const extremeVec = directionArray[i] < 0 ? box.max : box.min;
                let multiplier = -Math.sign(directionArray[i]);
                planeIntersectionPointArray[0] = i == 0 ? extremeVec.x : i == 1 ? extremeVec.y : extremeVec.z;
                let toSide = planeIntersectionPointArray[0] - originArray[i];

                if (toSide * multiplier < 0) {
                    const idx1 = (i + 1) % 3;
                    const idx2 = (i + 2) % 3;
                    planeIntersectionPointArray[2] = (directionArray[idx1] / directionArray[i]) * toSide + originArray[idx1];
                    planeIntersectionPointArray[1] = (directionArray[idx2] / directionArray[i]) * toSide + originArray[idx2];
                    planeIntersectionPoint.set(
                        planeIntersectionPointArray[i],
                        planeIntersectionPointArray[idx2],
                        planeIntersectionPointArray[idx1]
                    );
                    if (this.boxContainsPoint(box, planeIntersectionPoint, 0.0001)) {
                        if (outHit) {
                            outHit.origin.copy(planeIntersectionPoint);
                            outHit.normal.copy(hitNormal).multiplyScalar(multiplier);
                            outHit.distance = planeIntersectionPoint.sub(this.origin).length();
                        }
                        return true;
                    }
                }
            }

            return false;
        };
    })();

    intersectSphere = (function () {
        const toSphereCenterVec = new THREE.Vector3();

        return function (center, radius, outHit) {
            toSphereCenterVec.copy(center).sub(this.origin);
            const toClosestApproach = toSphereCenterVec.dot(this.direction);
            const toClosestApproachSq = toClosestApproach * toClosestApproach;
            const toSphereCenterSq = toSphereCenterVec.dot(toSphereCenterVec);
            const diffSq = toSphereCenterSq - toClosestApproachSq;
            const radiusSq = radius * radius;

            if (diffSq > radiusSq) return false;

            const thc = Math.sqrt(radiusSq - diffSq);
            const t0 = toClosestApproach - thc;
            const t1 = toClosestApproach + thc;

            if (t1 < 0) return false;
            let t = t0 < 0 ? t1 : t0;

            if (outHit) {
                outHit.origin.copy(this.origin).addScaledVector(this.direction, t);
                outHit.normal.copy(outHit.origin).sub(center).normalize();
                outHit.distance = t;
            }
            return true;
        };
    })();
}

class Hit {
    constructor() {
        this.origin = new THREE.Vector3();
        this.normal = new THREE.Vector3();
        this.distance = 0;
        this.splatIndex = 0;
    }

    set(origin, normal, distance, splatIndex) {
        this.origin.copy(origin);
        this.normal.copy(normal);
        this.distance = distance;
        this.splatIndex = splatIndex;
    }

    clone() {
        const hitClone = new Hit();
        hitClone.origin.copy(this.origin);
        hitClone.normal.copy(this.normal);
        hitClone.distance = this.distance;
        hitClone.splatIndex = this.splatIndex;
        return hitClone;
    }
}

const SplatRenderMode = {
    ThreeD: 0,
    TwoD: 1
};

class Raycaster {
    constructor(origin, direction, raycastAgainstTrueSplatEllipsoid = false) {
        this.ray = new Ray(origin, direction);
        this.raycastAgainstTrueSplatEllipsoid = raycastAgainstTrueSplatEllipsoid;
    }

    setFromCameraAndScreenPosition = (function () {
        const ndcCoords = new THREE.Vector2();

        return function (camera, screenPosition, screenDimensions) {
            ndcCoords.x = (screenPosition.x / screenDimensions.x) * 2.0 - 1.0;
            ndcCoords.y = ((screenDimensions.y - screenPosition.y) / screenDimensions.y) * 2.0 - 1.0;
            if (camera.isPerspectiveCamera) {
                this.ray.origin.setFromMatrixPosition(camera.matrixWorld);
                this.ray.direction.set(ndcCoords.x, ndcCoords.y, 0.5).unproject(camera).sub(this.ray.origin).normalize();
                this.camera = camera;
            } else if (camera.isOrthographicCamera) {
                this.ray.origin.set(ndcCoords.x, ndcCoords.y, (camera.near + camera.far) / (camera.near - camera.far)).unproject(camera);
                this.ray.direction.set(0, 0, -1).transformDirection(camera.matrixWorld);
                this.camera = camera;
            } else {
                throw new Error('Raycaster::setFromCameraAndScreenPosition() -> Unsupported camera type');
            }
        };
    })();

    intersectSplatMesh = (function () {
        const toLocal = new THREE.Matrix4();
        const fromLocal = new THREE.Matrix4();
        const sceneTransform = new THREE.Matrix4();
        const localRay = new Ray();
        const tempPoint = new THREE.Vector3();

        return function (splatMesh, outHits = []) {
            const splatTree = splatMesh.getSplatTree();

            if (!splatTree) return outHits;

            for (let s = 0; s < splatTree.subTrees.length; s++) {
                const subTree = splatTree.subTrees[s];

                fromLocal.copy(splatMesh.matrixWorld);
                if (splatMesh.dynamicMode) {
                    splatMesh.getSceneTransform(s, sceneTransform);
                    fromLocal.multiply(sceneTransform);
                }
                toLocal.copy(fromLocal).invert();

                localRay.origin.copy(this.ray.origin).applyMatrix4(toLocal);
                localRay.direction.copy(this.ray.origin).add(this.ray.direction);
                localRay.direction.applyMatrix4(toLocal).sub(localRay.origin).normalize();

                const outHitsForSubTree = [];
                if (subTree.rootNode) {
                    this.castRayAtSplatTreeNode(localRay, splatTree, subTree.rootNode, outHitsForSubTree);
                }

                outHitsForSubTree.forEach((hit) => {
                    hit.origin.applyMatrix4(fromLocal);
                    hit.normal.applyMatrix4(fromLocal).normalize();
                    hit.distance = tempPoint.copy(hit.origin).sub(this.ray.origin).length();
                });

                outHits.push(...outHitsForSubTree);
            }

            outHits.sort((a, b) => {
                if (a.distance > b.distance) return 1;
                else return -1;
            });

            return outHits;
        };
    })();

    castRayAtSplatTreeNode = (function () {
        const tempColor = new THREE.Vector4();
        const tempCenter = new THREE.Vector3();
        const tempScale = new THREE.Vector3();
        const tempRotation = new THREE.Quaternion();
        const tempHit = new Hit();
        const scaleEpsilon = 0.0000001;

        const origin = new THREE.Vector3(0, 0, 0);
        const uniformScaleMatrix = new THREE.Matrix4();
        const scaleMatrix = new THREE.Matrix4();
        const rotationMatrix = new THREE.Matrix4();
        const toSphereSpace = new THREE.Matrix4();
        const fromSphereSpace = new THREE.Matrix4();
        const tempRay = new Ray();

        return function (ray, splatTree, node, outHits = []) {
            if (!ray.intersectBox(node.boundingBox)) {
                return;
            }
            if (node.data && node.data.indexes && node.data.indexes.length > 0) {
                for (let i = 0; i < node.data.indexes.length; i++) {
                    const splatGlobalIndex = node.data.indexes[i];
                    const splatSceneIndex = splatTree.splatMesh.getSceneIndexForSplat(splatGlobalIndex);
                    const splatScene = splatTree.splatMesh.getScene(splatSceneIndex);
                    if (!splatScene.visible) continue;

                    splatTree.splatMesh.getSplatColor(splatGlobalIndex, tempColor);
                    splatTree.splatMesh.getSplatCenter(splatGlobalIndex, tempCenter);
                    splatTree.splatMesh.getSplatScaleAndRotation(splatGlobalIndex, tempScale, tempRotation);

                    if (
                        tempScale.x <= scaleEpsilon ||
                        tempScale.y <= scaleEpsilon ||
                        (splatTree.splatMesh.splatRenderMode === SplatRenderMode.ThreeD && tempScale.z <= scaleEpsilon)
                    ) {
                        continue;
                    }

                    if (!this.raycastAgainstTrueSplatEllipsoid) {
                        let radius = tempScale.x + tempScale.y;
                        let componentCount = 2;
                        if (splatTree.splatMesh.splatRenderMode === SplatRenderMode.ThreeD) {
                            radius += tempScale.z;
                            componentCount = 3;
                        }
                        radius = radius / componentCount;
                        if (ray.intersectSphere(tempCenter, radius, tempHit)) {
                            const hitClone = tempHit.clone();
                            hitClone.splatIndex = splatGlobalIndex;
                            outHits.push(hitClone);
                        }
                    } else {
                        scaleMatrix.makeScale(tempScale.x, tempScale.y, tempScale.z);
                        rotationMatrix.makeRotationFromQuaternion(tempRotation);
                        const uniformScale = Math.log10(tempColor.w) * 2.0;
                        uniformScaleMatrix.makeScale(uniformScale, uniformScale, uniformScale);
                        fromSphereSpace.copy(uniformScaleMatrix).multiply(rotationMatrix).multiply(scaleMatrix);
                        toSphereSpace.copy(fromSphereSpace).invert();
                        tempRay.origin.copy(ray.origin).sub(tempCenter).applyMatrix4(toSphereSpace);
                        tempRay.direction.copy(ray.origin).add(ray.direction).sub(tempCenter);
                        tempRay.direction.applyMatrix4(toSphereSpace).sub(tempRay.origin).normalize();
                        if (tempRay.intersectSphere(origin, 1.0, tempHit)) {
                            const hitClone = tempHit.clone();
                            hitClone.splatIndex = splatGlobalIndex;
                            hitClone.origin.applyMatrix4(fromSphereSpace).add(tempCenter);
                            outHits.push(hitClone);
                        }
                    }
                }
            }
            if (node.children && node.children.length > 0) {
                for (let child of node.children) {
                    this.castRayAtSplatTreeNode(ray, splatTree, child, outHits);
                }
            }
            return outHits;
        };
    })();
}

const DisplayMode = {
    Rgb: 0,
    Normal: 1
};

class SplatMaterial {
    static buildVertexShaderBase(enableOptionalEffects = false, maxSphericalHarmonicsDegree = 0, customVars = '') {
        let vertexShaderSource = `
        precision highp float;
        #include <common>

        attribute uint splatIndex;
        uniform highp usampler2D centersColorsTexture;
        uniform highp sampler2D sphericalHarmonicsTexture;
        uniform highp sampler2D sphericalHarmonicsTexture0;
        uniform highp sampler2D sphericalHarmonicsTexture1;
        uniform highp sampler2D sphericalHarmonicsTexture2;
        uniform highp sampler2D sphericalHarmonicsTexture3;
        uniform highp sampler2D sphericalHarmonicsTexture4;
        uniform highp sampler2D sphericalHarmonicsTexture5;

        uniform highp usampler2D sceneIndexesTexture;
        uniform vec2 sceneIndexesTextureSize;
        uniform int sceneCount;
        uniform int displayMode;
    `;

        if (enableOptionalEffects) {
            vertexShaderSource += `
            uniform float sceneOpacity[${Constants.MaxScenes}];
            uniform int sceneVisibility[${Constants.MaxScenes}];
        `;
        }

        vertexShaderSource += `
        ${customVars}
        uniform vec2 focal;
        uniform int pointCloudModeEnabled;
        uniform vec2 viewport;
        uniform vec2 basisViewport;
        uniform vec2 centersColorsTextureSize;
        uniform int sphericalHarmonicsDegree;
        uniform vec2 sphericalHarmonicsTextureSize;
        uniform int sphericalHarmonics8BitMode;
        uniform int sphericalHarmonicsMultiTextureMode;
        uniform float visibleRegionRadius;
        uniform float visibleRegionFadeStartRadius;
        uniform float firstRenderTime;
        uniform float currentTime;
        uniform int fadeInComplete;
        uniform vec3 sceneCenter;
        uniform float splatScale;
        uniform float sphericalHarmonics8BitCompressionRangeMin[${Constants.MaxScenes}];
        uniform float sphericalHarmonics8BitCompressionRangeMax[${Constants.MaxScenes}];

        varying vec4 vColor;
        varying vec2 vUv;
        varying vec2 vPosition;

        mat3 quaternionToRotationMatrix(float x, float y, float z, float w) {
            float s = 1.0 / sqrt(w * w + x * x + y * y + z * z);
        
            return mat3(
                1. - 2. * (y * y + z * z),
                2. * (x * y + w * z),
                2. * (x * z - w * y),
                2. * (x * y - w * z),
                1. - 2. * (x * x + z * z),
                2. * (y * z + w * x),
                2. * (x * z + w * y),
                2. * (y * z - w * x),
                1. - 2. * (x * x + y * y)
            );
        }

        const float sqrt8 = sqrt(8.0);
        const float minAlpha = 1.0 / 255.0;

        const vec4 encodeNorm4 = vec4(1.0 / 255.0, 1.0 / 255.0, 1.0 / 255.0, 1.0 / 255.0);
        const uvec4 mask4 = uvec4(uint(0x000000FF), uint(0x0000FF00), uint(0x00FF0000), uint(0xFF000000));
        const uvec4 shift4 = uvec4(0, 8, 16, 24);
        vec4 uintToRGBAVec (uint u) {
           uvec4 urgba = mask4 & u;
           urgba = urgba >> shift4;
           vec4 rgba = vec4(urgba) * encodeNorm4;
           return rgba;
        }

        vec2 getDataUV(in int stride, in int offset, in vec2 dimensions) {
            vec2 samplerUV = vec2(0.0, 0.0);
            float d = float(splatIndex * uint(stride) + uint(offset)) / dimensions.x;
            samplerUV.y = float(floor(d)) / dimensions.y;
            samplerUV.x = fract(d);
            return samplerUV;
        }

        vec2 getDataUVF(in uint sIndex, in float stride, in uint offset, in vec2 dimensions) {
            vec2 samplerUV = vec2(0.0, 0.0);
            float d = float(uint(float(sIndex) * stride) + offset) / dimensions.x;
            samplerUV.y = float(floor(d)) / dimensions.y;
            samplerUV.x = fract(d);
            return samplerUV;
        }

        const float SH_C1 = 0.4886025119029199f;
        const float[5] SH_C2 = float[](1.0925484, -1.0925484, 0.3153916, -1.0925484, 0.5462742);

        void main () {
            uint oddOffset = splatIndex & uint(0x00000001);
            uint doubleOddOffset = oddOffset * uint(2);
            bool isEven = oddOffset == uint(0);
            uint nearestEvenIndex = splatIndex - oddOffset;
            float fOddOffset = float(oddOffset);

            uvec4 sampledCenterColor = texture(centersColorsTexture, getDataUV(1, 0, centersColorsTextureSize));
            vec3 splatCenter = uintBitsToFloat(uvec3(sampledCenterColor.gba));

            uint sceneIndex = uint(0);
            if (sceneCount > 1) {
                sceneIndex = texture(sceneIndexesTexture, getDataUV(1, 0, sceneIndexesTextureSize)).r;
            }
            `;

        if (enableOptionalEffects) {
            vertexShaderSource += `
                float splatOpacityFromScene = sceneOpacity[sceneIndex];
                int sceneVisible = sceneVisibility[sceneIndex];
                if (splatOpacityFromScene <= 0.01 || sceneVisible == 0) {
                    gl_Position = vec4(0.0, 0.0, 2.0, 1.0);
                    return;
                }
            `;
        }

        vertexShaderSource += `mat4 transformModelViewMatrix = modelViewMatrix;`;

        vertexShaderSource += `
            float sh8BitCompressionRangeMinForScene = sphericalHarmonics8BitCompressionRangeMin[sceneIndex];
            float sh8BitCompressionRangeMaxForScene = sphericalHarmonics8BitCompressionRangeMax[sceneIndex];
            float sh8BitCompressionRangeForScene = sh8BitCompressionRangeMaxForScene - sh8BitCompressionRangeMinForScene;
            float sh8BitCompressionHalfRangeForScene = sh8BitCompressionRangeForScene / 2.0;
            vec3 vec8BitSHShift = vec3(sh8BitCompressionRangeMinForScene);

            vec4 viewCenter = transformModelViewMatrix * vec4(splatCenter, 1.0);

            vec4 clipCenter = projectionMatrix * viewCenter;

            float clip = 1.2 * clipCenter.w;
            if (clipCenter.z < -clip || clipCenter.x < -clip || clipCenter.x > clip || clipCenter.y < -clip || clipCenter.y > clip) {
                gl_Position = vec4(0.0, 0.0, 2.0, 1.0);
                return;
            }

            vec3 ndcCenter = clipCenter.xyz / clipCenter.w;

            vPosition = position.xy;
            vColor = uintToRGBAVec(sampledCenterColor.r);
        `;

        // Proceed to sampling and rendering 1st degree spherical harmonics
        if (maxSphericalHarmonicsDegree >= 1) {
            vertexShaderSource += `   
            if (sphericalHarmonicsDegree >= 1) {
            `;

            vertexShaderSource += `
                    vec3 worldViewDir = normalize(splatCenter - cameraPosition);
                    worldViewDir = worldViewDir * mat3(0, 0, 1, 1, 0, 0, 0, 1, 0);
                `;

            vertexShaderSource += `
                vec3 sh1;
                vec3 sh2;
                vec3 sh3;
            `;

            if (maxSphericalHarmonicsDegree >= 2) {
                vertexShaderSource += `
                    vec3 sh4;
                    vec3 sh5;
                    vec3 sh6;
                    vec3 sh7;
                    vec3 sh8;
                `;
            }

            // Determining how to sample spherical harmonics textures to get the coefficients for calculations for a given degree
            // depends on how many total degrees (maxSphericalHarmonicsDegree) are present in the textures. This is because that
            // number affects how they are packed in the textures, and therefore the offset & stride required to access them.

            // Sample spherical harmonics textures with 1 degree worth of data for 1st degree calculations, and store in sh1, sh2, and sh3
            if (maxSphericalHarmonicsDegree === 1) {
                vertexShaderSource += `
                    if (sphericalHarmonicsMultiTextureMode == 0) {
                        vec2 shUV = getDataUVF(nearestEvenIndex, 2.5, doubleOddOffset, sphericalHarmonicsTextureSize);
                        vec4 sampledSH0123 = texture(sphericalHarmonicsTexture, shUV);
                        shUV = getDataUVF(nearestEvenIndex, 2.5, doubleOddOffset + uint(1), sphericalHarmonicsTextureSize);
                        vec4 sampledSH4567 = texture(sphericalHarmonicsTexture, shUV);
                        shUV = getDataUVF(nearestEvenIndex, 2.5, doubleOddOffset + uint(2), sphericalHarmonicsTextureSize);
                        vec4 sampledSH891011 = texture(sphericalHarmonicsTexture, shUV);
                        sh1 = vec3(sampledSH0123.rgb) * (1.0 - fOddOffset) + vec3(sampledSH0123.ba, sampledSH4567.r) * fOddOffset;
                        sh2 = vec3(sampledSH0123.a, sampledSH4567.rg) * (1.0 - fOddOffset) + vec3(sampledSH4567.gba) * fOddOffset;
                        sh3 = vec3(sampledSH4567.ba, sampledSH891011.r) * (1.0 - fOddOffset) + vec3(sampledSH891011.rgb) * fOddOffset;
                    } else {
                        vec2 shUV = getDataUV(1, 0, sphericalHarmonicsTextureSize);
                        vec4 sampledSH0123 = texture(sphericalHarmonicsTexture0, shUV);
                        vec4 sampledSH4567 = texture(sphericalHarmonicsTexture1, shUV);
                        vec4 sampledSH891011 = texture(sphericalHarmonicsTexture2, shUV);
                        sh1 = sampledSH0123.rgb;
                        sh2 = vec3(sampledSH0123.a, sampledSH4567.rg);
                        sh3 = vec3(sampledSH4567.ba, sampledSH891011.r);
                    }
                `;
                // Sample spherical harmonics textures with 2 degrees worth of data for 1st degree calculations, and store in sh1, sh2, and sh3
            } else if (maxSphericalHarmonicsDegree === 2) {
                vertexShaderSource += `
                    vec4 sampledSH0123;
                    vec4 sampledSH4567;
                    vec4 sampledSH891011;

                    if (sphericalHarmonicsMultiTextureMode == 0) {
                        sampledSH0123 = texture(sphericalHarmonicsTexture, getDataUV(6, 0, sphericalHarmonicsTextureSize));
                        sampledSH4567 = texture(sphericalHarmonicsTexture, getDataUV(6, 1, sphericalHarmonicsTextureSize));
                        sampledSH891011 = texture(sphericalHarmonicsTexture, getDataUV(6, 2, sphericalHarmonicsTextureSize));
                    } else {
                        vec2 shUV = getDataUV(1, 0, sphericalHarmonicsTextureSize);
                        sampledSH0123 = texture(sphericalHarmonicsTexture0, shUV);
                        sampledSH4567 = texture(sphericalHarmonicsTexture1, shUV);
                        sampledSH891011 = texture(sphericalHarmonicsTexture2, shUV);
                    }
                    sh1 = sampledSH0123.rgb;
                    sh2 = vec3(sampledSH0123.a, sampledSH4567.rg);
                    sh3 = vec3(sampledSH4567.ba, sampledSH891011.r);
                `;
            }

            // Perform 1st degree spherical harmonics calculations
            vertexShaderSource += `
                    if (sphericalHarmonics8BitMode == 1) {
                        sh1 = sh1 * sh8BitCompressionRangeForScene + vec8BitSHShift;
                        sh2 = sh2 * sh8BitCompressionRangeForScene + vec8BitSHShift;
                        sh3 = sh3 * sh8BitCompressionRangeForScene + vec8BitSHShift;
                    }
                    float x = worldViewDir.x;
                    float y = worldViewDir.y;
                    float z = worldViewDir.z;
                    vColor.rgb += SH_C1 * (-sh1 * y + sh2 * z - sh3 * x);
            `;

            // Proceed to sampling and rendering 2nd degree spherical harmonics
            if (maxSphericalHarmonicsDegree >= 2) {
                vertexShaderSource += `
                    if (sphericalHarmonicsDegree >= 2) {
                        float xx = x * x;
                        float yy = y * y;
                        float zz = z * z;
                        float xy = x * y;
                        float yz = y * z;
                        float xz = x * z;
                `;

                // Sample spherical harmonics textures with 2 degrees worth of data for 2nd degree calculations,
                // and store in sh4, sh5, sh6, sh7, and sh8
                if (maxSphericalHarmonicsDegree === 2) {
                    vertexShaderSource += `
                        vec4 sampledSH12131415;
                        vec4 sampledSH16171819;
                        vec4 sampledSH20212223;

                        if (sphericalHarmonicsMultiTextureMode == 0) {
                            sampledSH12131415 = texture(sphericalHarmonicsTexture, getDataUV(6, 3, sphericalHarmonicsTextureSize));
                            sampledSH16171819 = texture(sphericalHarmonicsTexture, getDataUV(6, 4, sphericalHarmonicsTextureSize));
                            sampledSH20212223 = texture(sphericalHarmonicsTexture, getDataUV(6, 5, sphericalHarmonicsTextureSize));
                        } else {
                            vec2 shUV = getDataUV(1, 0, sphericalHarmonicsTextureSize);
                            sampledSH12131415 = texture(sphericalHarmonicsTexture3, shUV);
                            sampledSH16171819 = texture(sphericalHarmonicsTexture4, shUV);
                            sampledSH20212223 = texture(sphericalHarmonicsTexture5, shUV);
                        }
                        sh4 = sampledSH891011.gba;
                        sh5 = sampledSH12131415.rgb;
                        sh6 = vec3(sampledSH12131415.a, sampledSH16171819.rg);
                        sh7 = vec3(sampledSH16171819.ba, sampledSH20212223.r);
                        sh8 = sampledSH20212223.gba;
                    `;
                }

                // Perform 2nd degree spherical harmonics calculations
                vertexShaderSource += `
                        if (sphericalHarmonics8BitMode == 1) {
                            sh4 = sh4 * sh8BitCompressionRangeForScene + vec8BitSHShift;
                            sh5 = sh5 * sh8BitCompressionRangeForScene + vec8BitSHShift;
                            sh6 = sh6 * sh8BitCompressionRangeForScene + vec8BitSHShift;
                            sh7 = sh7 * sh8BitCompressionRangeForScene + vec8BitSHShift;
                            sh8 = sh8 * sh8BitCompressionRangeForScene + vec8BitSHShift;
                        }

                        vColor.rgb +=
                            (SH_C2[0] * xy) * sh4 +
                            (SH_C2[1] * yz) * sh5 +
                            (SH_C2[2] * (2.0 * zz - xx - yy)) * sh6 +
                            (SH_C2[3] * xz) * sh7 +
                            (SH_C2[4] * (xx - yy)) * sh8;
                    }
                `;
            }

            vertexShaderSource += `

                vColor.rgb = clamp(vColor.rgb, vec3(0.), vec3(1.));

            }

            `;
        }

        return vertexShaderSource;
    }

    static getVertexShaderFadeIn() {
        return `
            if (fadeInComplete == 0) {
                float opacityAdjust = 1.0;
                float centerDist = length(splatCenter - sceneCenter);
                float renderTime = max(currentTime - firstRenderTime, 0.0);

                float fadeDistance = 0.75;
                float distanceLoadFadeInFactor = step(visibleRegionFadeStartRadius, centerDist);
                distanceLoadFadeInFactor = (1.0 - distanceLoadFadeInFactor) +
                                        (1.0 - clamp((centerDist - visibleRegionFadeStartRadius) / fadeDistance, 0.0, 1.0)) *
                                        distanceLoadFadeInFactor;
                opacityAdjust *= distanceLoadFadeInFactor;
                vColor.a *= opacityAdjust;
            }
        `;
    }

    static getUniforms(enableOptionalEffects = false, maxSphericalHarmonicsDegree = 0, splatScale = 1.0, pointCloudModeEnabled = false) {
        const uniforms = {
            sceneCenter: {
                type: 'v3',
                value: new THREE.Vector3()
            },
            fadeInComplete: {
                type: 'i',
                value: 0
            },
            visibleRegionFadeStartRadius: {
                type: 'f',
                value: 0.0
            },
            visibleRegionRadius: {
                type: 'f',
                value: 0.0
            },
            currentTime: {
                type: 'f',
                value: 0.0
            },
            firstRenderTime: {
                type: 'f',
                value: 0.0
            },
            centersColorsTexture: {
                type: 't',
                value: null
            },
            sphericalHarmonicsTexture: {
                type: 't',
                value: null
            },
            sphericalHarmonicsTexture0: {
                type: 't',
                value: null
            },
            sphericalHarmonicsTexture1: {
                type: 't',
                value: null
            },
            sphericalHarmonicsTexture2: {
                type: 't',
                value: null
            },
            sphericalHarmonicsTexture3: {
                type: 't',
                value: null
            },
            sphericalHarmonicsTexture4: {
                type: 't',
                value: null
            },
            sphericalHarmonicsTexture5: {
                type: 't',
                value: null
            },
            sphericalHarmonics8BitCompressionRangeMin: {
                type: 'f',
                value: []
            },
            sphericalHarmonics8BitCompressionRangeMax: {
                type: 'f',
                value: []
            },
            focal: {
                type: 'v2',
                value: new THREE.Vector2()
            },
            viewport: {
                type: 'v2',
                value: new THREE.Vector2()
            },
            basisViewport: {
                type: 'v2',
                value: new THREE.Vector2()
            },
            debugColor: {
                type: 'v3',
                value: new THREE.Color()
            },
            centersColorsTextureSize: {
                type: 'v2',
                value: new THREE.Vector2(1024, 1024)
            },
            sphericalHarmonicsDegree: {
                type: 'i',
                value: maxSphericalHarmonicsDegree
            },
            sphericalHarmonicsTextureSize: {
                type: 'v2',
                value: new THREE.Vector2(1024, 1024)
            },
            sphericalHarmonics8BitMode: {
                type: 'i',
                value: 0
            },
            sphericalHarmonicsMultiTextureMode: {
                type: 'i',
                value: 0
            },
            splatScale: {
                type: 'f',
                value: splatScale
            },
            pointCloudModeEnabled: {
                type: 'i',
                value: pointCloudModeEnabled ? 1 : 0
            },
            sceneIndexesTexture: {
                type: 't',
                value: null
            },
            sceneIndexesTextureSize: {
                type: 'v2',
                value: new THREE.Vector2(1024, 1024)
            },
            sceneCount: {
                type: 'i',
                value: 1
            },
            displayMode: {
                type: 'i',
                value: DisplayMode.Rgb
            },
            depthPass: {
                type: 'i',
                value: 0
            }
        };
        for (let i = 0; i < Constants.MaxScenes; i++) {
            uniforms.sphericalHarmonics8BitCompressionRangeMin.value.push(-Constants.SphericalHarmonics8BitCompressionRange / 2.0);
            uniforms.sphericalHarmonics8BitCompressionRangeMax.value.push(Constants.SphericalHarmonics8BitCompressionRange / 2.0);
        }

        if (enableOptionalEffects) {
            const sceneOpacity = [];
            for (let i = 0; i < Constants.MaxScenes; i++) {
                sceneOpacity.push(1.0);
            }
            uniforms['sceneOpacity'] = {
                type: 'f',
                value: sceneOpacity
            };

            const sceneVisibility = [];
            for (let i = 0; i < Constants.MaxScenes; i++) {
                sceneVisibility.push(1);
            }
            uniforms['sceneVisibility'] = {
                type: 'i',
                value: sceneVisibility
            };
        }

        return uniforms;
    }
}

class SplatMaterial3D {
    /**
     * Build the Three.js material that is used to render the splats.
     * @param {boolean} enableOptionalEffects When true, allows for usage of extra properties and attributes in the shader for effects
     *                                        such as opacity adjustment. Default is false for performance reasons.
     * @param {number} maxScreenSpaceSplatSize The maximum clip space splat size
     * @param {number} splatScale Value by which all splats are scaled in screen-space (default is 1.0)
     * @param {number} pointCloudModeEnabled Render all splats as screen-space circles
     * @param {number} maxSphericalHarmonicsDegree Degree of spherical harmonics to utilize in rendering splats
     * @return {THREE.ShaderMaterial}
     */
    static build(
        enableOptionalEffects = false,
        maxScreenSpaceSplatSize = 2048,
        splatScale = 1.0,
        pointCloudModeEnabled = false,
        maxSphericalHarmonicsDegree = 0,
        kernel2DSize = 0.3
    ) {
        const customVertexVars = `
            uniform vec2 covariancesTextureSize;
            uniform highp sampler2D covariancesTexture;
            uniform highp usampler2D covariancesTextureHalfFloat;
            uniform int covariancesAreHalfFloat;

            void fromCovarianceHalfFloatV4(uvec4 val, out vec4 first, out vec4 second) {
                vec2 r = unpackHalf2x16(val.r);
                vec2 g = unpackHalf2x16(val.g);
                vec2 b = unpackHalf2x16(val.b);

                first = vec4(r.x, r.y, g.x, g.y);
                second = vec4(b.x, b.y, 0.0, 0.0);
            }
        `;

        let vertexShaderSource = SplatMaterial.buildVertexShaderBase(enableOptionalEffects, maxSphericalHarmonicsDegree, customVertexVars);
        vertexShaderSource += SplatMaterial3D.buildVertexShaderProjection(enableOptionalEffects, maxScreenSpaceSplatSize, kernel2DSize);
        const fragmentShaderSource = SplatMaterial3D.buildFragmentShader();

        const uniforms = SplatMaterial.getUniforms(enableOptionalEffects, maxSphericalHarmonicsDegree, splatScale, pointCloudModeEnabled);

        uniforms['covariancesTextureSize'] = {
            type: 'v2',
            value: new THREE.Vector2(1024, 1024)
        };
        uniforms['covariancesTexture'] = {
            type: 't',
            value: null
        };
        uniforms['covariancesTextureHalfFloat'] = {
            type: 't',
            value: null
        };
        uniforms['covariancesAreHalfFloat'] = {
            type: 'i',
            value: 0
        };

        const material = new THREE.ShaderMaterial({
            uniforms: uniforms,
            vertexShader: vertexShaderSource,
            fragmentShader: fragmentShaderSource,
            glslVersion: THREE.GLSL3,
            transparent: true,
            alphaTest: 1.0,
            blending: THREE.NormalBlending,
            depthTest: true,
            depthWrite: false,
            side: THREE.DoubleSide
        });

        return material;
    }

    static buildVertexShaderProjection(enableOptionalEffects, maxScreenSpaceSplatSize, kernel2DSize) {
        let vertexShaderSource = `

            vec4 sampledCovarianceA;
            vec4 sampledCovarianceB;
            vec3 cov3D_M11_M12_M13;
            vec3 cov3D_M22_M23_M33;
            if (covariancesAreHalfFloat == 0) {
                sampledCovarianceA = texture(covariancesTexture, getDataUVF(nearestEvenIndex, 1.5, oddOffset,
                                                                            covariancesTextureSize));
                sampledCovarianceB = texture(covariancesTexture, getDataUVF(nearestEvenIndex, 1.5, oddOffset + uint(1),
                                                                            covariancesTextureSize));

                cov3D_M11_M12_M13 = vec3(sampledCovarianceA.rgb) * (1.0 - fOddOffset) +
                                    vec3(sampledCovarianceA.ba, sampledCovarianceB.r) * fOddOffset;
                cov3D_M22_M23_M33 = vec3(sampledCovarianceA.a, sampledCovarianceB.rg) * (1.0 - fOddOffset) +
                                    vec3(sampledCovarianceB.gba) * fOddOffset;
            } else {
                uvec4 sampledCovarianceU = texture(covariancesTextureHalfFloat, getDataUV(1, 0, covariancesTextureSize));
                fromCovarianceHalfFloatV4(sampledCovarianceU, sampledCovarianceA, sampledCovarianceB);
                cov3D_M11_M12_M13 = sampledCovarianceA.rgb;
                cov3D_M22_M23_M33 = vec3(sampledCovarianceA.a, sampledCovarianceB.rg);
            }
        
            // Construct the 3D covariance matrix
            mat3 Vrk = mat3(
                cov3D_M11_M12_M13.x, cov3D_M11_M12_M13.y, cov3D_M11_M12_M13.z,
                cov3D_M11_M12_M13.y, cov3D_M22_M23_M33.x, cov3D_M22_M23_M33.y,
                cov3D_M11_M12_M13.z, cov3D_M22_M23_M33.y, cov3D_M22_M23_M33.z
            );

            mat3 J;
            // Construct the Jacobian of the affine approximation of the projection matrix. It will be used to transform the
            // 3D covariance matrix instead of using the actual projection matrix because that transformation would
            // require a non-linear component (perspective division) which would yield a non-gaussian result.
            float s = 1.0 / (viewCenter.z * viewCenter.z);
            J = mat3(
                focal.x / viewCenter.z, 0., -(focal.x * viewCenter.x) * s,
                0., focal.y / viewCenter.z, -(focal.y * viewCenter.y) * s,
                0., 0., 0.
            );

            // Concatenate the projection approximation with the model-view transformation
            mat3 W = transpose(mat3(transformModelViewMatrix));
            mat3 T = W * J;

            // Transform the 3D covariance matrix (Vrk) to compute the 2D covariance matrix
            mat3 cov2Dm = transpose(T) * Vrk * T;
            `;

        vertexShaderSource += `
            cov2Dm[0][0] += ${kernel2DSize};
            cov2Dm[1][1] += ${kernel2DSize};
        `;

        vertexShaderSource += `

            // We are interested in the upper-left 2x2 portion of the projected 3D covariance matrix because
            // we only care about the X and Y values. We want the X-diagonal, cov2Dm[0][0],
            // the Y-diagonal, cov2Dm[1][1], and the correlation between the two cov2Dm[0][1]. We don't
            // need cov2Dm[1][0] because it is a symetric matrix.
            vec3 cov2Dv = vec3(cov2Dm[0][0], cov2Dm[0][1], cov2Dm[1][1]);

            // We now need to solve for the eigen-values and eigen vectors of the 2D covariance matrix
            // so that we can determine the 2D basis for the splat. This is done using the method described
            // here: https://people.math.harvard.edu/~knill/teaching/math21b2004/exhibits/2dmatrices/index.html
            // After calculating the eigen-values and eigen-vectors, we calculate the basis for rendering the splat
            // by normalizing the eigen-vectors and then multiplying them by (sqrt(8) * sqrt(eigen-value)), which is
            // equal to scaling them by sqrt(8) standard deviations.
            //
            // This is a different approach than in the original work at INRIA. In that work they compute the
            // max extents of the projected splat in screen space to form a screen-space aligned bounding rectangle
            // which forms the geometry that is actually rasterized. The dimensions of that bounding box are 3.0
            // times the square root of the maximum eigen-value, or 3 standard deviations. They then use the inverse
            // 2D covariance matrix (called 'conic') in the CUDA rendering thread to determine fragment opacity by
            // calculating the full gaussian: exp(-0.5 * (X - mean) * conic * (X - mean)) * splat opacity
            float a = cov2Dv.x;
            float d = cov2Dv.z;
            float b = cov2Dv.y;
            float D = a * d - b * b;
            float trace = a + d;
            float traceOver2 = 0.5 * trace;
            float term2 = sqrt(max(0.1f, traceOver2 * traceOver2 - D));
            float eigenValue1 = traceOver2 + term2;
            float eigenValue2 = traceOver2 - term2;

            if (pointCloudModeEnabled == 1) {
                eigenValue1 = eigenValue2 = 0.2;
            }

            if (eigenValue2 <= 0.0) return;

            vec2 eigenVector1 = normalize(vec2(b, eigenValue1 - a));
            // since the eigen vectors are orthogonal, we derive the second one from the first
            vec2 eigenVector2 = vec2(eigenVector1.y, -eigenVector1.x);

            // We use sqrt(8) standard deviations instead of 3 to eliminate more of the splat with a very low opacity.
            vec2 basisVector1 = eigenVector1 * splatScale * min(sqrt8 * sqrt(eigenValue1), ${parseInt(maxScreenSpaceSplatSize)}.0);
            vec2 basisVector2 = eigenVector2 * splatScale * min(sqrt8 * sqrt(eigenValue2), ${parseInt(maxScreenSpaceSplatSize)}.0);
            `;

        if (enableOptionalEffects) {
            vertexShaderSource += `
                vColor.a *= splatOpacityFromScene;
            `;
        }

        vertexShaderSource += `
            vec2 ndcOffset = vec2(vPosition.x * basisVector1 + vPosition.y * basisVector2) *
                             basisViewport * 2.0;

            vec4 quadPos = vec4(ndcCenter.xy + ndcOffset, ndcCenter.z, 1.0);
            gl_Position = quadPos;

            // Scale the position data we send to the fragment shader
            vPosition *= sqrt8;
        `;

        vertexShaderSource += SplatMaterial.getVertexShaderFadeIn();
        vertexShaderSource += `}`;

        return vertexShaderSource;
    }

    static buildFragmentShader() {
        let fragmentShaderSource = `
            precision highp float;
            #include <common>

            uniform vec3 debugColor;
            uniform int depthPass;

            varying vec4 vColor;
            varying vec2 vUv;
            varying vec2 vPosition;

            layout(location = 0) out vec4 fragColor;
        `;

        fragmentShaderSource += `
            void main () {
                // Compute the positional squared distance from the center of the splat to the current fragment.
                float A = dot(vPosition, vPosition);
                // Since the positional data in vPosition has been scaled by sqrt(8), the squared result will be
                // scaled by a factor of 8. If the squared result is larger than 8, it means it is outside the ellipse
                // defined by the rectangle formed by vPosition. It also means it's farther
                // away than sqrt(8) standard deviations from the mean.
                if (A > 8.0) discard;

                // Since the rendered splat is scaled by sqrt(8), the inverse covariance matrix that is part of
                // the gaussian formula becomes the identity matrix. We're then left with (X - mean) * (X - mean),
                // and since 'mean' is zero, we have X * X, which is the same as A:
                float opacity = exp(-0.5 * A) * vColor.a;

                if (depthPass == 1) {
                    fragColor = vec4(gl_FragCoord.z, 0.0, 0.0, opacity);
                } else {
                    fragColor = vec4(vColor.rgb, opacity);
                }
            }
        `;

        return fragmentShaderSource;
    }
}

class SplatMaterial2D {
    /**
     * Build the Three.js material that is used to render the splats.
     * @param {boolean} enableOptionalEffects When true, allows for usage of extra properties and attributes in the shader for effects
     *                                        such as opacity adjustment. Default is false for performance reasons.
     * @param {number} splatScale Value by which all splats are scaled in screen-space (default is 1.0)
     * @param {number} pointCloudModeEnabled Render all splats as screen-space circles
     * @param {number} maxSphericalHarmonicsDegree Degree of spherical harmonics to utilize in rendering splats
     * @return {THREE.ShaderMaterial}
     */
    static build(enableOptionalEffects = false, splatScale = 1.0, pointCloudModeEnabled = false, maxSphericalHarmonicsDegree = 0) {
        const customVertexVars = `
            uniform vec2 scaleRotationsTextureSize;
            uniform highp sampler2D scaleRotationsTexture;
            uniform highp sampler2D scaleRotationsTexture1;
            uniform int scaleRotationsMultiTextureMode;
            varying mat3 vT;
            varying vec2 vQuadCenter;
            varying vec2 vOffset;
        `;

        let vertexShaderSource = SplatMaterial.buildVertexShaderBase(enableOptionalEffects, maxSphericalHarmonicsDegree, customVertexVars);
        vertexShaderSource += SplatMaterial2D.buildVertexShaderProjection();
        const fragmentShaderSource = SplatMaterial2D.buildFragmentShader();

        const uniforms = SplatMaterial.getUniforms(enableOptionalEffects, maxSphericalHarmonicsDegree, splatScale, pointCloudModeEnabled);

        uniforms['scaleRotationsTexture'] = {
            type: 't',
            value: null
        };
        uniforms['scaleRotationsTexture1'] = {
            type: 't',
            value: null
        };
        uniforms['scaleRotationsMultiTextureMode'] = {
            type: 'i',
            value: 0
        };
        uniforms['scaleRotationsTextureSize'] = {
            type: 'v2',
            value: new THREE.Vector2(1024, 1024)
        };

        const material = new THREE.ShaderMaterial({
            uniforms: uniforms,
            vertexShader: vertexShaderSource,
            fragmentShader: fragmentShaderSource,
            glslVersion: THREE.GLSL3,
            transparent: true,
            alphaTest: 1.0,
            blending: THREE.NormalBlending,
            depthTest: true,
            depthWrite: false,
            side: THREE.DoubleSide
        });

        return material;
    }

    static buildVertexShaderProjection() {
        // Original CUDA code for calculating splat-to-screen transformation, for reference
        /*
            glm::mat3 R = quat_to_rotmat(rot);
            glm::mat3 S = scale_to_mat(scale, mod);
            glm::mat3 L = R * S;

            // center of Gaussians in the camera coordinate
            glm::mat3x4 splat2world = glm::mat3x4(
                glm::vec4(L[0], 0.0),
                glm::vec4(L[1], 0.0),
                glm::vec4(p_orig.x, p_orig.y, p_orig.z, 1)
            );

            glm::mat4 world2ndc = glm::mat4(
                projmatrix[0], projmatrix[4], projmatrix[8], projmatrix[12],
                projmatrix[1], projmatrix[5], projmatrix[9], projmatrix[13],
                projmatrix[2], projmatrix[6], projmatrix[10], projmatrix[14],
                projmatrix[3], projmatrix[7], projmatrix[11], projmatrix[15]
            );

            glm::mat3x4 ndc2pix = glm::mat3x4(
                glm::vec4(float(W) / 2.0, 0.0, 0.0, float(W-1) / 2.0),
                glm::vec4(0.0, float(H) / 2.0, 0.0, float(H-1) / 2.0),
                glm::vec4(0.0, 0.0, 0.0, 1.0)
            );

            T = glm::transpose(splat2world) * world2ndc * ndc2pix;
            normal = transformVec4x3({L[2].x, L[2].y, L[2].z}, viewmatrix);
        */

        // Compute a 2D-to-2D mapping matrix from a tangent plane into a image plane
        // given a 2D gaussian parameters. T = WH (from the paper: https://arxiv.org/pdf/2403.17888)
        let vertexShaderSource = `

            vec3 scaleRotation123;
            vec3 scaleRotation456;
            if (scaleRotationsMultiTextureMode == 0) {
                vec4 scaleRotationA = texture(scaleRotationsTexture, getDataUVF(nearestEvenIndex, 1.5,
                                                                                oddOffset, scaleRotationsTextureSize));
                vec4 scaleRotationB = texture(scaleRotationsTexture, getDataUVF(nearestEvenIndex, 1.5,
                                                                                oddOffset + uint(1), scaleRotationsTextureSize));
                scaleRotation123 = vec3(scaleRotationA.rgb) * (1.0 - fOddOffset) +
                                   vec3(scaleRotationA.ba, scaleRotationB.r) * fOddOffset;
                scaleRotation456 = vec3(scaleRotationA.a, scaleRotationB.rg) * (1.0 - fOddOffset) +
                                   vec3(scaleRotationB.gba) * fOddOffset;
            } else {
                vec2 srUV = getDataUV(1, 0, scaleRotationsTextureSize);
                vec4 srTex0 = texture(scaleRotationsTexture, srUV);
                vec4 srTex1 = texture(scaleRotationsTexture1, srUV);
                scaleRotation123 = srTex0.rgb;
                scaleRotation456 = vec3(srTex0.a, srTex1.rg);
            }

            float missingW = sqrt(1.0 - scaleRotation456.x * scaleRotation456.x - scaleRotation456.y *
                                    scaleRotation456.y - scaleRotation456.z * scaleRotation456.z);
            mat3 R = quaternionToRotationMatrix(scaleRotation456.r, scaleRotation456.g, scaleRotation456.b, missingW);
            mat3 S = mat3(scaleRotation123.r, 0.0, 0.0,
                            0.0, scaleRotation123.g, 0.0,
                            0.0, 0.0, 0.0);
            
            mat3 RS = R * S;

            vec3 normal = vec3(viewMatrix * vec4(RS[0][2], RS[1][2], RS[2][2], 0.0));
        `;

        // Original CUDA code for projection to 2D, for reference
        /*
            float3 T0 = {T[0][0], T[0][1], T[0][2]};
            float3 T1 = {T[1][0], T[1][1], T[1][2]};
            float3 T3 = {T[2][0], T[2][1], T[2][2]};

            // Compute AABB
            float3 temp_point = {1.0f, 1.0f, -1.0f};
            float distance = sumf3(T3 * T3 * temp_point);
            float3 f = (1 / distance) * temp_point;
            if (distance == 0.0) return false;

            point_image = {
                sumf3(f * T0 * T3),
                sumf3(f * T1 * T3)
            };

            float2 temp = {
                sumf3(f * T0 * T0),
                sumf3(f * T1 * T1)
            };
            float2 half_extend = point_image * point_image - temp;
            extent = sqrtf2(maxf2(1e-4, half_extend));
            return true;
        */

        // Computing the bounding box of the 2D Gaussian and its center
        // The center of the bounding box is used to create a low pass filter.
        // This code is based off the reference implementation and creates an AABB aligned
        // with the screen for the quad to be rendered.
        const referenceQuadGeneration = `
            vec3 T0 = vec3(T[0][0], T[0][1], T[0][2]);
            vec3 T1 = vec3(T[1][0], T[1][1], T[1][2]);
            vec3 T3 = vec3(T[2][0], T[2][1], T[2][2]);

            vec3 tempPoint = vec3(1.0, 1.0, -1.0);
            float distance = (T3.x * T3.x * tempPoint.x) + (T3.y * T3.y * tempPoint.y) + (T3.z * T3.z * tempPoint.z);
            vec3 f = (1.0 / distance) * tempPoint;
            if (abs(distance) < 0.00001) return;

            float pointImageX = (T0.x * T3.x * f.x) + (T0.y * T3.y * f.y) + (T0.z * T3.z * f.z);
            float pointImageY = (T1.x * T3.x * f.x) + (T1.y * T3.y * f.y) + (T1.z * T3.z * f.z);
            vec2 pointImage = vec2(pointImageX, pointImageY);

            float tempX = (T0.x * T0.x * f.x) + (T0.y * T0.y * f.y) + (T0.z * T0.z * f.z);
            float tempY = (T1.x * T1.x * f.x) + (T1.y * T1.y * f.y) + (T1.z * T1.z * f.z);
            vec2 temp = vec2(tempX, tempY);

            vec2 halfExtend = pointImage * pointImage - temp;
            vec2 extent = sqrt(max(vec2(0.0001), halfExtend));
            float radius = max(extent.x, extent.y);

            vec2 ndcOffset = ((position.xy * radius * 3.0) * basisViewport * 2.0);

            vec4 quadPos = vec4(ndcCenter.xy + ndcOffset, ndcCenter.z, 1.0);
            gl_Position = quadPos;

            vT = T;
            vQuadCenter = pointImage;
            vFragCoord = (quadPos.xy * 0.5 + 0.5) * viewport;
        `;

        const useRefImplementation = false;
        if (useRefImplementation) {
            vertexShaderSource += referenceQuadGeneration;
        } else {
            // Create a quad that is aligned with the eigen vectors of the projected gaussian for rendering.
            // This is a different approach than the reference implementation, similar to how the rendering of
            // 3D gaussians in this viewer differs from the reference implementation. If the quad is too small
            // (smaller than a pixel), then revert to the reference implementation.
            vertexShaderSource += `

                float sqrt8 = sqrt(8.0);
                vec3 corner = splatCenter
                    + RS[0] * position.x * sqrt8
                    + RS[1] * position.y * sqrt8;

                vec4 cornerClip = projectionMatrix * transformModelViewMatrix * vec4(corner, 1.0f);

                // Emit the corner position
                gl_Position = cornerClip;

                // Emit the offset of the corner in perspective space
                vOffset = position.xy * sqrt8;

                // Normal is the third column of the rotation matrix, which is the normal of the tangent plane
                vec3 normalWorld = R[2];

                if (displayMode == 0) {
                    // RGB has already been set in the vertex shader
                } else if (displayMode == 1) {
                    // Normal mode
                    vColor.rgb = (normalWorld + 1.0) / 2.0;
                }
            `;
        }

        vertexShaderSource += SplatMaterial.getVertexShaderFadeIn();
        vertexShaderSource += `}`;

        return vertexShaderSource;
    }

    static buildFragmentShader() {
        // Original CUDA code for splat intersection, for reference
        /*
            const float2 xy = collected_xy[j];
            const float3 Tu = collected_Tu[j];
            const float3 Tv = collected_Tv[j];
            const float3 Tw = collected_Tw[j];
            float3 k = pix.x * Tw - Tu;
            float3 l = pix.y * Tw - Tv;
            float3 p = cross(k, l);
            if (p.z == 0.0) continue;
            float2 s = {p.x / p.z, p.y / p.z};
            float rho3d = (s.x * s.x + s.y * s.y);
            float2 d = {xy.x - pixf.x, xy.y - pixf.y};
            float rho2d = FilterInvSquare * (d.x * d.x + d.y * d.y);

            // compute intersection and depth
            float rho = min(rho3d, rho2d);
            float depth = (rho3d <= rho2d) ? (s.x * Tw.x + s.y * Tw.y) + Tw.z : Tw.z;
            if (depth < near_n) continue;
            float4 nor_o = collected_normal_opacity[j];
            float normal[3] = {nor_o.x, nor_o.y, nor_o.z};
            float opa = nor_o.w;

            float power = -0.5f * rho;
            if (power > 0.0f)
                continue;

            // Eq. (2) from 3D Gaussian splatting paper.
            // Obtain alpha by multiplying with Gaussian opacity
            // and its exponential falloff from mean.
            // Avoid numerical instabilities (see paper appendix).
            float alpha = min(0.99f, opa * exp(power));
            if (alpha < 1.0f / 255.0f)
                continue;
            float test_T = T * (1 - alpha);
            if (test_T < 0.0001f)
            {
                done = true;
                continue;
            }

            float w = alpha * T;
        */
        let fragmentShaderSource = `
            precision highp float;
            #include <common>

            uniform vec3 debugColor;
            uniform int depthPass;

            in vec4 vColor;
            in vec2 vOffset;

            layout(location = 0) out vec4 fragColor;

            void main ()
            {
                float sigma = dot(vOffset, vOffset);

                if (sigma > 8.0)
                {
                    discard;
                }

                float G = min(exp(-0.5 * sigma), 1.0);

                float opacity = G * vColor.a;

                if (depthPass == 1) {
                    fragColor = vec4(gl_FragCoord.z, 0.0, 0.0, opacity);
                } else {
                    fragColor = vec4(vColor.rgb, opacity);
                }
            }
                    `;

        return fragmentShaderSource;
    }
}

class SplatGeometry {
    /**
     * Build the Three.js geometry that will be used to render the splats. The geometry is instanced and is made up of
     * vertices for a single quad as well as an attribute buffer for the splat indexes.
     * @param {number} maxSplatCount The maximum number of splats that the geometry will need to accomodate
     * @return {THREE.InstancedBufferGeometry}
     */
    static build(maxSplatCount) {
        const baseGeometry = new THREE.BufferGeometry();
        baseGeometry.setIndex([0, 1, 2, 0, 2, 3]);

        // Vertices for the instanced quad
        const positionsArray = new Float32Array(4 * 3);
        const positions = new THREE.BufferAttribute(positionsArray, 3);
        baseGeometry.setAttribute('position', positions);
        positions.setXYZ(0, -1.0, -1.0, 0.0);
        positions.setXYZ(1, -1.0, 1.0, 0.0);
        positions.setXYZ(2, 1.0, 1.0, 0.0);
        positions.setXYZ(3, 1.0, -1.0, 0.0);
        positions.needsUpdate = true;

        const geometry = new THREE.InstancedBufferGeometry().copy(baseGeometry);

        // Splat index buffer
        const splatIndexArray = new Uint32Array(maxSplatCount);
        const splatIndexes = new THREE.InstancedBufferAttribute(splatIndexArray, 1, false);
        splatIndexes.setUsage(THREE.DynamicDrawUsage);
        geometry.setAttribute('splatIndex', splatIndexes);

        geometry.instanceCount = 0;

        return geometry;
    }
}

/**
 * SplatScene: Descriptor for a single splat scene managed by an instance of SplatMesh.
 */
class SplatScene extends THREE.Object3D {
    constructor(
        splatArray,
        position = new THREE.Vector3(),
        quaternion = new THREE.Quaternion(),
        scale = new THREE.Vector3(1, 1, 1),
        minimumAlpha = 1,
        opacity = 1.0,
        visible = true
    ) {
        super();
        this.splatArray = splatArray;
        this.position.copy(position);
        this.quaternion.copy(quaternion);
        this.scale.copy(scale);
        this.transform = new THREE.Matrix4();
        this.minimumAlpha = minimumAlpha;
        this.opacity = opacity;
        this.visible = visible;
    }

    copyTransformData(otherScene) {
        this.position.copy(otherScene.position);
        this.quaternion.copy(otherScene.quaternion);
        this.scale.copy(otherScene.scale);
        this.transform.copy(otherScene.transform);
    }

    updateTransform() {
        if (this.matrixAutoUpdate) this.updateMatrix();
        this.transform.copy(this.matrix);
    }
}

class SplatTreeNode {
    static idGen = 0;

    constructor(min, max, depth, id) {
        this.min = new THREE.Vector3().copy(min);
        this.max = new THREE.Vector3().copy(max);
        this.boundingBox = new THREE.Box3(this.min, this.max);
        this.center = new THREE.Vector3().copy(this.max).sub(this.min).multiplyScalar(0.5).add(this.min);
        this.depth = depth;
        this.children = [];
        this.data = null;
        this.id = id || SplatTreeNode.idGen++;
    }
}

class SplatSubTree {
    constructor(maxDepth, maxCentersPerNode) {
        this.maxDepth = maxDepth;
        this.maxCentersPerNode = maxCentersPerNode;
        this.sceneDimensions = new THREE.Vector3();
        this.sceneMin = new THREE.Vector3();
        this.sceneMax = new THREE.Vector3();
        this.rootNode = null;
        this.nodesWithIndexes = [];
        this.splatMesh = null;
    }

    static convertWorkerSubTreeNode(workerSubTreeNode) {
        const minVector = new THREE.Vector3().fromArray(workerSubTreeNode.min);
        const maxVector = new THREE.Vector3().fromArray(workerSubTreeNode.max);
        const convertedNode = new SplatTreeNode(minVector, maxVector, workerSubTreeNode.depth, workerSubTreeNode.id);
        if (workerSubTreeNode.data.indexes) {
            convertedNode.data = {
                indexes: []
            };
            for (let index of workerSubTreeNode.data.indexes) {
                convertedNode.data.indexes.push(index);
            }
        }
        if (workerSubTreeNode.children) {
            for (let child of workerSubTreeNode.children) {
                convertedNode.children.push(SplatSubTree.convertWorkerSubTreeNode(child));
            }
        }
        return convertedNode;
    }

    static convertWorkerSubTree(workerSubTree, splatMesh) {
        const convertedSubTree = new SplatSubTree(workerSubTree.maxDepth, workerSubTree.maxCentersPerNode);
        convertedSubTree.sceneMin = new THREE.Vector3().fromArray(workerSubTree.sceneMin);
        convertedSubTree.sceneMax = new THREE.Vector3().fromArray(workerSubTree.sceneMax);

        convertedSubTree.splatMesh = splatMesh;
        convertedSubTree.rootNode = SplatSubTree.convertWorkerSubTreeNode(workerSubTree.rootNode);

        const visitLeavesFromNode = (node, visitFunc) => {
            if (node.children.length === 0) visitFunc(node);
            for (let child of node.children) {
                visitLeavesFromNode(child, visitFunc);
            }
        };

        convertedSubTree.nodesWithIndexes = [];
        visitLeavesFromNode(convertedSubTree.rootNode, (node) => {
            if (node.data && node.data.indexes && node.data.indexes.length > 0) {
                convertedSubTree.nodesWithIndexes.push(node);
            }
        });

        return convertedSubTree;
    }
}

function createSplatTreeWorker(self) {
    let WorkerSplatTreeNodeIDGen = 0;

    class WorkerBox3 {
        constructor(min, max) {
            this.min = [min[0], min[1], min[2]];
            this.max = [max[0], max[1], max[2]];
        }

        containsPoint(point) {
            return (
                point[0] >= this.min[0] &&
                point[0] <= this.max[0] &&
                point[1] >= this.min[1] &&
                point[1] <= this.max[1] &&
                point[2] >= this.min[2] &&
                point[2] <= this.max[2]
            );
        }
    }

    class WorkerSplatSubTree {
        constructor(maxDepth, maxCentersPerNode) {
            this.maxDepth = maxDepth;
            this.maxCentersPerNode = maxCentersPerNode;
            this.sceneDimensions = [];
            this.sceneMin = [];
            this.sceneMax = [];
            this.rootNode = null;
            this.addedIndexes = {};
            this.nodesWithIndexes = [];
            this.splatMesh = null;
            this.disposed = false;
        }
    }

    class WorkerSplatTreeNode {
        constructor(min, max, depth, id) {
            this.min = [min[0], min[1], min[2]];
            this.max = [max[0], max[1], max[2]];
            this.center = [(max[0] - min[0]) * 0.5 + min[0], (max[1] - min[1]) * 0.5 + min[1], (max[2] - min[2]) * 0.5 + min[2]];
            this.depth = depth;
            this.children = [];
            this.data = null;
            this.id = id || WorkerSplatTreeNodeIDGen++;
        }
    }

    processSplatTreeNode = function (tree, node, indexToCenter, sceneCenters) {
        const splatCount = node.data.indexes.length;

        if (splatCount < tree.maxCentersPerNode || node.depth > tree.maxDepth) {
            const newIndexes = [];
            for (let i = 0; i < node.data.indexes.length; i++) {
                if (!tree.addedIndexes[node.data.indexes[i]]) {
                    newIndexes.push(node.data.indexes[i]);
                    tree.addedIndexes[node.data.indexes[i]] = true;
                }
            }
            node.data.indexes = newIndexes;
            node.data.indexes.sort((a, b) => {
                if (a > b) return 1;
                else return -1;
            });
            tree.nodesWithIndexes.push(node);
            return;
        }

        const nodeDimensions = [node.max[0] - node.min[0], node.max[1] - node.min[1], node.max[2] - node.min[2]];
        const halfDimensions = [nodeDimensions[0] * 0.5, nodeDimensions[1] * 0.5, nodeDimensions[2] * 0.5];
        const nodeCenter = [node.min[0] + halfDimensions[0], node.min[1] + halfDimensions[1], node.min[2] + halfDimensions[2]];

        const childrenBounds = [
            // top section, clockwise from upper-left (looking from above, +Y)
            new WorkerBox3(
                [nodeCenter[0] - halfDimensions[0], nodeCenter[1], nodeCenter[2] - halfDimensions[2]],
                [nodeCenter[0], nodeCenter[1] + halfDimensions[1], nodeCenter[2]]
            ),
            new WorkerBox3(
                [nodeCenter[0], nodeCenter[1], nodeCenter[2] - halfDimensions[2]],
                [nodeCenter[0] + halfDimensions[0], nodeCenter[1] + halfDimensions[1], nodeCenter[2]]
            ),
            new WorkerBox3(
                [nodeCenter[0], nodeCenter[1], nodeCenter[2]],
                [nodeCenter[0] + halfDimensions[0], nodeCenter[1] + halfDimensions[1], nodeCenter[2] + halfDimensions[2]]
            ),
            new WorkerBox3(
                [nodeCenter[0] - halfDimensions[0], nodeCenter[1], nodeCenter[2]],
                [nodeCenter[0], nodeCenter[1] + halfDimensions[1], nodeCenter[2] + halfDimensions[2]]
            ),

            // bottom section, clockwise from lower-left (looking from above, +Y)
            new WorkerBox3(
                [nodeCenter[0] - halfDimensions[0], nodeCenter[1] - halfDimensions[1], nodeCenter[2] - halfDimensions[2]],
                [nodeCenter[0], nodeCenter[1], nodeCenter[2]]
            ),
            new WorkerBox3(
                [nodeCenter[0], nodeCenter[1] - halfDimensions[1], nodeCenter[2] - halfDimensions[2]],
                [nodeCenter[0] + halfDimensions[0], nodeCenter[1], nodeCenter[2]]
            ),
            new WorkerBox3(
                [nodeCenter[0], nodeCenter[1] - halfDimensions[1], nodeCenter[2]],
                [nodeCenter[0] + halfDimensions[0], nodeCenter[1], nodeCenter[2] + halfDimensions[2]]
            ),
            new WorkerBox3(
                [nodeCenter[0] - halfDimensions[0], nodeCenter[1] - halfDimensions[1], nodeCenter[2]],
                [nodeCenter[0], nodeCenter[1], nodeCenter[2] + halfDimensions[2]]
            )
        ];

        const splatCounts = [];
        const baseIndexes = [];
        for (let i = 0; i < childrenBounds.length; i++) {
            splatCounts[i] = 0;
            baseIndexes[i] = [];
        }

        const center = [0, 0, 0];
        for (let i = 0; i < splatCount; i++) {
            const splatGlobalIndex = node.data.indexes[i];
            const centerBase = indexToCenter[splatGlobalIndex];
            center[0] = sceneCenters[centerBase];
            center[1] = sceneCenters[centerBase + 1];
            center[2] = sceneCenters[centerBase + 2];
            for (let j = 0; j < childrenBounds.length; j++) {
                if (childrenBounds[j].containsPoint(center)) {
                    splatCounts[j]++;
                    baseIndexes[j].push(splatGlobalIndex);
                }
            }
        }

        for (let i = 0; i < childrenBounds.length; i++) {
            const childNode = new WorkerSplatTreeNode(childrenBounds[i].min, childrenBounds[i].max, node.depth + 1);
            childNode.data = {
                indexes: baseIndexes[i]
            };
            node.children.push(childNode);
        }

        node.data = {};
        for (let child of node.children) {
            processSplatTreeNode(tree, child, indexToCenter, sceneCenters);
        }
        return;
    };

    const buildSubTree = (sceneCenters, maxDepth, maxCentersPerNode) => {
        const sceneMin = [0, 0, 0];
        const sceneMax = [0, 0, 0];
        const indexes = [];
        const centerCount = Math.floor(sceneCenters.length / 4);
        for (let i = 0; i < centerCount; i++) {
            const base = i * 4;
            const x = sceneCenters[base];
            const y = sceneCenters[base + 1];
            const z = sceneCenters[base + 2];
            const index = Math.round(sceneCenters[base + 3]);
            if (i === 0 || x < sceneMin[0]) sceneMin[0] = x;
            if (i === 0 || x > sceneMax[0]) sceneMax[0] = x;
            if (i === 0 || y < sceneMin[1]) sceneMin[1] = y;
            if (i === 0 || y > sceneMax[1]) sceneMax[1] = y;
            if (i === 0 || z < sceneMin[2]) sceneMin[2] = z;
            if (i === 0 || z > sceneMax[2]) sceneMax[2] = z;
            indexes.push(index);
        }
        const subTree = new WorkerSplatSubTree(maxDepth, maxCentersPerNode);
        subTree.sceneMin = sceneMin;
        subTree.sceneMax = sceneMax;
        subTree.rootNode = new WorkerSplatTreeNode(subTree.sceneMin, subTree.sceneMax, 0);
        subTree.rootNode.data = {
            indexes: indexes
        };

        return subTree;
    };

    function createSplatTree(allCenters, maxDepth, maxCentersPerNode) {
        const indexToCenter = [];
        for (let sceneCenters of allCenters) {
            const centerCount = Math.floor(sceneCenters.length / 4);
            for (let i = 0; i < centerCount; i++) {
                const base = i * 4;
                const index = Math.round(sceneCenters[base + 3]);
                indexToCenter[index] = base;
            }
        }
        const subTrees = [];
        for (let sceneCenters of allCenters) {
            const subTree = buildSubTree(sceneCenters, maxDepth, maxCentersPerNode);
            subTrees.push(subTree);
            processSplatTreeNode(subTree, subTree.rootNode, indexToCenter, sceneCenters);
        }
        self.postMessage({
            subTrees: subTrees
        });
    }

    self.onmessage = (e) => {
        if (e.data.process) {
            createSplatTree(e.data.process.centers, e.data.process.maxDepth, e.data.process.maxCentersPerNode);
        }
    };
}

function workerProcessCenters(splatTreeWorker, centers, transferBuffers, maxDepth, maxCentersPerNode) {
    splatTreeWorker.postMessage(
        {
            process: {
                centers: centers,
                maxDepth: maxDepth,
                maxCentersPerNode: maxCentersPerNode
            }
        },
        transferBuffers
    );
}

function checkAndCreateWorker() {
    const blobUrl = URL.createObjectURL(
        new Blob(['(', createSplatTreeWorker.toString(), ')(self)'], {
            type: 'application/javascript'
        })
    );
    const splatTreeWorker = new Worker(blobUrl);
    splatTreeWorker.blobUrl = blobUrl;
    return splatTreeWorker;
}

/**
 * SplatTree: Octree tailored to splat data from a SplatMesh instance
 */
class SplatTree {
    constructor(maxDepth, maxCentersPerNode) {
        this.maxDepth = maxDepth;
        this.maxCentersPerNode = maxCentersPerNode;
        this.subTrees = [];
        this.splatMesh = null;
    }

    dispose() {
        this.diposeSplatTreeWorker();
        this.splatMesh = null;
        this.subTrees = [];
        this.disposed = true;
    }

    diposeSplatTreeWorker() {
        if (this.splatTreeWorker) {
            if (this.splatTreeWorker.blobUrl) URL.revokeObjectURL(this.splatTreeWorker.blobUrl);
            this.splatTreeWorker.terminate();
        }
        this.splatTreeWorker = null;
    }

    /**
     * Construct this instance of SplatTree from an instance of SplatMesh.
     *
     * @param {SplatMesh} splatMesh The instance of SplatMesh from which to construct this splat tree.
     * @param {function} filterFunc Optional function to filter out unwanted splats.
     * @param {function} onIndexesUpload Function to be called when the upload of splat centers to the splat tree
     *                                   builder worker starts and finishes.
     * @param {function} onSplatTreeConstruction Function to be called when the conversion of the local splat tree from
     *                                           the format produced by the splat tree builder worker starts and ends.
     * @return {undefined}
     */
    processSplatMesh = function (splatMesh, filterFunc = () => true, onIndexesUpload, onSplatTreeConstruction) {
        if (!this.splatTreeWorker) this.splatTreeWorker = checkAndCreateWorker();

        this.splatMesh = splatMesh;
        this.subTrees = [];
        const center = new THREE.Vector3();

        const addCentersForScene = (splatOffset, splatCount) => {
            const sceneCenters = new Float32Array(splatCount * 4);
            let addedCount = 0;
            for (let i = 0; i < splatCount; i++) {
                const globalSplatIndex = i + splatOffset;
                if (filterFunc(globalSplatIndex)) {
                    splatMesh.getSplatCenter(globalSplatIndex, center);
                    const addBase = addedCount * 4;
                    sceneCenters[addBase] = center.x;
                    sceneCenters[addBase + 1] = center.y;
                    sceneCenters[addBase + 2] = center.z;
                    sceneCenters[addBase + 3] = globalSplatIndex;
                    addedCount++;
                }
            }
            return sceneCenters;
        };

        return new Promise((resolve) => {
            const checkForEarlyExit = () => {
                if (this.disposed) {
                    this.diposeSplatTreeWorker();
                    resolve();
                    return true;
                }
                return false;
            };

            if (onIndexesUpload) onIndexesUpload(false);

            delayedExecute(() => {
                if (checkForEarlyExit()) return;

                const allCenters = [];
                const sceneCenters = addCentersForScene(0, splatMesh.getSplatCount());
                allCenters.push(sceneCenters);

                this.splatTreeWorker.onmessage = (e) => {
                    if (checkForEarlyExit()) return;

                    if (e.data.subTrees) {
                        if (onSplatTreeConstruction) onSplatTreeConstruction(false);

                        delayedExecute(() => {
                            if (checkForEarlyExit()) return;

                            for (let workerSubTree of e.data.subTrees) {
                                const convertedSubTree = SplatSubTree.convertWorkerSubTree(workerSubTree, splatMesh);
                                this.subTrees.push(convertedSubTree);
                            }
                            this.diposeSplatTreeWorker();

                            if (onSplatTreeConstruction) onSplatTreeConstruction(true);

                            delayedExecute(() => {
                                resolve();
                            });
                        });
                    }
                };

                delayedExecute(() => {
                    if (checkForEarlyExit()) return;
                    if (onIndexesUpload) onIndexesUpload(true);
                    const transferBuffers = allCenters.map((array) => array.buffer);
                    workerProcessCenters(this.splatTreeWorker, allCenters, transferBuffers, this.maxDepth, this.maxCentersPerNode);
                });
            });
        });
    };

    countLeaves() {
        let leafCount = 0;
        this.visitLeaves(() => {
            leafCount++;
        });

        return leafCount;
    }

    visitLeaves(visitFunc) {
        const visitLeavesFromNode = (node, visitFunc) => {
            if (node.children.length === 0) visitFunc(node);
            for (let child of node.children) {
                visitLeavesFromNode(child, visitFunc);
            }
        };

        for (let subTree of this.subTrees) {
            visitLeavesFromNode(subTree.rootNode, visitFunc);
        }
    }
}

function WebGLExtensions(gl) {
    const extensions = {};

    function getExtension(name) {
        if (extensions[name] !== undefined) {
            return extensions[name];
        }

        let extension;

        switch (name) {
            case 'WEBGL_depth_texture':
                extension =
                    gl.getExtension('WEBGL_depth_texture') ||
                    gl.getExtension('MOZ_WEBGL_depth_texture') ||
                    gl.getExtension('WEBKIT_WEBGL_depth_texture');
                break;

            case 'EXT_texture_filter_anisotropic':
                extension =
                    gl.getExtension('EXT_texture_filter_anisotropic') ||
                    gl.getExtension('MOZ_EXT_texture_filter_anisotropic') ||
                    gl.getExtension('WEBKIT_EXT_texture_filter_anisotropic');
                break;

            case 'WEBGL_compressed_texture_s3tc':
                extension =
                    gl.getExtension('WEBGL_compressed_texture_s3tc') ||
                    gl.getExtension('MOZ_WEBGL_compressed_texture_s3tc') ||
                    gl.getExtension('WEBKIT_WEBGL_compressed_texture_s3tc');
                break;

            case 'WEBGL_compressed_texture_pvrtc':
                extension = gl.getExtension('WEBGL_compressed_texture_pvrtc') || gl.getExtension('WEBKIT_WEBGL_compressed_texture_pvrtc');
                break;

            default:
                extension = gl.getExtension(name);
        }

        extensions[name] = extension;

        return extension;
    }

    return {
        has: function (name) {
            return getExtension(name) !== null;
        },

        init: function (capabilities) {
            if (capabilities.isWebGL2) {
                getExtension('EXT_color_buffer_float');
                getExtension('WEBGL_clip_cull_distance');
            } else {
                getExtension('WEBGL_depth_texture');
                getExtension('OES_texture_float');
                getExtension('OES_texture_half_float');
                getExtension('OES_texture_half_float_linear');
                getExtension('OES_standard_derivatives');
                getExtension('OES_element_index_uint');
                getExtension('OES_vertex_array_object');
                getExtension('ANGLE_instanced_arrays');
            }

            getExtension('OES_texture_float_linear');
            getExtension('EXT_color_buffer_half_float');
            getExtension('WEBGL_multisampled_render_to_texture');
        },

        get: function (name) {
            const extension = getExtension(name);

            if (extension === null) {
                console.warn('THREE.WebGLRenderer: ' + name + ' extension not supported.');
            }

            return extension;
        }
    };
}

function WebGLCapabilities(gl, extensions, parameters) {
    let maxAnisotropy;

    function getMaxAnisotropy() {
        if (maxAnisotropy !== undefined) return maxAnisotropy;

        if (extensions.has('EXT_texture_filter_anisotropic') === true) {
            const extension = extensions.get('EXT_texture_filter_anisotropic');

            maxAnisotropy = gl.getParameter(extension.MAX_TEXTURE_MAX_ANISOTROPY_EXT);
        } else {
            maxAnisotropy = 0;
        }

        return maxAnisotropy;
    }

    function getMaxPrecision(precision) {
        if (precision === 'highp') {
            if (
                gl.getShaderPrecisionFormat(gl.VERTEX_SHADER, gl.HIGH_FLOAT).precision > 0 &&
                gl.getShaderPrecisionFormat(gl.FRAGMENT_SHADER, gl.HIGH_FLOAT).precision > 0
            ) {
                return 'highp';
            }

            precision = 'mediump';
        }

        if (precision === 'mediump') {
            if (
                gl.getShaderPrecisionFormat(gl.VERTEX_SHADER, gl.MEDIUM_FLOAT).precision > 0 &&
                gl.getShaderPrecisionFormat(gl.FRAGMENT_SHADER, gl.MEDIUM_FLOAT).precision > 0
            ) {
                return 'mediump';
            }
        }

        return 'lowp';
    }

    const isWebGL2 = typeof WebGL2RenderingContext !== 'undefined' && gl.constructor.name === 'WebGL2RenderingContext';

    let precision = parameters.precision !== undefined ? parameters.precision : 'highp';
    const maxPrecision = getMaxPrecision(precision);

    if (maxPrecision !== precision) {
        console.warn('THREE.WebGLRenderer:', precision, 'not supported, using', maxPrecision, 'instead.');
        precision = maxPrecision;
    }

    const drawBuffers = isWebGL2 || extensions.has('WEBGL_draw_buffers');

    const logarithmicDepthBuffer = parameters.logarithmicDepthBuffer === true;

    const maxTextures = gl.getParameter(gl.MAX_TEXTURE_IMAGE_UNITS);
    const maxVertexTextures = gl.getParameter(gl.MAX_VERTEX_TEXTURE_IMAGE_UNITS);
    const maxTextureSize = gl.getParameter(gl.MAX_TEXTURE_SIZE);
    const maxCubemapSize = gl.getParameter(gl.MAX_CUBE_MAP_TEXTURE_SIZE);

    const maxAttributes = gl.getParameter(gl.MAX_VERTEX_ATTRIBS);
    const maxVertexUniforms = gl.getParameter(gl.MAX_VERTEX_UNIFORM_VECTORS);
    const maxVaryings = gl.getParameter(gl.MAX_VARYING_VECTORS);
    const maxFragmentUniforms = gl.getParameter(gl.MAX_FRAGMENT_UNIFORM_VECTORS);

    const vertexTextures = maxVertexTextures > 0;
    const floatFragmentTextures = isWebGL2 || extensions.has('OES_texture_float');
    const floatVertexTextures = vertexTextures && floatFragmentTextures;

    const maxSamples = isWebGL2 ? gl.getParameter(gl.MAX_SAMPLES) : 0;

    return {
        isWebGL2: isWebGL2,

        drawBuffers: drawBuffers,

        getMaxAnisotropy: getMaxAnisotropy,
        getMaxPrecision: getMaxPrecision,

        precision: precision,
        logarithmicDepthBuffer: logarithmicDepthBuffer,

        maxTextures: maxTextures,
        maxVertexTextures: maxVertexTextures,
        maxTextureSize: maxTextureSize,
        maxCubemapSize: maxCubemapSize,

        maxAttributes: maxAttributes,
        maxVertexUniforms: maxVertexUniforms,
        maxVaryings: maxVaryings,
        maxFragmentUniforms: maxFragmentUniforms,

        vertexTextures: vertexTextures,
        floatFragmentTextures: floatFragmentTextures,
        floatVertexTextures: floatVertexTextures,

        maxSamples: maxSamples
    };
}

const SceneRevealMode = {
    Default: 0,
    Gradual: 1,
    Instant: 2
};

const LogLevel = {
    None: 0,
    Error: 1,
    Warning: 2,
    Info: 3,
    Debug: 4
};

const dummyGeometry = new THREE.BufferGeometry();
const dummyMaterial = new THREE.MeshBasicMaterial();

const COVARIANCES_ELEMENTS_PER_SPLAT = 6;
const CENTER_COLORS_ELEMENTS_PER_SPLAT = 4;

const COVARIANCES_ELEMENTS_PER_TEXEL_STORED = 4;
const COVARIANCES_ELEMENTS_PER_TEXEL_ALLOCATED = 4;
const COVARIANCES_ELEMENTS_PER_TEXEL_COMPRESSED_STORED = 6;
const COVARIANCES_ELEMENTS_PER_TEXEL_COMPRESSED_ALLOCATED = 8;
const SCALES_ROTATIONS_ELEMENTS_PER_TEXEL = 4;
const CENTER_COLORS_ELEMENTS_PER_TEXEL = 4;
const SCENE_INDEXES_ELEMENTS_PER_TEXEL = 1;

// Instant fade in
const SCENE_FADEIN_RATE_FAST = 1.0;
const SCENE_FADEIN_RATE_GRADUAL = 0.003;

const VISIBLE_REGION_EXPANSION_DELTA = 1;

// Based on my own observations across multiple devices, OSes and browsers, using textures that have one dimension
// greater than 4096 while the other is greater than or equal to 4096 causes issues (Essentially any texture larger
// than 4096 x 4096 (16777216) texels). Specifically it seems all texture data beyond the 4096 x 4096 texel boundary
// is corrupted, while data below that boundary is usable. In these cases the texture has been valid in the eyes of
// both Three.js and WebGL, and the texel format (RG, RGBA, etc.) has not mattered. More investigation will be needed,
// but for now the work-around is to split the spherical harmonics into three textures (one for each color channel).
const MAX_TEXTURE_TEXELS = 16777216;

/**
 * SplatMesh: Container for one or more splat scenes, abstracting them into a single unified container for
 * splat data. Additionally contains data structures and code to make the splat data renderable as a Three.js mesh.
 */

const ModelType = {
    GS3D: 0,
    GS2D: 1
};

class SplatMesh extends THREE.Mesh {
    constructor(
        enableOptionalEffects = false,
        halfPrecisionCovariancesOnGPU = false,
        devicePixelRatio = 1,
        enableDistancesComputationOnGPU = true,
        integerBasedDistancesComputation = false,
        maxScreenSpaceSplatSize = 1024,
        logLevel = LogLevel.None,
        sphericalHarmonicsDegree = 0,
        sceneFadeInRateMultiplier = 1.0,
        kernel2DSize = 0.3
    ) {
        super(dummyGeometry, dummyMaterial);

        // Reference to a Three.js renderer
        this.renderer = undefined;

        // Define which aspect of the scene is being rendered
        this.displayMode = DisplayMode.Rgb;

        // Determine how the splats are rendered
        this.splatModelType = undefined;

        // When true, allows for usage of extra properties and attributes during rendering for effects such as opacity adjustment.
        // Default is false for performance reasons. These properties are separate from transform properties (scale, rotation, position)
        // that are enabled by the 'dynamicScene' parameter.
        this.enableOptionalEffects = enableOptionalEffects;

        // Use 16-bit floating point values when storing splat covariance data in textures, instead of 32-bit
        this.halfPrecisionCovariancesOnGPU = halfPrecisionCovariancesOnGPU;

        // Ratio of the resolution in physical pixels to the resolution in CSS pixels for the current display device
        this.devicePixelRatio = devicePixelRatio;

        // Use a transform feedback to calculate splat distances from the camera
        this.enableDistancesComputationOnGPU = enableDistancesComputationOnGPU;

        // Use a faster integer-based approach for calculating splat distances from the camera
        this.integerBasedDistancesComputation = integerBasedDistancesComputation;

        // The size of the 2D kernel used for splat rendering
        // This will adjust the 2D kernel size after the projection
        this.kernel2DSize = kernel2DSize;

        // Specify the maximum clip space splat size, can help deal with large splats that get too unwieldy
        this.maxScreenSpaceSplatSize = maxScreenSpaceSplatSize;

        // The verbosity of console logging
        this.logLevel = logLevel;

        // Degree 0 means no spherical harmonics
        this.sphericalHarmonicsDegree = sphericalHarmonicsDegree;
        this.minSphericalHarmonicsDegree = 0;

        this.sceneFadeInRateMultiplier = sceneFadeInRateMultiplier;

        // The individual splat scenes stored in this splat mesh, each containing their own transform
        this.scenes = [];

        // Special octree tailored to SplatMesh instances
        this.splatTree = null;
        this.baseSplatTree = null;

        // Cache textures and the intermediate data used to populate them
        this.splatDataTextures = {};

        this.distancesTransformFeedback = {
            id: null,
            vertexShader: null,
            fragmentShader: null,
            program: null,
            centersBuffer: null,
            sceneIndexesBuffer: null,
            outDistancesBuffer: null,
            centersLoc: -1,
            modelViewProjLoc: -1,
            sceneIndexesLoc: -1,
            transformsLocs: []
        };

        this.globalSplatIndexToLocalSplatIndexMap = [];
        this.globalSplatIndexToSceneIndexMap = [];

        this.lastBuildSplatCount = 0;
        this.lastBuildScenes = [];
        this.lastBuildMaxSplatCount = 0;
        this.lastBuildSceneCount = 0;
        this.firstRenderTime = -1;
        this.finalBuild = false;

        this.webGLUtils = null;

        this.boundingBox = new THREE.Box3();
        this.calculatedSceneCenter = new THREE.Vector3();
        this.maxSplatDistanceFromSceneCenter = 0;
        this.visibleRegionBufferRadius = 0;
        this.visibleRegionRadius = 0;
        this.visibleRegionFadeStartRadius = 0;
        this.visibleRegionChanging = false;

        this.splatScale = 1.0;
        this.pointCloudModeEnabled = false;

        this.disposed = false;
        this.lastRenderer = null;
        this.visible = false;
    }

    /**
     * Build a container for each scene managed by this splat mesh based on an instance of SplatArray, along with optional
     * transform data (position, scale, rotation) passed to the splat mesh during the build process.
     * @param {Array<THREE.Matrix4>} splatArrays SplatArray instances containing splats for each scene
     * @param {Array<object>} sceneOptions Array of options objects: {
     *
     *         position (Array<number>):   Position of the scene, acts as an offset from its default position, defaults to [0, 0, 0]
     *
     *         rotation (Array<number>):   Rotation of the scene represented as a quaternion, defaults to [0, 0, 0, 1]
     *
     *         scale (Array<number>):      Scene's scale, defaults to [1, 1, 1]
     * }
     * @return {Array<THREE.Matrix4>}
     */
    static buildScenes(parentObject, splatArrays, sceneOptions) {
        const scenes = [];
        scenes.length = splatArrays.length;
        for (let i = 0; i < splatArrays.length; i++) {
            const splatArray = splatArrays[i];
            const options = sceneOptions[i] || {};
            let positionArray = options['position'] || [0, 0, 0];
            let rotationArray = options['rotation'] || [0, 0, 0, 1];
            let scaleArray = options['scale'] || [1, 1, 1];
            const position = new THREE.Vector3().fromArray(positionArray);
            const rotation = new THREE.Quaternion().fromArray(rotationArray);
            const scale = new THREE.Vector3().fromArray(scaleArray);
            const visible = options.visible !== undefined ? options.visible : true;
            const scene = SplatMesh.createScene(
                splatArray,
                position,
                rotation,
                scale,
                options.splatAlphaRemovalThreshold || 1,
                options.opacity,
                visible
            );

            // Model uses the convention of Z axis up while THREE uses Y axis up
            // This applies scene transformation that rotates the scene to match the THREE convention
            // As simply swapping Y an Z would cause the coordinate system to be left-handed
            scene.setRotationFromQuaternion(new THREE.Quaternion(0.5, 0.5, 0.5, -0.5));
            parentObject.add(scene);
            scenes[i] = scene;
        }
        return scenes;
    }

    static createScene(splatArray, position, rotation, scale, minimumAlpha, opacity = 1.0, visible = true) {
        return new SplatScene(splatArray, position, rotation, scale, minimumAlpha, opacity, visible);
    }

    /**
     * Build data structures that map global splat indexes (based on a unified index across all splat buffers) to
     * local data within a single scene.
     * @param {Array<SplatArray>} splatArrays Instances of SplatArray off which to build the maps
     * @return {object}
     */
    static buildSplatIndexMaps(splatArrays) {
        const localSplatIndexMap = [];
        const sceneIndexMap = [];
        let totalSplatCount = 0;
        for (let s = 0; s < splatArrays.length; s++) {
            const splatArray = splatArrays[s];
            const maxSplatCount = splatArray.getMaxSplatCount();
            for (let i = 0; i < maxSplatCount; i++) {
                localSplatIndexMap[totalSplatCount] = i;
                sceneIndexMap[totalSplatCount] = s;
                totalSplatCount++;
            }
        }
        return {
            localSplatIndexMap,
            sceneIndexMap
        };
    }

    /**
     * Build an instance of SplatTree (a specialized octree) for the given splat mesh.
     * @param {Array<number>} minAlphas Array of minimum splat alphas for each scene
     * @param {function} onSplatTreeIndexesUpload Function to be called when the upload of splat centers to the splat tree
     *                                            builder worker starts and finishes.
     * @param {function} onSplatTreeConstruction Function to be called when the conversion of the local splat tree from
     *                                           the format produced by the splat tree builder worker starts and ends.
     * @return {SplatTree}
     */
    buildSplatTree = function (minAlphas = [], onSplatTreeIndexesUpload, onSplatTreeConstruction) {
        return new Promise((resolve) => {
            this.disposeSplatTree();
            // TODO: expose SplatTree constructor parameters (maximumDepth and maxCentersPerNode) so that they can
            // be configured on a per-scene basis
            this.baseSplatTree = new SplatTree(8, 1000);
            const buildStartTime = performance.now();
            const splatColor = new THREE.Vector4();
            this.baseSplatTree
                .processSplatMesh(
                    this,
                    (splatIndex) => {
                        this.getSplatColor(splatIndex, splatColor);
                        const sceneIndex = this.getSceneIndexForSplat(splatIndex);
                        const minAlpha = minAlphas[sceneIndex] || 1;
                        return splatColor.w >= minAlpha;
                    },
                    onSplatTreeIndexesUpload,
                    onSplatTreeConstruction
                )
                .then(() => {
                    const buildTime = performance.now() - buildStartTime;
                    if (Constants.PERF_LOG) console.log('SplatTree build: ' + buildTime + ' ms');
                    if (this.disposed) {
                        resolve();
                    } else {
                        this.splatTree = this.baseSplatTree;
                        this.baseSplatTree = null;

                        let leavesWithVertices = 0;
                        let avgSplatCount = 0;
                        let maxSplatCount = 0;
                        let nodeCount = 0;

                        this.splatTree.visitLeaves((node) => {
                            const nodeSplatCount = node.data.indexes.length;
                            if (nodeSplatCount > 0) {
                                avgSplatCount += nodeSplatCount;
                                maxSplatCount = Math.max(maxSplatCount, nodeSplatCount);
                                nodeCount++;
                                leavesWithVertices++;
                            }
                        });
                        if (Constants.PERF_LOG) {
                            console.log(`SplatTree leaves: ${this.splatTree.countLeaves()}`);
                            console.log(`SplatTree leaves with splats:${leavesWithVertices}`);
                            avgSplatCount = avgSplatCount / nodeCount;
                            console.log(`Avg splat count per node: ${avgSplatCount}`);
                            console.log(`Total splat count: ${this.getSplatCount()}`);
                        }
                        resolve();
                    }
                });
        });
    };

    /**
     * Construct this instance of SplatMesh.
     * @param {Array<SplatArray>} splatArrays The base splat data, instances of SplatArray
     * @param {Array<object>} sceneOptions Dynamic options for each scene {
     *
     *         splatAlphaRemovalThreshold: Ignore any splats with an alpha less than the specified
     *                                     value (valid range: 0 - 255), defaults to 1
     *
     *         position (Array<number>):   Position of the scene, acts as an offset from its default position, defaults to [0, 0, 0]
     *
     *         rotation (Array<number>):   Rotation of the scene represented as a quaternion, defaults to [0, 0, 0, 1]
     *
     *         scale (Array<number>):      Scene's scale, defaults to [1, 1, 1]
     *
     * }
     * @param {boolean} keepSceneTransforms For a scene that already exists and is being overwritten, this flag
     *                                      says to keep the transform from the existing scene.
     * @param {boolean} finalBuild Will the splat mesh be in its final state after this build?
     * @param {function} onSplatTreeIndexesUpload Function to be called when the upload of splat centers to the splat tree
     *                                            builder worker starts and finishes.
     * @param {function} onSplatTreeConstruction Function to be called when the conversion of the local splat tree from
     *                                           the format produced by the splat tree builder worker starts and ends.
     * @return {object} Object containing info about the splats that are updated
     */
    build(
        splatArrays,
        sceneOptions,
        keepSceneTransforms = true,
        finalBuild = false,
        onSplatTreeIndexesUpload,
        onSplatTreeConstruction,
        preserveVisibleRegion = true,
        chunkedUpload = false,
        disableSplatTree = false
    ) {
        let start = 0;
        let end = 0;
        this.sceneOptions = sceneOptions;
        this.finalBuild = finalBuild;

        const maxSplatCount = SplatMesh.getTotalMaxSplatCountForSplatArrays(splatArrays);

        start = performance.now();
        const newScenes = SplatMesh.buildScenes(this, splatArrays, sceneOptions);
        end = performance.now();

        if (Constants.PERF_LOG) console.log(`SplatMesh.buildScenes took ${end - start} ms`);
        if (keepSceneTransforms) {
            for (let i = 0; i < this.scenes.length && i < newScenes.length; i++) {
                const newScene = newScenes[i];
                const existingScene = this.getScene(i);
                newScene.copyTransformData(existingScene);
            }
        }
        this.scenes = newScenes;

        let minSphericalHarmonicsDegree = 3;
        this.splatModelType = splatArrays[0].modelType;
        for (let splatArray of splatArrays) {
            const splatArraySphericalHarmonicsDegree = splatArray.getMinSphericalHarmonicsDegree();
            if (splatArraySphericalHarmonicsDegree < minSphericalHarmonicsDegree) {
                minSphericalHarmonicsDegree = splatArraySphericalHarmonicsDegree;
            }

            if (splatArray.modelType !== this.splatModelType) {
                throw new Error('SplatArrays with different model types cannot be combined in a single SplatMesh');
            }
        }
        this.minSphericalHarmonicsDegree = Math.min(minSphericalHarmonicsDegree, this.sphericalHarmonicsDegree);

        let splatArraysChanged = false;
        if (splatArrays.length !== this.lastBuildScenes.length) {
            splatArraysChanged = true;
        } else {
            for (let i = 0; i < splatArrays.length; i++) {
                const splatArray = splatArrays[i];
                if (splatArray !== this.lastBuildScenes[i].splatArray) {
                    splatArraysChanged = true;
                    break;
                }
            }
        }

        let isUpdateBuild = true;
        if (
            this.scenes.length !== 1 ||
            this.lastBuildSceneCount !== this.scenes.length ||
            this.lastBuildMaxSplatCount !== maxSplatCount ||
            splatArraysChanged
        ) {
            isUpdateBuild = false;
        }

        if (!isUpdateBuild) {
            this.boundingBox = new THREE.Box3();
            if (!preserveVisibleRegion) {
                this.maxSplatDistanceFromSceneCenter = 0;
                this.visibleRegionBufferRadius = 0;
                this.visibleRegionRadius = 0;
                this.visibleRegionFadeStartRadius = 0;
                this.firstRenderTime = -1;
            }
            this.lastBuildScenes = [];
            this.lastBuildSplatCount = 0;
            this.lastBuildMaxSplatCount = 0;
            this.disposeMeshData();
            this.geometry = SplatGeometry.build(maxSplatCount);
            if (this.splatModelType === ModelType.GS3D) {
                this.material = SplatMaterial3D.build(
                    this.enableOptionalEffects,
                    this.maxScreenSpaceSplatSize,
                    this.splatScale,
                    this.pointCloudModeEnabled,
                    this.minSphericalHarmonicsDegree,
                    this.kernel2DSize
                );
            } else {
                this.material = SplatMaterial2D.build(
                    this.enableOptionalEffects,
                    this.splatScale,
                    this.pointCloudModeEnabled,
                    this.minSphericalHarmonicsDegree
                );
            }

            const indexMaps = SplatMesh.buildSplatIndexMaps(splatArrays);
            this.globalSplatIndexToLocalSplatIndexMap = indexMaps.localSplatIndexMap;
            this.globalSplatIndexToSceneIndexMap = indexMaps.sceneIndexMap;
        }

        const splatArraySplatCount = this.getSplatCount(true);
        if (this.enableDistancesComputationOnGPU) this.setupDistancesComputationTransformFeedback();
        const dataUpdateResults = this.refreshGPUDataFromSplatArrays(isUpdateBuild, chunkedUpload);

        for (let i = 0; i < this.scenes.length; i++) {
            this.lastBuildScenes[i] = this.scenes[i];
        }
        this.lastBuildSplatCount = splatArraySplatCount;
        this.lastBuildMaxSplatCount = this.getMaxSplatCount();
        this.lastBuildSceneCount = this.scenes.length;

        if (disableSplatTree && this.splatTree) {
            this.disposeSplatTree();
        }

        if (finalBuild && this.scenes.length > 0 && !disableSplatTree) {
            this.buildSplatTree(
                sceneOptions.map((options) => options.splatAlphaRemovalThreshold || 1),
                onSplatTreeIndexesUpload,
                onSplatTreeConstruction
            ).then(() => {
                if (this.onSplatTreeReadyCallback) this.onSplatTreeReadyCallback(this.splatTree);
                this.onSplatTreeReadyCallback = null;
            });
        }

        this.visible = this.scenes.length > 0;

        return dataUpdateResults;
    }

    freeIntermediateSplatData() {
        const deleteTextureData = (texture) => {
            delete texture.source.data;
            delete texture.image;
            texture.onUpdate = null;
        };

        const scheduleTextureSourceCleanup = (texture) => {
            texture.needsUpdate = true;
            texture.onUpdate = () => {
                deleteTextureData(texture);
            };
        };

        // Free all baseData typed arrays
        const baseData = this.splatDataTextures.baseData;
        for (const key in baseData) {
            if (baseData.hasOwnProperty(key)) {
                delete baseData[key];
            }
        }

        // Free CPU-side padded data and schedule texture source cleanup for each texture descriptor
        const textureKeys = ['centerColors', 'covariances', 'scaleRotations', 'sphericalHarmonics', 'sceneIndexes'];
        for (const key of textureKeys) {
            const desc = this.splatDataTextures[key];
            if (!desc) continue;

            delete desc.data;

            if (desc.texture) {
                scheduleTextureSourceCleanup(desc.texture);
            }
            if (desc.textures) {
                for (const tex of desc.textures) {
                    scheduleTextureSourceCleanup(tex);
                }
            }
        }
    }
    /**
     * Dispose all resources held by the splat mesh
     */
    dispose() {
        this.disposeMeshData();
        this.disposeTextures();
        this.disposeSplatTree();
        if (this.enableDistancesComputationOnGPU) {
            if (this.computeDistancesOnGPUSyncTimeout) {
                clearTimeout(this.computeDistancesOnGPUSyncTimeout);
                this.computeDistancesOnGPUSyncTimeout = null;
            }
            this.disposeDistancesComputationGPUResources();
        }
        this.scenes = [];
        this.distancesTransformFeedback = {
            id: null,
            vertexShader: null,
            fragmentShader: null,
            program: null,
            centersBuffer: null,
            sceneIndexesBuffer: null,
            outDistancesBuffer: null,
            centersLoc: -1,
            modelViewProjLoc: -1,
            sceneIndexesLoc: -1,
            transformsLocs: []
        };
        this.renderer = null;

        this.globalSplatIndexToLocalSplatIndexMap = [];
        this.globalSplatIndexToSceneIndexMap = [];

        this.lastBuildSplatCount = 0;
        this.lastBuildScenes = [];
        this.lastBuildMaxSplatCount = 0;
        this.lastBuildSceneCount = 0;
        this.firstRenderTime = -1;
        this.finalBuild = false;

        this.webGLUtils = null;

        this.boundingBox = new THREE.Box3();
        this.calculatedSceneCenter = new THREE.Vector3();
        this.maxSplatDistanceFromSceneCenter = 0;
        this.visibleRegionBufferRadius = 0;
        this.visibleRegionRadius = 0;
        this.visibleRegionFadeStartRadius = 0;
        this.visibleRegionChanging = false;

        this.splatScale = 1.0;
        this.pointCloudModeEnabled = false;

        this.disposed = true;
        this.lastRenderer = null;
        this.visible = false;
    }

    /**
     * Dispose of only the Three.js mesh resources (geometry, material, and texture)
     */
    disposeMeshData() {
        if (this.geometry && this.geometry !== dummyGeometry) {
            this.geometry.dispose();
            this.geometry = null;
        }
        if (this.material) {
            this.material.dispose();
            this.material = null;
        }
    }

    disposeTextures() {
        for (let textureKey in this.splatDataTextures) {
            if (this.splatDataTextures.hasOwnProperty(textureKey)) {
                const textureContainer = this.splatDataTextures[textureKey];
                if (textureContainer.texture) {
                    textureContainer.texture.dispose();
                    textureContainer.texture = null;
                }
                if (textureContainer.textures) {
                    for (const tex of textureContainer.textures) tex.dispose();
                    textureContainer.textures = null;
                }
            }
        }
        this.splatDataTextures = null;
    }

    disposeSplatTree() {
        if (this.splatTree) {
            this.splatTree.dispose();
            this.splatTree = null;
        }
        if (this.baseSplatTree) {
            this.baseSplatTree.dispose();
            this.baseSplatTree = null;
        }
    }

    getSplatTree() {
        return this.splatTree;
    }

    onSplatTreeReady(callback) {
        this.onSplatTreeReadyCallback = callback;
    }

    /**
     * Get copies of data that are necessary for splat distance computation: splat center positions and splat
     * scene indexes (necessary for applying dynamic scene transformations during distance computation)
     * @param {*} start The index at which to start copying data
     * @param {*} end  The index at which to stop copying data
     * @return {object}
     */
    getDataForDistancesComputation(start, end) {
        const centers = this.integerBasedDistancesComputation
            ? this.getIntegerCenters(start, end, true)
            : this.getFloatCenters(start, end, true);
        const sceneIndexes = this.getSceneIndexes(start, end);
        return {
            centers,
            sceneIndexes
        };
    }

    /**
     * Refresh data textures and GPU buffers with splat data from the splat buffers belonging to this mesh.
     * @param {boolean} sinceLastBuildOnly Specify whether or not to only update for splats that have been added since the last build.
     * @return {object}
     */
    refreshGPUDataFromSplatArrays(sinceLastBuildOnly, chunkedUpload = false) {
        const splatCount = this.getSplatCount(true);
        const { centers, sceneIndexes, fromSplat, toSplat } = this.refreshDataTexturesFromSplatArrays(sinceLastBuildOnly, chunkedUpload);
        const updateStart = sinceLastBuildOnly ? this.lastBuildSplatCount : 0;

        if (this.enableDistancesComputationOnGPU) {
            this.refreshGPUBuffersForDistancesComputation(centers, sceneIndexes, sinceLastBuildOnly);
        }
        return {
            from: updateStart,
            to: splatCount - 1,
            count: splatCount - updateStart,
            centers: centers,
            sceneIndexes: sceneIndexes,
            fromSplat: fromSplat,
            toSplat: toSplat
        };
    }

    /**
     * Update the GPU buffers that are used for computing splat distances on the GPU.
     * @param {Array<number>} centers Splat center positions
     * @param {Array<number>} sceneIndexes Indexes of the scene to which each splat belongs
     * @param {boolean} sinceLastBuildOnly Specify whether or not to only update for splats that have been added since the last build.
     */
    refreshGPUBuffersForDistancesComputation(centers, sceneIndexes, sinceLastBuildOnly = false) {
        const offset = sinceLastBuildOnly ? this.lastBuildSplatCount : 0;
        this.updateGPUCentersBufferForDistancesComputation(sinceLastBuildOnly, centers, offset);
        this.updateGPUTransformIndexesBufferForDistancesComputation(sinceLastBuildOnly, sceneIndexes, offset);
    }

    /**
     * Refresh data textures with data from the splat buffers for this mesh.
     * @param {boolean} sinceLastBuildOnly Specify whether or not to only update for splats that have been added since the last build.
     * @param {boolean} chunkedUpload Skip synchronous texture upload, caller will use updateDataTexturesChunked instead.
     */
    refreshDataTexturesFromSplatArrays(sinceLastBuildOnly, chunkedUpload = false) {
        const splatCount = this.getSplatCount(true);
        const fromSplat = this.lastBuildSplatCount;
        const toSplat = splatCount - 1;

        if (!sinceLastBuildOnly) {
            const avgCenter = new THREE.Vector3();
            this.scenes.forEach((scene) => {
                avgCenter.add(scene.splatArray.sceneCenter);
            });
            avgCenter.multiplyScalar(1.0 / this.scenes.length);
            this.calculatedSceneCenter.copy(avgCenter);
            this.material.uniforms.sceneCenter.value.copy(this.calculatedSceneCenter);
            this.material.uniformsNeedUpdate = true;
        }

        let centers;
        if (!sinceLastBuildOnly) {
            this.setupDataTextures();
            centers = this.updateBaseDataFromSplatArrays();
        } else {
            centers = this.updateBaseDataFromSplatArrays(fromSplat, toSplat);
        }

        // Skip synchronous texture upload if chunkedUpload is requested
        // Caller will use updateDataTexturesChunked() instead
        if (!chunkedUpload) {
            this.updateDataTexturesFromBaseData(fromSplat, toSplat);
            this.updateVisibleRegion(sinceLastBuildOnly);
        }

        const sceneIndexes = this.getSceneIndexes(fromSplat, toSplat);
        return {
            centers,
            sceneIndexes,
            fromSplat,
            toSplat
        };
    }

    setupDataTextures() {
        const maxSplatCount = this.getMaxSplatCount();
        const splatCount = this.getSplatCount(true);

        this.disposeTextures();

        const computeDataTextureSize = (elementsPerTexel, elementsPerSplat) => {
            const texSize = new THREE.Vector2(4096, 1024);
            while (texSize.x * texSize.y * elementsPerTexel < maxSplatCount * elementsPerSplat) texSize.y *= 2;
            return texSize;
        };

        const getCovariancesElementsPertexelStored = (compressionLevel) => {
            return compressionLevel >= 1 ? COVARIANCES_ELEMENTS_PER_TEXEL_COMPRESSED_STORED : COVARIANCES_ELEMENTS_PER_TEXEL_STORED;
        };

        const getCovariancesInitialTextureSpecs = (compressionLevel) => {
            const elementsPerTexelStored = getCovariancesElementsPertexelStored(compressionLevel);
            const texSize = computeDataTextureSize(elementsPerTexelStored, 6);
            return { elementsPerTexelStored, texSize };
        };

        let covarianceCompressionLevel = this.getTargetCovarianceCompressionLevel();
        const scaleRotationCompressionLevel = 0;
        const shCompressionLevel = this.getTargetSphericalHarmonicsCompressionLevel();

        let covariances;
        let scales;
        let rotations;
        if (this.splatModelType === ModelType.GS3D) {
            const initialCovTexSpecs = getCovariancesInitialTextureSpecs(covarianceCompressionLevel);
            if (initialCovTexSpecs.texSize.x * initialCovTexSpecs.texSize.y > MAX_TEXTURE_TEXELS && covarianceCompressionLevel === 0) {
                covarianceCompressionLevel = 1;
            }
            covariances = new Float32Array(maxSplatCount * COVARIANCES_ELEMENTS_PER_SPLAT);
        } else {
            scales = new Float32Array(maxSplatCount * 3);
            rotations = new Float32Array(maxSplatCount * 4);
        }

        const centers = new Float32Array(maxSplatCount * 3);
        const colors = new Uint8Array(maxSplatCount * 4);

        let SphericalHarmonicsArrayType = Float32Array;
        if (shCompressionLevel === 1) SphericalHarmonicsArrayType = Uint16Array;
        else if (shCompressionLevel === 2) SphericalHarmonicsArrayType = Uint8Array;
        const shComponentCount = getSphericalHarmonicsComponentCountForDegree(this.minSphericalHarmonicsDegree);
        const shData = this.minSphericalHarmonicsDegree ? new SphericalHarmonicsArrayType(maxSplatCount * shComponentCount) : undefined;

        // set up centers/colors data texture
        const centersColsTexSize = computeDataTextureSize(CENTER_COLORS_ELEMENTS_PER_TEXEL, 4);
        const paddedCentersCols = new Uint32Array(centersColsTexSize.x * centersColsTexSize.y * CENTER_COLORS_ELEMENTS_PER_TEXEL);
        SplatMesh.updateCenterColorsPaddedData(0, splatCount - 1, centers, colors, paddedCentersCols);

        const centersColsTex = new THREE.DataTexture(
            paddedCentersCols,
            centersColsTexSize.x,
            centersColsTexSize.y,
            THREE.RGBAIntegerFormat,
            THREE.UnsignedIntType
        );
        centersColsTex.internalFormat = 'RGBA32UI';
        centersColsTex.needsUpdate = true;
        this.material.uniforms.centersColorsTexture.value = centersColsTex;
        this.material.uniforms.centersColorsTextureSize.value.copy(centersColsTexSize);
        this.material.uniformsNeedUpdate = true;

        this.splatDataTextures = {
            baseData: {
                covariances: covariances,
                scales: scales,
                rotations: rotations,
                centers: centers,
                colors: colors,
                sphericalHarmonics: shData
            },
            centerColors: {
                data: paddedCentersCols,
                texture: centersColsTex,
                size: centersColsTexSize
            }
        };

        if (this.splatModelType === ModelType.GS3D) {
            // set up covariances data texture

            const covTexSpecs = getCovariancesInitialTextureSpecs(covarianceCompressionLevel);
            const covariancesElementsPerTexelStored = covTexSpecs.elementsPerTexelStored;
            const covTexSize = covTexSpecs.texSize;

            let CovariancesDataType = covarianceCompressionLevel >= 1 ? Uint32Array : Float32Array;
            const covariancesElementsPerTexelAllocated =
                covarianceCompressionLevel >= 1
                    ? COVARIANCES_ELEMENTS_PER_TEXEL_COMPRESSED_ALLOCATED
                    : COVARIANCES_ELEMENTS_PER_TEXEL_ALLOCATED;
            const covariancesTextureData = new CovariancesDataType(covTexSize.x * covTexSize.y * covariancesElementsPerTexelAllocated);

            if (covarianceCompressionLevel === 0) {
                covariancesTextureData.set(covariances);
            } else {
                SplatMesh.updatePaddedCompressedCovariancesTextureData(covariances, covariancesTextureData, 0, 0, covariances.length);
            }

            let covTex;
            if (covarianceCompressionLevel >= 1) {
                covTex = new THREE.DataTexture(
                    covariancesTextureData,
                    covTexSize.x,
                    covTexSize.y,
                    THREE.RGBAIntegerFormat,
                    THREE.UnsignedIntType
                );
                covTex.internalFormat = 'RGBA32UI';
                this.material.uniforms.covariancesTextureHalfFloat.value = covTex;
            } else {
                covTex = new THREE.DataTexture(covariancesTextureData, covTexSize.x, covTexSize.y, THREE.RGBAFormat, THREE.FloatType);
                this.material.uniforms.covariancesTexture.value = covTex;

                // For some reason a usampler2D needs to have a valid texture attached or WebGL complains
                const dummyTex = new THREE.DataTexture(new Uint32Array(32), 2, 2, THREE.RGBAIntegerFormat, THREE.UnsignedIntType);
                dummyTex.internalFormat = 'RGBA32UI';
                this.material.uniforms.covariancesTextureHalfFloat.value = dummyTex;
                dummyTex.needsUpdate = true;
            }
            covTex.needsUpdate = true;

            this.material.uniforms.covariancesAreHalfFloat.value = covarianceCompressionLevel >= 1 ? 1 : 0;
            this.material.uniforms.covariancesTextureSize.value.copy(covTexSize);

            this.splatDataTextures['covariances'] = {
                data: covariancesTextureData,
                texture: covTex,
                size: covTexSize,
                compressionLevel: covarianceCompressionLevel,
                elementsPerTexelStored: covariancesElementsPerTexelStored,
                elementsPerTexelAllocated: covariancesElementsPerTexelAllocated
            };
        } else {
            // set up scale & rotations data texture
            const elementsPerSplat = 6;
            const scaleRotationsTexSize = computeDataTextureSize(SCALES_ROTATIONS_ELEMENTS_PER_TEXEL, elementsPerSplat);
            let ScaleRotationsDataType = scaleRotationCompressionLevel >= 1 ? Uint16Array : Float32Array;
            let scaleRotationsTextureType = scaleRotationCompressionLevel >= 1 ? THREE.HalfFloatType : THREE.FloatType;

            if (scaleRotationsTexSize.x * scaleRotationsTexSize.y <= MAX_TEXTURE_TEXELS) {
                const paddedScaleRotations = new ScaleRotationsDataType(
                    scaleRotationsTexSize.x * scaleRotationsTexSize.y * SCALES_ROTATIONS_ELEMENTS_PER_TEXEL
                );

                SplatMesh.updateScaleRotationsPaddedData(0, splatCount - 1, scales, rotations, paddedScaleRotations);

                const scaleRotationsTex = new THREE.DataTexture(
                    paddedScaleRotations,
                    scaleRotationsTexSize.x,
                    scaleRotationsTexSize.y,
                    THREE.RGBAFormat,
                    scaleRotationsTextureType
                );
                scaleRotationsTex.needsUpdate = true;
                this.material.uniforms.scaleRotationsTexture.value = scaleRotationsTex;
                this.material.uniforms.scaleRotationsTextureSize.value.copy(scaleRotationsTexSize);

                this.splatDataTextures['scaleRotations'] = {
                    data: paddedScaleRotations,
                    texture: scaleRotationsTex,
                    size: scaleRotationsTexSize,
                    compressionLevel: scaleRotationCompressionLevel
                };
            } else {
                // Scale/rotation texture exceeds GPU limit; split across 2 textures (4 elements each, 1 texel/splat)
                // Texture 0: [sx, sy, sz, rx], Texture 1: [ry, rz, 0, 0]
                const splitElementsPerTexel = SCALES_ROTATIONS_ELEMENTS_PER_TEXEL;
                const splitTexSize = computeDataTextureSize(splitElementsPerTexel, splitElementsPerTexel);
                const splitArraySize = splitTexSize.x * splitTexSize.y * splitElementsPerTexel;
                const paddedSRArray0 = new ScaleRotationsDataType(splitArraySize);
                const paddedSRArray1 = new ScaleRotationsDataType(splitArraySize);

                for (let c = 0; c < splatCount; c++) {
                    const scaleBase = c * 3;
                    const rotBase = c * 4;
                    const dest = c * splitElementsPerTexel;
                    paddedSRArray0[dest]     = scales[scaleBase];
                    paddedSRArray0[dest + 1] = scales[scaleBase + 1];
                    paddedSRArray0[dest + 2] = scales[scaleBase + 2];
                    paddedSRArray0[dest + 3] = rotations[rotBase];
                    paddedSRArray1[dest]     = rotations[rotBase + 1];
                    paddedSRArray1[dest + 1] = rotations[rotBase + 2];
                }

                const srTex0 = new THREE.DataTexture(paddedSRArray0, splitTexSize.x, splitTexSize.y, THREE.RGBAFormat, scaleRotationsTextureType);
                const srTex1 = new THREE.DataTexture(paddedSRArray1, splitTexSize.x, splitTexSize.y, THREE.RGBAFormat, scaleRotationsTextureType);
                srTex0.needsUpdate = true;
                srTex1.needsUpdate = true;
                this.material.uniforms.scaleRotationsTexture.value = srTex0;
                this.material.uniforms.scaleRotationsTexture1.value = srTex1;
                this.material.uniforms.scaleRotationsTextureSize.value.copy(splitTexSize);
                this.material.uniforms.scaleRotationsMultiTextureMode.value = 1;

                this.splatDataTextures['scaleRotations'] = {
                    data: [paddedSRArray0, paddedSRArray1],
                    textures: [srTex0, srTex1],
                    size: splitTexSize,
                    compressionLevel: scaleRotationCompressionLevel
                };
            }
        }

        if (shData) {
            const shTextureType = shCompressionLevel === 2 ? THREE.UnsignedByteType : THREE.HalfFloatType;

            let paddedSHComponentCount = shComponentCount;
            if (paddedSHComponentCount % 2 !== 0) paddedSHComponentCount++;
            let shElementsPerTexel = 4;
            let texelFormat = THREE.RGBAFormat;
            let shTexSize = computeDataTextureSize(shElementsPerTexel, paddedSHComponentCount);

            // Use one texture for all spherical harmonics data
            if (shTexSize.x * shTexSize.y <= MAX_TEXTURE_TEXELS) {
                const paddedSHArraySize = shTexSize.x * shTexSize.y * shElementsPerTexel;
                const paddedSHArray = new SphericalHarmonicsArrayType(paddedSHArraySize);
                for (let c = 0; c < splatCount; c++) {
                    const srcBase = shComponentCount * c;
                    const destBase = paddedSHComponentCount * c;
                    for (let i = 0; i < shComponentCount; i++) {
                        paddedSHArray[destBase + i] = shData[srcBase + i];
                    }
                }

                const shTexture = new THREE.DataTexture(paddedSHArray, shTexSize.x, shTexSize.y, texelFormat, shTextureType);
                shTexture.needsUpdate = true;
                this.material.uniforms.sphericalHarmonicsTexture.value = shTexture;
                this.splatDataTextures['sphericalHarmonics'] = {
                    componentCount: shComponentCount,
                    paddedComponentCount: paddedSHComponentCount,
                    data: paddedSHArray,
                    textureCount: 1,
                    texture: shTexture,
                    size: shTexSize,
                    compressionLevel: shCompressionLevel,
                    elementsPerTexel: shElementsPerTexel
                };
                // Split spherical harmonics data across multiple textures (groups of 4 consecutive components each)
            } else {
                const numTextures = Math.ceil(shComponentCount / 4);
                shElementsPerTexel = 4;
                paddedSHComponentCount = 4;
                shTexSize = computeDataTextureSize(shElementsPerTexel, paddedSHComponentCount);

                const paddedSHArraySize = shTexSize.x * shTexSize.y * shElementsPerTexel;
                const paddedSHArrays = [];
                const shTextures = [];
                for (let t = 0; t < numTextures; t++) {
                    const paddedSHArray = new SphericalHarmonicsArrayType(paddedSHArraySize);
                    paddedSHArrays.push(paddedSHArray);
                    const srcOffset = t * 4;
                    const componentsInThisTexture = Math.min(4, shComponentCount - srcOffset);
                    for (let c = 0; c < splatCount; c++) {
                        const srcBase = shComponentCount * c + srcOffset;
                        const destBase = 4 * c;
                        for (let i = 0; i < componentsInThisTexture; i++) {
                            paddedSHArray[destBase + i] = shData[srcBase + i];
                        }
                    }

                    const shTexture = new THREE.DataTexture(paddedSHArray, shTexSize.x, shTexSize.y, texelFormat, shTextureType);
                    shTextures.push(shTexture);
                    shTexture.needsUpdate = true;
                    this.material.uniforms[`sphericalHarmonicsTexture${t}`].value = shTexture;
                }

                this.material.uniforms.sphericalHarmonicsMultiTextureMode.value = 1;
                this.splatDataTextures['sphericalHarmonics'] = {
                    componentCount: shComponentCount,
                    paddedComponentCount: paddedSHComponentCount,
                    data: paddedSHArrays,
                    textureCount: numTextures,
                    textures: shTextures,
                    size: shTexSize,
                    compressionLevel: shCompressionLevel,
                    elementsPerTexel: shElementsPerTexel
                };
            }

            this.material.uniforms.sphericalHarmonicsTextureSize.value.copy(shTexSize);
            this.material.uniforms.sphericalHarmonics8BitMode.value = shCompressionLevel === 2 ? 1 : 0;
            for (let s = 0; s < this.scenes.length; s++) {
                const splatArray = this.scenes[s].splatArray;
                this.material.uniforms.sphericalHarmonics8BitCompressionRangeMin.value[s] = splatArray.minSphericalHarmonicsCoeff;
                this.material.uniforms.sphericalHarmonics8BitCompressionRangeMax.value[s] = splatArray.maxSphericalHarmonicsCoeff;
            }
            this.material.uniformsNeedUpdate = true;
        }

        const sceneIndexesTexSize = computeDataTextureSize(SCENE_INDEXES_ELEMENTS_PER_TEXEL, 4);
        const paddedTransformIndexes = new Uint32Array(sceneIndexesTexSize.x * sceneIndexesTexSize.y * SCENE_INDEXES_ELEMENTS_PER_TEXEL);
        for (let c = 0; c < splatCount; c++) paddedTransformIndexes[c] = this.globalSplatIndexToSceneIndexMap[c];
        const sceneIndexesTexture = new THREE.DataTexture(
            paddedTransformIndexes,
            sceneIndexesTexSize.x,
            sceneIndexesTexSize.y,
            THREE.RedIntegerFormat,
            THREE.UnsignedIntType
        );
        sceneIndexesTexture.internalFormat = 'R32UI';
        sceneIndexesTexture.needsUpdate = true;
        this.material.uniforms.sceneIndexesTexture.value = sceneIndexesTexture;
        this.material.uniforms.sceneIndexesTextureSize.value.copy(sceneIndexesTexSize);
        this.material.uniformsNeedUpdate = true;
        this.splatDataTextures['sceneIndexes'] = {
            data: paddedTransformIndexes,
            texture: sceneIndexesTexture,
            size: sceneIndexesTexSize
        };
        this.material.uniforms.sceneCount.value = this.scenes.length;
    }

    updateBaseDataFromSplatArrays(fromSplat, toSplat) {
        const covarancesTextureDesc = this.splatDataTextures['covariances'];
        const covarianceCompressionLevel = covarancesTextureDesc ? covarancesTextureDesc.compressionLevel : undefined;
        const scaleRotationsTextureDesc = this.splatDataTextures['scaleRotations'];
        const scaleRotationCompressionLevel = scaleRotationsTextureDesc ? scaleRotationsTextureDesc.compressionLevel : undefined;
        const shITextureDesc = this.splatDataTextures['sphericalHarmonics'];
        const shCompressionLevel = shITextureDesc ? shITextureDesc.compressionLevel : 0;

        const splatCount = !!fromSplat && !!toSplat ? toSplat - fromSplat : this.getSplatCount(true);
        let paddedFloatCenters = new Float32Array(splatCount * 4);
        let start = performance.now();
        this.fillSplatDataArrays(
            this.splatDataTextures.baseData.covariances,
            this.splatDataTextures.baseData.scales,
            this.splatDataTextures.baseData.rotations,
            this.splatDataTextures.baseData.centers,
            this.splatDataTextures.baseData.colors,
            this.splatDataTextures.baseData.sphericalHarmonics,
            paddedFloatCenters,
            true,
            covarianceCompressionLevel,
            scaleRotationCompressionLevel,
            shCompressionLevel,
            fromSplat,
            toSplat,
            fromSplat
        );
        let end = performance.now();

        if (Constants.PERF_LOG) {
            console.log(`SplatMesh.fillSplatDataArrays took ${end - start} ms`);
        }

        return paddedFloatCenters;
    }

    updateDataTexturesFromBaseData(fromSplat, toSplat) {
        const covarancesTextureDesc = this.splatDataTextures['covariances'];
        const covarianceCompressionLevel = covarancesTextureDesc ? covarancesTextureDesc.compressionLevel : undefined;
        const scaleRotationsTextureDesc = this.splatDataTextures['scaleRotations'];
        const scaleRotationCompressionLevel = scaleRotationsTextureDesc ? scaleRotationsTextureDesc.compressionLevel : undefined;
        const shTextureDesc = this.splatDataTextures['sphericalHarmonics'];
        const shCompressionLevel = shTextureDesc ? shTextureDesc.compressionLevel : 0;

        // Update center & color data texture
        const centerColorsTextureDescriptor = this.splatDataTextures['centerColors'];
        const paddedCenterColors = centerColorsTextureDescriptor.data;
        const centerColorsTexture = centerColorsTextureDescriptor.texture;
        SplatMesh.updateCenterColorsPaddedData(
            fromSplat,
            toSplat,
            this.splatDataTextures.baseData.centers,
            this.splatDataTextures.baseData.colors,
            paddedCenterColors
        );
        const centerColorsTextureProps = this.renderer ? this.renderer.properties.get(centerColorsTexture) : null;
        if (!centerColorsTextureProps || !centerColorsTextureProps.__webglTexture) {
            centerColorsTexture.needsUpdate = true;
        } else {
            this.updateDataTexture(
                paddedCenterColors,
                centerColorsTextureDescriptor.texture,
                centerColorsTextureDescriptor.size,
                centerColorsTextureProps,
                CENTER_COLORS_ELEMENTS_PER_TEXEL,
                CENTER_COLORS_ELEMENTS_PER_SPLAT,
                4,
                fromSplat,
                toSplat
            );
        }

        // update covariance data texture
        if (covarancesTextureDesc) {
            const covariancesTexture = covarancesTextureDesc.texture;
            const covarancesStartElement = fromSplat * COVARIANCES_ELEMENTS_PER_SPLAT;
            const covariancesEndElement = toSplat * COVARIANCES_ELEMENTS_PER_SPLAT;

            if (covarianceCompressionLevel === 0) {
                for (let i = covarancesStartElement; i <= covariancesEndElement; i++) {
                    const covariance = this.splatDataTextures.baseData.covariances[i];
                    covarancesTextureDesc.data[i] = covariance;
                }
            } else {
                SplatMesh.updatePaddedCompressedCovariancesTextureData(
                    this.splatDataTextures.baseData.covariances,
                    covarancesTextureDesc.data,
                    fromSplat * covarancesTextureDesc.elementsPerTexelAllocated,
                    covarancesStartElement,
                    covariancesEndElement
                );
            }

            const covariancesTextureProps = this.renderer ? this.renderer.properties.get(covariancesTexture) : null;
            if (!covariancesTextureProps || !covariancesTextureProps.__webglTexture) {
                covariancesTexture.needsUpdate = true;
            } else {
                if (covarianceCompressionLevel === 0) {
                    this.updateDataTexture(
                        covarancesTextureDesc.data,
                        covarancesTextureDesc.texture,
                        covarancesTextureDesc.size,
                        covariancesTextureProps,
                        covarancesTextureDesc.elementsPerTexelStored,
                        COVARIANCES_ELEMENTS_PER_SPLAT,
                        4,
                        fromSplat,
                        toSplat
                    );
                } else {
                    this.updateDataTexture(
                        covarancesTextureDesc.data,
                        covarancesTextureDesc.texture,
                        covarancesTextureDesc.size,
                        covariancesTextureProps,
                        covarancesTextureDesc.elementsPerTexelAllocated,
                        covarancesTextureDesc.elementsPerTexelAllocated,
                        2,
                        fromSplat,
                        toSplat
                    );
                }
            }
        }

        // update scale and rotation data texture
        if (scaleRotationsTextureDesc) {
            const bytesPerElement = scaleRotationCompressionLevel === 0 ? 4 : 2;
            const scales = this.splatDataTextures.baseData.scales;
            const rotations = this.splatDataTextures.baseData.rotations;

            if (!scaleRotationsTextureDesc.textures) {
                // Single-texture mode
                const paddedScaleRotations = scaleRotationsTextureDesc.data;
                const scaleRotationsTexture = scaleRotationsTextureDesc.texture;
                const elementsPerSplat = 6;
                SplatMesh.updateScaleRotationsPaddedData(fromSplat, toSplat, scales, rotations, paddedScaleRotations);
                const scaleRotationsTextureProps = this.renderer ? this.renderer.properties.get(scaleRotationsTexture) : null;
                if (!scaleRotationsTextureProps || !scaleRotationsTextureProps.__webglTexture) {
                    scaleRotationsTexture.needsUpdate = true;
                } else {
                    this.updateDataTexture(
                        paddedScaleRotations,
                        scaleRotationsTextureDesc.texture,
                        scaleRotationsTextureDesc.size,
                        scaleRotationsTextureProps,
                        SCALES_ROTATIONS_ELEMENTS_PER_TEXEL,
                        elementsPerSplat,
                        bytesPerElement,
                        fromSplat,
                        toSplat
                    );
                }
            } else {
                // Multi-texture mode: texture 0 = [sx,sy,sz,rx], texture 1 = [ry,rz,0,0]
                const [paddedSRArray0, paddedSRArray1] = scaleRotationsTextureDesc.data;
                for (let c = fromSplat; c <= toSplat; c++) {
                    const scaleBase = c * 3;
                    const rotBase = c * 4;
                    const dest = c * 4;
                    paddedSRArray0[dest]     = scales[scaleBase];
                    paddedSRArray0[dest + 1] = scales[scaleBase + 1];
                    paddedSRArray0[dest + 2] = scales[scaleBase + 2];
                    paddedSRArray0[dest + 3] = rotations[rotBase];
                    paddedSRArray1[dest]     = rotations[rotBase + 1];
                    paddedSRArray1[dest + 1] = rotations[rotBase + 2];
                }

                const { textures, size } = scaleRotationsTextureDesc;
                const elementsPerSplat = 4;
                const elementsPerTexel = 4;

                const paddedArrays = [paddedSRArray0, paddedSRArray1];
                for (let i = 0; i < textures.length; i++) {
                    const tex = textures[i];
                    const paddedArray = paddedArrays[i];
                    const texProps = this.renderer ? this.renderer.properties.get(tex) : null;
                    if (!texProps || !texProps.__webglTexture) {
                        tex.needsUpdate = true;
                    } else {
                        this.updateDataTexture(
                            paddedArray,
                            tex,
                            size,
                            texProps,
                            elementsPerTexel,
                            elementsPerSplat,
                            bytesPerElement,
                            fromSplat,
                            toSplat
                        );
                    }
                }
            }
        }

        // update spherical harmonics data texture
        const shData = this.splatDataTextures.baseData.sphericalHarmonics;
        if (shData) {
            let shBytesPerElement = 4;
            if (shCompressionLevel === 1) shBytesPerElement = 2;
            else if (shCompressionLevel === 2) shBytesPerElement = 1;

            const updateTexture = (shTexture, shTextureSize, elementsPerTexel, paddedSHArray, paddedSHComponentCount) => {
                const shTextureProps = this.renderer ? this.renderer.properties.get(shTexture) : null;
                if (!shTextureProps || !shTextureProps.__webglTexture) {
                    shTexture.needsUpdate = true;
                } else {
                    this.updateDataTexture(
                        paddedSHArray,
                        shTexture,
                        shTextureSize,
                        shTextureProps,
                        elementsPerTexel,
                        paddedSHComponentCount,
                        shBytesPerElement,
                        fromSplat,
                        toSplat
                    );
                }
            };

            const shComponentCount = shTextureDesc.componentCount;
            const paddedSHComponentCount = shTextureDesc.paddedComponentCount;

            // Update for the case of a single texture for all spherical harmonics data
            if (shTextureDesc.textureCount === 1) {
                const paddedSHArray = shTextureDesc.data;
                for (let c = fromSplat; c <= toSplat; c++) {
                    const srcBase = shComponentCount * c;
                    const destBase = paddedSHComponentCount * c;
                    for (let i = 0; i < shComponentCount; i++) {
                        paddedSHArray[destBase + i] = shData[srcBase + i];
                    }
                }
                updateTexture(
                    shTextureDesc.texture,
                    shTextureDesc.size,
                    shTextureDesc.elementsPerTexel,
                    paddedSHArray,
                    paddedSHComponentCount
                );
                // Update for the case of spherical harmonics data split across multiple textures
            } else {
                for (let t = 0; t < shTextureDesc.textureCount; t++) {
                    const paddedSHArray = shTextureDesc.data[t];
                    const srcOffset = t * 4;
                    const componentsInThisTexture = Math.min(4, shComponentCount - srcOffset);
                    for (let c = fromSplat; c <= toSplat; c++) {
                        const srcBase = shComponentCount * c + srcOffset;
                        const destBase = 4 * c;
                        for (let i = 0; i < componentsInThisTexture; i++) {
                            paddedSHArray[destBase + i] = shData[srcBase + i];
                        }
                    }
                    updateTexture(
                        shTextureDesc.textures[t],
                        shTextureDesc.size,
                        shTextureDesc.elementsPerTexel,
                        paddedSHArray,
                        paddedSHComponentCount
                    );
                }
            }
        }

        // update scene index & transform data
        const sceneIndexesTexDesc = this.splatDataTextures['sceneIndexes'];
        const paddedSceneIndexes = sceneIndexesTexDesc.data;
        for (let c = this.lastBuildSplatCount; c <= toSplat; c++) {
            paddedSceneIndexes[c] = this.globalSplatIndexToSceneIndexMap[c];
        }
        const sceneIndexesTexture = sceneIndexesTexDesc.texture;
        const sceneIndexesTextureProps = this.renderer ? this.renderer.properties.get(sceneIndexesTexture) : null;
        if (!sceneIndexesTextureProps || !sceneIndexesTextureProps.__webglTexture) {
            sceneIndexesTexture.needsUpdate = true;
        } else {
            this.updateDataTexture(
                paddedSceneIndexes,
                sceneIndexesTexDesc.texture,
                sceneIndexesTexDesc.size,
                sceneIndexesTextureProps,
                1,
                1,
                1,
                this.lastBuildSplatCount,
                toSplat
            );
        }
    }

    getTargetCovarianceCompressionLevel() {
        return this.halfPrecisionCovariancesOnGPU ? 1 : 0;
    }

    /**
     * Upload texture data in chunks across multiple frames to avoid blocking.
     * @param {number} fromSplat Starting splat index
     * @param {number} toSplat Ending splat index
     * @param {Object} options Chunking options
     * @param {number} options.splatsPerChunk Number of splats to upload per frame (default: 25000)
     * @param {Function} options.onProgress Callback with progress percentage
     * @return {Promise} Resolves when all chunks are uploaded
     */
    async updateDataTexturesChunked(fromSplat, toSplat, options = {}) {
        const { splatsPerChunk = 25000, onProgress } = options;
        const totalSplats = toSplat - fromSplat + 1;
        let uploadedSplats = 0;

        while (uploadedSplats < totalSplats) {
            const chunkStart = fromSplat + uploadedSplats;
            const chunkEnd = Math.min(chunkStart + splatsPerChunk - 1, toSplat);

            // Upload this chunk
            this.updateDataTexturesFromBaseData(chunkStart, chunkEnd);

            uploadedSplats += chunkEnd - chunkStart + 1;

            // Report progress
            if (onProgress) {
                const progress = Math.round((uploadedSplats / totalSplats) * 100);
                onProgress(progress);
            }

            // Yield to next frame if more chunks remain
            if (uploadedSplats < totalSplats) {
                await new Promise((resolve) => requestAnimationFrame(resolve));
            }
        }
    }

    getTargetSphericalHarmonicsCompressionLevel() {
        return Math.max(1, this.getMaximumSplatArrayCompressionLevel());
    }

    getMaximumSplatArrayCompressionLevel() {
        let maxCompressionLevel;
        for (let i = 0; i < this.scenes.length; i++) {
            const scene = this.getScene(i);
            const splatArray = scene.splatArray;
            if (i === 0 || splatArray.compressionLevel > maxCompressionLevel) {
                maxCompressionLevel = splatArray.compressionLevel;
            }
        }
        return maxCompressionLevel;
    }

    getMinimumSplatArrayCompressionLevel() {
        let minCompressionLevel;
        for (let i = 0; i < this.scenes.length; i++) {
            const scene = this.getScene(i);
            const splatArray = scene.splatArray;
            if (i === 0 || splatArray.compressionLevel < minCompressionLevel) {
                minCompressionLevel = splatArray.compressionLevel;
            }
        }
        return minCompressionLevel;
    }

    static computeTextureUpdateRegion(startSplat, endSplat, textureWidth, elementsPerTexel, elementsPerSplat) {
        const texelsPerSplat = elementsPerSplat / elementsPerTexel;

        const startSplatTexels = startSplat * texelsPerSplat;
        const startRow = Math.floor(startSplatTexels / textureWidth);
        const startRowElement = startRow * textureWidth * elementsPerTexel;

        const endSplatTexels = endSplat * texelsPerSplat;
        const endRow = Math.floor(endSplatTexels / textureWidth);
        const endRowEndElement = endRow * textureWidth * elementsPerTexel + textureWidth * elementsPerTexel;

        return {
            dataStart: startRowElement,
            dataEnd: endRowEndElement,
            startRow: startRow,
            endRow: endRow
        };
    }

    updateDataTexture(paddedData, texture, textureSize, textureProps, elementsPerTexel, elementsPerSplat, bytesPerElement, from, to) {
        const gl = this.renderer.getContext();
        const updateRegion = SplatMesh.computeTextureUpdateRegion(from, to, textureSize.x, elementsPerTexel, elementsPerSplat);
        const updateElementCount = updateRegion.dataEnd - updateRegion.dataStart;
        const updateDataView = new paddedData.constructor(paddedData.buffer, updateRegion.dataStart * bytesPerElement, updateElementCount);
        const updateHeight = updateRegion.endRow - updateRegion.startRow + 1;
        const glType = this.webGLUtils.convert(texture.type);
        const glFormat = this.webGLUtils.convert(texture.format, texture.colorSpace);
        const currentTexture = gl.getParameter(gl.TEXTURE_BINDING_2D);
        gl.bindTexture(gl.TEXTURE_2D, textureProps.__webglTexture);
        gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, updateRegion.startRow, textureSize.x, updateHeight, glFormat, glType, updateDataView);
        gl.bindTexture(gl.TEXTURE_2D, currentTexture);
    }

    static updatePaddedCompressedCovariancesTextureData(sourceData, textureData, textureDataStartIndex, fromElement, toElement) {
        let textureDataView = new DataView(textureData.buffer);
        let textureDataIndex = textureDataStartIndex;
        let sequentialCount = 0;
        for (let i = fromElement; i <= toElement; i += 2) {
            textureDataView.setUint16(textureDataIndex * 2, sourceData[i], true);
            textureDataView.setUint16(textureDataIndex * 2 + 2, sourceData[i + 1], true);
            textureDataIndex += 2;
            sequentialCount++;
            if (sequentialCount >= 3) {
                textureDataIndex += 2;
                sequentialCount = 0;
            }
        }
    }

    static updateCenterColorsPaddedData(from, to, centers, colors, paddedCenterColors) {
        for (let c = from; c <= to; c++) {
            const colorsBase = c * 4;
            const centersBase = c * 3;
            const centerColorsBase = c * 4;
            paddedCenterColors[centerColorsBase] = rgbaArrayToInteger(colors, colorsBase);
            paddedCenterColors[centerColorsBase + 1] = uintEncodedFloat(centers[centersBase]);
            paddedCenterColors[centerColorsBase + 2] = uintEncodedFloat(centers[centersBase + 1]);
            paddedCenterColors[centerColorsBase + 3] = uintEncodedFloat(centers[centersBase + 2]);
        }
    }

    static updateScaleRotationsPaddedData(from, to, scales, rotations, paddedScaleRotations) {
        const combinedSize = 6;
        for (let c = from; c <= to; c++) {
            const scaleBase = c * 3;
            const rotationBase = c * 4;
            const scaleRotationsBase = c * combinedSize;

            paddedScaleRotations[scaleRotationsBase] = scales[scaleBase];
            paddedScaleRotations[scaleRotationsBase + 1] = scales[scaleBase + 1];
            paddedScaleRotations[scaleRotationsBase + 2] = scales[scaleBase + 2];

            paddedScaleRotations[scaleRotationsBase + 3] = rotations[rotationBase];
            paddedScaleRotations[scaleRotationsBase + 4] = rotations[rotationBase + 1];
            paddedScaleRotations[scaleRotationsBase + 5] = rotations[rotationBase + 2];
        }
    }

    updateVisibleRegion(sinceLastBuildOnly) {
        const splatCount = this.getSplatCount(true);
        const tempCenter = new THREE.Vector3();
        if (!sinceLastBuildOnly) {
            const avgCenter = new THREE.Vector3();
            this.scenes.forEach((scene) => {
                avgCenter.add(scene.splatArray.sceneCenter);
            });
            avgCenter.multiplyScalar(1.0 / this.scenes.length);
            this.calculatedSceneCenter.copy(avgCenter);
            this.material.uniforms.sceneCenter.value.copy(this.calculatedSceneCenter);
            this.material.uniformsNeedUpdate = true;
        }

        if (this.maxSplatDistanceFromSceneCenter - this.visibleRegionBufferRadius > VISIBLE_REGION_EXPANSION_DELTA) {
            this.visibleRegionBufferRadius = this.maxSplatDistanceFromSceneCenter;
            this.visibleRegionRadius = Math.max(this.visibleRegionBufferRadius - VISIBLE_REGION_EXPANSION_DELTA, 0.0);
        }
        if (this.finalBuild) this.visibleRegionRadius = this.visibleRegionBufferRadius = this.maxSplatDistanceFromSceneCenter;
        this.updateVisibleRegionFadeDistance();
    }

    updateVisibleRegionFadeDistance(sceneRevealMode = SceneRevealMode.Default) {
        const fastFadeRate = SCENE_FADEIN_RATE_FAST * this.sceneFadeInRateMultiplier;
        const gradualFadeRate = SCENE_FADEIN_RATE_GRADUAL * this.sceneFadeInRateMultiplier;
        const defaultFadeInRate = this.finalBuild ? fastFadeRate : gradualFadeRate;
        const fadeInRate = sceneRevealMode === SceneRevealMode.Default ? defaultFadeInRate : gradualFadeRate;
        this.visibleRegionFadeStartRadius =
            (this.visibleRegionRadius - this.visibleRegionFadeStartRadius) * fadeInRate + this.visibleRegionFadeStartRadius;
        const fadeInPercentage =
            this.visibleRegionBufferRadius > 0 ? this.visibleRegionFadeStartRadius / this.visibleRegionBufferRadius : 0;
        const fadeInComplete = fadeInPercentage > 0.99;
        const shaderFadeInComplete = fadeInComplete || sceneRevealMode === SceneRevealMode.Instant ? 1 : 0;

        this.material.uniforms.visibleRegionFadeStartRadius.value = this.visibleRegionFadeStartRadius;
        this.material.uniforms.visibleRegionRadius.value = this.visibleRegionRadius;
        this.material.uniforms.firstRenderTime.value = this.firstRenderTime;
        this.material.uniforms.currentTime.value = performance.now();
        this.material.uniforms.fadeInComplete.value = shaderFadeInComplete;
        this.material.uniformsNeedUpdate = true;
        this.visibleRegionChanging = !fadeInComplete;
    }

    updateDisplayMode(displayMode) {
        if (this.displayMode === displayMode) return;
        this.material.uniforms.displayMode.value = displayMode;
        this.material.uniformsNeedUpdate = true;
        this.displayMode = displayMode;
    }

    nextDisplayMode() {
        this.updateDisplayMode((this.displayMode + 1) % Object.values(DisplayMode).length);
    }

    /**
     * Set the indexes of splats that should be rendered; should be sorted in desired render order.
     * @param {Uint32Array} globalIndexes Sorted index list of splats to be rendered
     * @param {number} renderSplatCount Total number of splats to be rendered. Necessary because we may not want to render
     *                                  every splat.
     */
    updateRenderIndexes(globalIndexes, renderSplatCount) {
        const geometry = this.geometry;
        geometry.attributes.splatIndex.set(globalIndexes);
        geometry.attributes.splatIndex.needsUpdate = true;
        if (renderSplatCount > 0 && this.firstRenderTime === -1) this.firstRenderTime = performance.now();
        geometry.instanceCount = renderSplatCount;
        geometry.setDrawRange(0, renderSplatCount);
    }

    /**
     * Update the transforms for each scene in this splat mesh from their individual components (position,
     * quaternion, and scale)
     */
    updateTransforms() {
        for (let i = 0; i < this.scenes.length; i++) {
            const scene = this.getScene(i);
            scene.updateTransform();
        }
    }

    updateUniforms = (function () {
        const viewport = new THREE.Vector2();

        return function (renderDimensions, cameraFocalLengthX, cameraFocalLengthY) {
            const splatCount = this.getSplatCount();
            if (splatCount > 0) {
                viewport.set(renderDimensions.x * this.devicePixelRatio, renderDimensions.y * this.devicePixelRatio);
                this.material.uniforms.viewport.value.copy(viewport);
                this.material.uniforms.basisViewport.value.set(1.0 / viewport.x, 1.0 / viewport.y);
                this.material.uniforms.focal.value.set(cameraFocalLengthX, cameraFocalLengthY);
                if (this.enableOptionalEffects) {
                    for (let i = 0; i < this.scenes.length; i++) {
                        this.material.uniforms.sceneOpacity.value[i] = clamp(this.getScene(i).opacity, 0.0, 1.0);
                        this.material.uniforms.sceneVisibility.value[i] = this.getScene(i).visible ? 1 : 0;
                        this.material.uniformsNeedUpdate = true;
                    }
                }
                this.material.uniformsNeedUpdate = true;
            }
        };
    })();

    setSplatScale(splatScale = 1) {
        this.splatScale = splatScale;
        this.material.uniforms.splatScale.value = splatScale;
        this.material.uniformsNeedUpdate = true;
    }

    getSplatScale() {
        return this.splatScale;
    }

    setPointCloudModeEnabled(enabled) {
        this.pointCloudModeEnabled = enabled;
        this.material.uniforms.pointCloudModeEnabled.value = enabled ? 1 : 0;
        this.material.uniformsNeedUpdate = true;
    }

    getPointCloudModeEnabled() {
        return this.pointCloudModeEnabled;
    }

    getSplatDataTextures() {
        return this.splatDataTextures;
    }

    getSplatCount(includeSinceLastBuild = false) {
        if (!includeSinceLastBuild) return this.lastBuildSplatCount;
        else return SplatMesh.getTotalSplatCountForScenes(this.scenes);
    }

    static getTotalSplatCountForScenes(scenes) {
        let totalSplatCount = 0;
        for (let scene of scenes) {
            if (scene && scene.splatArray) totalSplatCount += scene.splatArray.getSplatCount();
        }
        return totalSplatCount;
    }

    static getTotalSplatCountForSplatArrays(splatArrays) {
        let totalSplatCount = 0;
        for (let splatArray of splatArrays) totalSplatCount += splatArray.getSplatCount();
        return totalSplatCount;
    }

    getMaxSplatCount() {
        return SplatMesh.getTotalMaxSplatCountForScenes(this.scenes);
    }

    static getTotalMaxSplatCountForScenes(scenes) {
        let totalSplatCount = 0;
        for (let scene of scenes) {
            if (scene && scene.splatArray) totalSplatCount += scene.splatArray.getMaxSplatCount();
        }
        return totalSplatCount;
    }

    static getTotalMaxSplatCountForSplatArrays(splatArrays) {
        let totalSplatCount = 0;
        for (let splatArray of splatArrays) totalSplatCount += splatArray.getMaxSplatCount();
        return totalSplatCount;
    }

    disposeDistancesComputationGPUResources() {
        if (!this.renderer) return;

        const gl = this.renderer.getContext();

        if (this.distancesTransformFeedback.vao) {
            gl.deleteVertexArray(this.distancesTransformFeedback.vao);
            this.distancesTransformFeedback.vao = null;
        }
        if (this.distancesTransformFeedback.program) {
            gl.deleteProgram(this.distancesTransformFeedback.program);
            gl.deleteShader(this.distancesTransformFeedback.vertexShader);
            gl.deleteShader(this.distancesTransformFeedback.fragmentShader);
            this.distancesTransformFeedback.program = null;
            this.distancesTransformFeedback.vertexShader = null;
            this.distancesTransformFeedback.fragmentShader = null;
        }
        this.disposeDistancesComputationGPUBufferResources();
        if (this.distancesTransformFeedback.id) {
            gl.deleteTransformFeedback(this.distancesTransformFeedback.id);
            this.distancesTransformFeedback.id = null;
        }
    }

    disposeDistancesComputationGPUBufferResources() {
        if (!this.renderer) return;

        const gl = this.renderer.getContext();

        if (this.distancesTransformFeedback.centersBuffer) {
            gl.deleteBuffer(this.distancesTransformFeedback.centersBuffer);
            this.distancesTransformFeedback.centersBuffer = null;
        }
        if (this.distancesTransformFeedback.outDistancesBuffer) {
            gl.deleteBuffer(this.distancesTransformFeedback.outDistancesBuffer);
            this.distancesTransformFeedback.outDistancesBuffer = null;
        }
    }

    /**
     * Set the Three.js renderer used by this splat mesh
     * @param {THREE.WebGLRenderer} renderer Instance of THREE.WebGLRenderer
     */
    setRenderer(renderer) {
        if (renderer !== this.renderer) {
            this.renderer = renderer;
            const gl = this.renderer.getContext();
            const extensions = new WebGLExtensions(gl);
            const capabilities = new WebGLCapabilities(gl, extensions, {});
            extensions.init(capabilities);
            this.webGLUtils = new THREE.WebGLUtils(gl, extensions, capabilities);
            if (this.enableDistancesComputationOnGPU && this.getSplatCount() > 0) {
                this.setupDistancesComputationTransformFeedback();
                const { centers, sceneIndexes } = this.getDataForDistancesComputation(0, this.getSplatCount() - 1);
                this.refreshGPUBuffersForDistancesComputation(centers, sceneIndexes);
            }
        }
    }

    setupDistancesComputationTransformFeedback = (function () {
        let currentMaxSplatCount;

        return function () {
            const maxSplatCount = this.getMaxSplatCount();

            if (!this.renderer) return;

            const rebuildGPUObjects = this.lastRenderer !== this.renderer;
            const rebuildBuffers = currentMaxSplatCount !== maxSplatCount;

            if (!rebuildGPUObjects && !rebuildBuffers) return;

            if (rebuildGPUObjects) {
                this.disposeDistancesComputationGPUResources();
            } else if (rebuildBuffers) {
                this.disposeDistancesComputationGPUBufferResources();
            }

            const gl = this.renderer.getContext();

            const createShader = (gl, type, source) => {
                const shader = gl.createShader(type);
                if (!shader) {
                    console.error('Fatal error: gl could not create a shader object.');
                    return null;
                }

                gl.shaderSource(shader, source);
                gl.compileShader(shader);

                const compiled = gl.getShaderParameter(shader, gl.COMPILE_STATUS);
                if (!compiled) {
                    let typeName = 'unknown';
                    if (type === gl.VERTEX_SHADER) typeName = 'vertex shader';
                    else if (type === gl.FRAGMENT_SHADER) typeName = 'fragement shader';
                    const errors = gl.getShaderInfoLog(shader);
                    console.error('Failed to compile ' + typeName + ' with these errors:' + errors);
                    gl.deleteShader(shader);
                    return null;
                }

                return shader;
            };

            let vsSource;
            if (this.integerBasedDistancesComputation) {
                vsSource = `#version 300 es
                in ivec4 center;
                flat out int distance;`;

                vsSource += `
                    uniform ivec3 modelViewProj;
                    void main(void) {
                        distance = center.x * modelViewProj.x + center.y * modelViewProj.y + center.z * modelViewProj.z;
                    }
                `;
            } else {
                vsSource = `#version 300 es
                in vec4 center;
                flat out float distance;`;

                vsSource += `
                    uniform vec3 modelViewProj;
                    void main(void) {
                        distance = center.x * modelViewProj.x + center.y * modelViewProj.y + center.z * modelViewProj.z;
                    }
                `;
            }

            const fsSource = `#version 300 es
                precision lowp float;
                out vec4 fragColor;
                void main(){}
            `;

            const currentVao = gl.getParameter(gl.VERTEX_ARRAY_BINDING);
            const currentProgram = gl.getParameter(gl.CURRENT_PROGRAM);
            const currentProgramDeleted = currentProgram ? gl.getProgramParameter(currentProgram, gl.DELETE_STATUS) : false;

            if (rebuildGPUObjects) {
                this.distancesTransformFeedback.vao = gl.createVertexArray();
            }

            gl.bindVertexArray(this.distancesTransformFeedback.vao);

            if (rebuildGPUObjects) {
                const program = gl.createProgram();
                const vertexShader = createShader(gl, gl.VERTEX_SHADER, vsSource);
                const fragmentShader = createShader(gl, gl.FRAGMENT_SHADER, fsSource);
                if (!vertexShader || !fragmentShader) {
                    throw new Error('Could not compile shaders for distances computation on GPU.');
                }
                gl.attachShader(program, vertexShader);
                gl.attachShader(program, fragmentShader);
                gl.transformFeedbackVaryings(program, ['distance'], gl.SEPARATE_ATTRIBS);
                gl.linkProgram(program);

                const linked = gl.getProgramParameter(program, gl.LINK_STATUS);
                if (!linked) {
                    const error = gl.getProgramInfoLog(program);
                    console.error('Fatal error: Failed to link program: ' + error);
                    gl.deleteProgram(program);
                    gl.deleteShader(fragmentShader);
                    gl.deleteShader(vertexShader);
                    throw new Error('Could not link shaders for distances computation on GPU.');
                }

                this.distancesTransformFeedback.program = program;
                this.distancesTransformFeedback.vertexShader = vertexShader;
                this.distancesTransformFeedback.vertexShader = fragmentShader;
            }

            gl.useProgram(this.distancesTransformFeedback.program);

            this.distancesTransformFeedback.centersLoc = gl.getAttribLocation(this.distancesTransformFeedback.program, 'center');
            this.distancesTransformFeedback.modelViewProjLoc = gl.getUniformLocation(
                this.distancesTransformFeedback.program,
                'modelViewProj'
            );

            if (rebuildGPUObjects || rebuildBuffers) {
                this.distancesTransformFeedback.centersBuffer = gl.createBuffer();
                gl.bindBuffer(gl.ARRAY_BUFFER, this.distancesTransformFeedback.centersBuffer);
                gl.enableVertexAttribArray(this.distancesTransformFeedback.centersLoc);
                if (this.integerBasedDistancesComputation) {
                    gl.vertexAttribIPointer(this.distancesTransformFeedback.centersLoc, 4, gl.INT, 0, 0);
                } else {
                    gl.vertexAttribPointer(this.distancesTransformFeedback.centersLoc, 4, gl.FLOAT, false, 0, 0);
                }
            }

            if (rebuildGPUObjects || rebuildBuffers) {
                this.distancesTransformFeedback.outDistancesBuffer = gl.createBuffer();
            }
            gl.bindBuffer(gl.ARRAY_BUFFER, this.distancesTransformFeedback.outDistancesBuffer);
            gl.bufferData(gl.ARRAY_BUFFER, maxSplatCount * 4, gl.STATIC_READ);

            if (rebuildGPUObjects) {
                this.distancesTransformFeedback.id = gl.createTransformFeedback();
            }
            gl.bindTransformFeedback(gl.TRANSFORM_FEEDBACK, this.distancesTransformFeedback.id);
            gl.bindBufferBase(gl.TRANSFORM_FEEDBACK_BUFFER, 0, this.distancesTransformFeedback.outDistancesBuffer);

            if (currentProgram && currentProgramDeleted !== true) gl.useProgram(currentProgram);
            if (currentVao) gl.bindVertexArray(currentVao);

            this.lastRenderer = this.renderer;
            currentMaxSplatCount = maxSplatCount;
        };
    })();

    /**
     * Refresh GPU buffers used for computing splat distances with centers data from the scenes for this mesh.
     * @param {boolean} isUpdate Specify whether or not to update the GPU buffer or to initialize & fill
     * @param {Array<number>} centers The splat centers data
     * @param {number} offsetSplats Offset in the GPU buffer at which to start updating data, specified in splats
     */
    updateGPUCentersBufferForDistancesComputation(isUpdate, centers, offsetSplats) {
        if (!this.renderer) return;

        const gl = this.renderer.getContext();

        const currentVao = gl.getParameter(gl.VERTEX_ARRAY_BINDING);
        gl.bindVertexArray(this.distancesTransformFeedback.vao);

        const ArrayType = this.integerBasedDistancesComputation ? Uint32Array : Float32Array;
        const attributeBytesPerCenter = 16;
        const subBufferOffset = offsetSplats * attributeBytesPerCenter;

        gl.bindBuffer(gl.ARRAY_BUFFER, this.distancesTransformFeedback.centersBuffer);

        if (isUpdate) {
            gl.bufferSubData(gl.ARRAY_BUFFER, subBufferOffset, centers);
        } else {
            const maxArray = new ArrayType(this.getMaxSplatCount() * attributeBytesPerCenter);
            maxArray.set(centers);
            gl.bufferData(gl.ARRAY_BUFFER, maxArray, gl.STATIC_DRAW);
        }

        gl.bindBuffer(gl.ARRAY_BUFFER, null);

        if (currentVao) gl.bindVertexArray(currentVao);
    }

    /**
     * Refresh GPU buffers used for pre-computing splat distances with centers data from the scenes for this mesh.
     * @param {boolean} isUpdate Specify whether or not to update the GPU buffer or to initialize & fill
     * @param {Array<number>} sceneIndexes The splat scene indexes
     * @param {number} offsetSplats Offset in the GPU buffer at which to start updating data, specified in splats
     */
    updateGPUTransformIndexesBufferForDistancesComputation(isUpdate, sceneIndexes, offsetSplats) {
        if (!this.renderer || true) return;

        const gl = this.renderer.getContext();

        const currentVao = gl.getParameter(gl.VERTEX_ARRAY_BINDING);
        gl.bindVertexArray(this.distancesTransformFeedback.vao);

        const subBufferOffset = offsetSplats * 4;

        gl.bindBuffer(gl.ARRAY_BUFFER, this.distancesTransformFeedback.sceneIndexesBuffer);

        if (isUpdate) {
            gl.bufferSubData(gl.ARRAY_BUFFER, subBufferOffset, sceneIndexes);
        } else {
            const maxArray = new Uint32Array(this.getMaxSplatCount() * 4);
            maxArray.set(sceneIndexes);
            gl.bufferData(gl.ARRAY_BUFFER, maxArray, gl.STATIC_DRAW);
        }
        gl.bindBuffer(gl.ARRAY_BUFFER, null);

        if (currentVao) gl.bindVertexArray(currentVao);
    }

    /**
     * Get a typed array containing a mapping from global splat indexes to their scene index.
     * @param {number} start Starting splat index to store
     * @param {number} end Ending splat index to store
     * @return {Uint32Array}
     */
    getSceneIndexes(start, end) {
        let sceneIndexes;
        const fillCount = end - start + 1;
        sceneIndexes = new Uint32Array(fillCount);
        for (let i = start; i <= end; i++) {
            sceneIndexes[i] = this.globalSplatIndexToSceneIndexMap[i];
        }

        return sceneIndexes;
    }

    /**
     * Fill 'array' with the transforms for each scene in this splat mesh.
     * @param {Array} array Empty array to be filled with scene transforms. If not empty, contents will be overwritten.
     */
    fillTransformsArray = (function () {
        const tempArray = [];

        return function (array) {
            if (tempArray.length !== array.length) tempArray.length = array.length;
            for (let i = 0; i < this.scenes.length; i++) {
                const sceneTransform = this.getScene(i).transform;
                const sceneTransformElements = sceneTransform.elements;
                for (let j = 0; j < 16; j++) {
                    tempArray[i * 16 + j] = sceneTransformElements[j];
                }
            }
            array.set(tempArray);
        };
    })();

    computeDistancesOnGPU = (function () {
        return function (modelViewProjMatrix, outComputedDistances) {
            if (!this.renderer) return;

            // console.time("gpu_compute_distances");
            const gl = this.renderer.getContext();

            const currentVao = gl.getParameter(gl.VERTEX_ARRAY_BINDING);
            const currentProgram = gl.getParameter(gl.CURRENT_PROGRAM);
            const currentProgramDeleted = currentProgram ? gl.getProgramParameter(currentProgram, gl.DELETE_STATUS) : false;

            gl.bindVertexArray(this.distancesTransformFeedback.vao);
            gl.useProgram(this.distancesTransformFeedback.program);

            gl.enable(gl.RASTERIZER_DISCARD);

            if (this.integerBasedDistancesComputation) {
                const iViewProjMatrix = SplatMesh.getIntegerMatrixArray(modelViewProjMatrix);
                const iViewProj = [iViewProjMatrix[2], iViewProjMatrix[6], iViewProjMatrix[10]];
                gl.uniform3i(this.distancesTransformFeedback.modelViewProjLoc, iViewProj[0], iViewProj[1], iViewProj[2]);
            } else {
                const viewProj = [modelViewProjMatrix.elements[2], modelViewProjMatrix.elements[6], modelViewProjMatrix.elements[10]];
                gl.uniform3f(this.distancesTransformFeedback.modelViewProjLoc, viewProj[0], viewProj[1], viewProj[2]);
            }

            gl.bindBuffer(gl.ARRAY_BUFFER, this.distancesTransformFeedback.centersBuffer);
            gl.enableVertexAttribArray(this.distancesTransformFeedback.centersLoc);
            if (this.integerBasedDistancesComputation) {
                gl.vertexAttribIPointer(this.distancesTransformFeedback.centersLoc, 4, gl.INT, 0, 0);
            } else {
                gl.vertexAttribPointer(this.distancesTransformFeedback.centersLoc, 4, gl.FLOAT, false, 0, 0);
            }

            gl.bindTransformFeedback(gl.TRANSFORM_FEEDBACK, this.distancesTransformFeedback.id);
            gl.bindBufferBase(gl.TRANSFORM_FEEDBACK_BUFFER, 0, this.distancesTransformFeedback.outDistancesBuffer);

            gl.beginTransformFeedback(gl.POINTS);
            gl.drawArrays(gl.POINTS, 0, this.getSplatCount());
            gl.endTransformFeedback();

            gl.bindBufferBase(gl.TRANSFORM_FEEDBACK_BUFFER, 0, null);
            gl.bindTransformFeedback(gl.TRANSFORM_FEEDBACK, null);

            gl.disable(gl.RASTERIZER_DISCARD);

            const sync = gl.fenceSync(gl.SYNC_GPU_COMMANDS_COMPLETE, 0);
            gl.flush();

            const promise = new Promise((resolve) => {
                const checkSync = () => {
                    if (this.disposed) {
                        resolve();
                    } else {
                        const timeout = 0;
                        const bitflags = 0;
                        const status = gl.clientWaitSync(sync, bitflags, timeout);
                        switch (status) {
                            case gl.TIMEOUT_EXPIRED:
                                this.computeDistancesOnGPUSyncTimeout = setTimeout(checkSync);
                                return this.computeDistancesOnGPUSyncTimeout;
                            case gl.WAIT_FAILED:
                                throw new Error('should never get here');
                            default:
                                this.computeDistancesOnGPUSyncTimeout = null;
                                gl.deleteSync(sync);
                                const currentVao = gl.getParameter(gl.VERTEX_ARRAY_BINDING);
                                gl.bindVertexArray(this.distancesTransformFeedback.vao);
                                gl.bindBuffer(gl.ARRAY_BUFFER, this.distancesTransformFeedback.outDistancesBuffer);
                                gl.getBufferSubData(gl.ARRAY_BUFFER, 0, outComputedDistances);
                                gl.bindBuffer(gl.ARRAY_BUFFER, null);

                                if (currentVao) gl.bindVertexArray(currentVao);

                                // console.timeEnd("gpu_compute_distances");

                                resolve();
                        }
                    }
                };
                this.computeDistancesOnGPUSyncTimeout = setTimeout(checkSync);
            });

            if (currentProgram && currentProgramDeleted !== true) gl.useProgram(currentProgram);
            if (currentVao) gl.bindVertexArray(currentVao);

            return promise;
        };
    })();

    /**
     * Given a global splat index, return corresponding local data (splat buffer, index of splat in that splat
     * buffer, and the corresponding transform)
     * @param {number} globalIndex Global splat index
     * @param {object} paramsObj Object in which to store local data
     * @param {boolean} returnSceneTransform By default, the transform of the scene to which the splat at 'globalIndex' belongs will be
     *                                       returned via the 'sceneTransform' property of 'paramsObj' only if the splat mesh is static.
     *                                       If 'returnSceneTransform' is true, the 'sceneTransform' property will always contain the scene
     *                                       transform, and if 'returnSceneTransform' is false, the 'sceneTransform' property will always
     *                                       be null.
     */
    getLocalSplatParameters(globalIndex, paramsObj, returnSceneTransform) {
        if (returnSceneTransform === undefined || returnSceneTransform === null) {
            returnSceneTransform = true;
        }
        paramsObj.splatArray = this.getSplatArrayForSplat(globalIndex);
        paramsObj.localIndex = this.getSplatLocalIndex(globalIndex);
        paramsObj.sceneTransform = returnSceneTransform ? this.getSceneTransformForSplat(globalIndex) : null;
    }

    /**
     * Fill arrays with splat data and apply transforms if appropriate. Each array is optional.
     * @param {Float32Array} covariances Target storage for splat covariances
     * @param {Float32Array} scales Target storage for splat scales
     * @param {Float32Array} rotations Target storage for splat rotations
     * @param {Float32Array} centers Target storage for splat centers
     * @param {Uint8Array} colors Target storage for splat colors
     * @param {Float32Array} sphericalHarmonics Target storage for spherical harmonics
     * @param {boolean} applySceneTransform By default, scene transforms are applied to relevant splat data only if the splat mesh is
     *                                      static. If 'applySceneTransform' is true, scene transforms will always be applied and if
     *                                      it is false, they will never be applied. If undefined, the default behavior will apply.
     * @param {number} covarianceCompressionLevel The compression level for covariances in the destination array
     * @param {number} sphericalHarmonicsCompressionLevel The compression level for spherical harmonics in the destination array
     * @param {number} srcStart The start location from which to pull source data
     * @param {number} srcEnd The end location from which to pull source data
     * @param {number} destStart The start location from which to write data
     */
    fillSplatDataArrays(
        covariances,
        scales,
        rotations,
        centers,
        colors,
        sphericalHarmonics,
        centersPadded,
        applySceneTransform,
        covarianceCompressionLevel = 0,
        scaleRotationCompressionLevel = 0,
        sphericalHarmonicsCompressionLevel = 1,
        srcStart,
        srcEnd,
        destStart = 0,
        sceneIndex
    ) {
        const scaleOverride = new THREE.Vector3();
        scaleOverride.x = undefined;
        scaleOverride.y = undefined;
        if (this.splatModelType === ModelType.GS3D) {
            scaleOverride.z = undefined;
        } else {
            scaleOverride.z = 1;
        }
        const tempTransform = new THREE.Matrix4();

        let startSceneIndex = 0;
        let endSceneIndex = this.scenes.length - 1;
        if (sceneIndex !== undefined && sceneIndex !== null && sceneIndex >= 0 && sceneIndex <= this.scenes.length) {
            startSceneIndex = sceneIndex;
            endSceneIndex = sceneIndex;
        }
        for (let i = startSceneIndex; i <= endSceneIndex; i++) {
            if (applySceneTransform === undefined || applySceneTransform === null) {
                applySceneTransform = true;
            }

            const scene = this.getScene(i);
            const splatArray = scene.splatArray;
            let sceneTransform;
            if (applySceneTransform) {
                this.getSceneTransform(i, tempTransform);
                sceneTransform = tempTransform;
            }
            if (covariances) {
                splatArray.fillSplatCovarianceArray(covariances, sceneTransform, srcStart, srcEnd, destStart, covarianceCompressionLevel);
            }
            if (scales || rotations || centers) {
                let start = performance.now();
                this.maxSplatDistanceFromSceneCenter = splatArray.fillUnifiedSplatData(
                    centers,
                    scales,
                    rotations,
                    colors,
                    sphericalHarmonics,
                    centersPadded,
                    this.calculatedSceneCenter,
                    sceneTransform,
                    srcStart,
                    srcEnd,
                    destStart
                );
                let end = performance.now();
                if (Constants.PERF_LOG) console.log(`Filled unified splat data for scene ${i} in ${end - start} ms`);
            }
            destStart += splatArray.getSplatCount();
        }
    }

    /**
     * Convert splat centers, which are floating point values, to an array of integers and multiply
     * each by 1000. Centers will get transformed as appropriate before conversion to integer.
     * @param {number} start The index at which to start retrieving data
     * @param {number} end The index at which to stop retrieving data
     * @param {boolean} padFour Enforce alignment of 4 by inserting a 1 after every 3 values
     * @return {Int32Array}
     */
    getIntegerCenters(start, end, padFour = false) {
        const splatCount = end - start + 1;
        const floatCenters = new Float32Array(splatCount * 3);
        this.fillSplatDataArrays(null, null, null, floatCenters, null, null, undefined, undefined, undefined, undefined, start);
        let intCenters;
        let componentCount = padFour ? 4 : 3;
        intCenters = new Int32Array(splatCount * componentCount);
        for (let i = 0; i < splatCount; i++) {
            for (let t = 0; t < 3; t++) {
                intCenters[i * componentCount + t] = Math.round(floatCenters[i * 3 + t] * 1000.0);
            }
            if (padFour) intCenters[i * componentCount + 3] = 1000;
        }
        return intCenters;
    }

    /**
     * Returns an array of splat centers, transformed as appropriate, optionally padded.
     * @param {number} start The index at which to start retrieving data
     * @param {number} end The index at which to stop retrieving data
     * @param {boolean} padFour Enforce alignment of 4 by inserting a 1 after every 3 values
     * @return {Float32Array}
     */
    getFloatCenters(start, end, padFour = false) {
        const splatCount = end - start + 1;
        const floatCenters = new Float32Array(splatCount * 3);
        this.fillSplatDataArrays(null, null, null, floatCenters, null, null, undefined, undefined, undefined, undefined, start);
        if (!padFour) return floatCenters;
        let paddedFloatCenters = new Float32Array(splatCount * 4);
        for (let i = 0; i < splatCount; i++) {
            for (let t = 0; t < 3; t++) {
                paddedFloatCenters[i * 4 + t] = floatCenters[i * 3 + t];
            }
            paddedFloatCenters[i * 4 + 3] = 1.0;
        }
        return paddedFloatCenters;
    }

    /**
     * Get the center for a splat, transformed as appropriate.
     * @param {number} globalIndex Global index of splat
     * @param {THREE.Vector3} outCenter THREE.Vector3 instance in which to store splat center
     * @param {boolean} applySceneTransform By default, if the splat mesh is static, the transform of the scene to which the splat at
     *                                      'globalIndex' belongs will be applied to the splat center. If 'applySceneTransform' is true,
     *                                      the scene transform will always be applied and if 'applySceneTransform' is false, the
     *                                      scene transform will never be applied. If undefined, the default behavior will apply.
     */
    getSplatCenter = (function () {
        const paramsObj = {};

        return function (globalIndex, outCenter, applySceneTransform) {
            this.getLocalSplatParameters(globalIndex, paramsObj, applySceneTransform);
            paramsObj.splatArray.getSplatCenter(paramsObj.localIndex, outCenter, paramsObj.sceneTransform);
        };
    })();

    /**
     * Get the scale and rotation for a splat, transformed as appropriate.
     * @param {number} globalIndex Global index of splat
     * @param {THREE.Vector3} outScale THREE.Vector3 instance in which to store splat scale
     * @param {THREE.Quaternion} outRotation THREE.Quaternion instance in which to store splat rotation
     * @param {boolean} applySceneTransform By default, if the splat mesh is static, the transform of the scene to which the splat at
     *                                      'globalIndex' belongs will be applied to the splat scale and rotation. If
     *                                      'applySceneTransform' is true, the scene transform will always be applied and if
     *                                      'applySceneTransform' is false, the scene transform will never be applied. If undefined,
     *                                      the default behavior will apply.
     */
    getSplatScaleAndRotation = (function () {
        const paramsObj = {};
        const scaleOverride = new THREE.Vector3();

        return function (globalIndex, outScale, outRotation, applySceneTransform) {
            this.getLocalSplatParameters(globalIndex, paramsObj, applySceneTransform);
            scaleOverride.x = undefined;
            scaleOverride.y = undefined;
            scaleOverride.z = undefined;
            if (this.splatModelType === ModelType.GS2D) scaleOverride.z = 0;
            paramsObj.splatArray.getSplatScaleAndRotation(
                paramsObj.localIndex,
                outScale,
                outRotation,
                paramsObj.sceneTransform,
                scaleOverride
            );
        };
    })();

    /**
     * Get the color for a splat.
     * @param {number} globalIndex Global index of splat
     * @param {THREE.Vector4} outColor THREE.Vector4 instance in which to store splat color
     */
    getSplatColor = (function () {
        const paramsObj = {};

        return function (globalIndex, outColor) {
            this.getLocalSplatParameters(globalIndex, paramsObj);
            paramsObj.splatArray.getSplatColor(paramsObj.localIndex, outColor);
        };
    })();

    /**
     * Store the transform of the scene at 'sceneIndex' in 'outTransform'.
     * @param {number} sceneIndex Index of the desired scene
     * @param {THREE.Matrix4} outTransform Instance of THREE.Matrix4 in which to store the scene's transform
     */
    getSceneTransform(sceneIndex, outTransform) {
        const scene = this.getScene(sceneIndex);
        scene.updateTransform();
        outTransform.copy(scene.transform);
    }

    /**
     * Get the scene at 'sceneIndex'.
     * @param {number} sceneIndex Index of the desired scene
     * @return {SplatScene}
     */
    getScene(sceneIndex) {
        if (sceneIndex < 0 || sceneIndex >= this.scenes.length) {
            throw new Error('SplatMesh::getScene() -> Invalid scene index.');
        }
        return this.scenes[sceneIndex];
    }

    getSceneCount() {
        return this.scenes.length;
    }

    getSplatArrayForSplat(globalIndex) {
        return this.getScene(this.globalSplatIndexToSceneIndexMap[globalIndex]).splatArray;
    }

    getSceneIndexForSplat(globalIndex) {
        return this.globalSplatIndexToSceneIndexMap[globalIndex];
    }

    getSceneTransformForSplat(globalIndex) {
        return this.getScene(this.globalSplatIndexToSceneIndexMap[globalIndex]).transform;
    }

    getSplatLocalIndex(globalIndex) {
        return this.globalSplatIndexToLocalSplatIndexMap[globalIndex];
    }

    static getIntegerMatrixArray(matrix) {
        const matrixElements = matrix.elements;
        const intMatrixArray = [];
        for (let i = 0; i < 16; i++) {
            intMatrixArray[i] = Math.round(matrixElements[i] * 1000.0);
        }
        return intMatrixArray;
    }

    computeBoundingBox(applySceneTransforms = false, sceneIndex) {
        let splatCount = this.getSplatCount();
        if (sceneIndex !== undefined && sceneIndex !== null) {
            if (sceneIndex < 0 || sceneIndex >= this.scenes.length) {
                throw new Error('SplatMesh::computeBoundingBox() -> Invalid scene index.');
            }
            splatCount = this.scenes[sceneIndex].splatArray.getSplatCount();
        }

        const floatCenters = new Float32Array(splatCount * 3);
        this.fillSplatDataArrays(
            null,
            null,
            null,
            floatCenters,
            null,
            null,
            applySceneTransforms,
            undefined,
            undefined,
            undefined,
            undefined,
            sceneIndex
        );

        const min = new THREE.Vector3();
        const max = new THREE.Vector3();
        for (let i = 0; i < splatCount; i++) {
            const offset = i * 3;
            const x = floatCenters[offset];
            const y = floatCenters[offset + 1];
            const z = floatCenters[offset + 2];
            if (i === 0 || x < min.x) min.x = x;
            if (i === 0 || y < min.y) min.y = y;
            if (i === 0 || z < min.z) min.z = z;
            if (i === 0 || x > max.x) max.x = x;
            if (i === 0 || y > max.y) max.y = y;
            if (i === 0 || z > max.z) max.z = z;
        }

        return new THREE.Box3(min, max);
    }
}

var SorterWasm = "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";

var SorterWasmNoSIMD = "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";

var SorterWasmNonShared = "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";

var SorterWasmNoSIMDNonShared = "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";

function sortWorker(self) {
    let wasmInstance;
    let wasmMemory;
    let useSharedMemory;
    let integerBasedSort;
    let dynamicMode;
    let splatCount;
    let indexesToSortOffset;
    let sortedIndexesOffset;
    let sceneIndexesOffset;
    let transformsOffset;
    let precomputedDistancesOffset;
    let mappedDistancesOffset;
    let frequenciesOffset;
    let centersOffset;
    let modelViewProjOffset;
    let countsZero;
    let sortedIndexesOut;
    let distanceMapRange;
    let uploadedSplatCount;
    let Constants;

    function sort(
        splatSortCount,
        splatRenderCount,
        modelViewProj,
        usePrecomputedDistances,
        copyIndexesToSort,
        copyPrecomputedDistances,
        copyTransforms
    ) {
        const sortStartTime = performance.now();

        if (!useSharedMemory) {
            const indexesToSort = new Uint32Array(wasmMemory, indexesToSortOffset, copyIndexesToSort.byteLength / Constants.BytesPerInt);
            indexesToSort.set(copyIndexesToSort);
            const transforms = new Float32Array(wasmMemory, transformsOffset, copyTransforms.byteLength / Constants.BytesPerFloat);
            transforms.set(copyTransforms);
            if (usePrecomputedDistances) {
                let precomputedDistances;
                if (integerBasedSort) {
                    precomputedDistances = new Int32Array(
                        wasmMemory,
                        precomputedDistancesOffset,
                        copyPrecomputedDistances.byteLength / Constants.BytesPerInt
                    );
                } else {
                    precomputedDistances = new Float32Array(
                        wasmMemory,
                        precomputedDistancesOffset,
                        copyPrecomputedDistances.byteLength / Constants.BytesPerFloat
                    );
                }
                precomputedDistances.set(copyPrecomputedDistances);
            }
        }

        if (!countsZero) countsZero = new Uint32Array(distanceMapRange);
        new Float32Array(wasmMemory, modelViewProjOffset, 16).set(modelViewProj);
        new Uint32Array(wasmMemory, frequenciesOffset, distanceMapRange).set(countsZero);
        wasmInstance.exports.sortIndexes(
            indexesToSortOffset,
            centersOffset,
            precomputedDistancesOffset,
            mappedDistancesOffset,
            frequenciesOffset,
            modelViewProjOffset,
            sortedIndexesOffset,
            sceneIndexesOffset,
            transformsOffset,
            distanceMapRange,
            splatSortCount,
            splatRenderCount,
            splatCount,
            usePrecomputedDistances,
            integerBasedSort,
            dynamicMode
        );

        const sortMessage = {
            sortDone: true,
            splatSortCount: splatSortCount,
            splatRenderCount: splatRenderCount,
            sortTime: 0
        };
        if (!useSharedMemory) {
            const sortedIndexes = new Uint32Array(wasmMemory, sortedIndexesOffset, splatRenderCount);
            if (!sortedIndexesOut || sortedIndexesOut.length < splatRenderCount) {
                sortedIndexesOut = new Uint32Array(splatRenderCount);
            }
            sortedIndexesOut.set(sortedIndexes);
            sortMessage.sortedIndexes = sortedIndexesOut;
        }
        const sortEndTime = performance.now();

        sortMessage.sortTime = sortEndTime - sortStartTime;

        self.postMessage(sortMessage);
    }

    self.onmessage = (e) => {
        if (e.data.centers) {
            centers = e.data.centers;
            sceneIndexes = e.data.sceneIndexes;
            if (integerBasedSort) {
                new Int32Array(wasmMemory, centersOffset + e.data.range.from * Constants.BytesPerInt * 4, e.data.range.count * 4).set(
                    new Int32Array(centers)
                );
            } else {
                new Float32Array(wasmMemory, centersOffset + e.data.range.from * Constants.BytesPerFloat * 4, e.data.range.count * 4).set(
                    new Float32Array(centers)
                );
            }
            if (dynamicMode) {
                new Uint32Array(wasmMemory, sceneIndexesOffset + e.data.range.from * 4, e.data.range.count).set(
                    new Uint32Array(sceneIndexes)
                );
            }
            uploadedSplatCount = e.data.range.from + e.data.range.count;
            centers = null;
            sceneIndexes = null;
        } else if (e.data.sort) {
            const renderCount = Math.min(e.data.sort.splatRenderCount || 0, uploadedSplatCount);
            const sortCount = Math.min(e.data.sort.splatSortCount || 0, uploadedSplatCount);
            const usePrecomputedDistances = e.data.sort.usePrecomputedDistances;

            let copyIndexesToSort;
            let copyPrecomputedDistances;
            let copyTransforms;
            if (!useSharedMemory) {
                copyIndexesToSort = e.data.sort.indexesToSort;
                copyTransforms = e.data.sort.transforms;
                if (usePrecomputedDistances) copyPrecomputedDistances = e.data.sort.precomputedDistances;
            }
            sort(
                sortCount,
                renderCount,
                e.data.sort.modelViewProj,
                usePrecomputedDistances,
                copyIndexesToSort,
                copyPrecomputedDistances,
                copyTransforms
            );
        } else if (e.data.init) {
            // Yep, this is super hacky and gross :(
            Constants = e.data.init.Constants;

            splatCount = e.data.init.splatCount;
            useSharedMemory = e.data.init.useSharedMemory;
            integerBasedSort = e.data.init.integerBasedSort;
            dynamicMode = e.data.init.dynamicMode;
            distanceMapRange = e.data.init.distanceMapRange;
            uploadedSplatCount = 0;

            const CENTERS_BYTES_PER_ENTRY = integerBasedSort ? Constants.BytesPerInt * 4 : Constants.BytesPerFloat * 4;

            const sorterWasmBytes = new Uint8Array(e.data.init.sorterWasmBytes);

            const matrixSize = 16 * Constants.BytesPerFloat;
            const memoryRequiredForIndexesToSort = splatCount * Constants.BytesPerInt;
            const memoryRequiredForCenters = splatCount * CENTERS_BYTES_PER_ENTRY;
            const memoryRequiredForModelViewProjectionMatrix = matrixSize;
            const memoryRequiredForPrecomputedDistances = integerBasedSort
                ? splatCount * Constants.BytesPerInt
                : splatCount * Constants.BytesPerFloat;
            const memoryRequiredForMappedDistances = splatCount * Constants.BytesPerInt;
            const memoryRequiredForSortedIndexes = splatCount * Constants.BytesPerInt;
            const memoryRequiredForIntermediateSortBuffers = integerBasedSort
                ? distanceMapRange * Constants.BytesPerInt * 2
                : distanceMapRange * Constants.BytesPerFloat * 2;
            const memoryRequiredforTransformIndexes = dynamicMode ? splatCount * Constants.BytesPerInt : 0;
            const memoryRequiredforTransforms = dynamicMode ? Constants.MaxScenes * matrixSize : 0;
            const extraMemory = Constants.MemoryPageSize * 32;

            const totalRequiredMemory =
                memoryRequiredForIndexesToSort +
                memoryRequiredForCenters +
                memoryRequiredForModelViewProjectionMatrix +
                memoryRequiredForPrecomputedDistances +
                memoryRequiredForMappedDistances +
                memoryRequiredForIntermediateSortBuffers +
                memoryRequiredForSortedIndexes +
                memoryRequiredforTransformIndexes +
                memoryRequiredforTransforms +
                extraMemory;
            const totalPagesRequired = Math.floor(totalRequiredMemory / Constants.MemoryPageSize) + 1;
            const sorterWasmImport = {
                module: {},
                env: {
                    memory: new WebAssembly.Memory({
                        initial: totalPagesRequired,
                        maximum: totalPagesRequired,
                        shared: true
                    })
                }
            };
            WebAssembly.compile(sorterWasmBytes)
                .then((wasmModule) => {
                    return WebAssembly.instantiate(wasmModule, sorterWasmImport);
                })
                .then((instance) => {
                    wasmInstance = instance;
                    indexesToSortOffset = 0;
                    centersOffset = indexesToSortOffset + memoryRequiredForIndexesToSort;
                    modelViewProjOffset = centersOffset + memoryRequiredForCenters;
                    precomputedDistancesOffset = modelViewProjOffset + memoryRequiredForModelViewProjectionMatrix;
                    mappedDistancesOffset = precomputedDistancesOffset + memoryRequiredForPrecomputedDistances;
                    frequenciesOffset = mappedDistancesOffset + memoryRequiredForMappedDistances;
                    sortedIndexesOffset = frequenciesOffset + memoryRequiredForIntermediateSortBuffers;
                    sceneIndexesOffset = sortedIndexesOffset + memoryRequiredForSortedIndexes;
                    transformsOffset = sceneIndexesOffset + memoryRequiredforTransformIndexes;
                    wasmMemory = sorterWasmImport.env.memory.buffer;
                    if (useSharedMemory) {
                        self.postMessage({
                            sortSetupPhase1Complete: true,
                            indexesToSortBuffer: wasmMemory,
                            indexesToSortOffset: indexesToSortOffset,
                            sortedIndexesBuffer: wasmMemory,
                            sortedIndexesOffset: sortedIndexesOffset,
                            precomputedDistancesBuffer: wasmMemory,
                            precomputedDistancesOffset: precomputedDistancesOffset,
                            transformsBuffer: wasmMemory,
                            transformsOffset: transformsOffset
                        });
                    } else {
                        self.postMessage({
                            sortSetupPhase1Complete: true
                        });
                    }
                });
        }
    };
}

function createSortWorker(
    splatCount,
    useSharedMemory,
    enableSIMDInSort,
    integerBasedSort,
    dynamicMode,
    splatSortDistanceMapPrecision = Constants.DefaultSplatSortDistanceMapPrecision
) {
    const blobUrl = URL.createObjectURL(
        new Blob(['(', sortWorker.toString(), ')(self)'], {
            type: 'application/javascript'
        })
    );
    const worker = new Worker(blobUrl);

    let sourceWasm = SorterWasm;

    // iOS makes choosing the right WebAssembly configuration tricky :(
    const iOSSemVer = isIOS() ? getIOSSemever() : null;
    if (!enableSIMDInSort && !useSharedMemory) {
        sourceWasm = SorterWasmNoSIMD;
        // Testing on various devices has shown that even when shared memory is disabled, the WASM module with shared
        // memory can still be used most of the time -- the exception seems to be iOS devices below 16.4
        if (iOSSemVer && iOSSemVer.major <= 16 && iOSSemVer.minor < 4) {
            sourceWasm = SorterWasmNoSIMDNonShared;
        }
    } else if (!enableSIMDInSort) {
        sourceWasm = SorterWasmNoSIMD;
    } else if (!useSharedMemory) {
        // Same issue with shared memory as above on iOS devices
        if (iOSSemVer && iOSSemVer.major <= 16 && iOSSemVer.minor < 4) {
            sourceWasm = SorterWasmNonShared;
        }
    }

    const sorterWasmBinaryString = atob(sourceWasm);
    const sorterWasmBytes = new Uint8Array(sorterWasmBinaryString.length);
    for (let i = 0; i < sorterWasmBinaryString.length; i++) {
        sorterWasmBytes[i] = sorterWasmBinaryString.charCodeAt(i);
    }

    worker.postMessage({
        init: {
            sorterWasmBytes: sorterWasmBytes.buffer,
            splatCount: splatCount,
            useSharedMemory: useSharedMemory,
            integerBasedSort: integerBasedSort,
            dynamicMode: dynamicMode,
            distanceMapRange: 1 << splatSortDistanceMapPrecision,
            // Super hacky
            Constants: {
                BytesPerFloat: Constants.BytesPerFloat,
                BytesPerInt: Constants.BytesPerInt,
                MemoryPageSize: Constants.MemoryPageSize,
                MaxScenes: Constants.MaxScenes
            }
        }
    });
    worker.blobUrl = blobUrl;
    return worker;
}

const LoaderStatus = {
    Downloading: 0,
    Processing: 1,
    Done: 2
};

const RenderMode = {
    Always: 0,
    OnChange: 1,
    Never: 2
};

/**
 * OpenEXR loader currently supports uncompressed, ZIP(S), RLE, PIZ and DWA/B compression.
 * Supports reading as UnsignedByte, HalfFloat and Float type data texture.
 *
 * Referred to the original Industrial Light & Magic OpenEXR implementation and the TinyEXR / Syoyo Fujita
 * implementation, so I have preserved their copyright notices.
 */

// /*
// Copyright (c) 2014 - 2017, Syoyo Fujita
// All rights reserved.

// Redistribution and use in source and binary forms, with or without
// modification, are permitted provided that the following conditions are met:
//     * Redistributions of source code must retain the above copyright
//       notice, this list of conditions and the following disclaimer.
//     * Redistributions in binary form must reproduce the above copyright
//       notice, this list of conditions and the following disclaimer in the
//       documentation and/or other materials provided with the distribution.
//     * Neither the name of the Syoyo Fujita nor the
//       names of its contributors may be used to endorse or promote products
//       derived from this software without specific prior written permission.

// THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND
// ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED
// WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE
// DISCLAIMED. IN NO EVENT SHALL <COPYRIGHT HOLDER> BE LIABLE FOR ANY
// DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES
// (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
// LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND
// ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT
// (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS
// SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
// */

// // TinyEXR contains some OpenEXR code, which is licensed under ------------

// ///////////////////////////////////////////////////////////////////////////
// //
// // Copyright (c) 2002, Industrial Light & Magic, a division of Lucas
// // Digital Ltd. LLC
// //
// // All rights reserved.
// //
// // Redistribution and use in source and binary forms, with or without
// // modification, are permitted provided that the following conditions are
// // met:
// // *       Redistributions of source code must retain the above copyright
// // notice, this list of conditions and the following disclaimer.
// // *       Redistributions in binary form must reproduce the above
// // copyright notice, this list of conditions and the following disclaimer
// // in the documentation and/or other materials provided with the
// // distribution.
// // *       Neither the name of Industrial Light & Magic nor the names of
// // its contributors may be used to endorse or promote products derived
// // from this software without specific prior written permission.
// //
// // THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
// // "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
// // LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR
// // A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT
// // OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL,
// // SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT
// // LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE,
// // DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY
// // THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT
// // (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE
// // OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
// //
// ///////////////////////////////////////////////////////////////////////////

// // End of OpenEXR license -------------------------------------------------

class EXRLoader extends DataTextureLoader {
    constructor(manager) {
        super(manager);

        this.type = HalfFloatType;
    }

    parse(buffer) {
        const USHORT_RANGE = 1 << 16;
        const BITMAP_SIZE = USHORT_RANGE >> 3;

        const HUF_ENCBITS = 16; // literal (value) bit length
        const HUF_DECBITS = 14; // decoding bit size (>= 8)

        const HUF_ENCSIZE = (1 << HUF_ENCBITS) + 1; // encoding table size
        const HUF_DECSIZE = 1 << HUF_DECBITS; // decoding table size
        const HUF_DECMASK = HUF_DECSIZE - 1;

        const NBITS = 16;
        const A_OFFSET = 1 << (NBITS - 1);
        const MOD_MASK = (1 << NBITS) - 1;

        const SHORT_ZEROCODE_RUN = 59;
        const LONG_ZEROCODE_RUN = 63;
        const SHORTEST_LONG_RUN = 2 + LONG_ZEROCODE_RUN - SHORT_ZEROCODE_RUN;

        const ULONG_SIZE = 8;
        const FLOAT32_SIZE = 4;
        const INT32_SIZE = 4;
        const INT16_SIZE = 2;
        const INT8_SIZE = 1;

        const STATIC_HUFFMAN = 0;
        const DEFLATE = 1;

        const UNKNOWN = 0;
        const LOSSY_DCT = 1;
        const RLE = 2;

        const logBase = Math.pow(2.7182818, 2.2);

        function reverseLutFromBitmap(bitmap, lut) {
            let k = 0;

            for (let i = 0; i < USHORT_RANGE; ++i) {
                if (i == 0 || bitmap[i >> 3] & (1 << (i & 7))) {
                    lut[k++] = i;
                }
            }

            const n = k - 1;

            while (k < USHORT_RANGE) lut[k++] = 0;

            return n;
        }

        function hufClearDecTable(hdec) {
            for (let i = 0; i < HUF_DECSIZE; i++) {
                hdec[i] = {};
                hdec[i].len = 0;
                hdec[i].lit = 0;
                hdec[i].p = null;
            }
        }

        const getBitsReturn = { l: 0, c: 0, lc: 0 };

        function getBits(nBits, c, lc, uInt8Array, inOffset) {
            while (lc < nBits) {
                c = (c << 8) | parseUint8Array(uInt8Array, inOffset);
                lc += 8;
            }

            lc -= nBits;

            getBitsReturn.l = (c >> lc) & ((1 << nBits) - 1);
            getBitsReturn.c = c;
            getBitsReturn.lc = lc;
        }

        const hufTableBuffer = new Array(59);

        function hufCanonicalCodeTable(hcode) {
            for (let i = 0; i <= 58; ++i) hufTableBuffer[i] = 0;
            for (let i = 0; i < HUF_ENCSIZE; ++i) hufTableBuffer[hcode[i]] += 1;

            let c = 0;

            for (let i = 58; i > 0; --i) {
                const nc = (c + hufTableBuffer[i]) >> 1;
                hufTableBuffer[i] = c;
                c = nc;
            }

            for (let i = 0; i < HUF_ENCSIZE; ++i) {
                const l = hcode[i];
                if (l > 0) hcode[i] = l | (hufTableBuffer[l]++ << 6);
            }
        }

        function hufUnpackEncTable(uInt8Array, inOffset, ni, im, iM, hcode) {
            const p = inOffset;
            let c = 0;
            let lc = 0;

            for (; im <= iM; im++) {
                if (p.value - inOffset.value > ni) return false;

                getBits(6, c, lc, uInt8Array, p);

                const l = getBitsReturn.l;
                c = getBitsReturn.c;
                lc = getBitsReturn.lc;

                hcode[im] = l;

                if (l == LONG_ZEROCODE_RUN) {
                    if (p.value - inOffset.value > ni) {
                        throw new Error('Something wrong with hufUnpackEncTable');
                    }

                    getBits(8, c, lc, uInt8Array, p);

                    let zerun = getBitsReturn.l + SHORTEST_LONG_RUN;
                    c = getBitsReturn.c;
                    lc = getBitsReturn.lc;

                    if (im + zerun > iM + 1) {
                        throw new Error('Something wrong with hufUnpackEncTable');
                    }

                    while (zerun--) hcode[im++] = 0;

                    im--;
                } else if (l >= SHORT_ZEROCODE_RUN) {
                    let zerun = l - SHORT_ZEROCODE_RUN + 2;

                    if (im + zerun > iM + 1) {
                        throw new Error('Something wrong with hufUnpackEncTable');
                    }

                    while (zerun--) hcode[im++] = 0;

                    im--;
                }
            }

            hufCanonicalCodeTable(hcode);
        }

        function hufLength(code) {
            return code & 63;
        }

        function hufCode(code) {
            return code >> 6;
        }

        function hufBuildDecTable(hcode, im, iM, hdecod) {
            for (; im <= iM; im++) {
                const c = hufCode(hcode[im]);
                const l = hufLength(hcode[im]);

                if (c >> l) {
                    throw new Error('Invalid table entry');
                }

                if (l > HUF_DECBITS) {
                    const pl = hdecod[c >> (l - HUF_DECBITS)];

                    if (pl.len) {
                        throw new Error('Invalid table entry');
                    }

                    pl.lit++;

                    if (pl.p) {
                        const p = pl.p;
                        pl.p = new Array(pl.lit);

                        for (let i = 0; i < pl.lit - 1; ++i) {
                            pl.p[i] = p[i];
                        }
                    } else {
                        pl.p = new Array(1);
                    }

                    pl.p[pl.lit - 1] = im;
                } else if (l) {
                    let plOffset = 0;

                    for (let i = 1 << (HUF_DECBITS - l); i > 0; i--) {
                        const pl = hdecod[(c << (HUF_DECBITS - l)) + plOffset];

                        if (pl.len || pl.p) {
                            throw new Error('Invalid table entry');
                        }

                        pl.len = l;
                        pl.lit = im;

                        plOffset++;
                    }
                }
            }

            return true;
        }

        const getCharReturn = { c: 0, lc: 0 };

        function getChar(c, lc, uInt8Array, inOffset) {
            c = (c << 8) | parseUint8Array(uInt8Array, inOffset);
            lc += 8;

            getCharReturn.c = c;
            getCharReturn.lc = lc;
        }

        const getCodeReturn = { c: 0, lc: 0 };

        function getCode(po, rlc, c, lc, uInt8Array, inOffset, outBuffer, outBufferOffset, outBufferEndOffset) {
            if (po == rlc) {
                if (lc < 8) {
                    getChar(c, lc, uInt8Array, inOffset);
                    c = getCharReturn.c;
                    lc = getCharReturn.lc;
                }

                lc -= 8;

                let cs = c >> lc;
                cs = new Uint8Array([cs])[0];

                if (outBufferOffset.value + cs > outBufferEndOffset) {
                    return false;
                }

                const s = outBuffer[outBufferOffset.value - 1];

                while (cs-- > 0) {
                    outBuffer[outBufferOffset.value++] = s;
                }
            } else if (outBufferOffset.value < outBufferEndOffset) {
                outBuffer[outBufferOffset.value++] = po;
            } else {
                return false;
            }

            getCodeReturn.c = c;
            getCodeReturn.lc = lc;
        }

        function UInt16(value) {
            return value & 0xffff;
        }

        function Int16(value) {
            const ref = UInt16(value);
            return ref > 0x7fff ? ref - 0x10000 : ref;
        }

        const wdec14Return = { a: 0, b: 0 };

        function wdec14(l, h) {
            const ls = Int16(l);
            const hs = Int16(h);

            const hi = hs;
            const ai = ls + (hi & 1) + (hi >> 1);

            const as = ai;
            const bs = ai - hi;

            wdec14Return.a = as;
            wdec14Return.b = bs;
        }

        function wdec16(l, h) {
            const m = UInt16(l);
            const d = UInt16(h);

            const bb = (m - (d >> 1)) & MOD_MASK;
            const aa = (d + bb - A_OFFSET) & MOD_MASK;

            wdec14Return.a = aa;
            wdec14Return.b = bb;
        }

        function wav2Decode(buffer, j, nx, ox, ny, oy, mx) {
            const w14 = mx < 1 << 14;
            const n = nx > ny ? ny : nx;
            let p = 1;
            let p2;
            let py;

            while (p <= n) p <<= 1;

            p >>= 1;
            p2 = p;
            p >>= 1;

            while (p >= 1) {
                py = 0;
                const ey = py + oy * (ny - p2);
                const oy1 = oy * p;
                const oy2 = oy * p2;
                const ox1 = ox * p;
                const ox2 = ox * p2;
                let i00;
                let i01;
                let i10;
                let i11;

                for (; py <= ey; py += oy2) {
                    let px = py;
                    const ex = py + ox * (nx - p2);

                    for (; px <= ex; px += ox2) {
                        const p01 = px + ox1;
                        const p10 = px + oy1;
                        const p11 = p10 + ox1;

                        if (w14) {
                            wdec14(buffer[px + j], buffer[p10 + j]);

                            i00 = wdec14Return.a;
                            i10 = wdec14Return.b;

                            wdec14(buffer[p01 + j], buffer[p11 + j]);

                            i01 = wdec14Return.a;
                            i11 = wdec14Return.b;

                            wdec14(i00, i01);

                            buffer[px + j] = wdec14Return.a;
                            buffer[p01 + j] = wdec14Return.b;

                            wdec14(i10, i11);

                            buffer[p10 + j] = wdec14Return.a;
                            buffer[p11 + j] = wdec14Return.b;
                        } else {
                            wdec16(buffer[px + j], buffer[p10 + j]);

                            i00 = wdec14Return.a;
                            i10 = wdec14Return.b;

                            wdec16(buffer[p01 + j], buffer[p11 + j]);

                            i01 = wdec14Return.a;
                            i11 = wdec14Return.b;

                            wdec16(i00, i01);

                            buffer[px + j] = wdec14Return.a;
                            buffer[p01 + j] = wdec14Return.b;

                            wdec16(i10, i11);

                            buffer[p10 + j] = wdec14Return.a;
                            buffer[p11 + j] = wdec14Return.b;
                        }
                    }

                    if (nx & p) {
                        const p10 = px + oy1;

                        if (w14) {
                            wdec14(buffer[px + j], buffer[p10 + j]);
                        } else {
                            wdec16(buffer[px + j], buffer[p10 + j]);
                        }

                        i00 = wdec14Return.a;
                        buffer[p10 + j] = wdec14Return.b;

                        buffer[px + j] = i00;
                    }
                }

                if (ny & p) {
                    let px = py;
                    const ex = py + ox * (nx - p2);

                    for (; px <= ex; px += ox2) {
                        const p01 = px + ox1;

                        if (w14) {
                            wdec14(buffer[px + j], buffer[p01 + j]);
                        } else {
                            wdec16(buffer[px + j], buffer[p01 + j]);
                        }

                        i00 = wdec14Return.a;
                        buffer[p01 + j] = wdec14Return.b;

                        buffer[px + j] = i00;
                    }
                }

                p2 = p;
                p >>= 1;
            }

            return py;
        }

        function hufDecode(encodingTable, decodingTable, uInt8Array, inOffset, ni, rlc, no, outBuffer, outOffset) {
            let c = 0;
            let lc = 0;
            const outBufferEndOffset = no;
            const inOffsetEnd = Math.trunc(inOffset.value + (ni + 7) / 8);

            while (inOffset.value < inOffsetEnd) {
                getChar(c, lc, uInt8Array, inOffset);

                c = getCharReturn.c;
                lc = getCharReturn.lc;

                while (lc >= HUF_DECBITS) {
                    const index = (c >> (lc - HUF_DECBITS)) & HUF_DECMASK;
                    const pl = decodingTable[index];

                    if (pl.len) {
                        lc -= pl.len;

                        getCode(pl.lit, rlc, c, lc, uInt8Array, inOffset, outBuffer, outOffset, outBufferEndOffset);

                        c = getCodeReturn.c;
                        lc = getCodeReturn.lc;
                    } else {
                        if (!pl.p) {
                            throw new Error('hufDecode issues');
                        }

                        let j;

                        for (j = 0; j < pl.lit; j++) {
                            const l = hufLength(encodingTable[pl.p[j]]);

                            while (lc < l && inOffset.value < inOffsetEnd) {
                                getChar(c, lc, uInt8Array, inOffset);

                                c = getCharReturn.c;
                                lc = getCharReturn.lc;
                            }

                            if (lc >= l) {
                                if (hufCode(encodingTable[pl.p[j]]) == ((c >> (lc - l)) & ((1 << l) - 1))) {
                                    lc -= l;

                                    getCode(pl.p[j], rlc, c, lc, uInt8Array, inOffset, outBuffer, outOffset, outBufferEndOffset);

                                    c = getCodeReturn.c;
                                    lc = getCodeReturn.lc;

                                    break;
                                }
                            }
                        }

                        if (j == pl.lit) {
                            throw new Error('hufDecode issues');
                        }
                    }
                }
            }

            const i = (8 - ni) & 7;

            c >>= i;
            lc -= i;

            while (lc > 0) {
                const pl = decodingTable[(c << (HUF_DECBITS - lc)) & HUF_DECMASK];

                if (pl.len) {
                    lc -= pl.len;

                    getCode(pl.lit, rlc, c, lc, uInt8Array, inOffset, outBuffer, outOffset, outBufferEndOffset);

                    c = getCodeReturn.c;
                    lc = getCodeReturn.lc;
                } else {
                    throw new Error('hufDecode issues');
                }
            }

            return true;
        }

        function hufUncompress(uInt8Array, inDataView, inOffset, nCompressed, outBuffer, nRaw) {
            const outOffset = { value: 0 };
            const initialInOffset = inOffset.value;

            const im = parseUint32(inDataView, inOffset);
            const iM = parseUint32(inDataView, inOffset);

            inOffset.value += 4;

            const nBits = parseUint32(inDataView, inOffset);

            inOffset.value += 4;

            if (im < 0 || im >= HUF_ENCSIZE || iM < 0 || iM >= HUF_ENCSIZE) {
                throw new Error('Something wrong with HUF_ENCSIZE');
            }

            const freq = new Array(HUF_ENCSIZE);
            const hdec = new Array(HUF_DECSIZE);

            hufClearDecTable(hdec);

            const ni = nCompressed - (inOffset.value - initialInOffset);

            hufUnpackEncTable(uInt8Array, inOffset, ni, im, iM, freq);

            if (nBits > 8 * (nCompressed - (inOffset.value - initialInOffset))) {
                throw new Error('Something wrong with hufUncompress');
            }

            hufBuildDecTable(freq, im, iM, hdec);

            hufDecode(freq, hdec, uInt8Array, inOffset, nBits, iM, nRaw, outBuffer, outOffset);
        }

        function applyLut(lut, data, nData) {
            for (let i = 0; i < nData; ++i) {
                data[i] = lut[data[i]];
            }
        }

        function predictor(source) {
            for (let t = 1; t < source.length; t++) {
                const d = source[t - 1] + source[t] - 128;
                source[t] = d;
            }
        }

        function interleaveScalar(source, out) {
            let t1 = 0;
            let t2 = Math.floor((source.length + 1) / 2);
            let s = 0;
            const stop = source.length - 1;

            while (true) {
                if (s > stop) break;
                out[s++] = source[t1++];

                if (s > stop) break;
                out[s++] = source[t2++];
            }
        }

        function decodeRunLength(source) {
            let size = source.byteLength;
            const out = new Array();
            let p = 0;

            const reader = new DataView(source);

            while (size > 0) {
                const l = reader.getInt8(p++);

                if (l < 0) {
                    const count = -l;
                    size -= count + 1;

                    for (let i = 0; i < count; i++) {
                        out.push(reader.getUint8(p++));
                    }
                } else {
                    const count = l;
                    size -= 2;

                    const value = reader.getUint8(p++);

                    for (let i = 0; i < count + 1; i++) {
                        out.push(value);
                    }
                }
            }

            return out;
        }

        function lossyDctDecode(cscSet, rowPtrs, channelData, acBuffer, dcBuffer, outBuffer) {
            let dataView = new DataView(outBuffer.buffer);

            const width = channelData[cscSet.idx[0]].width;
            const height = channelData[cscSet.idx[0]].height;

            const numComp = 3;

            const numFullBlocksX = Math.floor(width / 8.0);
            const numBlocksX = Math.ceil(width / 8.0);
            const numBlocksY = Math.ceil(height / 8.0);
            const leftoverX = width - (numBlocksX - 1) * 8;
            const leftoverY = height - (numBlocksY - 1) * 8;

            const currAcComp = { value: 0 };
            const currDcComp = new Array(numComp);
            const dctData = new Array(numComp);
            const halfZigBlock = new Array(numComp);
            const rowBlock = new Array(numComp);
            const rowOffsets = new Array(numComp);

            for (let comp = 0; comp < numComp; ++comp) {
                rowOffsets[comp] = rowPtrs[cscSet.idx[comp]];
                currDcComp[comp] = comp < 1 ? 0 : currDcComp[comp - 1] + numBlocksX * numBlocksY;
                dctData[comp] = new Float32Array(64);
                halfZigBlock[comp] = new Uint16Array(64);
                rowBlock[comp] = new Uint16Array(numBlocksX * 64);
            }

            for (let blocky = 0; blocky < numBlocksY; ++blocky) {
                let maxY = 8;

                if (blocky == numBlocksY - 1) {
                    maxY = leftoverY;
                }

                let maxX = 8;

                for (let blockx = 0; blockx < numBlocksX; ++blockx) {
                    if (blockx == numBlocksX - 1) {
                        maxX = leftoverX;
                    }

                    for (let comp = 0; comp < numComp; ++comp) {
                        halfZigBlock[comp].fill(0);

                        // set block DC component
                        halfZigBlock[comp][0] = dcBuffer[currDcComp[comp]++];
                        // set block AC components
                        unRleAC(currAcComp, acBuffer, halfZigBlock[comp]);

                        // UnZigZag block to float
                        unZigZag(halfZigBlock[comp], dctData[comp]);
                        // decode float dct
                        dctInverse(dctData[comp]);
                    }

                    if (numComp == 3) {
                        csc709Inverse(dctData);
                    }

                    for (let comp = 0; comp < numComp; ++comp) {
                        convertToHalf(dctData[comp], rowBlock[comp], blockx * 64);
                    }
                } // blockx

                let offset = 0;

                for (let comp = 0; comp < numComp; ++comp) {
                    const type = channelData[cscSet.idx[comp]].type;

                    for (let y = 8 * blocky; y < 8 * blocky + maxY; ++y) {
                        offset = rowOffsets[comp][y];

                        for (let blockx = 0; blockx < numFullBlocksX; ++blockx) {
                            const src = blockx * 64 + (y & 0x7) * 8;

                            dataView.setUint16(offset + 0 * INT16_SIZE * type, rowBlock[comp][src + 0], true);
                            dataView.setUint16(offset + 1 * INT16_SIZE * type, rowBlock[comp][src + 1], true);
                            dataView.setUint16(offset + 2 * INT16_SIZE * type, rowBlock[comp][src + 2], true);
                            dataView.setUint16(offset + 3 * INT16_SIZE * type, rowBlock[comp][src + 3], true);

                            dataView.setUint16(offset + 4 * INT16_SIZE * type, rowBlock[comp][src + 4], true);
                            dataView.setUint16(offset + 5 * INT16_SIZE * type, rowBlock[comp][src + 5], true);
                            dataView.setUint16(offset + 6 * INT16_SIZE * type, rowBlock[comp][src + 6], true);
                            dataView.setUint16(offset + 7 * INT16_SIZE * type, rowBlock[comp][src + 7], true);

                            offset += 8 * INT16_SIZE * type;
                        }
                    }

                    // handle partial X blocks
                    if (numFullBlocksX != numBlocksX) {
                        for (let y = 8 * blocky; y < 8 * blocky + maxY; ++y) {
                            const offset = rowOffsets[comp][y] + 8 * numFullBlocksX * INT16_SIZE * type;
                            const src = numFullBlocksX * 64 + (y & 0x7) * 8;

                            for (let x = 0; x < maxX; ++x) {
                                dataView.setUint16(offset + x * INT16_SIZE * type, rowBlock[comp][src + x], true);
                            }
                        }
                    }
                } // comp
            } // blocky

            const halfRow = new Uint16Array(width);
            dataView = new DataView(outBuffer.buffer);

            // convert channels back to float, if needed
            for (let comp = 0; comp < numComp; ++comp) {
                channelData[cscSet.idx[comp]].decoded = true;
                const type = channelData[cscSet.idx[comp]].type;

                if (channelData[comp].type != 2) continue;

                for (let y = 0; y < height; ++y) {
                    const offset = rowOffsets[comp][y];

                    for (let x = 0; x < width; ++x) {
                        halfRow[x] = dataView.getUint16(offset + x * INT16_SIZE * type, true);
                    }

                    for (let x = 0; x < width; ++x) {
                        dataView.setFloat32(offset + x * INT16_SIZE * type, decodeFloat16(halfRow[x]), true);
                    }
                }
            }
        }

        function unRleAC(currAcComp, acBuffer, halfZigBlock) {
            let acValue;
            let dctComp = 1;

            while (dctComp < 64) {
                acValue = acBuffer[currAcComp.value];

                if (acValue == 0xff00) {
                    dctComp = 64;
                } else if (acValue >> 8 == 0xff) {
                    dctComp += acValue & 0xff;
                } else {
                    halfZigBlock[dctComp] = acValue;
                    dctComp++;
                }

                currAcComp.value++;
            }
        }

        function unZigZag(src, dst) {
            dst[0] = decodeFloat16(src[0]);
            dst[1] = decodeFloat16(src[1]);
            dst[2] = decodeFloat16(src[5]);
            dst[3] = decodeFloat16(src[6]);
            dst[4] = decodeFloat16(src[14]);
            dst[5] = decodeFloat16(src[15]);
            dst[6] = decodeFloat16(src[27]);
            dst[7] = decodeFloat16(src[28]);
            dst[8] = decodeFloat16(src[2]);
            dst[9] = decodeFloat16(src[4]);

            dst[10] = decodeFloat16(src[7]);
            dst[11] = decodeFloat16(src[13]);
            dst[12] = decodeFloat16(src[16]);
            dst[13] = decodeFloat16(src[26]);
            dst[14] = decodeFloat16(src[29]);
            dst[15] = decodeFloat16(src[42]);
            dst[16] = decodeFloat16(src[3]);
            dst[17] = decodeFloat16(src[8]);
            dst[18] = decodeFloat16(src[12]);
            dst[19] = decodeFloat16(src[17]);

            dst[20] = decodeFloat16(src[25]);
            dst[21] = decodeFloat16(src[30]);
            dst[22] = decodeFloat16(src[41]);
            dst[23] = decodeFloat16(src[43]);
            dst[24] = decodeFloat16(src[9]);
            dst[25] = decodeFloat16(src[11]);
            dst[26] = decodeFloat16(src[18]);
            dst[27] = decodeFloat16(src[24]);
            dst[28] = decodeFloat16(src[31]);
            dst[29] = decodeFloat16(src[40]);

            dst[30] = decodeFloat16(src[44]);
            dst[31] = decodeFloat16(src[53]);
            dst[32] = decodeFloat16(src[10]);
            dst[33] = decodeFloat16(src[19]);
            dst[34] = decodeFloat16(src[23]);
            dst[35] = decodeFloat16(src[32]);
            dst[36] = decodeFloat16(src[39]);
            dst[37] = decodeFloat16(src[45]);
            dst[38] = decodeFloat16(src[52]);
            dst[39] = decodeFloat16(src[54]);

            dst[40] = decodeFloat16(src[20]);
            dst[41] = decodeFloat16(src[22]);
            dst[42] = decodeFloat16(src[33]);
            dst[43] = decodeFloat16(src[38]);
            dst[44] = decodeFloat16(src[46]);
            dst[45] = decodeFloat16(src[51]);
            dst[46] = decodeFloat16(src[55]);
            dst[47] = decodeFloat16(src[60]);
            dst[48] = decodeFloat16(src[21]);
            dst[49] = decodeFloat16(src[34]);

            dst[50] = decodeFloat16(src[37]);
            dst[51] = decodeFloat16(src[47]);
            dst[52] = decodeFloat16(src[50]);
            dst[53] = decodeFloat16(src[56]);
            dst[54] = decodeFloat16(src[59]);
            dst[55] = decodeFloat16(src[61]);
            dst[56] = decodeFloat16(src[35]);
            dst[57] = decodeFloat16(src[36]);
            dst[58] = decodeFloat16(src[48]);
            dst[59] = decodeFloat16(src[49]);

            dst[60] = decodeFloat16(src[57]);
            dst[61] = decodeFloat16(src[58]);
            dst[62] = decodeFloat16(src[62]);
            dst[63] = decodeFloat16(src[63]);
        }

        function dctInverse(data) {
            const a = 0.5 * Math.cos(3.14159 / 4.0);
            const b = 0.5 * Math.cos(3.14159 / 16.0);
            const c = 0.5 * Math.cos(3.14159 / 8.0);
            const d = 0.5 * Math.cos((3.0 * 3.14159) / 16.0);
            const e = 0.5 * Math.cos((5.0 * 3.14159) / 16.0);
            const f = 0.5 * Math.cos((3.0 * 3.14159) / 8.0);
            const g = 0.5 * Math.cos((7.0 * 3.14159) / 16.0);

            const alpha = new Array(4);
            const beta = new Array(4);
            const theta = new Array(4);
            const gamma = new Array(4);

            for (let row = 0; row < 8; ++row) {
                const rowPtr = row * 8;

                alpha[0] = c * data[rowPtr + 2];
                alpha[1] = f * data[rowPtr + 2];
                alpha[2] = c * data[rowPtr + 6];
                alpha[3] = f * data[rowPtr + 6];

                beta[0] = b * data[rowPtr + 1] + d * data[rowPtr + 3] + e * data[rowPtr + 5] + g * data[rowPtr + 7];
                beta[1] = d * data[rowPtr + 1] - g * data[rowPtr + 3] - b * data[rowPtr + 5] - e * data[rowPtr + 7];
                beta[2] = e * data[rowPtr + 1] - b * data[rowPtr + 3] + g * data[rowPtr + 5] + d * data[rowPtr + 7];
                beta[3] = g * data[rowPtr + 1] - e * data[rowPtr + 3] + d * data[rowPtr + 5] - b * data[rowPtr + 7];

                theta[0] = a * (data[rowPtr + 0] + data[rowPtr + 4]);
                theta[3] = a * (data[rowPtr + 0] - data[rowPtr + 4]);
                theta[1] = alpha[0] + alpha[3];
                theta[2] = alpha[1] - alpha[2];

                gamma[0] = theta[0] + theta[1];
                gamma[1] = theta[3] + theta[2];
                gamma[2] = theta[3] - theta[2];
                gamma[3] = theta[0] - theta[1];

                data[rowPtr + 0] = gamma[0] + beta[0];
                data[rowPtr + 1] = gamma[1] + beta[1];
                data[rowPtr + 2] = gamma[2] + beta[2];
                data[rowPtr + 3] = gamma[3] + beta[3];

                data[rowPtr + 4] = gamma[3] - beta[3];
                data[rowPtr + 5] = gamma[2] - beta[2];
                data[rowPtr + 6] = gamma[1] - beta[1];
                data[rowPtr + 7] = gamma[0] - beta[0];
            }

            for (let column = 0; column < 8; ++column) {
                alpha[0] = c * data[16 + column];
                alpha[1] = f * data[16 + column];
                alpha[2] = c * data[48 + column];
                alpha[3] = f * data[48 + column];

                beta[0] = b * data[8 + column] + d * data[24 + column] + e * data[40 + column] + g * data[56 + column];
                beta[1] = d * data[8 + column] - g * data[24 + column] - b * data[40 + column] - e * data[56 + column];
                beta[2] = e * data[8 + column] - b * data[24 + column] + g * data[40 + column] + d * data[56 + column];
                beta[3] = g * data[8 + column] - e * data[24 + column] + d * data[40 + column] - b * data[56 + column];

                theta[0] = a * (data[column] + data[32 + column]);
                theta[3] = a * (data[column] - data[32 + column]);

                theta[1] = alpha[0] + alpha[3];
                theta[2] = alpha[1] - alpha[2];

                gamma[0] = theta[0] + theta[1];
                gamma[1] = theta[3] + theta[2];
                gamma[2] = theta[3] - theta[2];
                gamma[3] = theta[0] - theta[1];

                data[0 + column] = gamma[0] + beta[0];
                data[8 + column] = gamma[1] + beta[1];
                data[16 + column] = gamma[2] + beta[2];
                data[24 + column] = gamma[3] + beta[3];

                data[32 + column] = gamma[3] - beta[3];
                data[40 + column] = gamma[2] - beta[2];
                data[48 + column] = gamma[1] - beta[1];
                data[56 + column] = gamma[0] - beta[0];
            }
        }

        function csc709Inverse(data) {
            for (let i = 0; i < 64; ++i) {
                const y = data[0][i];
                const cb = data[1][i];
                const cr = data[2][i];

                data[0][i] = y + 1.5747 * cr;
                data[1][i] = y - 0.1873 * cb - 0.4682 * cr;
                data[2][i] = y + 1.8556 * cb;
            }
        }

        function convertToHalf(src, dst, idx) {
            for (let i = 0; i < 64; ++i) {
                dst[idx + i] = DataUtils.toHalfFloat(toLinear(src[i]));
            }
        }

        function toLinear(float) {
            if (float <= 1) {
                return Math.sign(float) * Math.pow(Math.abs(float), 2.2);
            } else {
                return Math.sign(float) * Math.pow(logBase, Math.abs(float) - 1.0);
            }
        }

        function uncompressRAW(info) {
            return new DataView(info.array.buffer, info.offset.value, info.size);
        }

        function uncompressRLE(info) {
            const compressed = info.viewer.buffer.slice(info.offset.value, info.offset.value + info.size);

            const rawBuffer = new Uint8Array(decodeRunLength(compressed));
            const tmpBuffer = new Uint8Array(rawBuffer.length);

            predictor(rawBuffer); // revert predictor

            interleaveScalar(rawBuffer, tmpBuffer); // interleave pixels

            return new DataView(tmpBuffer.buffer);
        }

        function uncompressZIP(info) {
            const compressed = info.array.slice(info.offset.value, info.offset.value + info.size);

            const rawBuffer = fflate.unzlibSync(compressed);
            const tmpBuffer = new Uint8Array(rawBuffer.length);

            predictor(rawBuffer); // revert predictor

            interleaveScalar(rawBuffer, tmpBuffer); // interleave pixels

            return new DataView(tmpBuffer.buffer);
        }

        function uncompressPIZ(info) {
            const inDataView = info.viewer;
            const inOffset = { value: info.offset.value };

            const outBuffer = new Uint16Array(info.columns * info.lines * (info.inputChannels.length * info.type));
            const bitmap = new Uint8Array(BITMAP_SIZE);

            // Setup channel info
            let outBufferEnd = 0;
            const pizChannelData = new Array(info.inputChannels.length);
            for (let i = 0, il = info.inputChannels.length; i < il; i++) {
                pizChannelData[i] = {};
                pizChannelData[i]['start'] = outBufferEnd;
                pizChannelData[i]['end'] = pizChannelData[i]['start'];
                pizChannelData[i]['nx'] = info.columns;
                pizChannelData[i]['ny'] = info.lines;
                pizChannelData[i]['size'] = info.type;

                outBufferEnd += pizChannelData[i].nx * pizChannelData[i].ny * pizChannelData[i].size;
            }

            // Read range compression data

            const minNonZero = parseUint16(inDataView, inOffset);
            const maxNonZero = parseUint16(inDataView, inOffset);

            if (maxNonZero >= BITMAP_SIZE) {
                throw new Error('Something is wrong with PIZ_COMPRESSION BITMAP_SIZE');
            }

            if (minNonZero <= maxNonZero) {
                for (let i = 0; i < maxNonZero - minNonZero + 1; i++) {
                    bitmap[i + minNonZero] = parseUint8(inDataView, inOffset);
                }
            }

            // Reverse LUT
            const lut = new Uint16Array(USHORT_RANGE);
            const maxValue = reverseLutFromBitmap(bitmap, lut);

            const length = parseUint32(inDataView, inOffset);

            // Huffman decoding
            hufUncompress(info.array, inDataView, inOffset, length, outBuffer, outBufferEnd);

            // Wavelet decoding
            for (let i = 0; i < info.inputChannels.length; ++i) {
                const cd = pizChannelData[i];

                for (let j = 0; j < pizChannelData[i].size; ++j) {
                    wav2Decode(outBuffer, cd.start + j, cd.nx, cd.size, cd.ny, cd.nx * cd.size, maxValue);
                }
            }

            // Expand the pixel data to their original range
            applyLut(lut, outBuffer, outBufferEnd);

            // Rearrange the pixel data into the format expected by the caller.
            let tmpOffset = 0;
            const tmpBuffer = new Uint8Array(outBuffer.buffer.byteLength);
            for (let y = 0; y < info.lines; y++) {
                for (let c = 0; c < info.inputChannels.length; c++) {
                    const cd = pizChannelData[c];

                    const n = cd.nx * cd.size;
                    const cp = new Uint8Array(outBuffer.buffer, cd.end * INT16_SIZE, n * INT16_SIZE);

                    tmpBuffer.set(cp, tmpOffset);
                    tmpOffset += n * INT16_SIZE;
                    cd.end += n;
                }
            }

            return new DataView(tmpBuffer.buffer);
        }

        function uncompressPXR(info) {
            const compressed = info.array.slice(info.offset.value, info.offset.value + info.size);

            const rawBuffer = fflate.unzlibSync(compressed);

            const byteSize = info.inputChannels.length * info.lines * info.columns * info.totalBytes;
            const tmpBuffer = new ArrayBuffer(byteSize);
            const viewer = new DataView(tmpBuffer);

            let tmpBufferEnd = 0;
            let writePtr = 0;
            const ptr = new Array(4);

            for (let y = 0; y < info.lines; y++) {
                for (let c = 0; c < info.inputChannels.length; c++) {
                    let pixel = 0;

                    const type = info.inputChannels[c].pixelType;
                    switch (type) {
                        case 1:
                            ptr[0] = tmpBufferEnd;
                            ptr[1] = ptr[0] + info.columns;
                            tmpBufferEnd = ptr[1] + info.columns;

                            for (let j = 0; j < info.columns; ++j) {
                                const diff = (rawBuffer[ptr[0]++] << 8) | rawBuffer[ptr[1]++];

                                pixel += diff;

                                viewer.setUint16(writePtr, pixel, true);
                                writePtr += 2;
                            }

                            break;

                        case 2:
                            ptr[0] = tmpBufferEnd;
                            ptr[1] = ptr[0] + info.columns;
                            ptr[2] = ptr[1] + info.columns;
                            tmpBufferEnd = ptr[2] + info.columns;

                            for (let j = 0; j < info.columns; ++j) {
                                const diff = (rawBuffer[ptr[0]++] << 24) | (rawBuffer[ptr[1]++] << 16) | (rawBuffer[ptr[2]++] << 8);

                                pixel += diff;

                                viewer.setUint32(writePtr, pixel, true);
                                writePtr += 4;
                            }

                            break;
                    }
                }
            }

            return viewer;
        }

        function uncompressDWA(info) {
            const inDataView = info.viewer;
            const inOffset = { value: info.offset.value };
            const outBuffer = new Uint8Array(info.columns * info.lines * (info.inputChannels.length * info.type * INT16_SIZE));

            // Read compression header information
            const dwaHeader = {
                version: parseInt64(inDataView, inOffset),
                unknownUncompressedSize: parseInt64(inDataView, inOffset),
                unknownCompressedSize: parseInt64(inDataView, inOffset),
                acCompressedSize: parseInt64(inDataView, inOffset),
                dcCompressedSize: parseInt64(inDataView, inOffset),
                rleCompressedSize: parseInt64(inDataView, inOffset),
                rleUncompressedSize: parseInt64(inDataView, inOffset),
                rleRawSize: parseInt64(inDataView, inOffset),
                totalAcUncompressedCount: parseInt64(inDataView, inOffset),
                totalDcUncompressedCount: parseInt64(inDataView, inOffset),
                acCompression: parseInt64(inDataView, inOffset)
            };

            if (dwaHeader.version < 2) {
                throw new Error('EXRLoader.parse: ' + EXRHeader.compression + ' version ' + dwaHeader.version + ' is unsupported');
            }

            // Read channel ruleset information
            const channelRules = new Array();
            let ruleSize = parseUint16(inDataView, inOffset) - INT16_SIZE;

            while (ruleSize > 0) {
                const name = parseNullTerminatedString(inDataView.buffer, inOffset);
                const value = parseUint8(inDataView, inOffset);
                const compression = (value >> 2) & 3;
                const csc = (value >> 4) - 1;
                const index = new Int8Array([csc])[0];
                const type = parseUint8(inDataView, inOffset);

                channelRules.push({
                    name: name,
                    index: index,
                    type: type,
                    compression: compression
                });

                ruleSize -= name.length + 3;
            }

            // Classify channels
            const channels = EXRHeader.channels;
            const channelData = new Array(info.inputChannels.length);

            for (let i = 0; i < info.inputChannels.length; ++i) {
                const cd = (channelData[i] = {});
                const channel = channels[i];

                cd.name = channel.name;
                cd.compression = UNKNOWN;
                cd.decoded = false;
                cd.type = channel.pixelType;
                cd.pLinear = channel.pLinear;
                cd.width = info.columns;
                cd.height = info.lines;
            }

            const cscSet = {
                idx: new Array(3)
            };

            for (let offset = 0; offset < info.inputChannels.length; ++offset) {
                const cd = channelData[offset];

                for (let i = 0; i < channelRules.length; ++i) {
                    const rule = channelRules[i];

                    if (cd.name == rule.name) {
                        cd.compression = rule.compression;

                        if (rule.index >= 0) {
                            cscSet.idx[rule.index] = offset;
                        }

                        cd.offset = offset;
                    }
                }
            }

            let acBuffer;
            let dcBuffer;
            let rleBuffer;

            // Read DCT - AC component data
            if (dwaHeader.acCompressedSize > 0) {
                switch (dwaHeader.acCompression) {
                    case STATIC_HUFFMAN:
                        acBuffer = new Uint16Array(dwaHeader.totalAcUncompressedCount);
                        hufUncompress(
                            info.array,
                            inDataView,
                            inOffset,
                            dwaHeader.acCompressedSize,
                            acBuffer,
                            dwaHeader.totalAcUncompressedCount
                        );
                        break;

                    case DEFLATE:
                        const compressed = info.array.slice(inOffset.value, inOffset.value + dwaHeader.totalAcUncompressedCount);
                        const data = fflate.unzlibSync(compressed);
                        acBuffer = new Uint16Array(data.buffer);
                        inOffset.value += dwaHeader.totalAcUncompressedCount;
                        break;
                }
            }

            // Read DCT - DC component data
            if (dwaHeader.dcCompressedSize > 0) {
                const zlibInfo = {
                    array: info.array,
                    offset: inOffset,
                    size: dwaHeader.dcCompressedSize
                };
                dcBuffer = new Uint16Array(uncompressZIP(zlibInfo).buffer);
                inOffset.value += dwaHeader.dcCompressedSize;
            }

            // Read RLE compressed data
            if (dwaHeader.rleRawSize > 0) {
                const compressed = info.array.slice(inOffset.value, inOffset.value + dwaHeader.rleCompressedSize);
                const data = fflate.unzlibSync(compressed);
                rleBuffer = decodeRunLength(data.buffer);

                inOffset.value += dwaHeader.rleCompressedSize;
            }

            // Prepare outbuffer data offset
            let outBufferEnd = 0;
            const rowOffsets = new Array(channelData.length);
            for (let i = 0; i < rowOffsets.length; ++i) {
                rowOffsets[i] = new Array();
            }

            for (let y = 0; y < info.lines; ++y) {
                for (let chan = 0; chan < channelData.length; ++chan) {
                    rowOffsets[chan].push(outBufferEnd);
                    outBufferEnd += channelData[chan].width * info.type * INT16_SIZE;
                }
            }

            // Lossy DCT decode RGB channels
            lossyDctDecode(cscSet, rowOffsets, channelData, acBuffer, dcBuffer, outBuffer);

            // Decode other channels
            for (let i = 0; i < channelData.length; ++i) {
                const cd = channelData[i];

                if (cd.decoded) continue;

                switch (cd.compression) {
                    case RLE:
                        let row = 0;
                        let rleOffset = 0;

                        for (let y = 0; y < info.lines; ++y) {
                            let rowOffsetBytes = rowOffsets[i][row];

                            for (let x = 0; x < cd.width; ++x) {
                                for (let byte = 0; byte < INT16_SIZE * cd.type; ++byte) {
                                    outBuffer[rowOffsetBytes++] = rleBuffer[rleOffset + byte * cd.width * cd.height];
                                }

                                rleOffset++;
                            }

                            row++;
                        }

                        break;

                    case LOSSY_DCT: // skip

                    default:
                        throw new Error('EXRLoader.parse: unsupported channel compression');
                }
            }

            return new DataView(outBuffer.buffer);
        }

        function parseNullTerminatedString(buffer, offset) {
            const uintBuffer = new Uint8Array(buffer);
            let endOffset = 0;

            while (uintBuffer[offset.value + endOffset] != 0) {
                endOffset += 1;
            }

            const stringValue = new TextDecoder().decode(uintBuffer.slice(offset.value, offset.value + endOffset));

            offset.value = offset.value + endOffset + 1;

            return stringValue;
        }

        function parseFixedLengthString(buffer, offset, size) {
            const stringValue = new TextDecoder().decode(new Uint8Array(buffer).slice(offset.value, offset.value + size));

            offset.value = offset.value + size;

            return stringValue;
        }

        function parseRational(dataView, offset) {
            const x = parseInt32(dataView, offset);
            const y = parseUint32(dataView, offset);

            return [x, y];
        }

        function parseTimecode(dataView, offset) {
            const x = parseUint32(dataView, offset);
            const y = parseUint32(dataView, offset);

            return [x, y];
        }

        function parseInt32(dataView, offset) {
            const Int32 = dataView.getInt32(offset.value, true);

            offset.value = offset.value + INT32_SIZE;

            return Int32;
        }

        function parseUint32(dataView, offset) {
            const Uint32 = dataView.getUint32(offset.value, true);

            offset.value = offset.value + INT32_SIZE;

            return Uint32;
        }

        function parseUint8Array(uInt8Array, offset) {
            const Uint8 = uInt8Array[offset.value];

            offset.value = offset.value + INT8_SIZE;

            return Uint8;
        }

        function parseUint8(dataView, offset) {
            const Uint8 = dataView.getUint8(offset.value);

            offset.value = offset.value + INT8_SIZE;

            return Uint8;
        }

        const parseInt64 = function (dataView, offset) {
            let int;

            if ('getBigInt64' in DataView.prototype) {
                int = Number(dataView.getBigInt64(offset.value, true));
            } else {
                int = dataView.getUint32(offset.value + 4, true) + Number(dataView.getUint32(offset.value, true) << 32);
            }

            offset.value += ULONG_SIZE;

            return int;
        };

        function parseFloat32(dataView, offset) {
            const float = dataView.getFloat32(offset.value, true);

            offset.value += FLOAT32_SIZE;

            return float;
        }

        function decodeFloat32(dataView, offset) {
            return DataUtils.toHalfFloat(parseFloat32(dataView, offset));
        }

        // https://stackoverflow.com/questions/5678432/decompressing-half-precision-floats-in-javascript
        function decodeFloat16(binary) {
            const exponent = (binary & 0x7c00) >> 10;
            const fraction = binary & 0x03ff;

            return (
                (binary >> 15 ? -1 : 1) *
                (exponent
                    ? exponent === 0x1f
                        ? fraction
                            ? NaN
                            : Infinity
                        : Math.pow(2, exponent - 15) * (1 + fraction / 0x400)
                    : 6.103515625e-5 * (fraction / 0x400))
            );
        }

        function parseUint16(dataView, offset) {
            const Uint16 = dataView.getUint16(offset.value, true);

            offset.value += INT16_SIZE;

            return Uint16;
        }

        function parseFloat16(buffer, offset) {
            return decodeFloat16(parseUint16(buffer, offset));
        }

        function parseChlist(dataView, buffer, offset, size) {
            const startOffset = offset.value;
            const channels = [];

            while (offset.value < startOffset + size - 1) {
                const name = parseNullTerminatedString(buffer, offset);
                const pixelType = parseInt32(dataView, offset);
                const pLinear = parseUint8(dataView, offset);
                offset.value += 3; // reserved, three chars
                const xSampling = parseInt32(dataView, offset);
                const ySampling = parseInt32(dataView, offset);

                channels.push({
                    name: name,
                    pixelType: pixelType,
                    pLinear: pLinear,
                    xSampling: xSampling,
                    ySampling: ySampling
                });
            }

            offset.value += 1;

            return channels;
        }

        function parseChromaticities(dataView, offset) {
            const redX = parseFloat32(dataView, offset);
            const redY = parseFloat32(dataView, offset);
            const greenX = parseFloat32(dataView, offset);
            const greenY = parseFloat32(dataView, offset);
            const blueX = parseFloat32(dataView, offset);
            const blueY = parseFloat32(dataView, offset);
            const whiteX = parseFloat32(dataView, offset);
            const whiteY = parseFloat32(dataView, offset);

            return {
                redX: redX,
                redY: redY,
                greenX: greenX,
                greenY: greenY,
                blueX: blueX,
                blueY: blueY,
                whiteX: whiteX,
                whiteY: whiteY
            };
        }

        function parseCompression(dataView, offset) {
            const compressionCodes = [
                'NO_COMPRESSION',
                'RLE_COMPRESSION',
                'ZIPS_COMPRESSION',
                'ZIP_COMPRESSION',
                'PIZ_COMPRESSION',
                'PXR24_COMPRESSION',
                'B44_COMPRESSION',
                'B44A_COMPRESSION',
                'DWAA_COMPRESSION',
                'DWAB_COMPRESSION'
            ];

            const compression = parseUint8(dataView, offset);

            return compressionCodes[compression];
        }

        function parseBox2i(dataView, offset) {
            const xMin = parseInt32(dataView, offset);
            const yMin = parseInt32(dataView, offset);
            const xMax = parseInt32(dataView, offset);
            const yMax = parseInt32(dataView, offset);

            return { xMin: xMin, yMin: yMin, xMax: xMax, yMax: yMax };
        }

        function parseLineOrder(dataView, offset) {
            const lineOrders = ['INCREASING_Y', 'DECREASING_Y', 'RANDOM_Y'];

            const lineOrder = parseUint8(dataView, offset);

            return lineOrders[lineOrder];
        }

        function parseEnvmap(dataView, offset) {
            const envmaps = ['ENVMAP_LATLONG', 'ENVMAP_CUBE'];

            const envmap = parseUint8(dataView, offset);

            return envmaps[envmap];
        }

        function parseTiledesc(dataView, offset) {
            const levelModes = ['ONE_LEVEL', 'MIPMAP_LEVELS', 'RIPMAP_LEVELS'];

            const roundingModes = ['ROUND_DOWN', 'ROUND_UP'];

            const xSize = parseUint32(dataView, offset);
            const ySize = parseUint32(dataView, offset);
            const modes = parseUint8(dataView, offset);

            return {
                xSize: xSize,
                ySize: ySize,
                levelMode: levelModes[modes & 0xf],
                roundingMode: roundingModes[modes >> 4]
            };
        }

        function parseV2f(dataView, offset) {
            const x = parseFloat32(dataView, offset);
            const y = parseFloat32(dataView, offset);

            return [x, y];
        }

        function parseV3f(dataView, offset) {
            const x = parseFloat32(dataView, offset);
            const y = parseFloat32(dataView, offset);
            const z = parseFloat32(dataView, offset);

            return [x, y, z];
        }

        function parseValue(dataView, buffer, offset, type, size) {
            if (type === 'string' || type === 'stringvector' || type === 'iccProfile') {
                return parseFixedLengthString(buffer, offset, size);
            } else if (type === 'chlist') {
                return parseChlist(dataView, buffer, offset, size);
            } else if (type === 'chromaticities') {
                return parseChromaticities(dataView, offset);
            } else if (type === 'compression') {
                return parseCompression(dataView, offset);
            } else if (type === 'box2i') {
                return parseBox2i(dataView, offset);
            } else if (type === 'envmap') {
                return parseEnvmap(dataView, offset);
            } else if (type === 'tiledesc') {
                return parseTiledesc(dataView, offset);
            } else if (type === 'lineOrder') {
                return parseLineOrder(dataView, offset);
            } else if (type === 'float') {
                return parseFloat32(dataView, offset);
            } else if (type === 'v2f') {
                return parseV2f(dataView, offset);
            } else if (type === 'v3f') {
                return parseV3f(dataView, offset);
            } else if (type === 'int') {
                return parseInt32(dataView, offset);
            } else if (type === 'rational') {
                return parseRational(dataView, offset);
            } else if (type === 'timecode') {
                return parseTimecode(dataView, offset);
            } else if (type === 'preview') {
                offset.value += size;
                return 'skipped';
            } else {
                offset.value += size;
                return undefined;
            }
        }

        function roundLog2(x, mode) {
            const log2 = Math.log2(x);
            return mode == 'ROUND_DOWN' ? Math.floor(log2) : Math.ceil(log2);
        }

        function calculateTileLevels(tiledesc, w, h) {
            let num = 0;

            switch (tiledesc.levelMode) {
                case 'ONE_LEVEL':
                    num = 1;
                    break;

                case 'MIPMAP_LEVELS':
                    num = roundLog2(Math.max(w, h), tiledesc.roundingMode) + 1;
                    break;

                case 'RIPMAP_LEVELS':
                    throw new Error('THREE.EXRLoader: RIPMAP_LEVELS tiles currently unsupported.');
            }

            return num;
        }

        function calculateTiles(count, dataSize, size, roundingMode) {
            const tiles = new Array(count);

            for (let i = 0; i < count; i++) {
                const b = 1 << i;
                let s = (dataSize / b) | 0;

                if (roundingMode == 'ROUND_UP' && s * b < dataSize) s += 1;

                const l = Math.max(s, 1);

                tiles[i] = ((l + size - 1) / size) | 0;
            }

            return tiles;
        }

        function parseTiles() {
            const EXRDecoder = this;
            const offset = EXRDecoder.offset;
            const tmpOffset = { value: 0 };

            for (let tile = 0; tile < EXRDecoder.tileCount; tile++) {
                const tileX = parseInt32(EXRDecoder.viewer, offset);
                const tileY = parseInt32(EXRDecoder.viewer, offset);
                offset.value += 8; // skip levels - only parsing top-level
                EXRDecoder.size = parseUint32(EXRDecoder.viewer, offset);

                const startX = tileX * EXRDecoder.blockWidth;
                const startY = tileY * EXRDecoder.blockHeight;
                EXRDecoder.columns = startX + EXRDecoder.blockWidth > EXRDecoder.width ? EXRDecoder.width - startX : EXRDecoder.blockWidth;
                EXRDecoder.lines =
                    startY + EXRDecoder.blockHeight > EXRDecoder.height ? EXRDecoder.height - startY : EXRDecoder.blockHeight;

                const bytesBlockLine = EXRDecoder.columns * EXRDecoder.totalBytes;
                const isCompressed = EXRDecoder.size < EXRDecoder.lines * bytesBlockLine;
                const viewer = isCompressed ? EXRDecoder.uncompress(EXRDecoder) : uncompressRAW(EXRDecoder);

                offset.value += EXRDecoder.size;

                for (let line = 0; line < EXRDecoder.lines; line++) {
                    const lineOffset = line * EXRDecoder.columns * EXRDecoder.totalBytes;

                    for (let channelID = 0; channelID < EXRDecoder.inputChannels.length; channelID++) {
                        const name = EXRHeader.channels[channelID].name;
                        const lOff = EXRDecoder.channelByteOffsets[name] * EXRDecoder.columns;
                        const cOff = EXRDecoder.decodeChannels[name];

                        if (cOff === undefined) continue;

                        tmpOffset.value = lineOffset + lOff;
                        const outLineOffset = (EXRDecoder.height - (1 + startY + line)) * EXRDecoder.outLineWidth;

                        for (let x = 0; x < EXRDecoder.columns; x++) {
                            const outIndex = outLineOffset + (x + startX) * EXRDecoder.outputChannels + cOff;
                            EXRDecoder.byteArray[outIndex] = EXRDecoder.getter(viewer, tmpOffset);
                        }
                    }
                }
            }
        }

        function parseScanline() {
            const EXRDecoder = this;
            const offset = EXRDecoder.offset;
            const tmpOffset = { value: 0 };

            for (let scanlineBlockIdx = 0; scanlineBlockIdx < EXRDecoder.height / EXRDecoder.blockHeight; scanlineBlockIdx++) {
                const line = parseInt32(EXRDecoder.viewer, offset) - EXRHeader.dataWindow.yMin; // line_no
                EXRDecoder.size = parseUint32(EXRDecoder.viewer, offset); // data_len
                EXRDecoder.lines = line + EXRDecoder.blockHeight > EXRDecoder.height ? EXRDecoder.height - line : EXRDecoder.blockHeight;

                const bytesPerLine = EXRDecoder.columns * EXRDecoder.totalBytes;
                const isCompressed = EXRDecoder.size < EXRDecoder.lines * bytesPerLine;
                const viewer = isCompressed ? EXRDecoder.uncompress(EXRDecoder) : uncompressRAW(EXRDecoder);

                offset.value += EXRDecoder.size;

                for (let line_y = 0; line_y < EXRDecoder.blockHeight; line_y++) {
                    const scan_y = scanlineBlockIdx * EXRDecoder.blockHeight;
                    const true_y = line_y + EXRDecoder.scanOrder(scan_y);
                    if (true_y >= EXRDecoder.height) continue;

                    const lineOffset = line_y * bytesPerLine;
                    const outLineOffset = (EXRDecoder.height - 1 - true_y) * EXRDecoder.outLineWidth;

                    for (let channelID = 0; channelID < EXRDecoder.inputChannels.length; channelID++) {
                        const name = EXRHeader.channels[channelID].name;
                        const lOff = EXRDecoder.channelByteOffsets[name] * EXRDecoder.columns;
                        const cOff = EXRDecoder.decodeChannels[name];

                        if (cOff === undefined) continue;

                        tmpOffset.value = lineOffset + lOff;

                        for (let x = 0; x < EXRDecoder.columns; x++) {
                            const outIndex = outLineOffset + x * EXRDecoder.outputChannels + cOff;
                            EXRDecoder.byteArray[outIndex] = EXRDecoder.getter(viewer, tmpOffset);
                        }
                    }
                }
            }
        }

        function parseHeader(dataView, buffer, offset) {
            const EXRHeader = {};

            if (dataView.getUint32(0, true) != 20000630) {
                // magic

                throw new Error("THREE.EXRLoader: Provided file doesn't appear to be in OpenEXR format.");
            }

            EXRHeader.version = dataView.getUint8(4);

            const spec = dataView.getUint8(5); // fullMask

            EXRHeader.spec = {
                singleTile: !!(spec & 2),
                longName: !!(spec & 4),
                deepFormat: !!(spec & 8),
                multiPart: !!(spec & 16)
            };

            // start of header

            offset.value = 8; // start at 8 - after pre-amble

            let keepReading = true;

            while (keepReading) {
                const attributeName = parseNullTerminatedString(buffer, offset);

                if (attributeName == 0) {
                    keepReading = false;
                } else {
                    const attributeType = parseNullTerminatedString(buffer, offset);
                    const attributeSize = parseUint32(dataView, offset);
                    const attributeValue = parseValue(dataView, buffer, offset, attributeType, attributeSize);

                    if (attributeValue === undefined) {
                        console.warn(`THREE.EXRLoader: Skipped unknown header attribute type \'${attributeType}\'.`);
                    } else {
                        EXRHeader[attributeName] = attributeValue;
                    }
                }
            }

            if ((spec & ~0x06) != 0) {
                // unsupported deep-image, multi-part

                console.error('THREE.EXRHeader:', EXRHeader);
                throw new Error('THREE.EXRLoader: Provided file is currently unsupported.');
            }

            return EXRHeader;
        }

        function setupDecoder(EXRHeader, dataView, uInt8Array, offset, outputType) {
            const EXRDecoder = {
                size: 0,
                viewer: dataView,
                array: uInt8Array,
                offset: offset,
                width: EXRHeader.dataWindow.xMax - EXRHeader.dataWindow.xMin + 1,
                height: EXRHeader.dataWindow.yMax - EXRHeader.dataWindow.yMin + 1,
                inputChannels: EXRHeader.channels,
                channelByteOffsets: {},
                scanOrder: null,
                totalBytes: null,
                columns: null,
                lines: null,
                type: null,
                uncompress: null,
                getter: null,
                format: null,
                colorSpace: LinearSRGBColorSpace
            };

            switch (EXRHeader.compression) {
                case 'NO_COMPRESSION':
                    EXRDecoder.blockHeight = 1;
                    EXRDecoder.uncompress = uncompressRAW;
                    break;

                case 'RLE_COMPRESSION':
                    EXRDecoder.blockHeight = 1;
                    EXRDecoder.uncompress = uncompressRLE;
                    break;

                case 'ZIPS_COMPRESSION':
                    EXRDecoder.blockHeight = 1;
                    EXRDecoder.uncompress = uncompressZIP;
                    break;

                case 'ZIP_COMPRESSION':
                    EXRDecoder.blockHeight = 16;
                    EXRDecoder.uncompress = uncompressZIP;
                    break;

                case 'PIZ_COMPRESSION':
                    EXRDecoder.blockHeight = 32;
                    EXRDecoder.uncompress = uncompressPIZ;
                    break;

                case 'PXR24_COMPRESSION':
                    EXRDecoder.blockHeight = 16;
                    EXRDecoder.uncompress = uncompressPXR;
                    break;

                case 'DWAA_COMPRESSION':
                    EXRDecoder.blockHeight = 32;
                    EXRDecoder.uncompress = uncompressDWA;
                    break;

                case 'DWAB_COMPRESSION':
                    EXRDecoder.blockHeight = 256;
                    EXRDecoder.uncompress = uncompressDWA;
                    break;

                default:
                    throw new Error('EXRLoader.parse: ' + EXRHeader.compression + ' is unsupported');
            }

            const channels = {};
            for (const channel of EXRHeader.channels) {
                switch (channel.name) {
                    case 'Y':
                    case 'R':
                    case 'G':
                    case 'B':
                    case 'A':
                        channels[channel.name] = true;
                        EXRDecoder.type = channel.pixelType;
                }
            }

            // RGB images will be converted to RGBA format, preventing software emulation in select devices.
            let fillAlpha = false;

            if (channels.R && channels.G && channels.B) {
                fillAlpha = !channels.A;
                EXRDecoder.outputChannels = 4;
                EXRDecoder.decodeChannels = { R: 0, G: 1, B: 2, A: 3 };
            } else if (channels.Y) {
                EXRDecoder.outputChannels = 1;
                EXRDecoder.decodeChannels = { Y: 0 };
            } else {
                throw new Error('EXRLoader.parse: file contains unsupported data channels.');
            }

            if (EXRDecoder.type == 1) {
                // half
                switch (outputType) {
                    case FloatType:
                        EXRDecoder.getter = parseFloat16;
                        break;

                    case HalfFloatType:
                        EXRDecoder.getter = parseUint16;
                        break;
                }
            } else if (EXRDecoder.type == 2) {
                // float
                switch (outputType) {
                    case FloatType:
                        EXRDecoder.getter = parseFloat32;
                        break;

                    case HalfFloatType:
                        EXRDecoder.getter = decodeFloat32;
                }
            } else {
                throw new Error('EXRLoader.parse: unsupported pixelType ' + EXRDecoder.type + ' for ' + EXRHeader.compression + '.');
            }

            EXRDecoder.columns = EXRDecoder.width;
            const size = EXRDecoder.width * EXRDecoder.height * EXRDecoder.outputChannels;

            switch (outputType) {
                case FloatType:
                    EXRDecoder.byteArray = new Float32Array(size);

                    // Fill initially with 1s for the alpha value if the texture is not RGBA, RGB values will be overwritten
                    if (fillAlpha) {
                        EXRDecoder.byteArray.fill(1, 0, size);
                    }

                    break;

                case HalfFloatType:
                    EXRDecoder.byteArray = new Uint16Array(size);

                    if (fillAlpha) {
                        EXRDecoder.byteArray.fill(0x3c00, 0, size);
                    } // Uint16Array holds half float data, 0x3C00 is 1

                    break;

                default:
                    console.error('THREE.EXRLoader: unsupported type: ', outputType);
                    break;
            }

            let byteOffset = 0;
            for (const channel of EXRHeader.channels) {
                if (EXRDecoder.decodeChannels[channel.name] !== undefined) {
                    EXRDecoder.channelByteOffsets[channel.name] = byteOffset;
                }

                byteOffset += channel.pixelType * 2;
            }

            EXRDecoder.totalBytes = byteOffset;
            EXRDecoder.outLineWidth = EXRDecoder.width * EXRDecoder.outputChannels;

            if (EXRHeader.lineOrder === 'INCREASING_Y') {
                EXRDecoder.scanOrder = (y) => y;
            } else {
                EXRDecoder.scanOrder = (y) => EXRDecoder.height - 1 - y;
            }

            if (EXRDecoder.outputChannels == 4) {
                EXRDecoder.format = RGBAFormat;
                EXRDecoder.colorSpace = LinearSRGBColorSpace;
            } else {
                EXRDecoder.format = RedFormat;
                EXRDecoder.colorSpace = NoColorSpace;
            }

            if (EXRHeader.spec.singleTile) {
                EXRDecoder.blockHeight = EXRHeader.tiles.ySize;
                EXRDecoder.blockWidth = EXRHeader.tiles.xSize;

                const numXLevels = calculateTileLevels(EXRHeader.tiles, EXRDecoder.width, EXRDecoder.height);
                // const numYLevels = calculateTileLevels( EXRHeader.tiles, EXRDecoder.width, EXRDecoder.height );

                const numXTiles = calculateTiles(numXLevels, EXRDecoder.width, EXRHeader.tiles.xSize, EXRHeader.tiles.roundingMode);
                const numYTiles = calculateTiles(numXLevels, EXRDecoder.height, EXRHeader.tiles.ySize, EXRHeader.tiles.roundingMode);

                EXRDecoder.tileCount = numXTiles[0] * numYTiles[0];

                for (let l = 0; l < numXLevels; l++) {
                    for (let y = 0; y < numYTiles[l]; y++) {
                        for (let x = 0; x < numXTiles[l]; x++) {
                            parseInt64(dataView, offset);
                        }
                    }
                } // tileOffset

                EXRDecoder.decode = parseTiles.bind(EXRDecoder);
            } else {
                EXRDecoder.blockWidth = EXRDecoder.width;
                const blockCount = Math.ceil(EXRDecoder.height / EXRDecoder.blockHeight);

                for (let i = 0; i < blockCount; i++) {
                    parseInt64(dataView, offset);
                } // scanlineOffset

                EXRDecoder.decode = parseScanline.bind(EXRDecoder);
            }

            return EXRDecoder;
        }

        // start parsing file [START]
        const offset = { value: 0 };
        const bufferDataView = new DataView(buffer);
        const uInt8Array = new Uint8Array(buffer);

        // get header information and validate format.
        const EXRHeader = parseHeader(bufferDataView, buffer, offset);

        // get input compression information and prepare decoding.
        const EXRDecoder = setupDecoder(EXRHeader, bufferDataView, uInt8Array, offset, this.type);

        // parse input data
        EXRDecoder.decode();

        return {
            header: EXRHeader,
            width: EXRDecoder.width,
            height: EXRDecoder.height,
            data: EXRDecoder.byteArray,
            format: EXRDecoder.format,
            colorSpace: EXRDecoder.colorSpace,
            type: this.type
        };
    }

    setDataType(value) {
        this.type = value;
        return this;
    }

    load(url, onLoad, onProgress, onError) {
        function onLoadCallback(texture, texData) {
            texture.colorSpace = texData.colorSpace;
            texture.minFilter = LinearFilter;
            texture.magFilter = LinearFilter;
            texture.generateMipmaps = false;
            texture.flipY = false;

            if (onLoad) onLoad(texture, texData);
        }

        return super.load(url, onLoadCallback, onProgress, onError);
    }
}

class EnvironmentMapLoader {
    constructor(renderer) {
        this.disposed = false;
        this.PMREMGenerator = new THREE.PMREMGenerator(renderer);
    }

    from_buffer(buffer, callback) {
        this.PMREMGenerator.compileEquirectangularShader();

        new EXRLoader().parse(buffer, (texture) => {
            if (this.disposed) {
                texture.dispose();
                return;
            }
            texture.mapping = THREE.EquirectangularReflectionMapping;
            const envMapRenderTarget = this.PMREMGenerator.fromEquirectangular(texture);
            if (this.disposed) {
                // Loader was disposed during async processing — clean up and do not call back.
                envMapRenderTarget.dispose();
                texture.dispose();
                return;
            }
            // Ownership of envMapRenderTarget and texture passes to the caller on success.
            callback(envMapRenderTarget, texture);
        });
    }

    from_url(url, callback, onProgress) {
        this.PMREMGenerator.compileEquirectangularShader();

        new EXRLoader().load(
            url,
            (texture) => {
                if (this.disposed) {
                    texture.dispose();
                    return;
                }
                texture.mapping = THREE.EquirectangularReflectionMapping;
                const envMapRenderTarget = this.PMREMGenerator.fromEquirectangular(texture);
                if (this.disposed) {
                    // Loader was disposed during async processing — clean up and do not call back.
                    envMapRenderTarget.dispose();
                    texture.dispose();
                    return;
                }
                // Ownership of envMapRenderTarget and texture passes to the caller on success.
                callback(envMapRenderTarget, texture);
            },
            onProgress
        );
    }

    dispose() {
        this.disposed = true;
        this.PMREMGenerator.dispose();
    }
}

/**
 * Geodetic utilities for converting between geographic coordinates (latitude, longitude, altitude)
 * and local ENU (East-North-Up) coordinates in meters.
 *
 * Uses WGS84 ellipsoid parameters.
 */

// WGS84 ellipsoid constants
const WGS84_A = 6378137.0; // Semi-major axis in meters
const WGS84_E2 = 0.00669437999014; // First eccentricity squared

/**
 * Convert degrees to radians
 * @param {number} degrees
 * @return {number} radians
 */
function degreesToRadians(degrees) {
    return (degrees * Math.PI) / 180.0;
}

/**
 * Convert radians to degrees
 * @param {number} radians
 * @return {number} degrees
 */
function radiansToDegrees(radians) {
    return (radians * 180.0) / Math.PI;
}

/**
 * Convert geodetic coordinates (latitude, longitude, altitude) to ECEF (Earth-Centered, Earth-Fixed)
 * @param {number} lat - Latitude in degrees
 * @param {number} lon - Longitude in degrees
 * @param {number} alt - Altitude in meters above WGS84 ellipsoid
 * @return {Object} {x, y, z} in meters
 */
function geodeticToECEF(lat, lon, alt) {
    const latRad = degreesToRadians(lat);
    const lonRad = degreesToRadians(lon);

    const sinLat = Math.sin(latRad);
    const cosLat = Math.cos(latRad);
    const sinLon = Math.sin(lonRad);
    const cosLon = Math.cos(lonRad);

    // Radius of curvature in the prime vertical
    const N = WGS84_A / Math.sqrt(1 - WGS84_E2 * sinLat * sinLat);

    const x = (N + alt) * cosLat * cosLon;
    const y = (N + alt) * cosLat * sinLon;
    const z = (N * (1 - WGS84_E2) + alt) * sinLat;

    return { x, y, z };
}

/**
 * Convert ECEF (Earth-Centered, Earth-Fixed) coordinates to geodetic (latitude, longitude, altitude)
 * Uses Bowring's iterative method which converges quickly (typically 2-3 iterations)
 * @param {number} x - ECEF X coordinate in meters
 * @param {number} y - ECEF Y coordinate in meters
 * @param {number} z - ECEF Z coordinate in meters
 * @return {Object} {lat, lon, alt} - latitude/longitude in degrees, altitude in meters
 */
function ECEFToGeodetic(x, y, z) {
    const p = Math.sqrt(x * x + y * y);
    const lon = Math.atan2(y, x);

    // Initial latitude estimate
    let lat = Math.atan2(z, p * (1 - WGS84_E2));

    // Iterate to refine latitude (Bowring's method)
    for (let i = 0; i < 5; i++) {
        const sinLat = Math.sin(lat);
        const N = WGS84_A / Math.sqrt(1 - WGS84_E2 * sinLat * sinLat);
        lat = Math.atan2(z + WGS84_E2 * N * sinLat, p);
    }

    // Compute altitude
    const sinLat = Math.sin(lat);
    const cosLat = Math.cos(lat);
    const N = WGS84_A / Math.sqrt(1 - WGS84_E2 * sinLat * sinLat);

    let alt;
    if (Math.abs(cosLat) > 1e-10) {
        alt = p / cosLat - N;
    } else {
        // Near poles, use z component
        alt = Math.abs(z) / Math.abs(sinLat) - N * (1 - WGS84_E2);
    }

    return {
        lat: radiansToDegrees(lat),
        lon: radiansToDegrees(lon),
        alt: alt
    };
}

/**
 * Convert a position from geodetic coordinates to ENU (East-North-Up) coordinates
 * relative to an observer position.
 *
 * @param {number} lat - Target latitude in degrees
 * @param {number} lon - Target longitude in degrees
 * @param {number} alt - Target altitude in meters
 * @param {number} observerLat - Observer latitude in degrees
 * @param {number} observerLon - Observer longitude in degrees
 * @param {number} observerAlt - Observer altitude in meters
 * @return {Object} {east, north, up} in meters relative to observer
 */
function geodeticToENU(lat, lon, alt, observerLat, observerLon, observerAlt) {
    // Convert both positions to ECEF
    const target = geodeticToECEF(lat, lon, alt);
    const observer = geodeticToECEF(observerLat, observerLon, observerAlt);

    // Compute ECEF difference
    const dx = target.x - observer.x;
    const dy = target.y - observer.y;
    const dz = target.z - observer.z;

    // Convert observer lat/lon to radians for rotation matrix
    const latRad = degreesToRadians(observerLat);
    const lonRad = degreesToRadians(observerLon);

    const sinLat = Math.sin(latRad);
    const cosLat = Math.cos(latRad);
    const sinLon = Math.sin(lonRad);
    const cosLon = Math.cos(lonRad);

    // Rotate ECEF difference to ENU
    const east = -sinLon * dx + cosLon * dy;
    const north = -sinLat * cosLon * dx - sinLat * sinLon * dy + cosLat * dz;
    const up = cosLat * cosLon * dx + cosLat * sinLon * dy + sinLat * dz;

    return { east, north, up };
}

/**
 * Convert a position from ENU (East-North-Up) coordinates to geodetic coordinates
 * relative to an observer position. This is the inverse of geodeticToENU.
 *
 * @param {number} east - East coordinate in meters
 * @param {number} north - North coordinate in meters
 * @param {number} up - Up coordinate in meters
 * @param {number} observerLat - Observer latitude in degrees
 * @param {number} observerLon - Observer longitude in degrees
 * @param {number} observerAlt - Observer altitude in meters
 * @return {Object} {lat, lon, alt} - latitude/longitude in degrees, altitude in meters
 */
function ENUToGeodetic(east, north, up, observerLat, observerLon, observerAlt) {
    // Convert observer position to ECEF
    const observer = geodeticToECEF(observerLat, observerLon, observerAlt);

    // Convert observer lat/lon to radians for rotation matrix
    const latRad = degreesToRadians(observerLat);
    const lonRad = degreesToRadians(observerLon);

    const sinLat = Math.sin(latRad);
    const cosLat = Math.cos(latRad);
    const sinLon = Math.sin(lonRad);
    const cosLon = Math.cos(lonRad);

    // Rotate ENU to ECEF difference (transpose of ENU rotation matrix)
    const dx = -sinLon * east - sinLat * cosLon * north + cosLat * cosLon * up;
    const dy = cosLon * east - sinLat * sinLon * north + cosLat * sinLon * up;
    const dz = cosLat * north + sinLat * up;

    // Add to observer ECEF to get target ECEF
    const targetX = observer.x + dx;
    const targetY = observer.y + dy;
    const targetZ = observer.z + dz;

    // Convert target ECEF to geodetic
    return ECEFToGeodetic(targetX, targetY, targetZ);
}

/**
 * SplatMeshEntry: Bundles a SplatMesh with its associated sort worker state.
 * Each entry represents one scene with its own mesh, sort worker, and rendering state.
 */
class SplatMeshEntry {
    constructor(id, splatMesh) {
        this.id = id;
        this.splatMesh = splatMesh;
        this.sortWorker = null;
        this.sortWorkerIndexesToSort = null;
        this.sortWorkerSortedIndexes = null;
        this.sortWorkerPrecomputedDistances = null;
        this.sortWorkerTransforms = null;
        this.sortRunning = false;
        this.sortPromise = null;
        this.sortPromiseResolver = null;
        this.preSortMessages = [];
        this.runAfterNextSort = [];
        this.splatRenderCount = 0;
        this.splatSortCount = 0;
        this.lastSplatSortCount = 0;
        this.splatRenderReady = false;
        this.viewBoundingBox = null;
    }
}

const MIN_SPLAT_COUNT_TO_SHOW_SPLAT_TREE_LOADING_SPINNER = 1500000;
const FOCUS_MARKER_FADE_IN_SPEED = 10.0;
const FOCUS_MARKER_FADE_OUT_SPEED = 2.5;
const CONSECUTIVE_RENDERED_FRAMES_FOR_FPS_CALCULATION = 60;

// Duration of one camera transition, in seconds, in case the scene size is not known
const TRANSITION_DURATION = 1;

// Time it should take to transition through the entire scene, in seconds
const SCENE_TRANSITION_DURATION = 2;

const SceneFormat = {
    Gaus: 0
};

const DEFAULT_OPTIONS = {
    cameraUp: [0, 1, 0],
    initialCameraPosition: [0, 0, 0],
    initialCameraLookAt: [0, 0, -1],
    cameraFoV: 90,
    boundingBox: new THREE.Box3(new THREE.Vector3(-Infinity, -Infinity, -Infinity), new THREE.Vector3(Infinity, Infinity, Infinity)),
    selfDrivenMode: true,
    useBuiltInControls: true,
    rootElement: null,
    ignoreDevicePixelRatio: false,
    halfPrecisionCovariancesOnGPU: false,
    threeScene: null,
    renderer: null,
    camera: null,
    gpuAcceleratedSort: false,
    integerBasedSort: false,
    sharedMemoryForWorkers: true,
    kernel2DSize: 0.3,
    renderMode: RenderMode.Always,
    sceneRevealMode: SceneRevealMode.Default,
    maxScreenSpaceSplatSize: 1024,
    logLevel: LogLevel.None,
    sphericalHarmonicsDegree: 2,
    enableOptionalEffects: false,
    enableSIMDInSort: true,
    inMemoryCompressionLevel: 0,
    cameraFar: 1000,
    sceneFadeInRateMultiplier: 1,
    splatSortDistanceMapPrecision: Constants.DefaultSplatSortDistanceMapPrecision,
    preloadingBoundsDistance: 20,
    unloadingBoundsDistance: 20,
    disableSplatTree: false,
    etaBasedLoading: true,
    debugMode: false,
    maxClickToMoveDistance: null
};

/**
 * Viewer: Manages the rendering of splat scenes. Manages an instance of SplatMesh as well as a web worker
 * that performs the sort for its splats.
 */
class Viewer {
    constructor(options = {}) {
        // The natural 'up' vector for viewing the scene (only has an effect when used with orbit controls and
        // when the viewer uses its own camera).
        if (!options.cameraUp) {
            options.cameraUp = DEFAULT_OPTIONS.cameraUp;
        }
        this.cameraUp = new THREE.Vector3().fromArray(options.cameraUp);

        // The camera's initial position (only used when the viewer uses its own camera).
        this.explicitStartPosition = !!options.initialCameraPosition;
        if (!options.initialCameraPosition) {
            options.initialCameraPosition = DEFAULT_OPTIONS.initialCameraPosition;
        }
        this.initialCameraPosition = new THREE.Vector3().fromArray(options.initialCameraPosition);

        // The initial focal point of the camera and center of the camera's orbit (only used when the viewer uses its own camera).
        if (!options.initialCameraLookAt) {
            options.initialCameraLookAt = DEFAULT_OPTIONS.initialCameraLookAt;
        }
        this.initialCameraLookAt = new THREE.Vector3().fromArray(options.initialCameraLookAt);

        // The camera's field of view in degrees (only used when the viewer uses its own camera).
        this.cameraFoV = options.cameraFoV || DEFAULT_OPTIONS.cameraFoV;

        // The bounding box of the scene, used to constrain camera movement inside the scene
        this.boundingBox = options.boundingBox || DEFAULT_OPTIONS.boundingBox;

        // If 'selfDrivenMode' is true, the viewer manages its own update/animation loop via requestAnimationFrame()
        if (options.selfDrivenMode === undefined || options.selfDrivenMode === null) {
            options.selfDrivenMode = DEFAULT_OPTIONS.selfDrivenMode;
        }
        this.selfDrivenMode = options.selfDrivenMode;
        this.selfDrivenUpdateFunc = this.selfDrivenUpdate.bind(this);

        // If 'useBuiltInControls' is true, the viewer will create its own instance of OrbitControls and attach to the camera
        if (options.useBuiltInControls === undefined) {
            options.useBuiltInControls = DEFAULT_OPTIONS.useBuiltInControls;
        }
        this.useBuiltInControls = options.useBuiltInControls;

        // parent element of the Three.js renderer canvas
        this.rootElement = options.rootElement || DEFAULT_OPTIONS.rootElement;

        // Tells the viewer to pretend the device pixel ratio is 1, which can boost performance on devices where it is larger,
        // at a small cost to visual quality
        this.ignoreDevicePixelRatio = options.ignoreDevicePixelRatio || DEFAULT_OPTIONS.ignoreDevicePixelRatio;
        this.devicePixelRatio = this.ignoreDevicePixelRatio ? 1 : window.devicePixelRatio || 1;

        // Tells the viewer to use 16-bit floating point values when storing splat covariance data in textures, instead of 32-bit
        if (options.halfPrecisionCovariancesOnGPU === undefined || options.halfPrecisionCovariancesOnGPU === null) {
            options.halfPrecisionCovariancesOnGPU = DEFAULT_OPTIONS.halfPrecisionCovariancesOnGPU;
        }
        this.halfPrecisionCovariancesOnGPU = options.halfPrecisionCovariancesOnGPU;

        // If 'threeScene' is valid, it will be rendered by the viewer along with the splat mesh
        this.threeScene = options.threeScene || DEFAULT_OPTIONS.threeScene;
        // Allows for usage of an external Three.js renderer
        this.renderer = options.renderer || DEFAULT_OPTIONS.renderer;
        // Allows for usage of an external Three.js camera
        this.camera = options.camera || DEFAULT_OPTIONS.camera;

        // Optional training camera poses
        this.imageCameras = [];
        this.currentCameraIndex = 0;

        // If 'gpuAcceleratedSort' is true, a partially GPU-accelerated approach to sorting splats will be used.
        // Currently this means pre-computing splat distances from the camera on the GPU
        if (options.gpuAcceleratedSort === undefined || options.gpuAcceleratedSort === null) {
            options.gpuAcceleratedSort = DEFAULT_OPTIONS.gpuAcceleratedSort;
        }
        this.gpuAcceleratedSort = options.gpuAcceleratedSort;

        // if 'integerBasedSort' is true, the integer version of splat centers as well as other values used to calculate
        // splat distances are used instead of the float version. This speeds up computation, but introduces the possibility of
        // overflow in larger scenes.
        if (options.integerBasedSort === undefined || options.integerBasedSort === null) {
            options.integerBasedSort = DEFAULT_OPTIONS.integerBasedSort;
        }
        this.integerBasedSort = options.integerBasedSort;

        // If 'sharedMemoryForWorkers' is true, a SharedArrayBuffer will be used to communicate with web workers. This method
        // is faster than copying memory to or from web workers, but comes with security implications as outlined here:
        // https://web.dev/articles/cross-origin-isolation-guide
        // If enabled, it requires specific CORS headers to be present in the response from the server that is sent when
        // loading the application. More information is available in the README.
        if (options.sharedMemoryForWorkers === undefined || options.sharedMemoryForWorkers === null) {
            options.sharedMemoryForWorkers = true;
        }
        this.sharedMemoryForWorkers = options.sharedMemoryForWorkers;

        // This constant is added to the projected 2D screen-space splat scales
        this.kernel2DSize = options.kernel2DSize || DEFAULT_OPTIONS.kernel2DSize;

        // if 'renderMode' is RenderMode.Always, then the viewer will rrender the scene on every update. If it is RenderMode.OnChange,
        // it will only render when something in the scene has changed.
        this.renderMode = options.renderMode || DEFAULT_OPTIONS.renderMode;

        // SceneRevealMode.Default results in a nice, slow fade-in effect for progressively loaded scenes,
        // and a fast fade-in for non progressively loaded scenes.
        // SceneRevealMode.Gradual will force a slow fade-in for all scenes.
        // SceneRevealMode.Instant will force all loaded scene data to be immediately visible.
        this.sceneRevealMode = options.sceneRevealMode || DEFAULT_OPTIONS.sceneRevealMode;

        // Specify the maximum screen-space splat size, can help deal with large splats that get too unwieldy
        this.maxScreenSpaceSplatSize = options.maxScreenSpaceSplatSize || DEFAULT_OPTIONS.maxScreenSpaceSplatSize;

        // The verbosity of console logging
        this.logLevel = options.logLevel || DEFAULT_OPTIONS.logLevel;

        // Degree of spherical harmonics to utilize in rendering splats (assuming the data is present in the splat scene).
        // Valid values are 0 - 2. Default value is 2, use maximum available degree.
        this.sphericalHarmonicsDegree = options.sphericalHarmonicsDegree || DEFAULT_OPTIONS.sphericalHarmonicsDegree;

        // When true, allows for usage of extra properties and attributes during rendering for effects such as opacity adjustment.
        // Default is false for performance reasons. These properties are separate from transform properties (scale, rotation, position)
        // that are enabled by the 'dynamicScene' parameter.
        if (options.enableOptionalEffects === undefined || options.enableOptionalEffects === null) {
            options.enableOptionalEffects = DEFAULT_OPTIONS.enableOptionalEffects;
        }

        this.enableOptionalEffects = options.enableOptionalEffects;

        // Enable the usage of SIMD WebAssembly instructions for the splat sort
        if (options.enableSIMDInSort === undefined || options.enableSIMDInSort === null) {
            options.enableSIMDInSort = DEFAULT_OPTIONS.enableSIMDInSort;
        }
        this.enableSIMDInSort = options.enableSIMDInSort;

        // Level to compress non KSPLAT files when loading them for direct rendering
        if (options.inMemoryCompressionLevel === undefined || options.inMemoryCompressionLevel === null) {
            options.inMemoryCompressionLevel = DEFAULT_OPTIONS.inMemoryCompressionLevel;
        }
        this.inMemoryCompressionLevel = options.inMemoryCompressionLevel;

        this.cameraFar = options.cameraFar || DEFAULT_OPTIONS.cameraFar;

        // It appears that for certain iOS versions, special actions need to be taken with the
        // usage of SIMD instructions and shared memory
        if (isIOS()) {
            const semver = getIOSSemever();
            if (semver.major < 17) {
                this.enableSIMDInSort = false;
            }
            if (semver.major < 16) {
                this.sharedMemoryForWorkers = false;
            }
        }

        // Customize the speed at which the scene is revealed
        this.sceneFadeInRateMultiplier = options.sceneFadeInRateMultiplier || DEFAULT_OPTIONS.sceneFadeInRateMultiplier;

        // Set the range for the depth map for the counting sort used to sort the splats
        this.splatSortDistanceMapPrecision = options.splatSortDistanceMapPrecision || DEFAULT_OPTIONS.splatSortDistanceMapPrecision;
        const maxPrecision = this.integerBasedSort ? 20 : 24;
        this.splatSortDistanceMapPrecision = clamp(this.splatSortDistanceMapPrecision, 10, maxPrecision);

        this.onSplatMeshChangedCallback = null;

        // Multi-mesh state: each SplatMeshEntry holds one scene with its own sort worker
        this.splatMeshes = new Map();
        this.activeSplatMeshId = null;
        this.nextSplatMeshId = 0;

        this.controls = null;

        this.perspectiveCamera = null;

        this.showMeshCursor = false;
        this.showInfo = false;

        this.sceneHelper = null;

        this.selfDrivenModeRunning = false;

        this.raycaster = new Raycaster();

        this.infoPanel = null;

        this.currentFPS = 0;
        this.lastSortTime = 0;
        this.consecutiveRenderFrames = 0;

        this.previousCamera = undefined;
        this.nextCamera = undefined;
        // Store the position where the user clicked to change the camera target
        this.clickPosition = new THREE.Vector3();
        this.transitionCameraSpeed = 0.0;
        this.medianCameraDistance = 0.0;

        this.mousePosition = new THREE.Vector2();
        this.mouseDownPosition = new THREE.Vector2();
        this.mouseDownTime = null;

        this.resizeObserver = null;
        this.mouseMoveListener = null;
        this.mouseDownListener = null;
        this.mouseUpListener = null;
        this.keyDownListener = null;

        this.splatSceneDownloadPromises = {};
        this.splatSceneDownloadAndBuildPromise = null;
        this.splatSceneRemovalPromise = null;

        // Lazy loading state for manifest-based scene loading
        this.lazyLoadSegments = null; // Map<segmentIndex, Segment> for O(1) lookup
        this.lazyLoadOptions = null;
        this.imagePosesUrl = null;
        this.currentEnvironmentMapUrl = null; // Track current environment map URL to avoid redundant loads
        this.envMapLoader = null; // Reusable environment map loader
        // Viewer owns these after EnvironmentMapLoader callback succeeds; EnvironmentMapLoader never disposes them on the success path.
        this.currentEnvMapRenderTarget = null;
        this.currentEnvMapTexture = null;
        this.lazyLoadQueue = []; // Queue of {type: 'load'|'unload', segmentIndex, resolve, reject}
        this.lazyLoadQueueProcessing = false;
        this.currentLazyFetchAbortable = null; // Tracks the in-flight fetch during lazy load

        // Camera velocity tracking for priority-based segment loading
        this.prevCameraPosition = new THREE.Vector3();
        this.cameraVelocity = new THREE.Vector3();

        // Do not use camera velocity until it's properly computed for the first time to avoid issues with sudden jumps in velocity when the camera starts moving
        this.cameraVelocityInitialized = false;
        this.prevVelocityUpdateTime = 0;

        // ParseWorker for off-main-thread parsing
        this.parseWorker = null;
        this.parseWorkerReady = false;

        // Initialize ParseWorker for off-main-thread parsing (non-blocking)
        this.initParseWorker().catch((error) => {
            console.warn('ParseWorker initialization failed, will use main thread:', error);
        });

        this.loadingSpinner = new LoadingSpinner(null, this.rootElement || document.body);
        this.loadingSpinner.hide();
        this.loadingProgressBar = new LoadingProgressBar(this.rootElement || document.body);
        this.loadingProgressBar.hide();
        this.infoPanel = new InfoPanel(this.rootElement || document.body);
        this.infoPanel.hide();

        this.usingExternalRenderer = !!this.renderer;

        this.createdRootElement = false;
        this.initialized = false;
        this.disposing = false;
        this.disposed = false;
        this.disposePromise = null;
        this.firstBuild = true;

        this.preloadingBoundsDistance = options.preloadingBoundsDistance ?? DEFAULT_OPTIONS.preloadingBoundsDistance;
        this.unloadingBoundsDistance = options.unloadingBoundsDistance ?? DEFAULT_OPTIONS.unloadingBoundsDistance;

        // When true, disables splat tree creation and related features such as click-to-move.
        this.disableSplatTree = options.disableSplatTree ?? DEFAULT_OPTIONS.disableSplatTree;

        // When true, uses ETA-based preloading (velocity prediction) instead of bounding box triggering.
        this.etaBasedLoading = options.etaBasedLoading ?? DEFAULT_OPTIONS.etaBasedLoading;

        this.debugMode = options.debugMode ?? DEFAULT_OPTIONS.debugMode;

        // Maximum distance (in meters) the camera can move in a single click-to-move action.
        // If null and lazyLoadSegments are present, movement is constrained to the current segment's view bounds.
        const rawMaxClickToMoveDistance =
            options.maxClickToMoveDistance ?? DEFAULT_OPTIONS.maxClickToMoveDistance;
        if (rawMaxClickToMoveDistance === null) {
            this.maxClickToMoveDistance = null;
        } else if (
            typeof rawMaxClickToMoveDistance === 'number' &&
            Number.isFinite(rawMaxClickToMoveDistance) &&
            rawMaxClickToMoveDistance >= 0
        ) {
            this.maxClickToMoveDistance = rawMaxClickToMoveDistance;
        } else {
            // Coerce invalid values (negative, NaN, non-numeric) to null to avoid unsafe behavior.
            this.maxClickToMoveDistance = null;
        }

        this.clock = new THREE.Clock();
        this.init();
    }

    get activeSplatMesh() {
        const entry = this.splatMeshes.get(this.activeSplatMeshId);
        return entry ? entry.splatMesh : null;
    }

    get activeEntry() {
        return this.splatMeshes.get(this.activeSplatMeshId) || null;
    }

    get splatRenderReady() {
        const entry = this.activeEntry;
        return entry ? entry.splatRenderReady : false;
    }

    get splatMesh() {
        return this.activeSplatMesh;
    }

    createSplatMeshEntry(id, viewBoundingBox = null) {
        const splatMesh = new SplatMesh(
            this.enableOptionalEffects,
            this.halfPrecisionCovariancesOnGPU,
            this.devicePixelRatio,
            this.gpuAcceleratedSort,
            this.integerBasedSort,
            this.maxScreenSpaceSplatSize,
            this.logLevel,
            this.sphericalHarmonicsDegree,
            this.sceneFadeInRateMultiplier,
            this.kernel2DSize
        );
        splatMesh.frustumCulled = false;
        const entry = new SplatMeshEntry(id, splatMesh);
        entry.viewBoundingBox = viewBoundingBox;
        this.splatMeshes.set(id, entry);
        if (this.activeSplatMeshId === null) {
            this.activeSplatMeshId = id;
        }
        if (this.onSplatMeshChangedCallback) this.onSplatMeshChangedCallback();
        return entry;
    }

    init() {
        if (this.initialized) return;

        if (!this.rootElement) {
            if (!this.usingExternalRenderer) {
                this.rootElement = document.createElement('div');
                this.rootElement.style.width = '100%';
                this.rootElement.style.height = '100%';
                this.rootElement.style.position = 'absolute';
                document.body.appendChild(this.rootElement);
                this.createdRootElement = true;
            } else {
                this.rootElement = this.renderer.domElement || document.body;
            }
        }

        this.setupCamera();
        this.setupRenderer();
        this.setupControls();
        this.setupEventHandlers();

        this.threeScene = this.threeScene || new THREE.Scene();
        this.sceneHelper = new SceneHelper(this.threeScene);
        this.sceneHelper.setupMeshCursor();
        this.sceneHelper.setupFocusMarker();
        this.sceneHelper.setupControlPlane();
        this.sceneHelper.setupPOIMarkers();
        this.sceneHelper.setupDepthCompositingObjects();

        this.loadingProgressBar.setContainer(this.rootElement);
        this.loadingSpinner.setContainer(this.rootElement);
        this.infoPanel.setContainer(this.rootElement);

        if (this.logLevel >= LogLevel.Debug) {
            this.positionLogInterval = setInterval(() => {
                const position = this.getCameraGeodeticPosition();

                if (!position) {
                    return;
                }
                const { lat, lon, alt, heading } = position;
                console.debug(`Current position: lat=${lat}, lon=${lon}, alt=${alt}, heading=${heading}`);
            }, 5000);
        }
        this.initialized = true;
    }

    setupCamera() {
        if (!this.camera) {
            const renderDimensions = new THREE.Vector2();
            this.getRenderDimensions(renderDimensions);

            this.perspectiveCamera = new THREE.PerspectiveCamera(
                this.cameraFoV,
                renderDimensions.x / renderDimensions.y,
                0.1,
                this.cameraFar
            );
            this.camera = this.perspectiveCamera;
            this.camera.position.copy(this.initialCameraPosition);
            this.camera.up.copy(this.cameraUp).normalize();
            this.camera.lookAt(this.initialCameraLookAt);
        } else {
            this.perspectiveCamera = this.camera;
        }
        this.previousCamera = this.camera.clone();
        this.nextCamera = this.camera.clone();

        this.focalLength = this.perspectiveCamera.getFocalLength();
    }

    setupRenderer() {
        if (!this.usingExternalRenderer) {
            const renderDimensions = new THREE.Vector2();
            this.getRenderDimensions(renderDimensions);

            this.renderer = new THREE.WebGLRenderer({
                precision: 'highp',
            });
            this.renderer.setPixelRatio(this.devicePixelRatio);
            this.renderer.autoClear = true;
            this.renderer.setClearColor(new THREE.Color(0x000000), 0.0);
            this.renderer.setSize(renderDimensions.x, renderDimensions.y);

            this.resizeObserver = new ResizeObserver(() => {
                this.getRenderDimensions(renderDimensions);
                this.renderer.setSize(renderDimensions.x, renderDimensions.y);
                this.forceRenderNextFrame();
            });
            this.resizeObserver.observe(this.rootElement);
            this.rootElement.appendChild(this.renderer.domElement);
        }
    }

    setupControls() {
        if (this.useBuiltInControls) {
            this.controls = new FirstPersonControls(this.camera, this.renderer.domElement);
            // this.controls.listenToKeyEvents(window);
            this.controls.movementSpeed = 5;
            this.controls.lookSpeed = 10;
            this.controls.rotateSpeed = 0.5;
            this.controls.maxPolarAngle = Math.PI * 0.75;
            this.controls.minPolarAngle = 0.1;
            this.controls.enableDamping = true;
            this.controls.dampingFactor = 0.05;
            this.controls.boundingBox = this.boundingBox;
            // this.controls.lookAt(this.initialCameraLookAt);
            this.controls.update(0);
        }
    }

    setupEventHandlers() {
        if (this.useBuiltInControls) {
            this.mouseMoveListener = this.onMouseMove.bind(this);
            this.renderer.domElement.addEventListener('pointermove', this.mouseMoveListener, false);
            this.mouseDownListener = this.onMouseDown.bind(this);
            this.renderer.domElement.addEventListener('pointerdown', this.mouseDownListener, false);
            this.mouseUpListener = this.onMouseUp.bind(this);
            this.renderer.domElement.addEventListener('pointerup', this.mouseUpListener, false);
            this.keyDownListener = this.onKeyDown.bind(this);
            window.addEventListener('keydown', this.keyDownListener, false);
        }
    }

    removeEventHandlers() {
        if (this.useBuiltInControls) {
            this.renderer.domElement.removeEventListener('pointermove', this.mouseMoveListener);
            this.mouseMoveListener = null;
            this.renderer.domElement.removeEventListener('pointerdown', this.mouseDownListener);
            this.mouseDownListener = null;
            this.renderer.domElement.removeEventListener('pointerup', this.mouseUpListener);
            this.mouseUpListener = null;
            window.removeEventListener('keydown', this.keyDownListener);
            this.keyDownListener = null;
        }
    }

    setRenderMode(renderMode) {
        this.renderMode = renderMode;
    }

    setActiveSphericalHarmonicsDegrees(activeSphericalHarmonicsDegrees) {
        for (const entry of this.splatMeshes.values()) {
            entry.splatMesh.material.uniforms.sphericalHarmonicsDegree.value = activeSphericalHarmonicsDegrees;
            entry.splatMesh.material.uniformsNeedUpdate = true;
        }
    }

    onSplatMeshChanged(callback) {
        this.onSplatMeshChangedCallback = callback;
    }

    onKeyDown = (function () {
        const forward = new THREE.Vector3();
        const tempMatrixLeft = new THREE.Matrix4();
        const tempMatrixRight = new THREE.Matrix4();

        return function (e) {
            forward.set(0, 0, -1);
            forward.transformDirection(this.camera.matrixWorld);
            tempMatrixLeft.makeRotationAxis(forward, Math.PI / 128);
            tempMatrixRight.makeRotationAxis(forward, -Math.PI / 128);
            switch (e.code) {
                case 'KeyC':
                    if (this.splatMesh) this.splatMesh.nextDisplayMode();
                    break;
                case 'KeyG':
                    this.focalLength += 0.1;
                    this.perspectiveCamera.setFocalLength(this.focalLength);
                    this.forceRenderNextFrame();
                    break;
                case 'KeyF':
                    this.focalLength -= 0.1;
                    this.perspectiveCamera.setFocalLength(this.focalLength);
                    this.forceRenderNextFrame();
                    break;
                case 'Equal':
                    if (this.splatMesh) this.splatMesh.setSplatScale(this.splatMesh.getSplatScale() + 0.05);
                    break;
                case 'Minus':
                    if (this.splatMesh) this.splatMesh.setSplatScale(Math.max(this.splatMesh.getSplatScale() - 0.05, 0.0));
                    break;
                // Space bar moves to the next training image pose, if available
                case 'Space':
                    if (this.imageCameras.length > 0) {
                        this.currentCameraIndex = (this.currentCameraIndex + 1) % this.imageCameras.length;
                        this.previousCamera.copy(this.camera);
                        this.nextCamera.copy(this.imageCameras[this.currentCameraIndex]);
                        this.transitioningCameraTarget = true;
                        this.clickTransitioning = false;
                        this.transitioningCameraTargetStartTime = getCurrentTime();
                    }
                    break;
                default:
                    if (this.debugMode) this.handleDebugKeyDown(e.code);
                    break;
            }
        };
    })();

    handleDebugKeyDown(code) {
        switch (code) {
            case 'KeyR':
                this.debugSaveDepthAccum();
                break;
            case 'KeyI':
                this.showInfo = !this.showInfo;
                if (this.showInfo) {
                    this.infoPanel.show();
                } else {
                    this.infoPanel.hide();
                }
                break;
        }
    }

    onMouseMove(mouse) {
        this.mousePosition.set(mouse.offsetX, mouse.offsetY);
    }

    onMouseDown() {
        this.mouseDownPosition.copy(this.mousePosition);
        this.mouseDownTime = getCurrentTime();
    }

    onMouseUp = (function () {
        const clickOffset = new THREE.Vector2();

        return function (mouse) {
            clickOffset.copy(this.mousePosition).sub(this.mouseDownPosition);
            const mouseUpTime = getCurrentTime();
            const wasClick = mouseUpTime - this.mouseDownTime < 0.5 && clickOffset.length() < 2;
            if (wasClick) {
                this.onMouseClick(mouse);
            }
        };
    })();

    onMouseClick(mouse) {
        this.mousePosition.set(mouse.offsetX, mouse.offsetY);
        // Navigate on left click only
        if (mouse.button === 0) {
            this.checkForFocalPointChange();
            this.clickTransitioning = true;
        }
    }

    checkForFocalPointChange = (function () {
        const renderDimensions = new THREE.Vector2();
        const toNewFocalPoint = new THREE.Vector3();
        const outHits = [];

        return function () {
            if (!this.transitioningCameraTarget) {
                this.getRenderDimensions(renderDimensions);
                outHits.length = 0;
                this.raycaster.setFromCameraAndScreenPosition(this.camera, this.mousePosition, renderDimensions);
                if (this.splatMesh) this.raycaster.intersectSplatMesh(this.splatMesh, outHits);
                if (outHits.length > 0) {
                    const hit = outHits[0];
                    const intersectionPoint = new THREE.Vector3();
                    intersectionPoint.copy(hit.origin);

                    // Compute the vertical distance between the intersection point and the camera
                    const cameraToHit = intersectionPoint.clone().sub(this.camera.position);
                    const cameraToHitHeight = Math.abs(cameraToHit.y);
                    cameraToHit.y = 0; // Ignore the vertical component for distance calculation
                    const backOffDistance = Math.max(this.medianCameraDistance, cameraToHitHeight);

                    // Keep the intersection point at the camera's y position
                    intersectionPoint.y = this.camera.position.y;
                    // Subtract the back-off distance to observe the intersection point at a 45 degree angle
                    // or to avoid going into walls
                    intersectionPoint.sub(cameraToHit.clone().normalize().multiplyScalar(backOffDistance));

                    // Constrain the movement distance
                    if (this.maxClickToMoveDistance !== null) {
                        // Explicit max distance: clamp the horizontal movement vector
                        const movement = intersectionPoint.clone().sub(this.camera.position);
                        movement.y = 0;
                        const horizontalDist = movement.length();
                        if (horizontalDist > this.maxClickToMoveDistance) {
                            movement.multiplyScalar(this.maxClickToMoveDistance / horizontalDist);
                            intersectionPoint.copy(this.camera.position).add(movement);
                            intersectionPoint.y = this.camera.position.y;
                        }
                    } else if (this.lazyLoadSegments) {
                        // Constrain to the current segment's view bounding box
                        const cameraPos = this.camera.position;
                        for (const segment of this.lazyLoadSegments.values()) {
                            if (segment.viewBoundingBox.containsPoint(cameraPos)) {
                                const box = segment.viewBoundingBox;
                                intersectionPoint.x = clamp(intersectionPoint.x, box.min.x, box.max.x);
                                intersectionPoint.z = clamp(intersectionPoint.z, box.min.z, box.max.z);
                                break;
                            }
                        }
                    }

                    toNewFocalPoint.copy(intersectionPoint).sub(this.camera.position);
                    if (toNewFocalPoint.length() > 0) {
                        this.clickPosition.copy(hit.origin);
                        this.previousCamera.position.copy(this.camera.position);
                        this.previousCamera.quaternion.copy(this.camera.quaternion);
                        this.nextCamera.position.copy(intersectionPoint);
                        this.nextCamera.quaternion.copy(this.camera.quaternion);
                        this.transitioningCameraTarget = true;
                        this.transitioningCameraTargetStartTime = getCurrentTime();
                    }
                }
            }
        };
    })();

    getRenderDimensions(outDimensions) {
        if (this.rootElement) {
            outDimensions.x = this.rootElement.offsetWidth;
            outDimensions.y = this.rootElement.offsetHeight;
        } else {
            this.renderer.getSize(outDimensions);
        }
    }

    updateSplatMesh = (function () {
        const renderDimensions = new THREE.Vector2();

        return function (entry) {
            if (!entry) return;
            const splatMesh = entry.splatMesh;
            const splatCount = splatMesh.getSplatCount();
            if (splatCount > 0) {
                splatMesh.updateVisibleRegionFadeDistance(this.sceneRevealMode);
                splatMesh.updateTransforms();
                this.getRenderDimensions(renderDimensions);
                const focalLengthX = this.camera.projectionMatrix.elements[0] * 0.5 * this.devicePixelRatio * renderDimensions.x;
                const focalLengthY = this.camera.projectionMatrix.elements[5] * 0.5 * this.devicePixelRatio * renderDimensions.y;

                splatMesh.updateUniforms(renderDimensions, focalLengthX, focalLengthY);
            }
        };
    })();

    isLoadingOrUnloading() {
        return (
            Object.keys(this.splatSceneDownloadPromises).length > 0 ||
            this.splatSceneDownloadAndBuildPromise !== null ||
            this.splatSceneRemovalPromise !== null
        );
    }

    isDisposingOrDisposed() {
        return this.disposing || this.disposed;
    }

    addSplatSceneDownloadPromise(promise) {
        this.splatSceneDownloadPromises[promise.id] = promise;
    }

    removeSplatSceneDownloadPromise(promise) {
        delete this.splatSceneDownloadPromises[promise.id];
    }

    setSplatSceneDownloadAndBuildPromise(promise) {
        this.splatSceneDownloadAndBuildPromise = promise;
    }

    clearSplatSceneDownloadAndBuildPromise() {
        this.splatSceneDownloadAndBuildPromise = null;
    }

    imagePosesLoaded = (imageCameras) => {
        this.imageCameras = imageCameras;
        this.currentCameraIndex = 0;
        // If training image poses are available, and explicit start position
        // is not specified set the camera to the first one
        if (this.imageCameras.length > 0 && !this.explicitStartPosition) {
            this.previousCamera.copy(imageCameras[0]);
            this.nextCamera.copy(imageCameras[0]);

            this.camera.position.copy(imageCameras[0].position);
            this.camera.quaternion.copy(imageCameras[0].quaternion);

            this.camera.updateMatrixWorld();
            this.controls.updateLookDirection();

            let consecutiveDistances = [];
            let allDistances = [];
            let maxDistance = 0;
            for (let i = 0; i < imageCameras.length - 1; i++) {
                consecutiveDistances.push(imageCameras[i].position.distanceTo(imageCameras[i + 1].position));
                for (let j = i + 1; j < imageCameras.length; j++) {
                    const distance = imageCameras[i].position.distanceTo(imageCameras[j].position);
                    allDistances.push(distance);
                    maxDistance = Math.max(maxDistance, distance);
                }
            }
            if (consecutiveDistances.length > 0) {
                const medianDistance = consecutiveDistances.sort((a, b) => a - b)[Math.floor(consecutiveDistances.length / 2)];
                this.medianCameraDistance = medianDistance;
            }
            if (allDistances.length > 0) {
                // Set the transition speed such that the entire scene can be traversed in the specified duration
                this.transitionCameraSpeed = maxDistance / SCENE_TRANSITION_DURATION;
                // this.controls.movementSpeed = maxDistance / SCENE_TRANSITION_DURATION;
                // this.controls.update(0);
            }
        }
    };

    /**
     * Add a splat scene to the viewer and display any loading UI if appropriate.
     * @param {string} path Path to splat scene to be loaded
     * @param {object} options {
     *
     *         showLoadingUI:              Display a loading spinner while the scene is loading, defaults to true
     *
     *         position (Array<number>):   Position of the scene, acts as an offset from its default position, defaults to [0, 0, 0]
     *
     *         rotation (Array<number>):   Rotation of the scene represented as a quaternion, defaults to [0, 0, 0, 1]
     *
     *         scale (Array<number>):      Scene's scale, defaults to [1, 1, 1]
     *
     *         onProgress:                 Function to be called as file data are received, or other processing occurs
     *
     *         headers:                    Optional HTTP headers to be sent along with splat requests
     *
     *         environmentMapPath:         Optional URL to a environment map file (an .exr file)
     *
     *         imagePosesPath:             Optional URL to a JSON file containing camera poses for training images
     * }
     * @return {AbortablePromise}
     */
    addSplatScene(path, options = {}) {
        if (this.isLoadingOrUnloading()) {
            throw new Error('Cannot add splat scene while another load or unload is already in progress.');
        }

        if (this.isDisposingOrDisposed()) {
            throw new Error('Cannot add splat scene after dispose() is called.');
        }

        if (options.environmentMapPath) {
            this.loadEnvironmentMapForSegment(options.environmentMapPath);
        }

        if (options.imagePosesPath && (!this.imageCameras || this.imageCameras.length === 0)) {
            const imagePosesLoader = new ImagePosesLoader();
            imagePosesLoader.from_url(options.imagePosesPath, this.imagePosesLoaded);
        }

        const format = options.format !== undefined && options.format !== null ? options.format : SceneFormat.Gaus;
        const progressiveLoad = Viewer.isProgressivelyLoadable(format) && options.progressiveLoad;
        const showLoadingUI = options.showLoadingUI !== undefined && options.showLoadingUI !== null ? options.showLoadingUI : true;

        let loadingUITaskId = null;
        if (showLoadingUI) {
            this.loadingSpinner.removeAllTasks();
            loadingUITaskId = this.loadingSpinner.addTask('Downloading...');
        }
        const hideLoadingUI = () => {
            this.loadingProgressBar.hide();
            this.loadingSpinner.removeAllTasks();
        };

        const onProgressUIUpdate = (percentComplete, percentCompleteLabel, loaderStatus) => {
            if (showLoadingUI) {
                if (loaderStatus === LoaderStatus.Downloading) {
                    if (percentComplete === 100) {
                        this.loadingSpinner.setMessageForTask(loadingUITaskId, 'Download complete!');
                    } else {
                        if (progressiveLoad) {
                            this.loadingSpinner.setMessageForTask(loadingUITaskId, 'Downloading splats...');
                        } else {
                            const suffix = percentCompleteLabel ? `: ${percentCompleteLabel}` : `...`;
                            this.loadingSpinner.setMessageForTask(loadingUITaskId, `Downloading${suffix}`);
                        }
                    }
                } else if (loaderStatus === LoaderStatus.Processing) {
                    this.loadingSpinner.setMessageForTask(loadingUITaskId, 'Processing splats...');
                }
            }
        };

        let downloadDone = false;
        let downloadedPercentage = 0;
        const splatArraysAddedUIUpdate = (firstBuild, finalBuild) => {
            if (showLoadingUI) {
                if ((firstBuild && progressiveLoad) || (finalBuild && !progressiveLoad)) {
                    this.loadingSpinner.removeTask(loadingUITaskId);
                    if (!finalBuild && !downloadDone) this.loadingProgressBar.show();
                }
                if (progressiveLoad) {
                    if (finalBuild) {
                        downloadDone = true;
                        this.loadingProgressBar.hide();
                    } else {
                        this.loadingProgressBar.setProgress(downloadedPercentage);
                    }
                }
            }
        };

        const onProgress = (percentComplete, percentCompleteLabel, loaderStatus) => {
            downloadedPercentage = percentComplete;
            onProgressUIUpdate(percentComplete, percentCompleteLabel, loaderStatus);
            if (options.onProgress) options.onProgress(percentComplete, percentCompleteLabel, loaderStatus);
        };

        let currentEntry = null;
        const buildSection = (splatArray, firstBuild, finalBuild) => {
            if (!progressiveLoad && options.onProgress) options.onProgress(0, '0%', LoaderStatus.Processing);
            const addSplatArrayOptions = {
                rotation: options.rotation || options.orientation,
                position: options.position,
                scale: options.scale,
                viewBoundingBox: options.viewBoundingBox
            };
            return this.addSplatArrays(
                currentEntry,
                [splatArray],
                [addSplatArrayOptions],
                finalBuild,
                firstBuild && showLoadingUI,
                firstBuild && showLoadingUI,
                progressiveLoad,
                progressiveLoad
            ).then((entry) => {
                currentEntry = entry;
                if (!progressiveLoad && options.onProgress) options.onProgress(100, '100%', LoaderStatus.Processing);
                splatArraysAddedUIUpdate(firstBuild, finalBuild);
            });
        };

        const loadFunc = progressiveLoad
            ? this.downloadAndBuildSingleSplatSceneProgressiveLoad.bind(this)
            : this.downloadAndBuildSingleSplatSceneStandardLoad.bind(this);
        return loadFunc(path, format, buildSection.bind(this), onProgress, hideLoadingUI.bind(this), options.headers);
    }

    /**
     * Download a single splat scene, convert to splat buffer and then rebuild the viewer's splat mesh
     * by calling 'buildFunc' -- all before displaying the scene. Also sets/clears relevant instance synchronization objects,
     * and calls appropriate functions on success or failure.
     * @param {string} path Path to splat scene to be loaded
     * @param {SceneFormat} format Format of the splat scene file
     * @param {function} buildFunc Function to build the viewer's splat mesh with the downloaded splat buffer
     * @param {function} onProgress Function to be called as file data are received, or other processing occurs
     * @param {function} onException Function to be called when exception occurs
     * @param {object} headers Optional HTTP headers to pass to use for downloading splat scene
     * @return {AbortablePromise}
     */
    downloadAndBuildSingleSplatSceneStandardLoad(path, format, buildFunc, onProgress, onException, headers) {
        const downloadPromise = this.downloadSplatSceneToSplatArray(path, onProgress, format, headers);
        const downloadAndBuildPromise = abortablePromiseWithExtractedComponents(downloadPromise.abortHandler);

        downloadPromise
            .then((splatArray) => {
                this.removeSplatSceneDownloadPromise(downloadPromise);
                return buildFunc(splatArray, this.firstBuild, true).then(() => {
                    downloadAndBuildPromise.resolve();
                    this.clearSplatSceneDownloadAndBuildPromise();
                    this.firstBuild = false;
                });
            })
            .catch((e) => {
                if (onException) onException();
                this.clearSplatSceneDownloadAndBuildPromise();
                this.removeSplatSceneDownloadPromise(downloadPromise);
                const error = e instanceof AbortedPromiseError ? e : new Error(`Viewer::addSplatScene -> Could not load file ${path}`);
                downloadAndBuildPromise.reject(error);
            });

        this.addSplatSceneDownloadPromise(downloadPromise);
        this.setSplatSceneDownloadAndBuildPromise(downloadAndBuildPromise.promise);

        return downloadAndBuildPromise.promise;
    }

    /**
     * Download a single splat scene and convert to splat buffer in a progressive manner, allowing rendering as the file downloads.
     * As each section is downloaded, the viewer's splat mesh is rebuilt by calling 'buildFunc'
     * Also sets/clears relevant instance synchronization objects, and calls appropriate functions on success or failure.
     * @param {string} path Path to splat scene to be loaded
     * @param {SceneFormat} format Format of the splat scene file
     * @param {function} buildFunc Function to rebuild the viewer's splat mesh after a new splat buffer section is downloaded
     * @param {function} onDownloadProgress Function to be called as file data are received
     * @param {function} onDownloadException Function to be called when exception occurs at any point during the full download
     * @param {object} headers Optional HTTP headers to pass to use for downloading splat scene
     * @return {AbortablePromise}
     */
    downloadAndBuildSingleSplatSceneProgressiveLoad(path, format, buildFunc, onDownloadProgress, onDownloadException, headers) {
        let progressiveLoadedSectionBuildCount = 0;
        let progressiveLoadedSectionBuilding = false;
        const queuedProgressiveLoadSectionBuilds = [];

        const checkAndBuildProgressiveLoadSections = () => {
            if (queuedProgressiveLoadSectionBuilds.length > 0 && !progressiveLoadedSectionBuilding && !this.isDisposingOrDisposed()) {
                progressiveLoadedSectionBuilding = true;
                const queuedBuild = queuedProgressiveLoadSectionBuilds.shift();
                buildFunc(queuedBuild.splatArray, queuedBuild.firstBuild, queuedBuild.finalBuild).then(() => {
                    progressiveLoadedSectionBuilding = false;
                    if (queuedBuild.firstBuild) {
                        progressiveLoadFirstSectionBuildPromise.resolve();
                    } else if (queuedBuild.finalBuild) {
                        splatSceneDownloadAndBuildPromise.resolve();
                        this.clearSplatSceneDownloadAndBuildPromise();
                    }
                    if (queuedProgressiveLoadSectionBuilds.length > 0) {
                        delayedExecute(() => checkAndBuildProgressiveLoadSections());
                    }
                });
            }
        };

        const onProgressiveLoadSectionProgress = (splatArray, finalBuild) => {
            if (!this.isDisposingOrDisposed()) {
                if (
                    finalBuild ||
                    queuedProgressiveLoadSectionBuilds.length === 0 ||
                    splatArray.getSplatCount() > queuedProgressiveLoadSectionBuilds[0].splatArray.getSplatCount()
                ) {
                    queuedProgressiveLoadSectionBuilds.push({
                        splatArray,
                        firstBuild: progressiveLoadedSectionBuildCount === 0,
                        finalBuild
                    });
                    progressiveLoadedSectionBuildCount++;
                    checkAndBuildProgressiveLoadSections();
                }
            }
        };

        const splatSceneDownloadPromise = this.downloadSplatSceneToSplatArray(path, onDownloadProgress, format, headers);

        const progressiveLoadFirstSectionBuildPromise = abortablePromiseWithExtractedComponents(splatSceneDownloadPromise.abortHandler);
        const splatSceneDownloadAndBuildPromise = abortablePromiseWithExtractedComponents();

        this.addSplatSceneDownloadPromise(splatSceneDownloadPromise);
        this.setSplatSceneDownloadAndBuildPromise(splatSceneDownloadAndBuildPromise.promise);

        splatSceneDownloadPromise
            .then(() => {
                this.removeSplatSceneDownloadPromise(splatSceneDownloadPromise);
            })
            .catch((e) => {
                this.clearSplatSceneDownloadAndBuildPromise();
                this.removeSplatSceneDownloadPromise(splatSceneDownloadPromise);
                const error =
                    e instanceof AbortedPromiseError ? e : new Error(`Viewer::addSplatScene -> Could not load one or more scenes`);
                progressiveLoadFirstSectionBuildPromise.reject(error);
                if (onDownloadException) onDownloadException(error);
            });

        return progressiveLoadFirstSectionBuildPromise.promise;
    }

    /**
     * Add multiple splat scenes to the viewer and display any loading UI if appropriate.
     * @param {Array<object>} sceneOptions Array of per-scene options: {
     *
     *         path: Path to splat scene to be loaded
     *
     *         position (Array<number>):   Position of the scene, acts as an offset from its default position, defaults to [0, 0, 0]
     *
     *         rotation (Array<number>):   Rotation of the scene represented as a quaternion, defaults to [0, 0, 0, 1]
     *
     *         scale (Array<number>):      Scene's scale, defaults to [1, 1, 1]
     *
     *         headers:                    Optional HTTP headers to be sent along with splat requests
     *
     *         format (SceneFormat)        Optional, the format of the scene data (.ply, .ksplat, .splat). If not present, the
     *                                     file extension in 'path' will be used to determine the format (if it is present)
     * }
     * @param {boolean} showLoadingUI Display a loading spinner while the scene is loading, defaults to true
     * @param {function} onProgress Function to be called as file data are received
     * @return {AbortablePromise}
     */
    addSplatScenes(sceneOptions, showLoadingUI = true, onProgress = undefined) {
        if (this.isLoadingOrUnloading()) {
            throw new Error('Cannot add splat scene while another load or unload is already in progress.');
        }

        if (this.isDisposingOrDisposed()) {
            throw new Error('Cannot add splat scene after dispose() is called.');
        }

        const fileCount = sceneOptions.length;
        const percentComplete = [];

        let loadingUITaskId;
        if (showLoadingUI) {
            this.loadingSpinner.removeAllTasks();
            loadingUITaskId = this.loadingSpinner.addTask('Downloading...');
        }

        const onLoadProgress = (fileIndex, percent, percentLabel, loaderStatus) => {
            percentComplete[fileIndex] = percent;
            let totalPercent = 0;
            for (let i = 0; i < fileCount; i++) totalPercent += percentComplete[i] || 0;
            totalPercent = totalPercent / fileCount;
            percentLabel = `${totalPercent.toFixed(2)}%`;
            if (showLoadingUI) {
                if (loaderStatus === LoaderStatus.Downloading) {
                    this.loadingSpinner.setMessageForTask(
                        loadingUITaskId,
                        totalPercent === 100 ? `Download complete!` : `Downloading: ${percentLabel}`
                    );
                }
            }
            if (onProgress) onProgress(totalPercent, percentLabel, loaderStatus);
        };

        const baseDownloadPromises = [];
        const nativeDownloadPromises = [];
        for (let i = 0; i < sceneOptions.length; i++) {
            const options = sceneOptions[i];
            const format = options.format !== undefined && options.format !== null ? options.format : SceneFormat.Gaus;
            const baseDownloadPromise = this.downloadSplatSceneToSplatArray(
                options.path,
                onLoadProgress.bind(this, i),
                format,
                options.headers
            );
            baseDownloadPromises.push(baseDownloadPromise);
            nativeDownloadPromises.push(baseDownloadPromise.promise);
        }

        const downloadAndBuildPromise = new AbortablePromise(
            (resolve, reject) => {
                Promise.all(nativeDownloadPromises)
                    .then(async (splatArrays) => {
                        if (showLoadingUI) this.loadingSpinner.removeTask(loadingUITaskId);
                        if (onProgress) onProgress(0, '0%', LoaderStatus.Processing);
                        for (let i = 0; i < splatArrays.length; i++) {
                            await this.addSplatArrays(
                                null,
                                [splatArrays[i]],
                                [sceneOptions[i]],
                                true,
                                showLoadingUI,
                                showLoadingUI,
                                false,
                                false
                            );
                        }
                        if (onProgress) onProgress(100, '100%', LoaderStatus.Processing);
                        this.clearSplatSceneDownloadAndBuildPromise();
                        resolve();
                    })
                    .catch((e) => {
                        if (showLoadingUI) this.loadingSpinner.removeTask(loadingUITaskId);
                        this.clearSplatSceneDownloadAndBuildPromise();
                        const error =
                            e instanceof AbortedPromiseError
                                ? e
                                : new Error(`Viewer::addSplatScenes -> Could not load one or more splat scenes.`);
                        reject(error);
                    })
                    .finally(() => {
                        this.removeSplatSceneDownloadPromise(downloadAndBuildPromise);
                    });
            },
            (reason) => {
                for (let baseDownloadPromise of baseDownloadPromises) {
                    baseDownloadPromise.abort(reason);
                }
            }
        );
        this.addSplatSceneDownloadPromise(downloadAndBuildPromise);
        this.setSplatSceneDownloadAndBuildPromise(downloadAndBuildPromise);
        return downloadAndBuildPromise;
    }

    /**
     * Load scenes on-demand based on camera position using a JSON manifest file.
     * Scenes are loaded when the camera enters their view bounding box.
     *
     * @param {string} manifestUrl URL to JSON manifest file containing segment bounding boxes
     * @param {object} options Optional settings for scene loading:
     *         showLoadingUI: Show loading spinner (default: true)
     *         latitude: Optional starting latitude in degrees (requires observer_coordinates in manifest)
     *         longitude: Optional starting longitude in degrees (requires observer_coordinates in manifest)
     *         altitude: Optional starting altitude in meters (requires observer_coordinates in manifest)
     *         heading: Optional true north heading in degrees (0=North, 90=East, 180=South, 270=West)
     *         Other options passed to addSplatScene
     * @return {Promise} Resolves with manifest data including segments, imagePosesUrl, and observerCoordinates
     */
    addSplatScenesFromManifest(manifestUrl, options = {}) {
        return new Promise((resolve, reject) => {
            fetchWithProgress(manifestUrl, undefined, true)
                .then((buffer) => {
                    const decoder = new TextDecoder('utf-8');
                    const jsonString = decoder.decode(buffer);
                    const manifest = JSON.parse(jsonString);

                    // Store options for lazy loading
                    this.lazyLoadOptions = options;

                    // Store segment loading callbacks
                    this.onSegmentLoadingStart = options.onSegmentLoadingStart || null;
                    this.onSegmentLoadingEnd = options.onSegmentLoadingEnd || null;

                    // Store image poses URL if provided
                    this.imagePosesUrl = manifest.image_poses_url || null;

                    // Store environment map URL if provided
                    this.environmentMapUrl = manifest.environment_map_url || null;

                    // Store observer coordinates if provided (for geodetic to ENU conversion)
                    // Format: [latitude, longitude, altitude] in degrees and meters
                    this.observerCoordinates = manifest.observer_coordinates || null;

                    // If starting coordinates are provided in options and observer coordinates exist in manifest,
                    // calculate starting camera position in ENU (then transform to Three.js Y-up)
                    if (
                        options.latitude !== undefined &&
                        options.longitude !== undefined &&
                        options.altitude !== undefined &&
                        this.observerCoordinates
                    ) {
                        const [observerLat, observerLon, observerAlt] = this.observerCoordinates;
                        const enu = geodeticToENU(
                            options.latitude,
                            options.longitude,
                            options.altitude,
                            observerLat,
                            observerLon,
                            observerAlt
                        );

                        // ENU is Z-up (East=X, North=Y, Up=Z), Three.js is Y-up
                        // Apply the same transformation as used for bounding boxes and scene data
                        const enuPosition = new THREE.Vector3(enu.east, enu.north, enu.up);
                        const worldTransform = new THREE.Quaternion(0.5, 0.5, 0.5, -0.5);
                        enuPosition.applyQuaternion(worldTransform);

                        this.camera.position.copy(enuPosition);
                        this.initialCameraPosition.copy(enuPosition);

                        // If heading is provided, compute the look-at direction
                        // Heading is in degrees from true north (0=North, 90=East, 180=South, 270=West)
                        if (options.heading !== undefined) {
                            const headingRad = (options.heading * Math.PI) / 180.0;

                            // In ENU (Z-up): North is +Y, East is +X
                            // Forward direction for heading θ: (sin(θ), cos(θ), 0)
                            // Heading 0° = North = (0, 1, 0)
                            // Heading 90° = East = (1, 0, 0)
                            const forwardENU = new THREE.Vector3(Math.sin(headingRad), Math.cos(headingRad), 0);

                            // Transform forward direction to Three.js Y-up coordinates
                            forwardENU.applyQuaternion(worldTransform);

                            // Set look-at point as position + forward direction
                            const lookAtPoint = enuPosition.clone().add(forwardENU);
                            this.camera.lookAt(lookAtPoint);
                            this.initialCameraLookAt.copy(lookAtPoint);

                            // Keep camera matrices and controls' internal state in sync with the new look direction
                            this.camera.updateMatrixWorld(true);
                            if (this.controls && typeof this.controls.updateLookDirection === 'function') {
                                this.controls.updateLookDirection();
                            }
                        }

                        this.explicitStartPosition = true;
                    }

                    this.lazyLoadSegments = new Map();
                    for (const segmentData of manifest.segments) {
                        const minCorner = new THREE.Vector3().fromArray(segmentData.view_bounding_volume.min_corner);
                        const maxCorner = new THREE.Vector3().fromArray(segmentData.view_bounding_volume.max_corner);

                        // Create bounding box (need to re-compute min/max after transform)
                        const viewBoundingBox = new THREE.Box3(minCorner, maxCorner);

                        // Transform the camera pose from ENU to EUN reference frame (Yugo uses Z-up, Three.js uses Y-up)
                        // The same transformation is applied to the scene in SplatMesh class
                        const worldTransform = new THREE.Quaternion(0.5, 0.5, 0.5, -0.5);
                        const transform = new THREE.Matrix4().makeRotationFromQuaternion(worldTransform);

                        viewBoundingBox.applyMatrix4(transform);

                        // Per-segment environment map URL (optional)
                        const environmentMapUrl = segmentData.environment_map_url ?? this.environmentMapUrl ?? null;

                        const segment = new Segment(
                            segmentData.segment_index,
                            segmentData.segment_url,
                            viewBoundingBox,
                            this.preloadingBoundsDistance,
                            this.unloadingBoundsDistance,
                            environmentMapUrl
                        );
                        this.lazyLoadSegments.set(segment.index, segment);
                    }

                    // Auto-load image poses if URL is provided
                    if (this.imagePosesUrl && (!this.imageCameras || this.imageCameras.length === 0)) {
                        const imagePosesLoader = new ImagePosesLoader();
                        imagePosesLoader.from_url(this.imagePosesUrl, this.imagePosesLoaded);
                    }

                    resolve({
                        segments: this.lazyLoadSegments,
                        imagePosesUrl: this.imagePosesUrl,
                        environmentMapUrl: this.environmentMapUrl,
                        observerCoordinates: this.observerCoordinates
                    });
                })
                .catch((error) => {
                    reject(new Error(`Failed to load manifest from ${manifestUrl}: ${error.message}`));
                });
        });
    }

    /**
     * Queue a lazy-load segment for loading.
     * @param {number} segmentIndex The segment index to load
     * @param {boolean} [prioritized=false] When true the segment is loaded before other queued items
     *        (e.g. because the camera is already inside it)
     * @return {Promise} Resolves when the scene is loaded
     */
    loadLazySegment(segmentIndex, prioritized = false) {
        return new Promise((resolve, reject) => {
            if (!this.lazyLoadSegments) {
                reject(new Error('No manifest loaded'));
                return;
            }

            const segment = this.lazyLoadSegments.get(segmentIndex);
            if (!segment) {
                reject(new Error(`Segment ${segmentIndex} not found`));
                return;
            }

            // Skip if already loading, loaded, or queued for loading
            if (!segment.canLoad()) {
                resolve();
                return;
            }

            // Check if already in queue
            const alreadyQueued = this.lazyLoadQueue.some((item) => item.type === 'load' && item.segmentIndex === segmentIndex);
            if (alreadyQueued) {
                resolve();
                return;
            }

            // Mark as queued to prevent duplicate queue entries
            segment.markQueued();

            this.lazyLoadQueue.push({ type: 'load', segmentIndex, prioritized, resolve, reject });
            this.processLazyLoadQueue();
        });
    }

    /**
     * Queue a lazy-load segment for unloading.
     * @param {number} segmentIndex The segment index to unload
     * @return {Promise} Resolves when the scene is unloaded
     */
    unloadLazySegment(segmentIndex) {
        return new Promise((resolve, reject) => {
            if (!this.lazyLoadSegments) {
                reject(new Error('No manifest loaded'));
                return;
            }

            const segment = this.lazyLoadSegments.get(segmentIndex);
            if (!segment) {
                reject(new Error(`Segment ${segmentIndex} not found`));
                return;
            }

            // Skip if not loaded
            if (!segment.canUnload()) {
                resolve();
                return;
            }

            // Check if already in queue for unloading
            const alreadyQueued = this.lazyLoadQueue.some((item) => item.type === 'unload' && item.segmentIndex === segmentIndex);
            if (alreadyQueued) {
                resolve();
                return;
            }

            // Mark as queued for unloading
            segment.markQueuedUnload();

            this.lazyLoadQueue.push({ type: 'unload', segmentIndex, resolve, reject });
            this.processLazyLoadQueue();
        });
    }

    /**
     * Process the lazy load/unload queue one item at a time.
     */
    async processLazyLoadQueue() {
        if (this.lazyLoadQueueProcessing || this.lazyLoadQueue.length === 0) return;

        this.lazyLoadQueueProcessing = true;

        while (this.lazyLoadQueue.length > 0) {
            let item;
            if (this.etaBasedLoading) {
                // First check for a prioritized item (camera is inside this segment's bounds)
                let itemIndex = this.lazyLoadQueue.findIndex((qi) => qi.prioritized);

                // Then prioritize unload items
                if (itemIndex === -1) {
                    itemIndex = this.lazyLoadQueue.findIndex((qi) => qi.type === 'unload');
                }

                if (itemIndex === -1) {
                    // No prioritized or unload items; re-compute ETAs and find the most urgent load item
                    this._recomputeQueueETAs();

                    let bestETA = Infinity;
                    let bestDist = Infinity;
                    for (let i = 0; i < this.lazyLoadQueue.length; i++) {
                        const qi = this.lazyLoadQueue[i];
                        if (qi.type === 'load') {
                            const segment = this.lazyLoadSegments.get(qi.segmentIndex);
                            const eta = segment ? segment.eta : Infinity;
                            const dist = segment ? segment.distanceToCenter : Infinity;
                            if (eta < bestETA || (eta === bestETA && dist < bestDist)) {
                                bestETA = eta;
                                bestDist = dist;
                                itemIndex = i;
                            }
                        }
                    }
                    if (itemIndex === -1) itemIndex = 0;
                }

                [item] = this.lazyLoadQueue.splice(itemIndex, 1);
            } else {
                item = this.lazyLoadQueue.shift();
            }

            try {
                if (item.type === 'load') {
                    await this._executeLazyLoad(item.segmentIndex);
                } else if (item.type === 'unload') {
                    await this._executeLazyUnload(item.segmentIndex);
                }
                item.resolve();
            } catch (error) {
                item.reject(error);
            }
        }

        this.lazyLoadQueueProcessing = false;
    }

    /**
     * Re-compute ETAs for all load items in the lazy load queue based on current camera state.
     */
    _recomputeQueueETAs = (function () {
        const cameraPosition = new THREE.Vector3();
        const toNearest = new THREE.Vector3();

        return function () {
            if (!this.camera || !this.lazyLoadSegments) return;

            cameraPosition.copy(this.camera.position);

            for (const item of this.lazyLoadQueue) {
                if (item.type === 'load') {
                    const segment = this.lazyLoadSegments.get(item.segmentIndex);
                    if (segment) {
                        segment.computeETA(cameraPosition, this.cameraVelocity, toNearest);
                    }
                }
            }
        };
    })();

    /**
     * Initialize the ParseWorker for off-main-thread parsing.
     * Call this before using parseInWorker().
     * @return {Promise} Resolves when worker is ready
     */
    initParseWorker() {
        if (this.parseWorker) {
            return Promise.resolve();
        }

        return new Promise((resolve, reject) => {
            try {
                this.parseWorker = createParseWorker();

                this.parseWorker.onerror = (error) => {
                    console.error('ParseWorker error:', error);
                    this.parseWorkerReady = false;
                };

                this.parseWorker.onmessage = (e) => {
                    if (e.data.status === 'ready') {
                        this.parseWorkerReady = true;
                        resolve();
                    }
                };

                // Send init message to worker
                this.parseWorker.postMessage({ task: 'init' });
            } catch (error) {
                console.warn('Failed to create ParseWorker:', error);
                reject(error);
            }
        });
    }

    /**
     * Dispose the ParseWorker.
     */
    disposeParseWorker() {
        if (this.parseWorker) {
            this.parseWorker.terminate();
            this.parseWorker = null;
            this.parseWorkerReady = false;
        }
    }

    /**
     * Parse a GAUS file in the worker and return a SplatArray.
     * @param {ArrayBuffer} arrayBuffer The raw file data
     * @return {Promise<SplatArray>} Resolves with the parsed SplatArray
     */
    parseInWorker(arrayBuffer) {
        return new Promise((resolve, reject) => {
            if (!this.parseWorkerReady) {
                reject(new Error('ParseWorker not initialized'));
                return;
            }

            const handler = (e) => {
                this.parseWorker.removeEventListener('message', handler);

                if (e.data.status === 'success') {
                    const { splatArrayData, metadata } = e.data;
                    console.log(`ParseWorker: parse=${metadata.parseTimeMs.toFixed(1)}ms, convert=${metadata.convertTimeMs.toFixed(1)}ms`);

                    // Create SplatArray from the transferred data
                    const splatArray = SplatArray.createFromTransferredData(splatArrayData);
                    resolve(splatArray);
                } else if (e.data.status === 'error') {
                    const error = new Error(e.data.error.message);
                    error.name = e.data.error.name;
                    reject(error);
                }
            };

            this.parseWorker.addEventListener('message', handler);

            // Transfer the ArrayBuffer to the worker (zero-copy)
            this.parseWorker.postMessage(
                {
                    task: 'parse',
                    arrayBuffer: arrayBuffer
                },
                [arrayBuffer]
            );
        });
    }

    /**
     * Execute the actual loading of a lazy segment (internal).
     * Uses ParseWorker for off-main-thread parsing if available.
     * @param {number} segmentIndex The segment index to load
     */
    async _executeLazyLoad(segmentIndex) {
        const segment = this.lazyLoadSegments.get(segmentIndex);
        if (!segment) throw new Error(`Segment ${segmentIndex} not found`);

        // Check if segment state changed while queued (e.g., already loaded by another mechanism)
        if (segment.status !== SegmentStatus.Queued) {
            console.log(`Segment ${segmentIndex} status changed to ${segment.status}, skipping load`);
            return;
        }

        segment.markLoading();

        const options = {
            ...this.lazyLoadOptions,
            viewBoundingBox: segment.viewBoundingBox,
            showLoadingUI: false // Suppress loading UI for lazy loads
        };

        try {
            let meshId;
            // Try worker-based loading first for non-blocking parsing
            if (this.parseWorkerReady) {
                const entry = await this._executeLazyLoadWithWorker(segment, options);
                meshId = entry.id;
            } else {
                // Fallback to main thread parsing
                const meshIdBefore = this.nextSplatMeshId;
                await this.addSplatScene(segment.url, options);
                // addSplatScene increments nextSplatMeshId; the entry created has the ID from before the call
                meshId = meshIdBefore;
            }
            segment.markLoaded(meshId);

            // Load environment map if segment has one and it differs from current
            if (segment.environmentMapUrl && segment.environmentMapUrl !== this.currentEnvironmentMapUrl) {
                this.loadEnvironmentMapForSegment(segment.environmentMapUrl);
            }

            console.log(`Loaded segment ${segmentIndex}`);
        } catch (error) {
            segment.markPending(); // Reset on failure so it can be retried
            throw error;
        }
    }

    /**
     * Execute lazy loading using the ParseWorker for off-main-thread parsing.
     * @param {Segment} segment The segment to load
     * @param {Object} options Loading options
     */
    async _executeLazyLoadWithWorker(segment, options) {
        // Fetch the raw file data
        const fetchAbortable = fetchWithProgress(segment.url, null, true, options.headers);
        this.currentLazyFetchAbortable = fetchAbortable;
        let arrayBuffer;
        try {
            arrayBuffer = await fetchAbortable;
        } finally {
            this.currentLazyFetchAbortable = null;
        }

        // Parse in worker (off main thread)
        const splatArray = await this.parseInWorker(arrayBuffer);

        // Build splat buffer options for addSplatArrays
        const splatArrayOptions = {
            position: options.position,
            rotation: options.rotation,
            scale: options.scale,
            viewBoundingBox: options.viewBoundingBox
        };

        // Add the parsed buffer to a new SplatMeshEntry
        const entry = await this.addSplatArrays(null, [splatArray], [splatArrayOptions], true, false, false, false, false, true);
        return entry;
    }

    /**
     * Execute the actual unloading of a lazy segment (internal).
     * @param {number} segmentIndex The segment index to unload
     */
    async _executeLazyUnload(segmentIndex) {
        const segment = this.lazyLoadSegments.get(segmentIndex);
        if (!segment) throw new Error(`Segment ${segmentIndex} not found`);

        // Check if segment state changed while queued
        if (segment.status !== SegmentStatus.QueuedUnload) {
            console.log(`Segment ${segmentIndex} status changed to ${segment.status}, skipping unload`);
            return;
        }

        const meshId = segment.sceneIndex;
        if (meshId === null || meshId === undefined) {
            throw new Error(`Segment ${segmentIndex} has no valid mesh ID`);
        }

        segment.markUnloading();

        try {
            await this.removeSplatMeshEntry(meshId);
            segment.markPending();
            console.log(`Unloaded segment ${segmentIndex}`);
        } catch (error) {
            // Reset to loaded on failure so state is consistent
            segment.markLoaded(meshId);
            throw error;
        }
    }

    /**
     * Download a splat scene and convert to SplatArray instance.
     * @param {string} path Path to splat scene to be loaded
     * @param {function} onProgress Function to be called as file data are received
     * @param {string} format File format of the scene
     * @param {object} headers Optional HTTP headers to pass to use for downloading splat scene
     * @return {AbortablePromise}
     */
    downloadSplatSceneToSplatArray(path, onProgress = undefined, format, headers) {
        try {
            if (format === SceneFormat.Gaus) {
                return GausLoader.loadFromURL(path, onProgress, headers);
            }
        } catch (e) {
            throw this.updateError(e, null);
        }

        throw new Error(`Viewer::downloadSplatSceneToSplatArray -> File format not supported: ${path}`);
    }

    static isProgressivelyLoadable(format) {
        return format === SceneFormat.Gaus;
    }

    /**
     * Add one or more instances of SplatArray to a SplatMeshEntry and set up the sorting web worker.
     * If no entry is provided, a new one is created.
     * @param {SplatMeshEntry} [entry] Optional existing entry (for progressive loading)
     * @param {Array<SplatArray>} splatArrays SplatArray instances
     * @param {Array<object>} splatArrayOptions Options per buffer
     * @param {boolean} finalBuild Is this the final build for this entry?
     * @param {boolean} showLoadingUI Show loading spinner
     * @param {boolean} showLoadingUIForSplatTreeBuild Show spinner during splat tree build
     * @param {boolean} replaceExisting Replace existing buffers in the entry's mesh
     * @param {boolean} enableRenderBeforeFirstSort Allow rendering before first sort completes
     * @param {boolean} preserveVisibleRegion Preserve visible region during build
     * @return {Promise<SplatMeshEntry>} Resolves with the entry
     */
    addSplatArrays = (function () {
        return function (
            entry,
            splatArrays,
            splatArrayOptions = [],
            finalBuild = true,
            showLoadingUI = true,
            showLoadingUIForSplatTreeBuild = true,
            replaceExisting = false,
            enableRenderBeforeFirstSort = false,
            preserveVisibleRegion = true,
            chunkedUpload = false
        ) {
            if (this.isDisposingOrDisposed()) return Promise.resolve();

            // Create a new entry if none provided
            if (!entry) {
                const id = this.nextSplatMeshId++;
                const viewBoundingBox = splatArrayOptions[0]?.viewBoundingBox || null;
                entry = this.createSplatMeshEntry(id, viewBoundingBox);
            }

            let splatProcessingTaskId = null;
            const removeSplatProcessingTask = () => {
                if (splatProcessingTaskId !== null) {
                    this.loadingSpinner.removeTask(splatProcessingTaskId);
                    splatProcessingTaskId = null;
                }
            };

            entry.splatRenderReady = false;
            return new Promise((resolve) => {
                if (showLoadingUI) {
                    splatProcessingTaskId = this.loadingSpinner.addTask('Processing splats...');
                }
                delayedExecute(async () => {
                    if (this.isDisposingOrDisposed()) {
                        resolve(entry);
                    } else {
                        let start = performance.now();
                        const buildResults = this.addSplatArraysToMesh(
                            entry,
                            splatArrays,
                            splatArrayOptions,
                            finalBuild,
                            showLoadingUIForSplatTreeBuild,
                            replaceExisting,
                            preserveVisibleRegion,
                            chunkedUpload
                        );

                        let end = performance.now();
                        if (Constants.PERF_LOG) console.log(`addSplatArraysToMesh took ${end - start} ms`);

                        const maxSplatCount = entry.splatMesh.getMaxSplatCount();
                        if (entry.sortWorker && entry.sortWorker.maxSplatCount !== maxSplatCount) {
                            this.disposeSortWorkerForEntry(entry);
                        }
                        // If we aren't calculating the splat distances from the center on the GPU, the sorting worker needs
                        // splat centers and transform indexes so that it can calculate those distance values.
                        if (!this.gpuAcceleratedSort) {
                            entry.preSortMessages.push({
                                centers: buildResults.centers.buffer,
                                sceneIndexes: buildResults.sceneIndexes.buffer,
                                range: {
                                    from: buildResults.from,
                                    to: buildResults.to,
                                    count: buildResults.count
                                }
                            });
                        }
                        const sortWorkerSetupPromise =
                            !entry.sortWorker && maxSplatCount > 0 ? this.setupSortWorker(entry) : Promise.resolve();
                        sortWorkerSetupPromise.then(() => {
                            if (this.isDisposingOrDisposed()) return;
                            this.runSplatSort(entry, true, true).then((sortRunning) => {
                                if (!entry.sortWorker || !sortRunning) {
                                    entry.splatRenderReady = true;
                                    removeSplatProcessingTask();
                                    resolve(entry);
                                } else {
                                    if (enableRenderBeforeFirstSort) {
                                        entry.splatRenderReady = true;
                                    } else {
                                        entry.runAfterNextSort.push(() => {
                                            entry.splatRenderReady = true;
                                        });
                                    }
                                    entry.runAfterNextSort.push(() => {
                                        removeSplatProcessingTask();
                                        resolve(entry);
                                    });
                                }
                            });
                        });
                    }
                }, true);
            });
        };
    })();

    addEnvironmentMap = (envMapBuffer) => {
        if (this.isDisposingOrDisposed() || !this.renderer) {
            return;
        }

        // Reuse or create loader
        if (!this.envMapLoader) {
            this.envMapLoader = new EnvironmentMapLoader(this.renderer);
        }

        const blobURL = URL.createObjectURL(new Blob([envMapBuffer], { type: 'application/octet-stream' }));
        this.currentEnvironmentMapUrl = blobURL;

        this.envMapLoader.from_url(blobURL, (envMapRenderTarget, envMapTexture) => {
            // Guard against stale response or disposed viewer — EnvironmentMapLoader did not dispose these on this path, so we must.
            if (
                this.isDisposingOrDisposed() ||
                !this.threeScene ||
                blobURL !== this.currentEnvironmentMapUrl
            ) {
                envMapRenderTarget.dispose();
                envMapTexture.dispose();
                URL.revokeObjectURL(blobURL);
                return;
            }

            // Dispose previous env map resources before replacing.
            this.currentEnvMapRenderTarget?.dispose();
            this.currentEnvMapTexture?.dispose();

            // Viewer takes ownership of render target and raw texture.
            this.currentEnvMapRenderTarget = envMapRenderTarget;
            this.currentEnvMapTexture = envMapTexture;
            this.threeScene.background = envMapRenderTarget.texture;
            this.threeScene.environment = envMapRenderTarget.texture;
            this.threeScene.backgroundRotation.set(0, -Math.PI / 2, -Math.PI);
            URL.revokeObjectURL(blobURL);
        });
    };

    /**
     * Load an environment map from URL if it differs from the current one.
     * Used by lazy-loaded segments to apply their environment maps.
     * @param {string} url URL of the environment map to load
     */
    loadEnvironmentMapForSegment(url) {
        if (this.isDisposingOrDisposed() || !this.renderer) {
            return;
        }

        // Update URL immediately to prevent duplicate loads from concurrent segment loads
        this.currentEnvironmentMapUrl = url;

        // Reuse or create loader to avoid leaking PMREMGenerator resources
        if (!this.envMapLoader) {
            this.envMapLoader = new EnvironmentMapLoader(this.renderer);
        }

        this.envMapLoader.from_url(url, (envMapRenderTarget, envMapTexture) => {
            // Guard against stale response from out-of-order completion — dispose both since EnvironmentMapLoader did not.
            if (
                this.isDisposingOrDisposed() ||
                !this.threeScene ||
                url !== this.currentEnvironmentMapUrl
            ) {
                envMapRenderTarget.dispose();
                envMapTexture.dispose();
                return;
            }

            // Dispose previous env map resources before replacing.
            this.currentEnvMapRenderTarget?.dispose();
            this.currentEnvMapTexture?.dispose();

            // Viewer takes ownership of render target and raw texture.
            this.currentEnvMapRenderTarget = envMapRenderTarget;
            this.currentEnvMapTexture = envMapTexture;
            this.threeScene.background = envMapRenderTarget.texture;
            this.threeScene.environment = envMapRenderTarget.texture;
            this.threeScene.backgroundRotation.set(0, -Math.PI / 2, -Math.PI);
        });
    }

    addImagePoses = (imagePosesBuffer) => {
        const imagePosesLoader = new ImagePosesLoader();

        const blobURL = URL.createObjectURL(new Blob([imagePosesBuffer], { type: 'application/octet-stream' }));
        imagePosesLoader.from_url(blobURL, this.imagePosesLoaded);
    };

    /**
     * Add one or more instances of SplatArray to the SplatMesh instance managed by the viewer. By default, this function is additive;
     * all splat buffers contained by the viewer's splat mesh before calling this function will be preserved. This behavior can be
     * changed by passing 'true' for 'replaceExisting'.
     * @param {Array<SplatArray>} splatArrays SplatArray instances
     * @param {Array<object>} splatArrayOptions Array of options objects: {
     *         position (Array<number>):   Position of the scene, acts as an offset from its default position, defaults to [0, 0, 0]
     *
     *         rotation (Array<number>):   Rotation of the scene represented as a quaternion, defaults to [0, 0, 0, 1]
     *
     *         scale (Array<number>):      Scene's scale, defaults to [1, 1, 1]
     * }
     * @param {boolean} finalBuild Will the splat mesh be in its final state after this build?
     * @param {boolean} showLoadingUIForSplatTreeBuild Whether or not to show the loading spinner during construction of the splat tree.
     * @return {object} Object containing info about the splats that are updated
     */
    addSplatArraysToMesh = (function () {
        let splatOptimizingTaskId;

        return function (
            entry,
            splatArrays,
            splatArrayOptions,
            finalBuild = true,
            showLoadingUIForSplatTreeBuild = false,
            replaceExisting = false,
            preserveVisibleRegion = true,
            chunkedUpload = false
        ) {
            if (this.isDisposingOrDisposed()) return;

            const splatMesh = entry.splatMesh;
            let allSplatArrays = [];
            let allSplatArrayOptions = [];
            if (!replaceExisting) {
                allSplatArrays = splatMesh.scenes.map((scene) => scene.splatArray) || [];
                allSplatArrayOptions = splatMesh.sceneOptions ? splatMesh.sceneOptions.map((sceneOptions) => sceneOptions) : [];
            }
            allSplatArrays.push(...splatArrays);
            allSplatArrayOptions.push(...splatArrayOptions);
            if (this.renderer) splatMesh.setRenderer(this.renderer);
            const onSplatTreeIndexesUpload = (finished) => {
                if (this.isDisposingOrDisposed()) return;
                const splatCount = splatMesh.getSplatCount();
                if (showLoadingUIForSplatTreeBuild && splatCount >= MIN_SPLAT_COUNT_TO_SHOW_SPLAT_TREE_LOADING_SPINNER) {
                    if (!finished && !splatOptimizingTaskId) {
                        this.loadingSpinner.setMinimized(true, true);
                        splatOptimizingTaskId = this.loadingSpinner.addTask('Optimizing data structures...');
                    }
                }
            };
            const onSplatTreeReady = (finished) => {
                if (this.isDisposingOrDisposed()) return;
                if (finished && splatOptimizingTaskId) {
                    this.loadingSpinner.removeTask(splatOptimizingTaskId);
                    splatOptimizingTaskId = null;
                }
            };
            let start = performance.now();
            const buildResults = splatMesh.build(
                allSplatArrays,
                allSplatArrayOptions,
                true,
                finalBuild,
                onSplatTreeIndexesUpload,
                onSplatTreeReady,
                preserveVisibleRegion,
                chunkedUpload,
                this.disableSplatTree
            );
            let end = performance.now();
            if (Constants.PERF_LOG) console.log(`SplatMesh.build took ${end - start} ms`);
            if (finalBuild && !chunkedUpload) splatMesh.freeIntermediateSplatData();
            return buildResults;
        };
    })();

    /**
     * Set up the splat sorting web worker for a SplatMeshEntry.
     * @param {SplatMeshEntry} entry The entry to set up sorting for
     * @return {Promise}
     */
    setupSortWorker(entry) {
        if (this.isDisposingOrDisposed()) return Promise.resolve();
        return new Promise((resolve) => {
            const splatMesh = entry.splatMesh;
            const DistancesArrayType = this.integerBasedSort ? Int32Array : Float32Array;
            const splatCount = splatMesh.getSplatCount();
            const maxSplatCount = splatMesh.getMaxSplatCount();
            entry.sortWorker = createSortWorker(
                maxSplatCount,
                this.sharedMemoryForWorkers,
                this.enableSIMDInSort,
                this.integerBasedSort,
                false,
                this.splatSortDistanceMapPrecision
            );
            entry.sortWorker.onmessage = (e) => {
                if (e.data.sortDone) {
                    entry.sortRunning = false;
                    if (this.sharedMemoryForWorkers) {
                        entry.splatMesh.updateRenderIndexes(entry.sortWorkerSortedIndexes, e.data.splatRenderCount);
                    } else {
                        const sortedIndexes = new Uint32Array(e.data.sortedIndexes.buffer, 0, e.data.splatRenderCount);
                        entry.splatMesh.updateRenderIndexes(sortedIndexes, e.data.splatRenderCount);
                    }

                    entry.lastSplatSortCount = entry.splatSortCount;

                    this.lastSortTime = e.data.sortTime;
                    entry.sortPromiseResolver();
                    entry.sortPromiseResolver = null;
                    if (entry.id === this.activeSplatMeshId) {
                        this.forceRenderNextFrame();
                    }
                    if (entry.runAfterNextSort.length > 0) {
                        entry.runAfterNextSort.forEach((func) => {
                            func();
                        });
                        entry.runAfterNextSort.length = 0;
                    }
                } else if (e.data.sortCanceled) {
                    entry.sortRunning = false;
                } else if (e.data.sortSetupPhase1Complete) {
                    if (this.sharedMemoryForWorkers) {
                        entry.sortWorkerSortedIndexes = new Uint32Array(
                            e.data.sortedIndexesBuffer,
                            e.data.sortedIndexesOffset,
                            maxSplatCount
                        );
                        entry.sortWorkerIndexesToSort = new Uint32Array(
                            e.data.indexesToSortBuffer,
                            e.data.indexesToSortOffset,
                            maxSplatCount
                        );
                        entry.sortWorkerPrecomputedDistances = new DistancesArrayType(
                            e.data.precomputedDistancesBuffer,
                            e.data.precomputedDistancesOffset,
                            maxSplatCount
                        );
                        entry.sortWorkerTransforms = new Float32Array(
                            e.data.transformsBuffer,
                            e.data.transformsOffset,
                            Constants.MaxScenes * 16
                        );
                    } else {
                        entry.sortWorkerIndexesToSort = new Uint32Array(maxSplatCount);
                        entry.sortWorkerPrecomputedDistances = new DistancesArrayType(maxSplatCount);
                        entry.sortWorkerTransforms = new Float32Array(Constants.MaxScenes * 16);
                    }
                    for (let i = 0; i < splatCount; i++) entry.sortWorkerIndexesToSort[i] = i;
                    entry.sortWorker.maxSplatCount = maxSplatCount;

                    resolve();
                }
            };
        });
    }

    disposeSortWorkerForEntry(entry) {
        if (entry.sortWorker) {
            if (entry.sortWorker.blobUrl) URL.revokeObjectURL(entry.sortWorker.blobUrl);
            entry.sortWorker.terminate();
        }
        entry.sortWorker = null;
        entry.sortWorkerIndexesToSort = null;
        entry.sortWorkerSortedIndexes = null;
        entry.sortWorkerPrecomputedDistances = null;
        entry.sortWorkerTransforms = null;
        entry.sortPromise = null;
        if (entry.sortPromiseResolver) {
            entry.sortPromiseResolver();
            entry.sortPromiseResolver = null;
        }
        entry.preSortMessages = [];
        entry.sortRunning = false;
    }

    /**
     * Remove a SplatMeshEntry by its ID. Disposes the sort worker and mesh, removes from the Map.
     * If the removed entry was the active one, switches to another or sets null.
     * @param {number} entryId The ID of the entry to remove
     */
    removeSplatMeshEntry(entryId) {
        const entry = this.splatMeshes.get(entryId);
        if (!entry) return Promise.resolve();

        // Wait for any in-flight sort to complete before disposing
        const sortPromise = entry.sortPromise || Promise.resolve();
        return sortPromise.then(() => {
            this.disposeSortWorkerForEntry(entry);
            entry.splatMesh.dispose();
            this.splatMeshes.delete(entryId);

            // If this was the active mesh, pick another or set null
            if (this.activeSplatMeshId === entryId) {
                if (this.splatMeshes.size > 0) {
                    this.activeSplatMeshId = this.splatMeshes.keys().next().value;
                } else {
                    this.activeSplatMeshId = null;
                }
                if (this.onSplatMeshChangedCallback) this.onSplatMeshChangedCallback();
            }
            this.forceRenderNextFrame();
        });
    }

    /**
     * @deprecated Use removeSplatMeshEntry(entryId) instead.
     */
    removeSplatScene(entryId) {
        return this.removeSplatMeshEntry(entryId);
    }

    /**
     * Start self-driven mode
     */
    start() {
        if (this.selfDrivenMode) {
            this.requestFrameId = requestAnimationFrame(this.selfDrivenUpdateFunc);
            this.selfDrivenModeRunning = true;
        } else {
            throw new Error('Cannot start viewer unless it is in self driven mode.');
        }
    }

    /**
     * Stop self-driven mode
     */
    stop() {
        if (this.selfDrivenMode && this.selfDrivenModeRunning) {
            cancelAnimationFrame(this.requestFrameId);
            this.selfDrivenModeRunning = false;
        }
    }

    /**
     * Dispose of all resources held directly and indirectly by this viewer.
     */
    async dispose() {
        if (this.isDisposingOrDisposed()) return this.disposePromise;

        let waitPromises = [];
        let promisesToAbort = [];
        for (let promiseKey in this.splatSceneDownloadPromises) {
            if (this.splatSceneDownloadPromises.hasOwnProperty(promiseKey)) {
                const downloadPromiseToAbort = this.splatSceneDownloadPromises[promiseKey];
                promisesToAbort.push(downloadPromiseToAbort);
                waitPromises.push(downloadPromiseToAbort.promise);
            }
        }
        // Collect all in-flight sort promises from all entries
        for (const [, entry] of this.splatMeshes) {
            if (entry.sortPromise) {
                waitPromises.push(entry.sortPromise);
            }
        }

        this.disposing = true;

        // Reject and flush any pending lazy load queue items
        for (const item of this.lazyLoadQueue) {
            item.reject(new Error('Viewer disposed'));
        }
        this.lazyLoadQueue.length = 0;

        this.disposePromise = Promise.all(waitPromises).finally(() => {
            this.stop();
            if (this.controls) {
                this.controls.dispose();
                this.controls = null;
            }

            // Dispose all SplatMeshEntries
            for (const [, entry] of this.splatMeshes) {
                this.disposeSortWorkerForEntry(entry);
                entry.splatMesh.dispose();
            }
            this.splatMeshes.clear();
            this.activeSplatMeshId = null;

            if (this.resizeObserver) {
                this.resizeObserver.unobserve(this.rootElement);
                this.resizeObserver = null;
            }
            this.disposeParseWorker();
            this.removeEventHandlers();

            // Null URL first so any in-flight callbacks see a mismatch and exit before touching the scene.
            this.currentEnvironmentMapUrl = null;
            if (this.envMapLoader) {
                this.envMapLoader.dispose();
                this.envMapLoader = null;
            }
            if (this.currentEnvMapRenderTarget) {
                this.currentEnvMapRenderTarget.dispose();
                this.currentEnvMapRenderTarget = null;
            }
            if (this.currentEnvMapTexture) {
                this.currentEnvMapTexture.dispose();
                this.currentEnvMapTexture = null;
            }

            // Clean up lazy loading state
            if (this.lazyLoadSegments) {
                this.lazyLoadSegments.clear();
                this.lazyLoadSegments = null;
            }

            this.loadingSpinner.removeAllTasks();
            this.loadingSpinner.setContainer(null);
            this.loadingProgressBar.hide();
            this.loadingProgressBar.setContainer(null);
            this.infoPanel.setContainer(null);

            this.camera = null;
            this.threeScene = null;
            this.initialized = false;
            if (this.renderer) {
                if (!this.usingExternalRenderer) {
                    this.rootElement.removeChild(this.renderer.domElement);
                    this.renderer.dispose();
                }
                this.renderer = null;
            }

            if (this.createdRootElement) {
                document.body.removeChild(this.rootElement);
            }

            this.disposed = true;
            this.disposing = false;
            this.disposePromise = null;

            if (this.positionLogInterval) {
                clearInterval(this.positionLogInterval);
                this.positionLogInterval = null;
            }
        });
        promisesToAbort.forEach((toAbort) => {
            toAbort.abort('Scene disposed');
        });
        return this.disposePromise;
    }

    selfDrivenUpdate() {
        if (this.selfDrivenMode) {
            this.requestFrameId = requestAnimationFrame(this.selfDrivenUpdateFunc);
        }
        this.update();
        if (this.shouldRender()) {
            this.render();
            this.consecutiveRenderFrames++;
        } else {
            this.consecutiveRenderFrames = 0;
        }
        this.renderNextFrame = false;
    }

    forceRenderNextFrame() {
        this.renderNextFrame = true;
    }

    // On-demand screenshot capture without preserveDrawingBuffer. Forces a single render,
    // reads the framebuffer synchronously before the GPU can clear it, and returns a JPEG data URL.
    captureFrame(maxWidth = 1280) {
        if (
            !this.initialized ||
            this.isDisposingOrDisposed() ||
            !this.renderer ||
            !this.renderer.domElement
        ) return null;

        if (!this.activeEntry || !this.activeEntry.splatRenderReady) return null;

        const safeMaxWidth = Number.isFinite(maxWidth) && maxWidth > 0 ? maxWidth : 1280;

        this.render();

        const canvas = this.renderer.domElement;
        if (canvas.width === 0 || canvas.height === 0) return null;

        const scale = Math.min(1, safeMaxWidth / canvas.width);
        const offscreen = document.createElement('canvas');
        offscreen.width = Math.round(canvas.width * scale);
        offscreen.height = Math.round(canvas.height * scale);

        const ctx = offscreen.getContext('2d');
        if (!ctx) return null;

        ctx.drawImage(canvas, 0, 0, offscreen.width, offscreen.height);
        return offscreen.toDataURL('image/jpeg', 0.7);
    }

    shouldRender = (function () {
        let renderCount = 0;
        const lastCameraPosition = new THREE.Vector3();
        const lastCameraOrientation = new THREE.Quaternion();
        const changeEpsilon = 0.0001;

        return function () {
            const entry = this.activeEntry;
            if (!this.initialized || !entry || !entry.splatRenderReady || this.isDisposingOrDisposed()) return false;

            let shouldRender = false;
            let cameraChanged = false;
            if (this.camera) {
                const cp = this.camera.position;
                const co = this.camera.quaternion;
                cameraChanged =
                    Math.abs(cp.x - lastCameraPosition.x) > changeEpsilon ||
                    Math.abs(cp.y - lastCameraPosition.y) > changeEpsilon ||
                    Math.abs(cp.z - lastCameraPosition.z) > changeEpsilon ||
                    Math.abs(co.x - lastCameraOrientation.x) > changeEpsilon ||
                    Math.abs(co.y - lastCameraOrientation.y) > changeEpsilon ||
                    Math.abs(co.z - lastCameraOrientation.z) > changeEpsilon ||
                    Math.abs(co.w - lastCameraOrientation.w) > changeEpsilon;
            }

            shouldRender =
                this.renderMode !== RenderMode.Never &&
                (renderCount === 0 ||
                    entry.splatMesh.visibleRegionChanging ||
                    cameraChanged ||
                    this.renderMode === RenderMode.Always ||
                    this.renderNextFrame);

            if (this.camera) {
                lastCameraPosition.copy(this.camera.position);
                lastCameraOrientation.copy(this.camera.quaternion);
            }

            renderCount++;
            return shouldRender;
        };
    })();

    render = (function () {
        const renderDimensions = new THREE.Vector2();

        return function () {
            const entry = this.activeEntry;
            if (!this.initialized || !entry || !entry.splatRenderReady || this.isDisposingOrDisposed()) return;

            const savedAutoClear = this.renderer.autoClear;

            this.getRenderDimensions(renderDimensions);

            if (this.sceneHelper.hasPOIMarkers()) {
                // POI pipeline: depth capture + standard color rendering
                this.sceneHelper.updateDepthTargetForRenderDimensions(renderDimensions.x, renderDimensions.y);
                const depthTarget = this.sceneHelper.getDepthTarget();

                // Pass 1: Render splats to depth target (depth accumulation only)
                entry.splatMesh.material.uniforms.depthPass.value = 1;
                this.renderer.setRenderTarget(depthTarget);
                this.renderer.setClearColor(0x000000, 0);
                this.renderer.clear(true, false, false);
                this.renderer.render(entry.splatMesh, this.camera);
                entry.splatMesh.material.uniforms.depthPass.value = 0;

                // Pass 2: Render background scene to main framebuffer
                this.renderer.setRenderTarget(null);
                this.renderer.render(this.threeScene, this.camera);

                // Pass 3: Render splats to main framebuffer (standard blending with background)
                this.renderer.autoClear = false;
                this.renderer.render(entry.splatMesh, this.camera);

                // Pass 4: Write accumulated depth to main depth buffer (depth-only, no color)
                this.sceneHelper.updateDepthCompositingUniforms();
                this.renderer.render(
                    this.sceneHelper.getDepthCompositingQuad(),
                    this.sceneHelper.getDepthCompositingCamera()
                );

                // Pass 5: Render focus marker (always on top, no depth test)
                if (this.sceneHelper.getFocusMarkerOpacity() > 0.0) {
                    this.renderer.render(this.sceneHelper.focusMarker, this.camera);
                }

                // Pass 6: Render POI markers (depth-tested against splat depth)
                this.renderer.render(this.sceneHelper.getPOIMarkerGroup(), this.camera);
            } else {
                // Standard pipeline: No POI markers, no depth capture overhead
                this.renderer.render(this.threeScene, this.camera);
                this.renderer.autoClear = false;
                this.renderer.render(entry.splatMesh, this.camera);
                this.renderer.autoClear = false;
                if (this.sceneHelper.getFocusMarkerOpacity() > 0.0) {
                    this.renderer.render(this.sceneHelper.focusMarker, this.camera);
                }
            }

            this.renderer.autoClear = savedAutoClear;
        };
    })();

    update() {
        if (!this.initialized || this.isDisposingOrDisposed()) return;

        // Update controls first so camera position is current
        if (this.controls) {
            const delta = this.clock.getDelta();
            this.controls.update(delta);
        }

        // Update camera velocity tracking for ETA-based segment loading
        if (this.etaBasedLoading) this.updateCameraVelocity();

        // Check lazy load segments even before splatRenderReady,
        // so manifest-based loading can initiate scene loading
        this.checkLazyLoadSegments();

        // Select which mesh to render based on camera position
        this.updateSceneVisibilityFromCamera();

        const entry = this.activeEntry;
        if (!entry || !entry.splatRenderReady) return;

        this.runSplatSort(entry);
        this.updateForRendererSizeChanges();
        this.timingSensitiveUpdates();
        this.updateSplatMesh(entry);
        this.updateMeshCursor();
        this.updatePOIMarkers();
        this.updateFPS();
        this.updateInfoPanel();
    }

    updatePOIMarkers() {
        this.sceneHelper.updatePOIMarkers();
    }

    updateFPS = (function () {
        let lastCalcTime = getCurrentTime();
        let frameCount = 0;

        return function () {
            if (this.consecutiveRenderFrames > CONSECUTIVE_RENDERED_FRAMES_FOR_FPS_CALCULATION) {
                const currentTime = getCurrentTime();
                const calcDelta = currentTime - lastCalcTime;
                if (calcDelta >= 1.0) {
                    this.currentFPS = frameCount;
                    frameCount = 0;
                    lastCalcTime = currentTime;
                } else {
                    frameCount++;
                }
            } else {
                this.currentFPS = null;
            }
        };
    })();

    updateForRendererSizeChanges = (function () {
        const lastRendererSize = new THREE.Vector2();
        const currentRendererSize = new THREE.Vector2();

        return function () {
            this.renderer.getSize(currentRendererSize);
            if (currentRendererSize.x !== lastRendererSize.x || currentRendererSize.y !== lastRendererSize.y) {
                this.camera.aspect = currentRendererSize.x / currentRendererSize.y;
                this.camera.updateProjectionMatrix();
                lastRendererSize.copy(currentRendererSize);
            }
        };
    })();

    timingSensitiveUpdates = (function () {
        let lastUpdateTime;

        return function () {
            const currentTime = getCurrentTime();
            if (!lastUpdateTime) lastUpdateTime = currentTime;
            const timeDelta = currentTime - lastUpdateTime;

            this.updateCameraTransition(currentTime);
            this.updateFocusMarker(timeDelta);

            lastUpdateTime = currentTime;
        };
    })();

    updateCameraTransition = (function () {
        let tempCameraPosition = new THREE.Vector3();
        let tempCameraRotation = new THREE.Quaternion();
        return function (currentTime) {
            if (this.transitioningCameraTarget) {
                let t = 0;

                // If the transitionCameraSpeed is set, use it to calculate the transition time
                if (this.transitionCameraSpeed > 0) {
                    const distance = this.previousCamera.position.distanceTo(this.nextCamera.position);
                    if (distance > 0) {
                        t = ((currentTime - this.transitioningCameraTargetStartTime) * this.transitionCameraSpeed) / distance;
                    } else {
                        t = 1.0; // If the distance is zero, we are already at the target
                    }
                } else {
                    // Fallback to a fixed transition duration
                    t = (currentTime - this.transitioningCameraTargetStartTime) / TRANSITION_DURATION;
                }
                // Clamp t to [0, 1]
                if (t > 1.0) t = 1.0;
                if (t < 0.0) t = 0.0;
                tempCameraPosition.copy(this.previousCamera.position).lerp(this.nextCamera.position, t);
                tempCameraRotation.copy(this.previousCamera.quaternion).slerp(this.nextCamera.quaternion, t);
                this.camera.position.copy(tempCameraPosition);
                this.camera.quaternion.copy(tempCameraRotation);

                this.camera.updateMatrixWorld();
                this.controls.updateLookDirection();
                if (t >= 1.0) {
                    this.transitioningCameraTarget = false;
                    this.clickTransitioning = false;
                }
            }
        };
    })();

    updateFocusMarker = (function () {
        const renderDimensions = new THREE.Vector2();
        let wasTransitioning = false;

        return function (timeDelta) {
            this.getRenderDimensions(renderDimensions);
            if (this.transitioningCameraTarget && this.clickTransitioning) {
                this.sceneHelper.setFocusMarkerVisibility(true);
                const currentFocusMarkerOpacity = Math.max(this.sceneHelper.getFocusMarkerOpacity(), 0.0);
                let newFocusMarkerOpacity = Math.min(currentFocusMarkerOpacity + FOCUS_MARKER_FADE_IN_SPEED * timeDelta, 1.0);
                this.sceneHelper.setFocusMarkerOpacity(newFocusMarkerOpacity);
                this.sceneHelper.updateFocusMarker(this.clickPosition, this.camera, renderDimensions);
                wasTransitioning = true;
                this.forceRenderNextFrame();
            } else {
                let currentFocusMarkerOpacity;
                if (wasTransitioning) currentFocusMarkerOpacity = 1.0;
                else currentFocusMarkerOpacity = Math.min(this.sceneHelper.getFocusMarkerOpacity(), 1.0);
                if (currentFocusMarkerOpacity > 0) {
                    this.sceneHelper.updateFocusMarker(this.clickPosition, this.camera, renderDimensions);
                    let newFocusMarkerOpacity = Math.max(currentFocusMarkerOpacity - FOCUS_MARKER_FADE_OUT_SPEED * timeDelta, 0.0);
                    this.sceneHelper.setFocusMarkerOpacity(newFocusMarkerOpacity);
                    if (newFocusMarkerOpacity === 0.0) this.sceneHelper.setFocusMarkerVisibility(false);
                }
                if (currentFocusMarkerOpacity > 0.0) this.forceRenderNextFrame();
                wasTransitioning = false;
            }
        };
    })();

    /**
     * Update the smoothed camera velocity vector using exponential moving average.
     */
    updateCameraVelocity = (function () {
        const instantVelocity = new THREE.Vector3();

        return function () {
            if (!this.camera) return;

            const now = performance.now() / 1000.0;

            if (!this.cameraVelocityInitialized) {
                this.prevCameraPosition.copy(this.camera.position);
                this.prevVelocityUpdateTime = now;
                this.cameraVelocityInitialized = true;
                return;
            }

            const dt = now - this.prevVelocityUpdateTime;
            // Guard against zero or negative time deltas (e.g., same-frame or non-monotonic timestamps)
            if (dt <= 0) return;

            const alpha = Constants.CameraVelocitySmoothingFactor;
            instantVelocity.subVectors(this.camera.position, this.prevCameraPosition).divideScalar(dt);
            this.cameraVelocity.lerp(instantVelocity, alpha);

            this.prevCameraPosition.copy(this.camera.position);
            this.prevVelocityUpdateTime = now;
        };
    })();

    /**
     * Check lazy load segments and trigger loading.
     * When etaBasedLoading is enabled, segments are loaded based on estimated time of arrival.
     * Otherwise, segments are loaded when the camera enters their preload bounding box (original behavior).
     * This can be called even before splatRenderReady is true, allowing manifest-based loading to work.
     */
    checkLazyLoadSegments = (function () {
        const cameraPosition = new THREE.Vector3();
        const scratch = new THREE.Vector3();
        const warnedSegments = new Set();

        return function () {
            if (!this.lazyLoadSegments || !this.camera) return;

            cameraPosition.copy(this.camera.position);

            if (this.etaBasedLoading) {
                const etaThreshold = Constants.SegmentLoadETAThreshold;
                const candidates = [];
                for (const segment of this.lazyLoadSegments.values()) {
                    if (segment.status === SegmentStatus.Pending) {
                        const eta = segment.computeETA(cameraPosition, this.cameraVelocity, scratch);
                        if (eta <= etaThreshold) {
                            candidates.push(segment);
                        }
                    } else if (segment.shouldUnload(cameraPosition)) {
                        console.log(`Unloading lazy segment ${segment.index}...`);
                        this.unloadLazySegment(segment.index).catch((error) => {
                            console.warn(`Failed to unload segment ${segment.index}:`, error);
                        });
                    }

                    this._checkSegmentVisibilityWarning(segment, cameraPosition, warnedSegments);
                }

                // Precompute whether the camera is inside each candidate's bounds
                const insideFlags = new Map();
                for (const segment of candidates) {
                    insideFlags.set(segment.index, segment.viewBoundingBox.containsPoint(cameraPosition));
                }

                // Sort candidates: segments the camera is inside first, then by ETA, then distance
                candidates.sort((a, b) => {
                    const aInside = insideFlags.get(a.index) ? 0 : 1;
                    const bInside = insideFlags.get(b.index) ? 0 : 1;
                    return aInside - bInside || a.eta - b.eta || a.distanceToCenter - b.distanceToCenter;
                });
                for (const segment of candidates) {
                    console.log(`Loading lazy segment ${segment.index} (ETA: ${segment.eta.toFixed(1)}s)...`);
                    this.loadLazySegment(segment.index, insideFlags.get(segment.index))
                        .catch((error) => {
                            console.warn(`Failed to load segment ${segment.index}:`, error);
                        });
                }
            } else {
                for (const segment of this.lazyLoadSegments.values()) {
                    if (segment.shouldLoad(cameraPosition)) {
                        console.log(`Loading lazy segment ${segment.index}...`);
                        this.loadLazySegment(segment.index).catch((error) => {
                            console.warn(`Failed to load segment ${segment.index}:`, error);
                        });
                    } else if (segment.shouldUnload(cameraPosition)) {
                        console.log(`Unloading lazy segment ${segment.index}...`);
                        this.unloadLazySegment(segment.index).catch((error) => {
                            console.warn(`Failed to unload segment ${segment.index}:`, error);
                        });
                    }

                    this._checkSegmentVisibilityWarning(segment, cameraPosition, warnedSegments);
                }
            }
        };
    })();

    /**
     * Warn once when the camera is inside a segment's view bounds but the segment hasn't loaded yet.
     * @param {Segment} segment
     * @param {THREE.Vector3} cameraPosition
     * @param {Set} warnedSegments Tracks which segments have already had the warning issued this episode
     */
    _checkSegmentVisibilityWarning(segment, cameraPosition, warnedSegments) {
        const isWaiting = segment.status === SegmentStatus.Queued || segment.status === SegmentStatus.Loading;
        if (isWaiting && segment.viewBoundingBox.containsPoint(cameraPosition)) {
            if (!warnedSegments.has(segment.index)) {
                if (this.logLevel >= LogLevel.Warning) {
                    console.warn(
                        `Segment ${segment.index} is visible but still ${segment.status} — ` +
                        'other segments may be blocking the queue.'
                    );
                }
                warnedSegments.add(segment.index);
                if (this.onSegmentLoadingStart) this.onSegmentLoadingStart(segment.index);
                this._prioritizeVisibleSegment(segment);
            }
        } else {
            if (warnedSegments.has(segment.index)) {
                warnedSegments.delete(segment.index);
                if (this.onSegmentLoadingEnd) this.onSegmentLoadingEnd(segment.index);
            }
        }
    }

    /**
     * When the camera is inside a segment that is still queued, mark it as prioritized
     * so the queue processor picks it next regardless of ETA ordering.
     * Called only once per visibility episode.
     * @param {Segment} segment The segment the camera is currently inside
     */
    _prioritizeVisibleSegment(segment) {
        if (segment.status !== SegmentStatus.Queued) return;

        const queueItem = this.lazyLoadQueue.find(
            (item) => item.type === 'load' && item.segmentIndex === segment.index
        );
        if (queueItem) {
            queueItem.prioritized = true;
        }
    }

    switchActiveMesh(newId) {
        if (this.activeSplatMeshId === newId) return;
        this.activeSplatMeshId = newId;
        const entry = this.activeEntry;
        if (entry && entry.splatRenderReady) {
            this.runSplatSort(entry, true, true);
        }
        if (this.onSplatMeshChangedCallback) this.onSplatMeshChangedCallback();
        this.forceRenderNextFrame();
    }

    updateSceneVisibilityFromCamera = (function () {
        const cameraPosition = new THREE.Vector3();

        return function () {
            if (this.splatMeshes.size <= 1) return;

            cameraPosition.copy(this.camera.position);

            let bestEntryId = null;
            for (const [id, entry] of this.splatMeshes) {
                if (!entry.viewBoundingBox) continue;
                if (entry.viewBoundingBox.containsPoint(cameraPosition)) {
                    bestEntryId = id;

                    // Prefer the first matching entry
                    break;
                }
            }

            if (bestEntryId !== null && bestEntryId !== this.activeSplatMeshId) {
                this.switchActiveMesh(bestEntryId);
            }
        };
    })();

    updateMeshCursor = (function () {
        const outHits = [];
        const renderDimensions = new THREE.Vector2();

        return function () {
            if (this.showMeshCursor) {
                this.forceRenderNextFrame();
                this.getRenderDimensions(renderDimensions);
                outHits.length = 0;
                this.raycaster.setFromCameraAndScreenPosition(this.camera, this.mousePosition, renderDimensions);
                if (this.splatMesh) this.raycaster.intersectSplatMesh(this.splatMesh, outHits);
                if (outHits.length > 0) {
                    this.sceneHelper.setMeshCursorVisibility(true);
                    this.sceneHelper.positionAndOrientMeshCursor(outHits[0].origin, this.camera);
                } else {
                    this.sceneHelper.setMeshCursorVisibility(false);
                }
            } else {
                if (this.sceneHelper.getMeschCursorVisibility()) this.forceRenderNextFrame();
                this.sceneHelper.setMeshCursorVisibility(false);
            }
        };
    })();

    updateInfoPanel = (function () {
        return function () {
            if (!this.showInfo) return;
            const entry = this.activeEntry;
            const splatCount = entry ? entry.splatMesh.getSplatCount() : 0;
            const splatRenderCount = entry ? entry.splatRenderCount : 0;
            const splatRenderCountPct = splatCount > 0 ? (splatRenderCount / splatCount) * 100 : 0;
            this.infoPanel.update(
                this.currentFPS || 'N/A',
                splatCount,
                splatRenderCount,
                splatRenderCountPct,
                this.lastSortTime,
                this.lazyLoadSegments,
                this.camera ? this.camera.position : null
            );
        };
    })();

    runSplatSort = (function () {
        const mvpMatrix = new THREE.Matrix4();
        const cameraPositionArray = [];
        const lastSortViewDir = new THREE.Vector3(1, 0, 0);
        const sortViewDir = new THREE.Vector3(1, 0, 0);
        const lastSortViewPos = new THREE.Vector3();
        const sortViewOffset = new THREE.Vector3();
        const queuedSorts = [];

        const partialSorts = [
            {
                angleThreshold: 0.55,
                sortFractions: [0.125, 0.33333, 0.75]
            },
            {
                angleThreshold: 0.65,
                sortFractions: [0.33333, 0.66667]
            },
            {
                angleThreshold: 0.8,
                sortFractions: [0.5]
            }
        ];

        return function (entry, force = false, forceSortAll = false) {
            if (!entry) entry = this.activeEntry;
            if (!this.initialized || !entry || !entry.sortWorker) return Promise.resolve(false);
            if (entry.sortRunning) return Promise.resolve(true);
            if (entry.splatMesh.getSplatCount() <= 0) {
                entry.splatRenderCount = 0;
                return Promise.resolve(false);
            }

            let angleDiff = 0;
            let positionDiff = 0;
            let needsRefreshForRotation = false;
            let needsRefreshForPosition = false;

            sortViewDir.set(0, 0, -1).applyQuaternion(this.camera.quaternion);
            angleDiff = sortViewDir.dot(lastSortViewDir);
            positionDiff = sortViewOffset.copy(this.camera.position).sub(lastSortViewPos).length();

            if (!force) {
                if (queuedSorts.length === 0) {
                    if (angleDiff <= 0.99) needsRefreshForRotation = true;
                    if (positionDiff >= 1.0) needsRefreshForPosition = true;
                    if (!needsRefreshForRotation && !needsRefreshForPosition) return Promise.resolve(false);
                }
            }

            entry.sortRunning = true;
            let { splatRenderCount, shouldSortAll } = this.gatherSceneNodesForSort(entry);
            shouldSortAll = shouldSortAll || forceSortAll;
            entry.splatRenderCount = splatRenderCount;

            mvpMatrix.copy(this.camera.matrixWorld).invert();
            const mvpCamera = this.perspectiveCamera || this.camera;
            mvpMatrix.premultiply(mvpCamera.projectionMatrix);
            mvpMatrix.multiply(entry.splatMesh.matrixWorld);

            let gpuAcceleratedSortPromise = Promise.resolve(true);
            if (this.gpuAcceleratedSort && (queuedSorts.length <= 1 || queuedSorts.length % 2 === 0)) {
                gpuAcceleratedSortPromise = entry.splatMesh.computeDistancesOnGPU(mvpMatrix, entry.sortWorkerPrecomputedDistances);
            }

            gpuAcceleratedSortPromise.then(() => {
                if (queuedSorts.length === 0) {
                    if (shouldSortAll) {
                        queuedSorts.push(entry.splatRenderCount);
                    } else {
                        for (let partialSort of partialSorts) {
                            if (angleDiff < partialSort.angleThreshold) {
                                for (let sortFraction of partialSort.sortFractions) {
                                    queuedSorts.push(Math.floor(entry.splatRenderCount * sortFraction));
                                }
                                break;
                            }
                        }
                        queuedSorts.push(entry.splatRenderCount);
                    }
                }
                let sortCount = Math.min(queuedSorts.shift(), entry.splatRenderCount);
                entry.splatSortCount = sortCount;

                cameraPositionArray[0] = this.camera.position.x;
                cameraPositionArray[1] = this.camera.position.y;
                cameraPositionArray[2] = this.camera.position.z;

                const sortMessage = {
                    modelViewProj: mvpMatrix.elements,
                    cameraPosition: cameraPositionArray,
                    splatRenderCount: entry.splatRenderCount,
                    splatSortCount: sortCount,
                    usePrecomputedDistances: this.gpuAcceleratedSort
                };
                if (!this.sharedMemoryForWorkers) {
                    sortMessage.indexesToSort = entry.sortWorkerIndexesToSort;
                    sortMessage.transforms = entry.sortWorkerTransforms;
                    if (this.gpuAcceleratedSort) {
                        sortMessage.precomputedDistances = entry.sortWorkerPrecomputedDistances;
                    }
                }

                entry.sortPromise = new Promise((resolve) => {
                    entry.sortPromiseResolver = resolve;
                });

                if (entry.preSortMessages.length > 0) {
                    entry.preSortMessages.forEach((message) => {
                        const transferList = [];
                        if (message.centers) transferList.push(message.centers);
                        if (message.sceneIndexes) transferList.push(message.sceneIndexes);
                        entry.sortWorker.postMessage(message, transferList);
                    });
                    entry.preSortMessages = [];
                }
                entry.sortWorker.postMessage({
                    sort: sortMessage
                });

                if (queuedSorts.length === 0) {
                    lastSortViewPos.copy(this.camera.position);
                    lastSortViewDir.copy(sortViewDir);
                }

                return true;
            });

            return gpuAcceleratedSortPromise;
        };
    })();

    /**
     * Determine which splats to render by checking which are inside or close to the view frustum
     */
    gatherSceneNodesForSort = (function () {
        const nodeRenderList = [];
        let allSplatsSortBuffer = null;
        const tempVectorYZ = new THREE.Vector3();
        const tempVectorXZ = new THREE.Vector3();
        const tempVector = new THREE.Vector3();
        const modelView = new THREE.Matrix4();
        const baseModelView = new THREE.Matrix4();
        const renderDimensions = new THREE.Vector2();
        const forward = new THREE.Vector3(0, 0, -1);

        const tempMax = new THREE.Vector3();
        const nodeSize = (node) => {
            return tempMax.copy(node.max).sub(node.min).length();
        };

        return function (entry, gatherAllNodes = false) {
            const splatMesh = entry.splatMesh;
            this.getRenderDimensions(renderDimensions);
            const cameraFocalLength = renderDimensions.y / 2.0 / Math.tan((this.camera.fov / 2.0) * THREE.MathUtils.DEG2RAD);
            const fovXOver2 = Math.atan(renderDimensions.x / 2.0 / cameraFocalLength);
            const fovYOver2 = Math.atan(renderDimensions.y / 2.0 / cameraFocalLength);
            const cosFovXOver2 = Math.cos(fovXOver2);
            const cosFovYOver2 = Math.cos(fovYOver2);

            const splatTree = splatMesh.getSplatTree();

            if (splatTree) {
                baseModelView.copy(this.camera.matrixWorld).invert();
                baseModelView.multiply(splatMesh.matrixWorld);

                let nodeRenderCount = 0;
                let splatRenderCount = 0;

                for (let s = 0; s < splatTree.subTrees.length; s++) {
                    const subTree = splatTree.subTrees[s];
                    modelView.copy(baseModelView);
                    const nodeCount = subTree.nodesWithIndexes.length;
                    for (let i = 0; i < nodeCount; i++) {
                        const node = subTree.nodesWithIndexes[i];
                        if (!node.data || !node.data.indexes || node.data.indexes.length === 0) continue;
                        tempVector.copy(node.center).applyMatrix4(modelView);

                        const distanceToNode = tempVector.length();
                        tempVector.normalize();

                        tempVectorYZ.copy(tempVector).setX(0).normalize();
                        tempVectorXZ.copy(tempVector).setY(0).normalize();

                        const cameraAngleXZDot = forward.dot(tempVectorXZ);
                        const cameraAngleYZDot = forward.dot(tempVectorYZ);

                        const ns = nodeSize(node);
                        const outOfFovY = cameraAngleYZDot < cosFovYOver2 - 0.6;
                        const outOfFovX = cameraAngleXZDot < cosFovXOver2 - 0.6;
                        if (!gatherAllNodes && (outOfFovX || outOfFovY) && distanceToNode > ns) {
                            continue;
                        }
                        splatRenderCount += node.data.indexes.length;
                        nodeRenderList[nodeRenderCount] = node;
                        node.data.distanceToNode = distanceToNode;
                        nodeRenderCount++;
                    }
                }

                nodeRenderList.length = nodeRenderCount;
                nodeRenderList.sort((a, b) => {
                    if (a.data.distanceToNode < b.data.distanceToNode) return -1;
                    else return 1;
                });

                let currentByteOffset = splatRenderCount * Constants.BytesPerInt;
                for (let i = 0; i < nodeRenderCount; i++) {
                    const node = nodeRenderList[i];
                    const windowSizeInts = node.data.indexes.length;
                    const windowSizeBytes = windowSizeInts * Constants.BytesPerInt;
                    let destView = new Uint32Array(
                        entry.sortWorkerIndexesToSort.buffer,
                        currentByteOffset - windowSizeBytes,
                        windowSizeInts
                    );
                    destView.set(node.data.indexes);
                    currentByteOffset -= windowSizeBytes;
                }

                return {
                    splatRenderCount: splatRenderCount,
                    shouldSortAll: false
                };
            } else {
                const totalSplatCount = splatMesh.getSplatCount();
                if (!allSplatsSortBuffer || allSplatsSortBuffer.length !== totalSplatCount) {
                    allSplatsSortBuffer = new Uint32Array(totalSplatCount);
                    for (let i = 0; i < totalSplatCount; i++) {
                        allSplatsSortBuffer[i] = i;
                    }
                }
                entry.sortWorkerIndexesToSort.set(allSplatsSortBuffer);
                return {
                    splatRenderCount: totalSplatCount,
                    shouldSortAll: true
                };
            }
        };
    })();

    getSplatMesh() {
        return this.splatMesh;
    }

    /**
     * Get a reference to a splat scene.
     * @param {number} sceneIndex The index of the scene (maps to mesh entry in insertion order)
     * @return {SplatScene}
     */
    getSplatScene(sceneIndex) {
        const entries = Array.from(this.splatMeshes.values());
        if (sceneIndex < entries.length) {
            return entries[sceneIndex].splatMesh.getScene(0);
        }
        return null;
    }

    getSceneCount() {
        return this.splatMeshes.size;
    }

    isMobile() {
        return navigator.userAgent.includes('Mobi');
    }

    /**
     * Add a POI (Point of Interest) marker at a 3D position
     * @param {Object} options
     * @param {number[]} options.position - World position [x, y, z] in Y-up coordinates
     * @param {number[]} options.geoPosition - Geodetic position [latitude, longitude, altitude]
     * @param {THREE.Texture|string} options.texture - Texture or URL to load
     * @param {number} [options.scale=1.0] - Base scale multiplier
     * @param {Object} [options.userData] - Custom user data
     * @returns {POIMarker} The created marker
     */
    addPOIMarker(options) {
        if (!options || !options.texture || (!options.position && !options.geoPosition)) {
            console.warn('addPOIMarker requires position or geoPosition and texture options');
            return null;
        }

        if (!options.position && !!options.geoPosition) {
            if (!this.observerCoordinates) {
                console.warn('Cannot add POI marker with geoPosition without observerCoordinates set on viewer');
                return null;
            }
            const [lat, lon, alt] = options.geoPosition;
            const enu = geodeticToENU(lat, lon, alt, this.observerCoordinates[0], this.observerCoordinates[1], this.observerCoordinates[2]);
            // ENU is Z-up (East=X, North=Y, Up=Z), Three.js is Y-up
            const enuPosition = new THREE.Vector3(enu.east, enu.north, enu.up);
            const worldTransform = new THREE.Quaternion(0.5, 0.5, 0.5, -0.5);
            enuPosition.applyQuaternion(worldTransform);
            options.position = enuPosition;
        } else {
            options.position = new THREE.Vector3(options.position[0], options.position[1], options.position[2]);
        }

        return this.sceneHelper.addPOIMarker(options);
    }

    /**
     * Remove a POI marker
     * @param {POIMarker} marker - The marker to remove
     */
    removePOIMarker(marker) {
        this.sceneHelper.removePOIMarker(marker);
    }

    /**
     * Remove all POI markers
     */
    clearPOIMarkers() {
        this.sceneHelper.clearPOIMarkers();
    }

    /**
     * Debug: Read back the MRT depth accumulation texture and download as PNG.
     * Call from console: viewer.debugSaveDepthAccum()
     */
    debugSaveDepthAccum() {
        const depthTarget = this.sceneHelper.getDepthTarget();
        if (!depthTarget) {
            console.warn('No depth target available');
            return;
        }
        const w = depthTarget.width;
        const h = depthTarget.height;
        const buffer = new Float32Array(w * h * 4);

        const gl = this.renderer.getContext();
        this.renderer.setRenderTarget(depthTarget);
        gl.readPixels(0, 0, w, h, gl.RGBA, gl.FLOAT, buffer);
        this.renderer.setRenderTarget(null);

        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        const imageData = ctx.createImageData(w, h);

        for (let i = 0; i < w * h; i++) {
            const depth = buffer[i * 4];
            const row = h - 1 - Math.floor(i / w);
            const col = i % w;
            const j = (row * w + col) * 4;
            const v = Math.min(255, Math.max(0, depth * 255));
            imageData.data[j] = v;
            imageData.data[j + 1] = v;
            imageData.data[j + 2] = v;
            imageData.data[j + 3] = 255;
        }

        ctx.putImageData(imageData, 0, 0);
        const link = document.createElement('a');
        link.download = 'depth_accum_debug.png';
        link.href = canvas.toDataURL();
        link.click();
    }

    /**
     * Gets the camera position in geodetic coordinates (latitude, longitude, altitude) and heading (compass direction) if observer coordinates are set.
     * @returns {{lat: number, lon: number, alt: number, heading: number | null} | null} An object containing latitude, longitude, altitude, and heading in degrees.
     * Heading is null when camera is looking straight up or down (degenerate case). Returns null if observer coordinates or camera are not set.
     */
    getCameraGeodeticPosition() {
        if (!this.camera) return null;
        if (!this.observerCoordinates) return null;

        const worldTransform = new THREE.Quaternion(0.5, 0.5, 0.5, -0.5);
        const inverseWorldTransform = worldTransform.clone().invert();

        // Convert camera position from Three.js Y-up to ENU Z-up
        const enuPosition = new THREE.Vector3();
        this.camera.getWorldPosition(enuPosition);
        enuPosition.applyQuaternion(inverseWorldTransform);

        const { lat, lon, alt } = ENUToGeodetic(
            enuPosition.x,
            enuPosition.y,
            enuPosition.z,
            this.observerCoordinates[0],
            this.observerCoordinates[1],
            this.observerCoordinates[2]
        );

        // Get camera forward direction (camera looks along -Z in local space)
        const forward = new THREE.Vector3();
        this.camera.getWorldDirection(forward);

        // Transform forward direction from Three.js Y-up to ENU Z-up
        forward.applyQuaternion(inverseWorldTransform);

        // Calculate heading from forward direction in ENU
        // In ENU: North is +Y, East is +X
        // Heading θ: forward = (sin(θ), cos(θ), 0)
        // So θ = atan2(forward.x, forward.y)
        const horizontalMagnitude = Math.sqrt(forward.x * forward.x + forward.y * forward.y);

        let heading = null;
        if (horizontalMagnitude > 1e-6) {
            // Normalize horizontal components for stable atan2
            const headingRad = Math.atan2(forward.x / horizontalMagnitude, forward.y / horizontalMagnitude);
            heading = (headingRad * 180.0) / Math.PI;

            // Normalize to 0-360 range
            if (heading < 0) {
                heading += 360;
            }
        }

        return { lat, lon, alt, heading };
    }

    /**
     * Teleport the camera to a new position given by XYZ or lat/lon/alt.
     *
     * Without lazy loading: moves the camera immediately.
     * With lazy loading: aborts the current load, drops the pending queue, loads the segment
     * at the target location (if not already loaded), calls the optional callback once loading
     * is complete, then moves the camera.
     *
     * @param {Object} options
     * @param {number[]} [options.position] World position [x, y, z]
     * @param {number[]} [options.geoPosition] Geodetic position [latitude, longitude, altitude]
     * @param {Function} [options.callback] Optional async/sync callback called after segment
     *   loading completes and before the camera is moved. Awaited if it returns a Promise.
     * @returns {Promise<void>} Resolves when the teleport is complete
     */
    async teleportTo(options = {}) {
        // --- Resolve target world position ---
        let targetPosition;
        if (options.position) {
            targetPosition = new THREE.Vector3(...options.position);
        } else if (options.geoPosition) {
            if (!this.observerCoordinates) {
                throw new Error('teleportTo: geoPosition requires observerCoordinates to be set on the viewer');
            }
            const [lat, lon, alt] = options.geoPosition;
            const enu = geodeticToENU(
                lat, lon, alt,
                this.observerCoordinates[0],
                this.observerCoordinates[1],
                this.observerCoordinates[2]
            );
            // ENU is Z-up (East=X, North=Y, Up=Z); Three.js is Y-up
            targetPosition = new THREE.Vector3(enu.east, enu.north, enu.up);
            targetPosition.applyQuaternion(new THREE.Quaternion(0.5, 0.5, 0.5, -0.5));
        } else {
            throw new Error('teleportTo: requires position or geoPosition');
        }

        if (!this.lazyLoadSegments) {
            // No lazy loading — just call the callback then move the camera
            if (options.callback) await options.callback();
            this._applyCameraPosition(targetPosition);
            return;
        }

        // --- Drop pending queue items and reset their segment statuses ---
        for (const item of this.lazyLoadQueue) {
            if (item.type === 'load') {
                const seg = this.lazyLoadSegments.get(item.segmentIndex);
                if (seg && seg.status === SegmentStatus.Queued) seg.markPending();
            } else if (item.type === 'unload') {
                const seg = this.lazyLoadSegments.get(item.segmentIndex);
                if (seg && seg.status === SegmentStatus.QueuedUnload) seg.markLoaded(seg.sceneIndex);
            }
            item.reject(new Error('Teleport'));
        }
        this.lazyLoadQueue.length = 0;

        // --- Abort the in-flight fetch (if any) ---
        if (this.currentLazyFetchAbortable) {
            this.currentLazyFetchAbortable.abort('Teleport');
            this.currentLazyFetchAbortable = null;
        }

        // --- Find the segment that contains the target position ---
        let targetSegment = null;
        for (const segment of this.lazyLoadSegments.values()) {
            if (segment.viewBoundingBox.containsPoint(targetPosition)) {
                targetSegment = segment;
                break;
            }
        }

        // --- Load the target segment if it is not already loaded ---
        if (targetSegment && targetSegment.status !== SegmentStatus.Loaded) {
            // After aborting, a Loading segment's async catch hasn't run yet —
            // force it back to Pending so loadLazySegment can re-queue it.
            if (targetSegment.status === SegmentStatus.Loading) {
                targetSegment.markPending();
            }
            if (targetSegment.status === SegmentStatus.Pending) {
                await this.loadLazySegment(targetSegment.index);
            }
        }

        // --- Callback before moving the camera ---
        if (options.callback) await options.callback();

        // --- Move the camera ---
        this._applyCameraPosition(targetPosition);
    }

    /**
     * Immediately place the camera at the given world position.
     * Cancels any ongoing camera transition and resets velocity tracking.
     * @param {THREE.Vector3} position Target world position
     */
    _applyCameraPosition(position) {
        this.camera.position.copy(position);
        this.camera.updateMatrixWorld();
        if (this.controls) this.controls.updateLookDirection();

        // Cancel any in-progress animated transition
        this.transitioningCameraTarget = false;
        this.clickTransitioning = false;

        // Reset velocity tracking so sudden position jump doesn't skew ETA predictions
        this.prevCameraPosition.copy(position);
        this.cameraVelocityInitialized = false;
    }
}

/*
Copyright © 2010-2024 three.js authors & Mark Kellogg

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in
all copies or substantial portions of the Software.
*/


// OrbitControls performs orbiting, dollying (zooming), and panning.
// Unlike TrackballControls, it maintains the "up" direction object.up (+Y by default).
//
//    Orbit - left mouse / touch: one-finger move
//    Zoom - middle mouse, or mousewheel / touch: two-finger spread or squish
//    Pan - right mouse, or left mouse + ctrl/meta/shiftKey, or arrow keys / touch: two-finger move

const _changeEvent = { type: 'change' };
const _startEvent = { type: 'start' };
const _endEvent = { type: 'end' };
const _ray = new Ray$1();
const _plane = new Plane();
const TILT_LIMIT = Math.cos(70 * MathUtils.DEG2RAD);

class OrbitControls extends EventDispatcher {
    constructor(object, domElement) {
        super();

        this.object = object;
        this.domElement = domElement;
        this.domElement.style.touchAction = 'none'; // disable touch scroll

        // Set to false to disable this control
        this.enabled = true;

        // "target" sets the location of focus, where the object orbits around
        this.target = new Vector3();

        // How far you can dolly in and out ( PerspectiveCamera only )
        this.minDistance = 0;
        this.maxDistance = Infinity;

        // How far you can orbit vertically, upper and lower limits.
        // Range is 0 to Math.PI radians.
        this.minPolarAngle = 0; // radians
        this.maxPolarAngle = Math.PI; // radians

        // How far you can orbit horizontally, upper and lower limits.
        // If set, the interval [min, max] must be a sub-interval of [- 2 PI, 2 PI], with ( max - min < 2 PI )
        this.minAzimuthAngle = -Infinity; // radians
        this.maxAzimuthAngle = Infinity; // radians

        // Set to true to enable damping (inertia)
        // If damping is enabled, you must call controls.update() in your animation loop
        this.enableDamping = false;
        this.dampingFactor = 0.05;

        // This option actually enables dollying in and out; left as "zoom" for backwards compatibility.
        // Set to false to disable zooming
        this.enableZoom = true;
        this.zoomSpeed = 1.0;

        // Set to false to disable rotating
        this.enableRotate = true;
        this.rotateSpeed = 1.0;

        // Set to false to disable panning
        this.enablePan = true;
        this.panSpeed = 1.0;
        this.screenSpacePanning = true; // if false, pan orthogonal to world-space direction camera.up
        this.keyPanSpeed = 7.0; // pixels moved per arrow key push
        this.zoomToCursor = false;

        // Set to true to automatically rotate around the target
        // If auto-rotate is enabled, you must call controls.update() in your animation loop
        this.autoRotate = false;
        this.autoRotateSpeed = 2.0; // 30 seconds per orbit when fps is 60

        // The four arrow keys
        this.keys = { LEFT: 'KeyA', UP: 'KeyW', RIGHT: 'KeyD', BOTTOM: 'KeyS' };

        // Mouse buttons
        this.mouseButtons = {
            LEFT: MOUSE.ROTATE,
            MIDDLE: MOUSE.DOLLY,
            RIGHT: MOUSE.PAN
        };

        // Touch fingers
        this.touches = { ONE: TOUCH.ROTATE, TWO: TOUCH.DOLLY_PAN };

        // for reset
        this.target0 = this.target.clone();
        this.position0 = this.object.position.clone();
        this.zoom0 = this.object.zoom;

        // the target DOM element for key events
        this._domElementKeyEvents = null;

        //
        // public methods
        //

        this.getPolarAngle = function () {
            return spherical.phi;
        };

        this.getAzimuthalAngle = function () {
            return spherical.theta;
        };

        this.getDistance = function () {
            return this.object.position.distanceTo(this.target);
        };

        this.listenToKeyEvents = function (domElement) {
            domElement.addEventListener('keydown', onKeyDown);
            this._domElementKeyEvents = domElement;
        };

        this.stopListenToKeyEvents = function () {
            this._domElementKeyEvents.removeEventListener('keydown', onKeyDown);
            this._domElementKeyEvents = null;
        };

        this.saveState = function () {
            scope.target0.copy(scope.target);
            scope.position0.copy(scope.object.position);
            scope.zoom0 = scope.object.zoom;
        };

        this.reset = function () {
            scope.target.copy(scope.target0);
            scope.object.position.copy(scope.position0);
            scope.object.zoom = scope.zoom0;
            this.clearDampedRotation();
            this.clearDampedPan();

            scope.object.updateProjectionMatrix();
            scope.dispatchEvent(_changeEvent);

            scope.update();

            state = STATE.NONE;
        };

        this.clearDampedRotation = function () {
            sphericalDelta.theta = 0.0;
            sphericalDelta.phi = 0.0;
        };

        this.clearDampedPan = function () {
            panOffset.set(0, 0, 0);
        };

        // this method is exposed, but perhaps it would be better if we can make it private...
        this.update = (function () {
            const offset = new Vector3();

            // so camera.up is the orbit axis
            const quat = new Quaternion().setFromUnitVectors(object.up, new Vector3(0, 1, 0));
            const quatInverse = quat.clone().invert();

            const lastPosition = new Vector3();
            const lastQuaternion = new Quaternion();
            const lastTargetPosition = new Vector3();

            const twoPI = 2 * Math.PI;

            return function update() {
                quat.setFromUnitVectors(object.up, new Vector3(0, 1, 0));
                quatInverse.copy(quat).invert();

                const position = scope.object.position;

                offset.copy(position).sub(scope.target);

                // rotate offset to "y-axis-is-up" space
                offset.applyQuaternion(quat);

                // angle from z-axis around y-axis
                spherical.setFromVector3(offset);

                if (scope.autoRotate && state === STATE.NONE) {
                    rotateLeft(getAutoRotationAngle());
                }

                if (scope.enableDamping) {
                    spherical.theta += sphericalDelta.theta * scope.dampingFactor;
                    spherical.phi += sphericalDelta.phi * scope.dampingFactor;
                } else {
                    spherical.theta += sphericalDelta.theta;
                    spherical.phi += sphericalDelta.phi;
                }

                // restrict theta to be between desired limits

                let min = scope.minAzimuthAngle;
                let max = scope.maxAzimuthAngle;

                if (isFinite(min) && isFinite(max)) {
                    if (min < -Math.PI) min += twoPI;
                    else if (min > Math.PI) min -= twoPI;

                    if (max < -Math.PI) max += twoPI;
                    else if (max > Math.PI) max -= twoPI;

                    if (min <= max) {
                        spherical.theta = Math.max(min, Math.min(max, spherical.theta));
                    } else {
                        spherical.theta =
                            spherical.theta > (min + max) / 2 ? Math.max(min, spherical.theta) : Math.min(max, spherical.theta);
                    }
                }

                // restrict phi to be between desired limits
                spherical.phi = Math.max(scope.minPolarAngle, Math.min(scope.maxPolarAngle, spherical.phi));

                spherical.makeSafe();

                // move target to panned location

                if (scope.enableDamping === true) {
                    scope.target.addScaledVector(panOffset, scope.dampingFactor);
                } else {
                    scope.target.add(panOffset);
                }

                // adjust the camera position based on zoom only if we're not zooming to the cursor
                // we adjust zoom later in these cases
                if (scope.zoomToCursor && performCursorZoom) {
                    spherical.radius = clampDistance(spherical.radius);
                } else {
                    spherical.radius = clampDistance(spherical.radius * scale);
                }

                offset.setFromSpherical(spherical);

                // rotate offset back to "camera-up-vector-is-up" space
                offset.applyQuaternion(quatInverse);

                position.copy(scope.target).add(offset);

                scope.object.lookAt(scope.target);

                if (scope.enableDamping === true) {
                    sphericalDelta.theta *= 1 - scope.dampingFactor;
                    sphericalDelta.phi *= 1 - scope.dampingFactor;

                    panOffset.multiplyScalar(1 - scope.dampingFactor);
                } else {
                    sphericalDelta.set(0, 0, 0);

                    panOffset.set(0, 0, 0);
                }

                // adjust camera position
                let zoomChanged = false;
                if (scope.zoomToCursor && performCursorZoom) {
                    let newRadius = null;
                    if (scope.object.isPerspectiveCamera) {
                        // move the camera down the pointer ray
                        // this method avoids floating point error
                        const prevRadius = offset.length();
                        newRadius = clampDistance(prevRadius * scale);

                        const radiusDelta = prevRadius - newRadius;
                        scope.object.position.addScaledVector(dollyDirection, radiusDelta);
                        scope.object.updateMatrixWorld();
                    } else {
                        console.warn('WARNING: OrbitControls.js encountered an unknown camera type - zoom to cursor disabled.');
                        scope.zoomToCursor = false;
                    }

                    // handle the placement of the target
                    if (newRadius !== null) {
                        if (this.screenSpacePanning) {
                            // position the orbit target in front of the new camera position
                            scope.target
                                .set(0, 0, -1)
                                .transformDirection(scope.object.matrix)
                                .multiplyScalar(newRadius)
                                .add(scope.object.position);
                        } else {
                            // get the ray and translation plane to compute target
                            _ray.origin.copy(scope.object.position);
                            _ray.direction.set(0, 0, -1).transformDirection(scope.object.matrix);

                            // if the camera is 20 degrees above the horizon then don't adjust the focus target to avoid
                            // extremely large values
                            if (Math.abs(scope.object.up.dot(_ray.direction)) < TILT_LIMIT) {
                                object.lookAt(scope.target);
                            } else {
                                _plane.setFromNormalAndCoplanarPoint(scope.object.up, scope.target);
                                _ray.intersectPlane(_plane, scope.target);
                            }
                        }
                    }
                }

                scale = 1;
                performCursorZoom = false;

                // update condition is:
                // min(camera displacement, camera rotation in radians)^2 > EPS
                // using small-angle approximation cos(x/2) = 1 - x^2 / 8

                if (
                    zoomChanged ||
                    lastPosition.distanceToSquared(scope.object.position) > EPS ||
                    8 * (1 - lastQuaternion.dot(scope.object.quaternion)) > EPS ||
                    lastTargetPosition.distanceToSquared(scope.target) > 0
                ) {
                    scope.dispatchEvent(_changeEvent);

                    lastPosition.copy(scope.object.position);
                    lastQuaternion.copy(scope.object.quaternion);
                    lastTargetPosition.copy(scope.target);

                    zoomChanged = false;

                    return true;
                }

                return false;
            };
        })();

        this.dispose = function () {
            scope.domElement.removeEventListener('contextmenu', onContextMenu);

            scope.domElement.removeEventListener('pointerdown', onPointerDown);
            scope.domElement.removeEventListener('pointercancel', onPointerUp);
            scope.domElement.removeEventListener('wheel', onMouseWheel);

            scope.domElement.removeEventListener('pointermove', onPointerMove);
            scope.domElement.removeEventListener('pointerup', onPointerUp);

            if (scope._domElementKeyEvents !== null) {
                scope._domElementKeyEvents.removeEventListener('keydown', onKeyDown);
                scope._domElementKeyEvents = null;
            }
        };

        //
        // internals
        //

        const scope = this;

        const STATE = {
            NONE: -1,
            ROTATE: 0,
            DOLLY: 1,
            PAN: 2,
            TOUCH_ROTATE: 3,
            TOUCH_PAN: 4,
            TOUCH_DOLLY_PAN: 5,
            TOUCH_DOLLY_ROTATE: 6
        };

        let state = STATE.NONE;

        const EPS = 0.000001;

        // current position in spherical coordinates
        const spherical = new Spherical();
        const sphericalDelta = new Spherical();

        let scale = 1;
        const panOffset = new Vector3();

        const rotateStart = new Vector2();
        const rotateEnd = new Vector2();
        const rotateDelta = new Vector2();

        const panStart = new Vector2();
        const panEnd = new Vector2();
        const panDelta = new Vector2();

        const dollyStart = new Vector2();
        const dollyEnd = new Vector2();
        const dollyDelta = new Vector2();

        const dollyDirection = new Vector3();
        const mouse = new Vector2();
        let performCursorZoom = false;

        const pointers = [];
        const pointerPositions = {};

        function getAutoRotationAngle() {
            return ((2 * Math.PI) / 60 / 60) * scope.autoRotateSpeed;
        }

        function getZoomScale() {
            return Math.pow(0.95, scope.zoomSpeed);
        }

        function rotateLeft(angle) {
            sphericalDelta.theta -= angle;
        }

        function rotateUp(angle) {
            sphericalDelta.phi -= angle;
        }

        const panLeft = (function () {
            const v = new Vector3();

            return function panLeft(distance, objectMatrix) {
                v.setFromMatrixColumn(objectMatrix, 0); // get X column of objectMatrix
                v.multiplyScalar(-distance);

                panOffset.add(v);
            };
        })();

        const panUp = (function () {
            const v = new Vector3();

            return function panUp(distance, objectMatrix) {
                if (scope.screenSpacePanning === true) {
                    v.setFromMatrixColumn(objectMatrix, 1);
                } else {
                    v.setFromMatrixColumn(objectMatrix, 0);
                    v.crossVectors(scope.object.up, v);
                }

                v.multiplyScalar(distance);

                panOffset.add(v);
            };
        })();

        // deltaX and deltaY are in pixels; right and down are positive
        const pan = (function () {
            const offset = new Vector3();

            return function pan(deltaX, deltaY) {
                const element = scope.domElement;

                if (scope.object.isPerspectiveCamera) {
                    // perspective
                    const position = scope.object.position;
                    offset.copy(position).sub(scope.target);
                    let targetDistance = offset.length();

                    // half of the fov is center to top of screen
                    targetDistance *= Math.tan(((scope.object.fov / 2) * Math.PI) / 180.0);

                    // we use only clientHeight here so aspect ratio does not distort speed
                    panLeft((2 * deltaX * targetDistance) / element.clientHeight, scope.object.matrix);
                    panUp((2 * deltaY * targetDistance) / element.clientHeight, scope.object.matrix);
                } else {
                    // camera not perspective
                    console.warn('WARNING: OrbitControls.js encountered an unknown camera type - pan disabled.');
                    scope.enablePan = false;
                }
            };
        })();

        function dollyOut(dollyScale) {
            if (scope.object.isPerspectiveCamera) {
                scale /= dollyScale;
            } else {
                console.warn('WARNING: OrbitControls.js encountered an unknown camera type - dolly/zoom disabled.');
                scope.enableZoom = false;
            }
        }

        function dollyIn(dollyScale) {
            if (scope.object.isPerspectiveCamera) {
                scale *= dollyScale;
            } else {
                console.warn('WARNING: OrbitControls.js encountered an unknown camera type - dolly/zoom disabled.');
                scope.enableZoom = false;
            }
        }

        function updateMouseParameters(event) {
            if (!scope.zoomToCursor) {
                return;
            }

            performCursorZoom = true;

            const rect = scope.domElement.getBoundingClientRect();
            const x = event.clientX - rect.left;
            const y = event.clientY - rect.top;
            const w = rect.width;
            const h = rect.height;

            mouse.x = (x / w) * 2 - 1;
            mouse.y = -(y / h) * 2 + 1;

            dollyDirection.set(mouse.x, mouse.y, 1).unproject(object).sub(object.position).normalize();
        }

        function clampDistance(dist) {
            return Math.max(scope.minDistance, Math.min(scope.maxDistance, dist));
        }

        //
        // event callbacks - update the object state
        //

        function handleMouseDownRotate(event) {
            rotateStart.set(event.clientX, event.clientY);
        }

        function handleMouseDownDolly(event) {
            updateMouseParameters(event);
            dollyStart.set(event.clientX, event.clientY);
        }

        function handleMouseDownPan(event) {
            panStart.set(event.clientX, event.clientY);
        }

        function handleMouseMoveRotate(event) {
            rotateEnd.set(event.clientX, event.clientY);

            rotateDelta.subVectors(rotateEnd, rotateStart).multiplyScalar(scope.rotateSpeed);

            const element = scope.domElement;

            rotateLeft((2 * Math.PI * rotateDelta.x) / element.clientHeight); // yes, height

            rotateUp((2 * Math.PI * rotateDelta.y) / element.clientHeight);

            rotateStart.copy(rotateEnd);

            scope.update();
        }

        function handleMouseMoveDolly(event) {
            dollyEnd.set(event.clientX, event.clientY);

            dollyDelta.subVectors(dollyEnd, dollyStart);

            if (dollyDelta.y > 0) {
                dollyOut(getZoomScale());
            } else if (dollyDelta.y < 0) {
                dollyIn(getZoomScale());
            }

            dollyStart.copy(dollyEnd);

            scope.update();
        }

        function handleMouseMovePan(event) {
            panEnd.set(event.clientX, event.clientY);

            panDelta.subVectors(panEnd, panStart).multiplyScalar(scope.panSpeed);

            pan(panDelta.x, panDelta.y);

            panStart.copy(panEnd);

            scope.update();
        }

        function handleMouseWheel(event) {
            updateMouseParameters(event);

            if (event.deltaY < 0) {
                dollyIn(getZoomScale());
            } else if (event.deltaY > 0) {
                dollyOut(getZoomScale());
            }

            scope.update();
        }

        function handleKeyDown(event) {
            let needsUpdate = false;

            switch (event.code) {
                case scope.keys.UP:
                    if (event.ctrlKey || event.metaKey || event.shiftKey) {
                        rotateUp((2 * Math.PI * scope.rotateSpeed) / scope.domElement.clientHeight);
                    } else {
                        pan(0, scope.keyPanSpeed);
                    }

                    needsUpdate = true;
                    break;

                case scope.keys.BOTTOM:
                    if (event.ctrlKey || event.metaKey || event.shiftKey) {
                        rotateUp((-2 * Math.PI * scope.rotateSpeed) / scope.domElement.clientHeight);
                    } else {
                        pan(0, -scope.keyPanSpeed);
                    }

                    needsUpdate = true;
                    break;

                case scope.keys.LEFT:
                    if (event.ctrlKey || event.metaKey || event.shiftKey) {
                        rotateLeft((2 * Math.PI * scope.rotateSpeed) / scope.domElement.clientHeight);
                    } else {
                        pan(scope.keyPanSpeed, 0);
                    }

                    needsUpdate = true;
                    break;

                case scope.keys.RIGHT:
                    if (event.ctrlKey || event.metaKey || event.shiftKey) {
                        rotateLeft((-2 * Math.PI * scope.rotateSpeed) / scope.domElement.clientHeight);
                    } else {
                        pan(-scope.keyPanSpeed, 0);
                    }

                    needsUpdate = true;
                    break;
            }

            if (needsUpdate) {
                // prevent the browser from scrolling on cursor keys
                event.preventDefault();

                scope.update();
            }
        }

        function handleTouchStartRotate() {
            if (pointers.length === 1) {
                rotateStart.set(pointers[0].pageX, pointers[0].pageY);
            } else {
                const x = 0.5 * (pointers[0].pageX + pointers[1].pageX);
                const y = 0.5 * (pointers[0].pageY + pointers[1].pageY);

                rotateStart.set(x, y);
            }
        }

        function handleTouchStartPan() {
            if (pointers.length === 1) {
                panStart.set(pointers[0].pageX, pointers[0].pageY);
            } else {
                const x = 0.5 * (pointers[0].pageX + pointers[1].pageX);
                const y = 0.5 * (pointers[0].pageY + pointers[1].pageY);

                panStart.set(x, y);
            }
        }

        function handleTouchStartDolly() {
            const dx = pointers[0].pageX - pointers[1].pageX;
            const dy = pointers[0].pageY - pointers[1].pageY;

            const distance = Math.sqrt(dx * dx + dy * dy);

            dollyStart.set(0, distance);
        }

        function handleTouchStartDollyPan() {
            if (scope.enableZoom) handleTouchStartDolly();

            if (scope.enablePan) handleTouchStartPan();
        }

        function handleTouchStartDollyRotate() {
            if (scope.enableZoom) handleTouchStartDolly();

            if (scope.enableRotate) handleTouchStartRotate();
        }

        function handleTouchMoveRotate(event) {
            if (pointers.length == 1) {
                rotateEnd.set(event.pageX, event.pageY);
            } else {
                const position = getSecondPointerPosition(event);

                const x = 0.5 * (event.pageX + position.x);
                const y = 0.5 * (event.pageY + position.y);

                rotateEnd.set(x, y);
            }

            rotateDelta.subVectors(rotateEnd, rotateStart).multiplyScalar(scope.rotateSpeed);

            const element = scope.domElement;

            rotateLeft((2 * Math.PI * rotateDelta.x) / element.clientHeight); // yes, height

            rotateUp((2 * Math.PI * rotateDelta.y) / element.clientHeight);

            rotateStart.copy(rotateEnd);
        }

        function handleTouchMovePan(event) {
            if (pointers.length === 1) {
                panEnd.set(event.pageX, event.pageY);
            } else {
                const position = getSecondPointerPosition(event);

                const x = 0.5 * (event.pageX + position.x);
                const y = 0.5 * (event.pageY + position.y);

                panEnd.set(x, y);
            }

            panDelta.subVectors(panEnd, panStart).multiplyScalar(scope.panSpeed);

            pan(panDelta.x, panDelta.y);

            panStart.copy(panEnd);
        }

        function handleTouchMoveDolly(event) {
            const position = getSecondPointerPosition(event);

            const dx = event.pageX - position.x;
            const dy = event.pageY - position.y;

            const distance = Math.sqrt(dx * dx + dy * dy);

            dollyEnd.set(0, distance);

            dollyDelta.set(0, Math.pow(dollyEnd.y / dollyStart.y, scope.zoomSpeed));

            dollyOut(dollyDelta.y);

            dollyStart.copy(dollyEnd);
        }

        function handleTouchMoveDollyPan(event) {
            if (scope.enableZoom) handleTouchMoveDolly(event);

            if (scope.enablePan) handleTouchMovePan(event);
        }

        function handleTouchMoveDollyRotate(event) {
            if (scope.enableZoom) handleTouchMoveDolly(event);

            if (scope.enableRotate) handleTouchMoveRotate(event);
        }

        //
        // event handlers - FSM: listen for events and reset state
        //

        function onPointerDown(event) {
            if (scope.enabled === false) return;

            if (pointers.length === 0) {
                scope.domElement.setPointerCapture(event.pointerId);

                scope.domElement.addEventListener('pointermove', onPointerMove);
                scope.domElement.addEventListener('pointerup', onPointerUp);
            }

            //

            addPointer(event);

            if (event.pointerType === 'touch') {
                onTouchStart(event);
            } else {
                onMouseDown(event);
            }
        }

        function onPointerMove(event) {
            if (scope.enabled === false) return;

            if (event.pointerType === 'touch') {
                onTouchMove(event);
            } else {
                onMouseMove(event);
            }
        }

        function onPointerUp(event) {
            removePointer(event);

            if (pointers.length === 0) {
                scope.domElement.releasePointerCapture(event.pointerId);

                scope.domElement.removeEventListener('pointermove', onPointerMove);
                scope.domElement.removeEventListener('pointerup', onPointerUp);
            }

            scope.dispatchEvent(_endEvent);

            state = STATE.NONE;
        }

        function onMouseDown(event) {
            let mouseAction;

            switch (event.button) {
                case 0:
                    mouseAction = scope.mouseButtons.LEFT;
                    break;

                case 1:
                    mouseAction = scope.mouseButtons.MIDDLE;
                    break;

                case 2:
                    mouseAction = scope.mouseButtons.RIGHT;
                    break;

                default:
                    mouseAction = -1;
            }

            switch (mouseAction) {
                case MOUSE.DOLLY:
                    if (scope.enableZoom === false) return;

                    handleMouseDownDolly(event);

                    state = STATE.DOLLY;

                    break;

                case MOUSE.ROTATE:
                    if (event.ctrlKey || event.metaKey || event.shiftKey) {
                        if (scope.enablePan === false) return;

                        handleMouseDownPan(event);

                        state = STATE.PAN;
                    } else {
                        if (scope.enableRotate === false) return;

                        handleMouseDownRotate(event);

                        state = STATE.ROTATE;
                    }

                    break;

                case MOUSE.PAN:
                    if (event.ctrlKey || event.metaKey || event.shiftKey) {
                        if (scope.enableRotate === false) return;

                        handleMouseDownRotate(event);

                        state = STATE.ROTATE;
                    } else {
                        if (scope.enablePan === false) return;

                        handleMouseDownPan(event);

                        state = STATE.PAN;
                    }

                    break;

                default:
                    state = STATE.NONE;
            }

            if (state !== STATE.NONE) {
                scope.dispatchEvent(_startEvent);
            }
        }

        function onMouseMove(event) {
            switch (state) {
                case STATE.ROTATE:
                    if (scope.enableRotate === false) return;

                    handleMouseMoveRotate(event);

                    break;

                case STATE.DOLLY:
                    if (scope.enableZoom === false) return;

                    handleMouseMoveDolly(event);

                    break;

                case STATE.PAN:
                    if (scope.enablePan === false) return;

                    handleMouseMovePan(event);

                    break;
            }
        }

        function onMouseWheel(event) {
            if (scope.enabled === false || scope.enableZoom === false || state !== STATE.NONE) return;

            event.preventDefault();

            scope.dispatchEvent(_startEvent);

            handleMouseWheel(event);

            scope.dispatchEvent(_endEvent);
        }

        function onKeyDown(event) {
            if (scope.enabled === false || scope.enablePan === false) return;

            handleKeyDown(event);
        }

        function onTouchStart(event) {
            trackPointer(event);

            switch (pointers.length) {
                case 1:
                    switch (scope.touches.ONE) {
                        case TOUCH.ROTATE:
                            if (scope.enableRotate === false) return;

                            handleTouchStartRotate();

                            state = STATE.TOUCH_ROTATE;

                            break;

                        case TOUCH.PAN:
                            if (scope.enablePan === false) return;

                            handleTouchStartPan();

                            state = STATE.TOUCH_PAN;

                            break;

                        default:
                            state = STATE.NONE;
                    }

                    break;

                case 2:
                    switch (scope.touches.TWO) {
                        case TOUCH.DOLLY_PAN:
                            if (scope.enableZoom === false && scope.enablePan === false) return;

                            handleTouchStartDollyPan();

                            state = STATE.TOUCH_DOLLY_PAN;

                            break;

                        case TOUCH.DOLLY_ROTATE:
                            if (scope.enableZoom === false && scope.enableRotate === false) return;

                            handleTouchStartDollyRotate();

                            state = STATE.TOUCH_DOLLY_ROTATE;

                            break;

                        default:
                            state = STATE.NONE;
                    }

                    break;

                default:
                    state = STATE.NONE;
            }

            if (state !== STATE.NONE) {
                scope.dispatchEvent(_startEvent);
            }
        }

        function onTouchMove(event) {
            trackPointer(event);

            switch (state) {
                case STATE.TOUCH_ROTATE:
                    if (scope.enableRotate === false) return;

                    handleTouchMoveRotate(event);

                    scope.update();

                    break;

                case STATE.TOUCH_PAN:
                    if (scope.enablePan === false) return;

                    handleTouchMovePan(event);

                    scope.update();

                    break;

                case STATE.TOUCH_DOLLY_PAN:
                    if (scope.enableZoom === false && scope.enablePan === false) return;

                    handleTouchMoveDollyPan(event);

                    scope.update();

                    break;

                case STATE.TOUCH_DOLLY_ROTATE:
                    if (scope.enableZoom === false && scope.enableRotate === false) return;

                    handleTouchMoveDollyRotate(event);

                    scope.update();

                    break;

                default:
                    state = STATE.NONE;
            }
        }

        function onContextMenu(event) {
            if (scope.enabled === false) return;

            event.preventDefault();
        }

        function addPointer(event) {
            pointers.push(event);
        }

        function removePointer(event) {
            delete pointerPositions[event.pointerId];

            for (let i = 0; i < pointers.length; i++) {
                if (pointers[i].pointerId == event.pointerId) {
                    pointers.splice(i, 1);
                    return;
                }
            }
        }

        function trackPointer(event) {
            let position = pointerPositions[event.pointerId];

            if (position === undefined) {
                position = new Vector2();
                pointerPositions[event.pointerId] = position;
            }

            position.set(event.pageX, event.pageY);
        }

        function getSecondPointerPosition(event) {
            const pointer = event.pointerId === pointers[0].pointerId ? pointers[1] : pointers[0];

            return pointerPositions[pointer.pointerId];
        }

        //

        scope.domElement.addEventListener('contextmenu', onContextMenu);

        scope.domElement.addEventListener('pointerdown', onPointerDown);
        scope.domElement.addEventListener('pointercancel', onPointerUp);
        scope.domElement.addEventListener('wheel', onMouseWheel, {
            passive: false
        });

        // force an update at start

        this.update();
    }
}

export { AbortablePromise, ENUToGeodetic, FirstPersonControls, GausLoader, ImagePosesLoader, LogLevel, OrbitControls, RenderMode, SceneRevealMode, Segment, SegmentStatus, SplatArray, Viewer, geodeticToENU };
//# sourceMappingURL=gaussian-splats-3d.module.js.map
